import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../context/ThemeContext';
import { useModal } from '../context/ModalDialogContext';
import { isDarkColor, resolveSolidColor } from '../utils/themeUtils';
import DesignStudio, { AssetManager } from './DesignStudio';
import DataManager from './DataManager';
import CoinVaultTab from './CoinVaultTab';
import CompanyManager from './CompanyManager';
import OverviewTab from './OverviewTab';
import AssetSelectorModal from './AssetSelectorModal';
import GradientModal from './GradientModal';
import MobileWebDesigner from './MobileWebDesigner';
import TenantMailSettingsTab from './TenantMailSettingsTab';
import AddressAutocompleteInput from './AddressAutocompleteInput';
import PaginationBar from './PaginationBar';
import AgentNetworkManager from './AgentNetworkManager';
import AgentPortal from './AgentPortal';
import Footer from './Footer';
import PackageArtShowcase from './PackageArtShowcase';
import SupportPage from './SupportPage';

const extractErrorMessage = (detail, fallback = 'An unexpected error occurred.') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(item => {
      if (typeof item === 'string') return item;
      if (item && item.msg) return item.msg;
      return JSON.stringify(item);
    }).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
};

function ColorOrGradientField({
  label,
  value,
  onChange,
  onOpenGradient,
  defaultSolid = '#ffffff'
}) {
  const isGradient = Boolean(value && value.includes('gradient'));
  const currentHex = resolveSolidColor(value, defaultSolid);

  return (
    <div style={{ flex: 1, minWidth: '240px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>{label}</label>
        {isGradient && (
          <span style={{
            fontSize: '10px',
            fontWeight: '600',
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: 'rgba(99, 102, 241, 0.15)',
            color: 'var(--primary-color)'
          }}>
            Gradient Active
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Interactive Preview Swatch */}
        <div
          onClick={onOpenGradient}
          title={isGradient ? "Click to edit gradient" : "Click to define gradient"}
          style={{
            width: '42px',
            height: '38px',
            borderRadius: '6px',
            border: isGradient ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
            background: value || defaultSolid,
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            flexShrink: 0
          }}
        />

        {/* Native solid color picker if not gradient */}
        {!isGradient && (
          <input
            type="color"
            value={currentHex}
            onChange={(e) => onChange(e.target.value)}
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              width: '42px',
              height: '38px',
              padding: '2px',
              cursor: 'pointer',
              backgroundColor: 'transparent'
            }}
            title="Pick solid color"
          />
        )}

        {/* Text Input for value */}
        <input
          type="text"
          data-testid={`color-field-input-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          value={value || ''}
          placeholder={defaultSolid}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1,
            minWidth: '110px',
            backgroundColor: 'var(--input-bg)',
            border: '1px solid var(--input-border)',
            borderRadius: '6px',
            padding: '9px 10px',
            color: 'var(--input-text)',
            fontSize: '13px'
          }}
          title={value || ''}
        />

        {/* Action Button: Gradient or Solid Toggle */}
        {isGradient ? (
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              type="button"
              onClick={onOpenGradient}
              style={{
                padding: '8px 10px',
                fontSize: '12px',
                fontWeight: '500',
                borderRadius: '6px',
                border: '1px solid var(--primary-color)',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                color: 'var(--text-main)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Edit gradient in gradient editor"
            >
              <span>🎨</span> Edit
            </button>
            <button
              type="button"
              onClick={() => onChange(currentHex)}
              style={{
                padding: '8px 8px',
                fontSize: '11px',
                fontWeight: '500',
                borderRadius: '6px',
                border: '1px solid var(--border-color)',
                backgroundColor: 'transparent',
                color: 'var(--text-mute)',
                cursor: 'pointer'
              }}
              title="Revert to solid color"
            >
              Solid
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onOpenGradient}
            style={{
              padding: '8px 10px',
              fontSize: '12px',
              fontWeight: '500',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
              backgroundColor: 'var(--input-bg)',
              color: 'var(--text-main)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Define a gradient for this color"
          >
            <span>🌈</span> Gradient
          </button>
        )}
      </div>
    </div>
  );
}

const PRODUCTION_STAGES = [
  { id: 1, name: 'Generate 3D Mesh', short: '3D Mesh', percent: 20 },
  { id: 2, name: 'Print Coins', short: 'Print Coins', percent: 40 },
  { id: 3, name: 'Write Coin URL to Chip', short: 'Write NFC', percent: 60 },
  { id: 4, name: 'Quality Control', short: 'QC Check', percent: 80 },
  { id: 5, name: 'Package', short: 'Package', percent: 100 },
];

export default function DashboardLayout({ setView, user, setUser, activeTab, setActiveTab }) {
  const { tenantConfig, refreshTheme } = useTheme();
  const modal = useModal();
  const isGlobalRole = ["GLOBAL_ADMIN", "GLOBAL_POWER", "GLOBAL_STANDARD"].includes(user?.role);

  // Ref to main scrollable workspace container
  const mainContentRef = useRef(null);

  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Navigation & view states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Current user's complete profile
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  // Create User form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newRole, setNewRole] = useState('END_USER');
  const [newTenantId, setNewTenantId] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [formError, setFormError] = useState('');

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL'); // 'ALL', 'TENANT_ADMIN', 'OPERATOR', 'AGENT', 'END_USER'
  const [tenants, setTenants] = useState([]);
  const [selectedTenantFilter, setSelectedTenantFilter] = useState('ALL'); // 'ALL', 'GLOBAL', or tenant_id (string)
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  
  // Edit User states
  const [editingUser, setEditingUser] = useState(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editRole, setEditRole] = useState('END_USER');
  const [editTenantId, setEditTenantId] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editTwoFactorOverride, setEditTwoFactorOverride] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editZip, setEditZip] = useState('');
  const [editCountry, setEditCountry] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  // Dedicated Reset Password states
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetMode, setResetMode] = useState('auto_email'); // 'auto_email' | 'manual'
  const [resetHidePassword, setResetHidePassword] = useState(true);
  const [resetSendEmail, setResetSendEmail] = useState(true);
  const [newResetPassword, setNewResetPassword] = useState('');
  const [resetRevealedPassword, setResetRevealedPassword] = useState('');
  const [resetCopied, setResetCopied] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Organisation States
  const [orgName, setOrgName] = useState('');
  const [orgWebsite, setOrgWebsite] = useState('');
  const [orgBookings, setOrgBookings] = useState('');
  const [orgEnquiriesEmail, setOrgEnquiriesEmail] = useState('');
  const [orgTwitter, setOrgTwitter] = useState('');
  const [orgFacebook, setOrgFacebook] = useState('');
  const [orgInstagram, setOrgInstagram] = useState('');
  const [orgBillingAddress, setOrgBillingAddress] = useState('');
  const [orgBillingCity, setOrgBillingCity] = useState('');
  const [orgBillingState, setOrgBillingState] = useState('');
  const [orgBillingZip, setOrgBillingZip] = useState('');
  const [orgBillingCountry, setOrgBillingCountry] = useState('');
  const [orgSubmitting, setOrgSubmitting] = useState(false);
  const [orgError, setOrgError] = useState('');
  const [orgSuccess, setOrgSuccess] = useState('');
  const [orgData, setOrgData] = useState(null);
  const [orgOperators, setOrgOperators] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [isEditingOrg, setIsEditingOrg] = useState(false);

  // Organisation Delivery Addresses states
  const [orgAddresses, setOrgAddresses] = useState([]);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [addrLabel, setAddrLabel] = useState('');
  const [addrRecipient, setAddrRecipient] = useState('');
  const [addrPhone, setAddrPhone] = useState('');
  const [addrStreet, setAddrStreet] = useState('');
  const [addrStreet2, setAddrStreet2] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrState, setAddrState] = useState('');
  const [addrZip, setAddrZip] = useState('');
  const [addrCountry, setAddrCountry] = useState('');
  const [addrCountryCode, setAddrCountryCode] = useState('');
  const [addrLat, setAddrLat] = useState(null);
  const [addrLng, setAddrLng] = useState(null);
  const [addrInstructions, setAddrInstructions] = useState('');
  const [addrSubmitting, setAddrSubmitting] = useState(false);
  const [addrError, setAddrError] = useState('');
  const [addrSuccess, setAddrSuccess] = useState('');
  const [orgLogoUrl, setOrgLogoUrl] = useState('');
  const [showAssetPicker, setShowAssetPicker] = useState(false);

  // Organisation Team Member Invitations states
  const [orgInvitations, setOrgInvitations] = useState([]);
  const [showOrgInviteModal, setShowOrgInviteModal] = useState(false);
  const [orgInviteEmail, setOrgInviteEmail] = useState('');
  const [orgInviteName, setOrgInviteName] = useState('');
  const [orgInviteRole, setOrgInviteRole] = useState('OPERATOR');
  const [orgInviteSubmitting, setOrgInviteSubmitting] = useState(false);
  const [orgInviteError, setOrgInviteError] = useState('');
  const [orgInviteSuccess, setOrgInviteSuccess] = useState('');
  const [orgInviteCreatedLink, setOrgInviteCreatedLink] = useState('');

  // Assigned Agent communication & QoS rating states for Operator
  const [showAgentChatModal, setShowAgentChatModal] = useState(false);
  const [agentChatMessages, setAgentChatMessages] = useState([]);
  const [agentChatNewMsg, setAgentChatNewMsg] = useState('');
  const [agentChatLoading, setAgentChatLoading] = useState(false);
  const [showAgentRatingModal, setShowAgentRatingModal] = useState(false);
  const [agentRatingScore, setAgentRatingScore] = useState(5);
  const [agentRatingFeedback, setAgentRatingFeedback] = useState('');
  const [agentRatingSubmitting, setAgentRatingSubmitting] = useState(false);
  const [agentRatingSuccess, setAgentRatingSuccess] = useState('');


  // Profile Edit states (for the current logged-in user tab)
  const [profFirstName, setProfFirstName] = useState('');
  const [profLastName, setProfLastName] = useState('');
  const [profPassword, setProfPassword] = useState('');
  const [profPhone, setProfPhone] = useState('');
  const [profCompany, setProfCompany] = useState('');
  const [profAddress, setProfAddress] = useState('');
  const [profCity, setProfCity] = useState('');
  const [profState, setProfState] = useState('');
  const [profZip, setProfZip] = useState('');
  const [profCountry, setProfCountry] = useState('');
  const [profLoading, setProfLoading] = useState(false);
  const [profTwoFactorLoading, setProfTwoFactorLoading] = useState(false);
  const [profError, setProfError] = useState('');
  const [profSuccessMsg, setProfSuccessMsg] = useState('');

  // Settings Form States
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [styleBrandName, setStyleBrandName] = useState('');
  const [stylePrimaryColor, setStylePrimaryColor] = useState('#bdb2ff');
  const [styleAccentColor, setStyleAccentColor] = useState('#a0c4ff');
  const [styleCardColor, setStyleCardColor] = useState('#ffffff');
  const [stylePanelColor, setStylePanelColor] = useState('#f8f9fa');
  const [styleMenuColor, setStyleMenuColor] = useState('#f8f9fa');
  const [styleHeaderColor, setStyleHeaderColor] = useState('#f8f9fa');
  const [styleTextMainColor, setStyleTextMainColor] = useState('#111827');
  const [styleTextMuteColor, setStyleTextMuteColor] = useState('#4b5563');
  const [styleInputBgColor, setStyleInputBgColor] = useState('#ffffff');
  const [styleDisabledColor, setStyleDisabledColor] = useState('#000000');
  const [styleToastBgColor, setStyleToastBgColor] = useState('');
  const [styleToastTextColor, setStyleToastTextColor] = useState('');
  const [styleButtonPrimaryTextColor, setStyleButtonPrimaryTextColor] = useState('');
  const [styleButtonSecondaryBg, setStyleButtonSecondaryBg] = useState('');
  const [styleButtonSecondaryText, setStyleButtonSecondaryText] = useState('');
  const [styleButtonSecondaryBorder, setStyleButtonSecondaryBorder] = useState('');
  const [styleTableHeaderBg, setStyleTableHeaderBg] = useState('');
  const [styleTableHeaderText, setStyleTableHeaderText] = useState('');
  const [styleTableRowSelected, setStyleTableRowSelected] = useState('');
  const [styleModalBgColor, setStyleModalBgColor] = useState('');
  const [styleModalBorderColor, setStyleModalBorderColor] = useState('');
  const [styleInputBorderColor, setStyleInputBorderColor] = useState('');
  const [styleInputTextColor, setStyleInputTextColor] = useState('');
  const [styleBorderColor, setStyleBorderColor] = useState('');
  const [styleBadgeBgColor, setStyleBadgeBgColor] = useState('');
  const [styleBadgeTextColor, setStyleBadgeTextColor] = useState('');
  const [styleFontFamily, setStyleFontFamily] = useState('');
  const [styleBorderRadius, setStyleBorderRadius] = useState('standard');
  const [styleLogoUrl, setStyleLogoUrl] = useState('');
  const [styleSiteIconUrl, setStyleSiteIconUrl] = useState('');
  const [styleLogoOffsetY, setStyleLogoOffsetY] = useState(0);
  const [styleLogoOffsetX, setStyleLogoOffsetX] = useState(0);
  const [styleLogoHeight, setStyleLogoHeight] = useState(40);
  const [styleBannerHeight, setStyleBannerHeight] = useState(70);
  const [styleBackgroundUrl, setStyleBackgroundUrl] = useState('');
  const [styleGeminiApiKey, setStyleGeminiApiKey] = useState('');
  const [styleOnboardingVideoUrl, setStyleOnboardingVideoUrl] = useState('');
  const [styleOnboardingVideoTitle, setStyleOnboardingVideoTitle] = useState('');
  const [styleSharePayoffLine, setStyleSharePayoffLine] = useState('');
  const [styleNoreplyEmail, setStyleNoreplyEmail] = useState('');
  const [styleEnforce2faRoles, setStyleEnforce2faRoles] = useState([]);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [testEmailError, setTestEmailError] = useState('');
  const [isTestEmailModalOpen, setIsTestEmailModalOpen] = useState(false);
  const [assetSelectorTarget, setAssetSelectorTarget] = useState(null); // 'logo' | 'site_icon' | 'background' | 'video' | null
  const [styleLoading, setStyleLoading] = useState(false);
  const [styleSuccess, setStyleSuccess] = useState('');
  const [styleError, setStyleError] = useState('');

  // Create New Tenant Modal States
  const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = useState(false);
  const [newTenantDomain, setNewTenantDomain] = useState('');
  const [newTenantBrand, setNewTenantBrand] = useState('');
  const [newTenantPrimaryColor, setNewTenantPrimaryColor] = useState('#bdb2ff');
  const [newTenantAccentColor, setNewTenantAccentColor] = useState('#a0c4ff');
  const [newTenantLogoUrl, setNewTenantLogoUrl] = useState('');
  const [newTenantNoreplyEmail, setNewTenantNoreplyEmail] = useState('');
  const [newTenantCreateAdmin, setNewTenantCreateAdmin] = useState(false);
  const [newTenantAdminEmail, setNewTenantAdminEmail] = useState('');
  const [newTenantAdminPassword, setNewTenantAdminPassword] = useState('');
  const [createTenantLoading, setCreateTenantLoading] = useState(false);
  const [createTenantError, setCreateTenantError] = useState('');

  // Domain Aliases States
  const [aliasInput, setAliasInput] = useState('');
  const [aliasLoading, setAliasLoading] = useState(false);
  const [aliasError, setAliasError] = useState('');
  const [aliasSuccess, setAliasSuccess] = useState('');

  // Gradient Selector Modal states
  const [gradientModalOpen, setGradientModalOpen] = useState(false);
  const [gradientModalTarget, setGradientModalTarget] = useState(null);

  const handleOpenGradientModal = (target) => {
    setGradientModalTarget(target);
    setGradientModalOpen(true);
  };

  const handleApplyGradient = (gradientCss) => {
    switch (gradientModalTarget) {
      case 'primary':
        setStylePrimaryColor(gradientCss);
        break;
      case 'accent':
        setStyleAccentColor(gradientCss);
        break;
      case 'card':
        setStyleCardColor(gradientCss);
        break;
      case 'panel':
        setStylePanelColor(gradientCss);
        break;
      case 'menu':
        setStyleMenuColor(gradientCss);
        break;
      case 'header':
        setStyleHeaderColor(gradientCss);
        break;
      case 'textMain':
        setStyleTextMainColor(gradientCss);
        break;
      case 'textMute':
        setStyleTextMuteColor(gradientCss);
        break;
      case 'input':
        setStyleInputBgColor(gradientCss);
        break;
      case 'disabled':
        setStyleDisabledColor(gradientCss);
        break;
      case 'toastBg':
        setStyleToastBgColor(gradientCss);
        break;
      case 'toastText':
        setStyleToastTextColor(gradientCss);
        break;
      case 'buttonPrimaryText':
        setStyleButtonPrimaryTextColor(gradientCss);
        break;
      case 'buttonSecondaryBg':
        setStyleButtonSecondaryBg(gradientCss);
        break;
      case 'buttonSecondaryText':
        setStyleButtonSecondaryText(gradientCss);
        break;
      case 'buttonSecondaryBorder':
        setStyleButtonSecondaryBorder(gradientCss);
        break;
      case 'tableHeaderBg':
        setStyleTableHeaderBg(gradientCss);
        break;
      case 'tableHeaderText':
        setStyleTableHeaderText(gradientCss);
        break;
      case 'tableRowSelected':
        setStyleTableRowSelected(gradientCss);
        break;
      case 'modalBg':
        setStyleModalBgColor(gradientCss);
        break;
      case 'modalBorder':
        setStyleModalBorderColor(gradientCss);
        break;
      case 'inputBorder':
        setStyleInputBorderColor(gradientCss);
        break;
      case 'inputText':
        setStyleInputTextColor(gradientCss);
        break;
      case 'border':
        setStyleBorderColor(gradientCss);
        break;
      case 'badgeBg':
        setStyleBadgeBgColor(gradientCss);
        break;
      case 'badgeText':
        setStyleBadgeTextColor(gradientCss);
        break;
      default:
        break;
    }
  };

  const getGradientModalCurrentValue = () => {
    switch (gradientModalTarget) {
      case 'primary': return stylePrimaryColor;
      case 'accent': return styleAccentColor;
      case 'card': return styleCardColor;
      case 'panel': return stylePanelColor;
      case 'menu': return styleMenuColor;
      case 'header': return styleHeaderColor;
      case 'textMain': return styleTextMainColor;
      case 'textMute': return styleTextMuteColor;
      case 'input': return styleInputBgColor;
      case 'disabled': return styleDisabledColor;
      case 'toastBg': return styleToastBgColor || 'rgba(15, 23, 42, 0.95)';
      case 'toastText': return styleToastTextColor || '#ffffff';
      case 'buttonPrimaryText': return styleButtonPrimaryTextColor || '#ffffff';
      case 'buttonSecondaryBg': return styleButtonSecondaryBg || 'rgba(255, 255, 255, 0.05)';
      case 'buttonSecondaryText': return styleButtonSecondaryText || '#e2e8f0';
      case 'buttonSecondaryBorder': return styleButtonSecondaryBorder || '#30363d';
      case 'tableHeaderBg': return styleTableHeaderBg || 'rgba(255, 255, 255, 0.02)';
      case 'tableHeaderText': return styleTableHeaderText || '#8b949e';
      case 'tableRowSelected': return styleTableRowSelected || 'rgba(56, 139, 253, 0.12)';
      case 'modalBg': return styleModalBgColor || styleCardColor || '#161b22';
      case 'modalBorder': return styleModalBorderColor || styleBorderColor || '#30363d';
      case 'inputBorder': return styleInputBorderColor || '#30363d';
      case 'inputText': return styleInputTextColor || styleTextMainColor || '#f8fafc';
      case 'border': return styleBorderColor || '#30363d';
      case 'badgeBg': return styleBadgeBgColor || 'rgba(255, 255, 255, 0.08)';
      case 'badgeText': return styleBadgeTextColor || '#8b949e';
      default: return '#1e293b';
    }
  };

  const getGradientModalTitle = () => {
    switch (gradientModalTarget) {
      case 'primary': return "Primary Theme Gradient";
      case 'accent': return "Accent Theme Gradient";
      case 'card': return "Card Background Gradient";
      case 'panel': return "Panel Canvas Gradient";
      case 'menu': return "Menu Panel Gradient";
      case 'header': return "Header Strip Gradient";
      case 'textMain': return "Main Text Color Gradient";
      case 'textMute': return "Muted Headings Gradient";
      case 'input': return "Input Background Gradient";
      case 'disabled': return "Disabled State Gradient";
      case 'toastBg': return "Pop-Down Message Background";
      case 'toastText': return "Pop-Down Message Text Color";
      case 'buttonPrimaryText': return "Primary Button Text Color";
      case 'buttonSecondaryBg': return "Secondary Button Background";
      case 'buttonSecondaryText': return "Secondary Button Text Color";
      case 'buttonSecondaryBorder': return "Secondary Button Border Color";
      case 'tableHeaderBg': return "Table Header Background";
      case 'tableHeaderText': return "Table Header Text Color";
      case 'tableRowSelected': return "Table Selected Row Highlight";
      case 'modalBg': return "Modal Dialog Canvas Background";
      case 'modalBorder': return "Modal Dialog Border Color";
      case 'inputBorder': return "Input Border Color";
      case 'inputText': return "Input Text Color";
      case 'border': return "Global Border & Divider Color";
      case 'badgeBg': return "Status Badge / Tag Background";
      case 'badgeText': return "Status Badge / Tag Text Color";
      default: return "Color / Gradient Editor";
    }
  };

  // Physical Coin Mint States
  const [packages, setPackages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [allDesigns, setAllDesigns] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [packagesError, setPackagesError] = useState('');
  const [ordersError, setOrdersError] = useState('');

  const getDesignInfo = (designId) => {
    if (!designId) return { name: 'None', preview: null, previewBack: null, isSystemTemplate: false, isTemplate: false, design: null };
    const d = allDesigns.find(item => item.id === parseInt(designId));
    if (!d) return { name: `Design #${designId}`, preview: null, previewBack: null, isSystemTemplate: false, isTemplate: false, design: null };
    const conf = d.config || {};
    const preview = conf.previewImage || conf.imageUrl || conf.photoUrl || conf.preview_image || null;
    const previewBack = conf.preview_image_back || conf.previewImageBack || null;
    const isSys = Boolean(conf.is_system_template === true || conf.is_system_template === 'true' || conf.is_from_system_template);
    const isTpl = Boolean(conf.is_template === true || conf.is_template === 'true');
    return {
      name: d.name,
      preview,
      previewBack,
      isSystemTemplate: isSys,
      isTemplate: isTpl,
      design: d
    };
  };

  // Checkout modal
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  // Checkout/Billing form
  const [checkoutBillingAddress, setCheckoutBillingAddress] = useState('');
  const [checkoutBillingCity, setCheckoutBillingCity] = useState('');
  const [checkoutBillingState, setCheckoutBillingState] = useState('');
  const [checkoutBillingZip, setCheckoutBillingZip] = useState('');
  const [checkoutBillingCountry, setCheckoutBillingCountry] = useState('');
  
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState('');

  // Cart states
  const [cart, setCart] = useState([]);

  // Custom Package Form
  const [showCustomPackageModal, setShowCustomPackageModal] = useState(false);
  const [customPkgName, setCustomPkgName] = useState('');
  const [customPkgDescription, setCustomPkgDescription] = useState('');
  const [customPkgPrice, setCustomPkgPrice] = useState('');
  const [customPkgCoins, setCustomPkgCoins] = useState('25');
  const [customPkgItems, setCustomPkgItems] = useState({
    annual_hosting: 0,
    standard_coin: 25,
    custom_face_design: 0,
    custom_brag_page: 0,
    custom_species_design: 0,
    challenge_coin_set: 0,
    special_edition_setup: 0,
    special_metallic_coin: 0,
    special_mounts: 0,
    shipping_cost: 0
  });
  const [customPkgStartDate, setCustomPkgStartDate] = useState('');
  const [customPkgEndDate, setCustomPkgEndDate] = useState('');
  const [customPkgLoading, setCustomPkgLoading] = useState(false);
  const [customPkgError, setCustomPkgError] = useState('');
  const [customPkgSuccess, setCustomPkgSuccess] = useState('');
  const [customPkgCoinTemplate, setCustomPkgCoinTemplate] = useState('none');
  const [customPkgCardTemplate, setCustomPkgCardTemplate] = useState('none');

  // RFID base domain state for Global Admin
  const [rfidBaseDomain, setRfidBaseDomain] = useState('https://tcoin.fyi');
  const [rfidDomainSuccess, setRfidDomainSuccess] = useState('');
  const [rfidDomainError, setRfidDomainError] = useState('');
  const [rfidDomainLoading, setRfidDomainLoading] = useState(false);

  // Site Settings Sub-Tabs: 'styling' | 'mail' | 'keys' | 'onboarding' | 'mobile'
  const [settingsTab, setSettingsTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sub = (params.get('subtab') || params.get('section') || '').toLowerCase();
      if (['mail', 'email', 'emails', 'smtp'].includes(sub)) return 'mail';
      if (['keys', 'key', 'token', 'auth', 'auth/keys', 'auth-keys', '2fa'].includes(sub)) return 'keys';
      if (['onboarding', 'tutorial', 'guide', 'video'].includes(sub)) return 'onboarding';
      if (['mobile', 'mobileweb', 'mobile-web', 'phone'].includes(sub)) return 'mobile';
      const hash = (window.location.hash || '').toLowerCase();
      if (hash === '#mail' || hash === '#email') return 'mail';
      if (hash === '#keys' || hash === '#auth' || hash === '#auth/keys' || hash === '#auth-keys') return 'keys';
      if (hash === '#onboarding') return 'onboarding';
      if (hash === '#mobile') return 'mobile';
    } catch (_) {}
    return 'styling';
  });


  // Filament options assign states (Operator)
  const [selectedOrderForFilaments, setSelectedOrderForFilaments] = useState(null);
  const [operatorFilamentPrimary, setOperatorFilamentPrimary] = useState('Gold PLA');
  const [operatorFilamentAccent, setOperatorFilamentAccent] = useState('Silver PLA');
  const [filamentLoading, setFilamentLoading] = useState(false);

  // Production Sub-Tabs & Filaments Catalog States
  const [productionSubTab, setProductionSubTab] = useState('orders'); // 'orders' | 'production_line' | 'filaments'
  const [inProductionSearch, setInProductionSearch] = useState('');
  
  // Vaulted Coins modal & selection state
  const [selectedProductionOrderId, setSelectedProductionOrderId] = useState(null);
  const [vaultedCoinsModalOrder, setVaultedCoinsModalOrder] = useState(null);
  const [vaultedCoinsList, setVaultedCoinsList] = useState([]);
  const [vaultedCoinsLoading, setVaultedCoinsLoading] = useState(false);
  const [vaultedCoinsError, setVaultedCoinsError] = useState('');
  const [copiedCoinCode, setCopiedCoinCode] = useState(null);

  const [filaments, setFilaments] = useState([]);
  const [loadingFilaments, setLoadingFilaments] = useState(false);
  const [filamentsError, setFilamentsError] = useState(null);
  const [filamentSearch, setFilamentSearch] = useState('');
  const [filamentMaterialFilter, setFilamentMaterialFilter] = useState('ALL');
  const [filamentTenantFilter, setFilamentTenantFilter] = useState('ALL');
  const [isAddFilamentModalOpen, setIsAddFilamentModalOpen] = useState(false);
  const [editingFilament, setEditingFilament] = useState(null);

  // Filament Form Fields
  const [filamentBrand, setFilamentBrand] = useState('');
  const [filamentProductName, setFilamentProductName] = useState('');
  const [filamentColorName, setFilamentColorName] = useState('');
  const [filamentColorHex, setFilamentColorHex] = useState('#D4AF37');
  const [filamentMaterialType, setFilamentMaterialType] = useState('PLA');
  const [filamentWeight, setFilamentWeight] = useState('1kg');
  const [filamentSku, setFilamentSku] = useState('');
  const [filamentIsShared, setFilamentIsShared] = useState(false);
  const [filamentTargetTenantId, setFilamentTargetTenantId] = useState('');
  const [savingFilament, setSavingFilament] = useState(false);
  const [filamentFormError, setFilamentFormError] = useState(null);
  const [lookingUpSku, setLookingUpSku] = useState(false);
  const [skuLookupSuccess, setSkuLookupSuccess] = useState(null);
  const [skuLookupNotice, setSkuLookupNotice] = useState(null);
  const [skuPresets, setSkuPresets] = useState([]);

  const availableFilamentOptions = useMemo(() => {
    const list = [];
    if (filaments && filaments.length > 0) {
      filaments.forEach(f => {
        const val = `${f.brand} - ${f.color_name} (${f.material_type})`;
        list.push({ value: val, label: val, color: f.color_hex });
      });
    }
    const defaults = ['Gold PLA', 'Silver PLA', 'Copper PLA', 'Forest Green', 'Bronze', 'Steel'];
    defaults.forEach(d => {
      if (!list.some(item => item.value === d || item.label.includes(d))) {
        list.push({ value: d, label: d, color: '#D4AF37' });
      }
    });
    return list;
  }, [filaments]);

  const resolveFilamentHex = (filamentStr, defaultHex = '#888888') => {
    if (!filamentStr) return defaultHex;
    const clean = filamentStr.trim().toLowerCase();

    if (clean.startsWith('#') && (clean.length === 4 || clean.length === 7)) {
      return filamentStr.trim();
    }

    if (Array.isArray(filaments) && filaments.length > 0) {
      let match = filaments.find(f => {
        const full = `${f.brand || ''} ${f.product_name || ''} - ${f.color_name || ''}`.toLowerCase();
        return full === clean || `${f.product_name || ''} - ${f.color_name || ''}`.toLowerCase() === clean;
      });
      if (match?.color_hex) return match.color_hex;

      match = filaments.find(f => f.color_name && clean.includes(f.color_name.toLowerCase()));
      if (match?.color_hex) return match.color_hex;
    }

    const COLOR_MAP = {
      'galaxy black': '#212721',
      'black': '#1A1A1A',
      'white': '#FFFFFF',
      'cold white': '#F8F9FA',
      'dark purple': '#46394E',
      'gold': '#D4AF37',
      'silk gold': '#D4AF37',
      'silver': '#C0C0C0',
      'silk silver': '#C0C0C0',
      'grey': '#707070',
      'gray': '#707070',
      'red': '#D32F2F',
      'crimson red': '#990000',
      'blue': '#1976D2',
      'sky blue': '#4FC3F7',
      'dark blue': '#0D47A1',
      'green': '#2E7D32',
      'neon green': '#39FF14',
      'lime green': '#76FF03',
      'yellow': '#FFEB3B',
      'neon magenta': '#FF007F',
      'magenta': '#E91E63',
      'orange': '#FF6D00',
      'teal': '#009688',
      'purple': '#8E24AA',
      'brown': '#5D4037',
      'bronze': '#CD7F32',
      'copper': '#B87333',
      'steel': '#71797E',
      'pink': '#FF80AB'
    };

    for (const [colorName, hex] of Object.entries(COLOR_MAP)) {
      if (clean.includes(colorName)) {
        return hex;
      }
    }

    return defaultHex;
  };

  // Package Editing States
  const [selectedPackageForEdit, setSelectedPackageForEdit] = useState(null);
  const [editPkgName, setEditPkgName] = useState('');
  const [editPkgDescription, setEditPkgDescription] = useState('');
  const [editPkgPrice, setEditPkgPrice] = useState('');
  const [editPkgCoins, setEditPkgCoins] = useState('');
  const [editPkgItems, setEditPkgItems] = useState({});
  const [editPkgStartDate, setEditPkgStartDate] = useState('');
  const [editPkgEndDate, setEditPkgEndDate] = useState('');
  const [editPkgIsActive, setEditPkgIsActive] = useState(true);
  const [editPkgLoading, setEditPkgLoading] = useState(false);
  const [editPkgError, setEditPkgError] = useState('');
  const [editPkgSuccess, setEditPkgSuccess] = useState('');
  const [editPkgCoinTemplate, setEditPkgCoinTemplate] = useState('none');
  const [editPkgCardTemplate, setEditPkgCardTemplate] = useState('none');
  const [availableSystemCoins, setAvailableSystemCoins] = useState([]);
  const [allPhysicalCoins, setAllPhysicalCoins] = useState([]);
  const [designStudioInitialCoinId, setDesignStudioInitialCoinId] = useState(null);
  const [designStudioInitialDesignId, setDesignStudioInitialDesignId] = useState(null);
  const [designStudioInitialTab, setDesignStudioInitialTab] = useState(null);

  // Operator Library Order Selection & Assignment States
  const [selectedLibraryCoinsForOrders, setSelectedLibraryCoinsForOrders] = useState({});
  const [selectedLibraryCardsForOrders, setSelectedLibraryCardsForOrders] = useState({});
  const [assigningLibraryLoading, setAssigningLibraryLoading] = useState({});
  const [editingLibraryCoinOrderId, setEditingLibraryCoinOrderId] = useState(null);
  const [editingLibraryCardOrderId, setEditingLibraryCardOrderId] = useState(null);
  const [checkoutLibraryCoinId, setCheckoutLibraryCoinId] = useState('');
  const [checkoutLibraryCardId, setCheckoutLibraryCardId] = useState('');

  // Universal Top Banner Slide-in Notification Observers for Dashboard alerts
  useEffect(() => {
    if (styleSuccess) modal.notify(styleSuccess, 'success');
  }, [styleSuccess, modal]);
  useEffect(() => {
    if (styleError) modal.notify(styleError, 'error');
  }, [styleError, modal]);

  useEffect(() => {
    if (rfidDomainSuccess) modal.notify(rfidDomainSuccess, 'success');
  }, [rfidDomainSuccess, modal]);
  useEffect(() => {
    if (rfidDomainError) modal.notify(rfidDomainError, 'error');
  }, [rfidDomainError, modal]);

  useEffect(() => {
    if (aliasSuccess) modal.notify(aliasSuccess, 'success');
  }, [aliasSuccess, modal]);
  useEffect(() => {
    if (aliasError) modal.notify(aliasError, 'error');
  }, [aliasError, modal]);

  useEffect(() => {
    if (addrSuccess) modal.notify(addrSuccess, 'success');
  }, [addrSuccess, modal]);
  useEffect(() => {
    if (addrError) modal.notify(addrError, 'error');
  }, [addrError, modal]);

  useEffect(() => {
    if (orgSuccess) modal.notify(orgSuccess, 'success');
  }, [orgSuccess, modal]);
  useEffect(() => {
    if (orgError) modal.notify(orgError, 'error');
  }, [orgError, modal]);

  useEffect(() => {
    if (profSuccessMsg) modal.notify(profSuccessMsg, 'success');
  }, [profSuccessMsg, modal]);
  useEffect(() => {
    if (profError) modal.notify(profError, 'error');
  }, [profError, modal]);

  useEffect(() => {
    if (formSuccess) modal.notify(formSuccess, 'success');
  }, [formSuccess, modal]);
  useEffect(() => {
    if (formError) modal.notify(formError, 'error');
  }, [formError, modal]);

  useEffect(() => {
    if (editSuccess) modal.notify(editSuccess, 'success');
  }, [editSuccess, modal]);
  useEffect(() => {
    if (editError) modal.notify(editError, 'error');
  }, [editError, modal]);

  useEffect(() => {
    if (resetSuccess) modal.notify(resetSuccess, 'success');
  }, [resetSuccess, modal]);
  useEffect(() => {
    if (resetError) modal.notify(resetError, 'error');
  }, [resetError, modal]);

  useEffect(() => {
    if (checkoutSuccess) modal.notify(checkoutSuccess, 'success');
  }, [checkoutSuccess, modal]);
  useEffect(() => {
    if (checkoutError) modal.notify(checkoutError, 'error');
  }, [checkoutError, modal]);

  useEffect(() => {
    if (customPkgSuccess) modal.notify(customPkgSuccess, 'success');
  }, [customPkgSuccess, modal]);
  useEffect(() => {
    if (customPkgError) modal.notify(customPkgError, 'error');
  }, [customPkgError, modal]);

  useEffect(() => {
    if (editPkgSuccess) modal.notify(editPkgSuccess, 'success');
  }, [editPkgSuccess, modal]);
  useEffect(() => {
    if (editPkgError) modal.notify(editPkgError, 'error');
  }, [editPkgError, modal]);

  useEffect(() => {
    if (agentRatingSuccess) modal.notify(agentRatingSuccess, 'success');
  }, [agentRatingSuccess, modal]);


  const navigateToCoinProofing = (coinId) => {
    if (!coinId) return;
    setDesignStudioInitialCoinId(coinId);
    setDesignStudioInitialDesignId(null);
    if (user.role === 'OPERATOR') {
      setActiveTab('operator_coins');
    } else {
      setDesignStudioInitialTab('coin');
      setActiveTab('design');
    }
  };

  const navigateToCardProofing = (designId) => {
    if (!designId) return;
    setDesignStudioInitialDesignId(designId);
    setDesignStudioInitialCoinId(null);
    if (user.role === 'OPERATOR' || user.role === 'END_USER') {
      setActiveTab('brag');
    } else {
      setDesignStudioInitialTab('brag');
      setActiveTab('design');
    }
  };

  const hasSettingsAccess = ["GLOBAL_ADMIN", "GLOBAL_POWER", "TENANT_ADMIN"].includes(user.role);

  // Restrict operators to orders subtab
  useEffect(() => {
    if (user?.role === 'OPERATOR' && productionSubTab !== 'orders') {
      setProductionSubTab('orders');
    }
  }, [user?.role, productionSubTab]);

  // Pre-fill styles form on activeTab settings
  useEffect(() => {
    if (activeTab === 'settings') {
      if (!isGlobalRole) {
        setStyleBrandName(tenantConfig?.brand_name || '');
        setStylePrimaryColor(tenantConfig?.primary_color || '#bdb2ff');
        setStyleAccentColor(tenantConfig?.accent_color || '#a0c4ff');
        setStyleCardColor(tenantConfig?.card_color || '#ffffff');
        setStylePanelColor(tenantConfig?.panel_color || '#f8f9fa');
        setStyleMenuColor(tenantConfig?.menu_color || '#f8f9fa');
        setStyleHeaderColor(tenantConfig?.header_color || '#f8f9fa');
        setStyleTextMainColor(tenantConfig?.text_main_color || '#111827');
        setStyleTextMuteColor(tenantConfig?.text_mute_color || '#4b5563');
        setStyleInputBgColor(tenantConfig?.input_bg_color || '#ffffff');
        setStyleDisabledColor(tenantConfig?.disabled_color || '#000000');
        setStyleToastBgColor(tenantConfig?.toast_bg_color || '');
        setStyleToastTextColor(tenantConfig?.toast_text_color || '');
        setStyleLogoUrl(tenantConfig?.logo_url || '');
        setStyleSiteIconUrl(tenantConfig?.site_icon_url || '');
        setStyleLogoOffsetY(tenantConfig?.logo_offset_y !== undefined && tenantConfig?.logo_offset_y !== null ? tenantConfig.logo_offset_y : 0);
        setStyleLogoOffsetX(tenantConfig?.logo_offset_x !== undefined && tenantConfig?.logo_offset_x !== null ? tenantConfig.logo_offset_x : 0);
        setStyleLogoHeight(tenantConfig?.logo_height !== undefined && tenantConfig?.logo_height !== null ? tenantConfig.logo_height : 40);
        setStyleBannerHeight(tenantConfig?.banner_height !== undefined && tenantConfig?.banner_height !== null ? tenantConfig.banner_height : 70);
        setStyleBackgroundUrl(tenantConfig?.background_url || '');
        setStyleGeminiApiKey(tenantConfig?.gemini_api_key || '');
        setStyleOnboardingVideoUrl(tenantConfig?.onboarding_video_url || '');
        setStyleOnboardingVideoTitle(tenantConfig?.onboarding_video_title || 'How to Log Your Trophy Coin');
        setStyleSharePayoffLine(tenantConfig?.share_payoff_line || 'Official Certified Trophy Card');
        setStyleNoreplyEmail(tenantConfig?.noreply_email || '');
        setStyleEnforce2faRoles(
          Array.isArray(tenantConfig?.enforce_2fa_roles)
            ? tenantConfig.enforce_2fa_roles
            : (tenantConfig?.enforce_2fa_roles ? tenantConfig.enforce_2fa_roles.split(',').map(r => r.trim()) : [])
        );
        setStyleButtonPrimaryTextColor(tenantConfig?.button_primary_text_color || '');
        setStyleButtonSecondaryBg(tenantConfig?.button_secondary_bg || '');
        setStyleButtonSecondaryText(tenantConfig?.button_secondary_text || '');
        setStyleButtonSecondaryBorder(tenantConfig?.button_secondary_border || '');
        setStyleTableHeaderBg(tenantConfig?.table_header_bg || '');
        setStyleTableHeaderText(tenantConfig?.table_header_text || '');
        setStyleTableRowSelected(tenantConfig?.table_row_selected || '');
        setStyleModalBgColor(tenantConfig?.modal_bg_color || '');
        setStyleModalBorderColor(tenantConfig?.modal_border_color || '');
        setStyleInputBorderColor(tenantConfig?.input_border_color || '');
        setStyleInputTextColor(tenantConfig?.input_text_color || '');
        setStyleBorderColor(tenantConfig?.border_color || '');
        setStyleBadgeBgColor(tenantConfig?.badge_bg_color || '');
        setStyleBadgeTextColor(tenantConfig?.badge_text_color || '');
        setStyleFontFamily(tenantConfig?.font_family || '');
        setStyleBorderRadius(tenantConfig?.border_radius || 'standard');
        const configTenantId = tenantConfig?.id?.toString() || '';
        if (selectedTenantId !== configTenantId) {
          setSelectedTenantId(configTenantId);
        }
      } else {
        const sel = tenants.find(t => t.id.toString() === selectedTenantId);
        if (sel) {
          setStyleBrandName(sel.brand_name || '');
          setStylePrimaryColor(sel.primary_color || '#bdb2ff');
          setStyleAccentColor(sel.accent_color || '#a0c4ff');
          setStyleCardColor(sel.card_color || '#ffffff');
          setStylePanelColor(sel.panel_color || '#f8f9fa');
          setStyleMenuColor(sel.menu_color || '#f8f9fa');
          setStyleHeaderColor(sel.header_color || '#f8f9fa');
          setStyleTextMainColor(sel.text_main_color || '#111827');
          setStyleTextMuteColor(sel.text_mute_color || '#4b5563');
          setStyleInputBgColor(sel.input_bg_color || '#ffffff');
          setStyleDisabledColor(sel.disabled_color || '#000000');
          setStyleToastBgColor(sel.toast_bg_color || '');
          setStyleToastTextColor(sel.toast_text_color || '');
          setStyleButtonPrimaryTextColor(sel.button_primary_text_color || '');
          setStyleButtonSecondaryBg(sel.button_secondary_bg || '');
          setStyleButtonSecondaryText(sel.button_secondary_text || '');
          setStyleButtonSecondaryBorder(sel.button_secondary_border || '');
          setStyleTableHeaderBg(sel.table_header_bg || '');
          setStyleTableHeaderText(sel.table_header_text || '');
          setStyleTableRowSelected(sel.table_row_selected || '');
          setStyleModalBgColor(sel.modal_bg_color || '');
          setStyleModalBorderColor(sel.modal_border_color || '');
          setStyleInputBorderColor(sel.input_border_color || '');
          setStyleInputTextColor(sel.input_text_color || '');
          setStyleBorderColor(sel.border_color || '');
          setStyleBadgeBgColor(sel.badge_bg_color || '');
          setStyleBadgeTextColor(sel.badge_text_color || '');
          setStyleFontFamily(sel.font_family || '');
          setStyleBorderRadius(sel.border_radius || 'standard');
          setStyleLogoUrl(sel.logo_url || '');
          setStyleSiteIconUrl(sel.site_icon_url || '');
          setStyleLogoOffsetY(sel.logo_offset_y !== undefined && sel.logo_offset_y !== null ? sel.logo_offset_y : 0);
          setStyleLogoOffsetX(sel.logo_offset_x !== undefined && sel.logo_offset_x !== null ? sel.logo_offset_x : 0);
          setStyleLogoHeight(sel.logo_height !== undefined && sel.logo_height !== null ? sel.logo_height : 40);
          setStyleBannerHeight(sel.banner_height !== undefined && sel.banner_height !== null ? sel.banner_height : 70);
          setStyleBackgroundUrl(sel.background_url || '');
          setStyleGeminiApiKey(sel.gemini_api_key || '');
          setStyleOnboardingVideoUrl(sel.onboarding_video_url || '');
          setStyleOnboardingVideoTitle(sel.onboarding_video_title || 'How to Log Your Trophy Coin');
          setStyleSharePayoffLine(sel.share_payoff_line || 'Official Certified Trophy Card');
          setStyleNoreplyEmail(sel.noreply_email || '');
          setStyleEnforce2faRoles(sel.enforce_2fa_roles || []);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedTenantId, tenants, tenantConfig?.id, isGlobalRole]);

  // Live preview for site customizer changes
  useEffect(() => {
    if (activeTab === 'settings') {
      if (stylePanelColor) {
        document.documentElement.style.setProperty('--panel-bg', stylePanelColor);
        document.documentElement.style.setProperty('--panel-color', stylePanelColor);
      }
      if (styleMenuColor) {
        document.documentElement.style.setProperty('--menu-bg', styleMenuColor);
        document.documentElement.style.setProperty('--menu-color', styleMenuColor);
      }
      if (styleCardColor) {
        document.documentElement.style.setProperty('--card-bg', styleCardColor);
      }
      if (styleHeaderColor) {
        document.documentElement.style.setProperty('--header-bg', styleHeaderColor);
      }
      if (stylePrimaryColor) {
        const solidPrimary = resolveSolidColor(stylePrimaryColor, '#bdb2ff');
        document.documentElement.style.setProperty('--primary-color', solidPrimary);
        document.documentElement.style.setProperty('--primary-gradient', stylePrimaryColor);
        document.documentElement.style.setProperty('--primary-bg', stylePrimaryColor);
      }
      if (styleAccentColor) {
        const solidAccent = resolveSolidColor(styleAccentColor, '#a0c4ff');
        document.documentElement.style.setProperty('--accent-color', solidAccent);
        document.documentElement.style.setProperty('--accent-gradient', styleAccentColor);
        document.documentElement.style.setProperty('--accent-bg', styleAccentColor);
      }
      if (styleTextMainColor) {
        document.documentElement.style.setProperty('--text-main', resolveSolidColor(styleTextMainColor, '#111827'));
      }
      if (styleTextMuteColor) {
        document.documentElement.style.setProperty('--text-mute', resolveSolidColor(styleTextMuteColor, '#4b5563'));
      }
      if (styleInputBgColor) {
        document.documentElement.style.setProperty('--input-bg', styleInputBgColor);
      }
      if (styleDisabledColor) {
        const solidDis = resolveSolidColor(styleDisabledColor, '#000000');
        const isDisDark = isDarkColor(styleDisabledColor);
        document.documentElement.style.setProperty('--disabled-color', solidDis);
        document.documentElement.style.setProperty('--disabled-bg', styleDisabledColor);
        document.documentElement.style.setProperty('--disabled-text', isDisDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.4)');
      }
      if (styleToastBgColor) {
        document.documentElement.style.setProperty('--toast-bg', styleToastBgColor);
        const isTDark = isDarkColor(styleToastBgColor);
        document.documentElement.style.setProperty('--toast-border', isTDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.12)');
      }
      if (styleToastTextColor) {
        document.documentElement.style.setProperty('--toast-text', resolveSolidColor(styleToastTextColor, '#ffffff'));
      }
      if (styleButtonPrimaryTextColor) {
        document.documentElement.style.setProperty('--button-primary-text', resolveSolidColor(styleButtonPrimaryTextColor, '#ffffff'));
      }
      if (styleButtonSecondaryBg) {
        document.documentElement.style.setProperty('--button-secondary-bg', styleButtonSecondaryBg);
      }
      if (styleButtonSecondaryText) {
        document.documentElement.style.setProperty('--button-secondary-text', resolveSolidColor(styleButtonSecondaryText, '#374151'));
      }
      if (styleButtonSecondaryBorder) {
        document.documentElement.style.setProperty('--button-secondary-border', resolveSolidColor(styleButtonSecondaryBorder, 'rgba(255, 255, 255, 0.15)'));
      }
      if (styleTableHeaderBg) {
        document.documentElement.style.setProperty('--table-header-bg', styleTableHeaderBg);
      }
      if (styleTableHeaderText) {
        document.documentElement.style.setProperty('--table-header-text', resolveSolidColor(styleTableHeaderText, '#6b7280'));
      }
      if (styleTableRowSelected) {
        document.documentElement.style.setProperty('--table-row-selected', styleTableRowSelected);
      }
      if (styleModalBgColor) {
        document.documentElement.style.setProperty('--modal-bg', styleModalBgColor);
      }
      if (styleModalBorderColor) {
        document.documentElement.style.setProperty('--modal-border', resolveSolidColor(styleModalBorderColor, 'rgba(255, 255, 255, 0.15)'));
      }
      if (styleInputBorderColor) {
        document.documentElement.style.setProperty('--input-border', resolveSolidColor(styleInputBorderColor, 'rgba(255, 255, 255, 0.15)'));
      }
      if (styleInputTextColor) {
        document.documentElement.style.setProperty('--input-text', resolveSolidColor(styleInputTextColor, '#111827'));
      }
      if (styleBorderColor) {
        document.documentElement.style.setProperty('--border-color', resolveSolidColor(styleBorderColor, 'rgba(255, 255, 255, 0.1)'));
      }
      if (styleBadgeBgColor) {
        document.documentElement.style.setProperty('--badge-bg', styleBadgeBgColor);
      }
      if (styleBadgeTextColor) {
        document.documentElement.style.setProperty('--badge-text', resolveSolidColor(styleBadgeTextColor, '#ffffff'));
      }
      if (styleFontFamily) {
        document.documentElement.style.setProperty('--font-family', styleFontFamily);
        document.body.style.fontFamily = styleFontFamily;
      }
      if (styleBorderRadius) {
        const radiusMap = {
          sharp: { sm: '0px', md: '0px', lg: '0px' },
          standard: { sm: '4px', md: '8px', lg: '12px' },
          smooth: { sm: '6px', md: '12px', lg: '16px' },
          rounded: { sm: '8px', md: '16px', lg: '24px' }
        };
        const radii = radiusMap[styleBorderRadius] || radiusMap.standard;
        document.documentElement.style.setProperty('--radius-sm', radii.sm);
        document.documentElement.style.setProperty('--radius-md', radii.md);
        document.documentElement.style.setProperty('--radius-lg', radii.lg);
      }
    }
  }, [
    activeTab,
    stylePanelColor,
    styleMenuColor,
    styleCardColor,
    styleHeaderColor,
    stylePrimaryColor,
    styleAccentColor,
    styleTextMainColor,
    styleTextMuteColor,
    styleInputBgColor,
    styleDisabledColor,
    styleToastBgColor,
    styleToastTextColor,
    styleButtonPrimaryTextColor,
    styleButtonSecondaryBg,
    styleButtonSecondaryText,
    styleButtonSecondaryBorder,
    styleTableHeaderBg,
    styleTableHeaderText,
    styleTableRowSelected,
    styleModalBgColor,
    styleModalBorderColor,
    styleInputBorderColor,
    styleInputTextColor,
    styleBorderColor,
    styleBadgeBgColor,
    styleBadgeTextColor,
    styleFontFamily,
    styleBorderRadius
  ]);

  // Global Admin: auto-select first tenant
  useEffect(() => {
    if (activeTab === 'settings' && isGlobalRole && tenants.length > 0 && !selectedTenantId) {
      setSelectedTenantId(tenants[0].id.toString());
    }
  }, [activeTab, isGlobalRole, tenants, selectedTenantId]);

  // Load system settings (RFID Base Domain) for Global Admin
  useEffect(() => {
    const currentToken = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (activeTab === 'settings' && isGlobalRole && currentToken) {
      fetch(`${API_BASE}/api/v1/coin-vault/settings/system`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'X-Tenant-Domain': window.location.hostname
        }
      })
      .then(res => res.json())
      .then(data => {
        if (data && data.rfid_base_domain) {
          setRfidBaseDomain(data.rfid_base_domain);
        }
      })
      .catch(err => console.error('Failed to load system settings:', err));
    }
  }, [activeTab, isGlobalRole]);

  const handleSaveRfidDomain = async (e) => {
    e.preventDefault();
    setRfidDomainLoading(true);
    setRfidDomainSuccess('');
    setRfidDomainError('');
    try {
      const currentToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/settings/system`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({ rfid_base_domain: rfidBaseDomain })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update RFID base domain');
      setRfidDomainSuccess('RFID Base Domain successfully updated!');
    } catch (err) {
      setRfidDomainError(err.message || 'Failed to save RFID domain');
    } finally {
      setRfidDomainLoading(false);
    }
  };

  const handleSaveSiteSettings = async (e) => {
    e.preventDefault();
    setStyleLoading(true);
    setStyleSuccess('');
    setStyleError('');
    try {
      const cleanLogoUrl = styleLogoUrl.startsWith(API_BASE) 
        ? styleLogoUrl.replace(API_BASE, '') 
        : styleLogoUrl;
      const cleanSiteIconUrl = styleSiteIconUrl.startsWith(API_BASE)
        ? styleSiteIconUrl.replace(API_BASE, '')
        : styleSiteIconUrl;
      const cleanBackgroundUrl = styleBackgroundUrl.startsWith(API_BASE) 
        ? styleBackgroundUrl.replace(API_BASE, '') 
        : styleBackgroundUrl;
      const cleanVideoUrl = styleOnboardingVideoUrl.startsWith(API_BASE)
        ? styleOnboardingVideoUrl.replace(API_BASE, '')
        : styleOnboardingVideoUrl;

      const res = await fetch(`${API_BASE}/api/v1/users/tenants/${selectedTenantId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          brand_name: styleBrandName,
          primary_color: stylePrimaryColor,
          accent_color: styleAccentColor,
          card_color: styleCardColor,
          panel_color: stylePanelColor,
          menu_color: styleMenuColor,
          header_color: styleHeaderColor,
          text_main_color: styleTextMainColor,
          text_mute_color: styleTextMuteColor,
          input_bg_color: styleInputBgColor,
          disabled_color: styleDisabledColor,
          toast_bg_color: styleToastBgColor || null,
          toast_text_color: styleToastTextColor || null,
          logo_url: cleanLogoUrl,
          site_icon_url: cleanSiteIconUrl,
          logo_offset_y: styleLogoOffsetY !== '' ? parseInt(styleLogoOffsetY, 10) : 0,
          logo_offset_x: styleLogoOffsetX !== '' ? parseInt(styleLogoOffsetX, 10) : 0,
          logo_height: styleLogoHeight !== '' ? parseInt(styleLogoHeight, 10) : 40,
          banner_height: styleBannerHeight !== '' ? parseInt(styleBannerHeight, 10) : 70,
          background_url: cleanBackgroundUrl,
          gemini_api_key: styleGeminiApiKey,
          onboarding_video_url: cleanVideoUrl || null,
          onboarding_video_title: styleOnboardingVideoTitle || 'How to Log Your Trophy Coin',
          share_payoff_line: styleSharePayoffLine !== undefined && styleSharePayoffLine !== null ? styleSharePayoffLine.trim() : 'Official Certified Trophy Card',
          button_primary_text_color: styleButtonPrimaryTextColor || null,
          button_secondary_bg: styleButtonSecondaryBg || null,
          button_secondary_text: styleButtonSecondaryText || null,
          button_secondary_border: styleButtonSecondaryBorder || null,
          table_header_bg: styleTableHeaderBg || null,
          table_header_text: styleTableHeaderText || null,
          table_row_selected: styleTableRowSelected || null,
          modal_bg_color: styleModalBgColor || null,
          modal_border_color: styleModalBorderColor || null,
          input_border_color: styleInputBorderColor || null,
          input_text_color: styleInputTextColor || null,
          border_color: styleBorderColor || null,
          badge_bg_color: styleBadgeBgColor || null,
          badge_text_color: styleBadgeTextColor || null,
          font_family: styleFontFamily || null,
          border_radius: styleBorderRadius || 'standard',
          enforce_2fa_roles: (() => {
            let roles = Array.isArray(styleEnforce2faRoles) ? [...styleEnforce2faRoles] : [];
            if (!isGlobalRole) {
              roles = roles.filter(r => r !== 'GLOBAL_ADMIN');
              const existing = (tenantConfig?.enforce_2fa_roles || '').split(',').map(r => r.trim());
              if (existing.includes('GLOBAL_ADMIN')) {
                roles.push('GLOBAL_ADMIN');
              }
            }
            return roles.length ? roles.join(',') : null;
          })()
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save site custom styles.');
      }
      setStyleSuccess('Site styles and branding successfully saved!');
      
      // If we updated the currently loaded tenant domain's styling, trigger live update
      if (selectedTenantId === tenantConfig?.id?.toString() || (!tenantConfig?.id && selectedTenantId === '')) {
        await refreshTheme();
      }
      
      // Update global tenants state list to sync selection
      if (isGlobalRole) {
        // Resolve paths for local list state
        const resolvedData = {
          ...data,
          logo_url: data.logo_url && data.logo_url.includes('/uploads/') ? `${API_BASE ? API_BASE.replace(/\/+$/, '') : ''}${data.logo_url.substring(data.logo_url.indexOf('/uploads/'))}` : (data.logo_url || ''),
          site_icon_url: data.site_icon_url && data.site_icon_url.includes('/uploads/') ? `${API_BASE ? API_BASE.replace(/\/+$/, '') : ''}${data.site_icon_url.substring(data.site_icon_url.indexOf('/uploads/'))}` : (data.site_icon_url || ''),
          background_url: data.background_url && data.background_url.includes('/uploads/') ? `${API_BASE ? API_BASE.replace(/\/+$/, '') : ''}${data.background_url.substring(data.background_url.indexOf('/uploads/'))}` : (data.background_url || ''),
          logo_offset_y: data.logo_offset_y !== undefined && data.logo_offset_y !== null ? data.logo_offset_y : 0,
          logo_offset_x: data.logo_offset_x !== undefined && data.logo_offset_x !== null ? data.logo_offset_x : 0,
          logo_height: data.logo_height !== undefined && data.logo_height !== null ? data.logo_height : 40,
          banner_height: data.banner_height !== undefined && data.banner_height !== null ? data.banner_height : 70,
          share_payoff_line: data.share_payoff_line !== undefined ? data.share_payoff_line : styleSharePayoffLine,
          noreply_email: data.noreply_email !== undefined ? data.noreply_email : styleNoreplyEmail,
          menu_color: data.menu_color !== undefined ? data.menu_color : styleMenuColor,
          disabled_color: data.disabled_color !== undefined ? data.disabled_color : styleDisabledColor,
          button_primary_text_color: data.button_primary_text_color !== undefined ? data.button_primary_text_color : styleButtonPrimaryTextColor,
          button_secondary_bg: data.button_secondary_bg !== undefined ? data.button_secondary_bg : styleButtonSecondaryBg,
          button_secondary_text: data.button_secondary_text !== undefined ? data.button_secondary_text : styleButtonSecondaryText,
          button_secondary_border: data.button_secondary_border !== undefined ? data.button_secondary_border : styleButtonSecondaryBorder,
          table_header_bg: data.table_header_bg !== undefined ? data.table_header_bg : styleTableHeaderBg,
          table_header_text: data.table_header_text !== undefined ? data.table_header_text : styleTableHeaderText,
          table_row_selected: data.table_row_selected !== undefined ? data.table_row_selected : styleTableRowSelected,
          modal_bg_color: data.modal_bg_color !== undefined ? data.modal_bg_color : styleModalBgColor,
          modal_border_color: data.modal_border_color !== undefined ? data.modal_border_color : styleModalBorderColor,
          input_border_color: data.input_border_color !== undefined ? data.input_border_color : styleInputBorderColor,
          input_text_color: data.input_text_color !== undefined ? data.input_text_color : styleInputTextColor,
          border_color: data.border_color !== undefined ? data.border_color : styleBorderColor,
          badge_bg_color: data.badge_bg_color !== undefined ? data.badge_bg_color : styleBadgeBgColor,
          badge_text_color: data.badge_text_color !== undefined ? data.badge_text_color : styleBadgeTextColor,
          font_family: data.font_family !== undefined ? data.font_family : styleFontFamily,
          border_radius: data.border_radius !== undefined ? data.border_radius : styleBorderRadius
        };
        setTenants(prev => prev.map(t => t.id.toString() === selectedTenantId ? resolvedData : t));
      }
    } catch (err) {
      setStyleError(err.message);
    } finally {
      setStyleLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCreateTenantError('');

    if (!newTenantDomain || newTenantDomain.trim().length < 3) {
      setCreateTenantError('Please provide a valid tenant domain name (e.g. acme.com or acme.local).');
      return;
    }
    if (!newTenantBrand || !newTenantBrand.trim()) {
      setCreateTenantError('Please provide a brand name.');
      return;
    }
    if (newTenantCreateAdmin) {
      if (!newTenantAdminEmail || !newTenantAdminEmail.trim()) {
        setCreateTenantError('Please provide an email address for the initial tenant administrator.');
        return;
      }
      if (!newTenantAdminPassword || newTenantAdminPassword.length < 8) {
        setCreateTenantError('Password must be at least 8 characters long for the administrator.');
        return;
      }
    }

    setCreateTenantLoading(true);
    try {
      const payload = {
        domain_name: newTenantDomain.trim(),
        brand_name: newTenantBrand.trim(),
        primary_color: newTenantPrimaryColor,
        accent_color: newTenantAccentColor,
        logo_url: newTenantLogoUrl.trim() || null,
        noreply_email: newTenantNoreplyEmail && newTenantNoreplyEmail.trim() ? newTenantNoreplyEmail.trim() : null,
        admin_email: newTenantCreateAdmin ? newTenantAdminEmail.trim().toLowerCase() : null,
        admin_password: newTenantCreateAdmin ? newTenantAdminPassword : null
      };

      const res = await fetch(`${API_BASE}/api/v1/users/tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create new tenant domain.');
      }

      const newTenantObj = {
        ...data,
        logo_url: data.logo_url && data.logo_url.includes('/uploads/') ? `${API_BASE ? API_BASE.replace(/\/+$/, '') : ''}${data.logo_url.substring(data.logo_url.indexOf('/uploads/'))}` : data.logo_url,
        logo_offset_y: data.logo_offset_y || 0,
        logo_offset_x: data.logo_offset_x || 0,
        logo_height: data.logo_height || 40,
        banner_height: data.banner_height || 70,
        menu_color: data.menu_color || null
      };

      setTenants(prev => [...prev, newTenantObj]);
      setSelectedTenantId(data.id.toString());
      setStyleSuccess(`Tenant '${data.brand_name}' (${data.domain_name}) created successfully!`);

      // Close modal and reset form
      setIsCreateTenantModalOpen(false);
      setNewTenantDomain('');
      setNewTenantBrand('');
      setNewTenantPrimaryColor('#bdb2ff');
      setNewTenantAccentColor('#a0c4ff');
      setNewTenantLogoUrl('');
      setNewTenantNoreplyEmail('');
      setNewTenantCreateAdmin(false);
      setNewTenantAdminEmail('');
      setNewTenantAdminPassword('');
    } catch (err) {
      setCreateTenantError(err.message);
    } finally {
      setCreateTenantLoading(false);
    }
  };

  const handleSendTestNoreplyEmail = async () => {
    setTestEmailLoading(true);
    setTestEmailError('');
    setTestEmailResult(null);

    const targetTenantId = selectedTenantId || tenantConfig?.id?.toString();
    if (!targetTenantId) {
      setTestEmailError('Unable to detect active tenant ID for testing.');
      setTestEmailLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1/users/tenants/${targetTenantId}/test-noreply-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          recipient_email: (testEmailRecipient || user?.email || '').trim(),
          noreply_email: (styleNoreplyEmail || '').trim() || null
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to dispatch test verification email.');
      }
      setTestEmailResult(data);
    } catch (err) {
      setTestEmailError(err.message);
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleAddAlias = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setAliasError('');
    setAliasSuccess('');

    if (!selectedTenantId) {
      setAliasError('Please select a tenant first.');
      return;
    }

    const cleanInput = aliasInput.trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .split('/')[0]
      .split(':')[0]
      .trim();

    if (!cleanInput || cleanInput.length < 3) {
      setAliasError('Please provide a valid domain name (e.g. fishermanstale.net).');
      return;
    }

    setAliasLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/tenants/${selectedTenantId}/domains`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({ domain_name: cleanInput })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to add domain alias.');
      }

      setTenants(prev => prev.map(t => {
        if (t.id.toString() === selectedTenantId) {
          const currentAliases = t.domain_aliases || [];
          return {
            ...t,
            domain_aliases: [...currentAliases, data]
          };
        }
        return t;
      }));

      setAliasInput('');
      setAliasSuccess(`Domain alias '${data.domain_name}' successfully added!`);
    } catch (err) {
      setAliasError(err.message);
    } finally {
      setAliasLoading(false);
    }
  };

  const handleDeleteAlias = async (domainNameOrId) => {
    setAliasError('');
    setAliasSuccess('');

    if (!selectedTenantId) return;

    setAliasLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/tenants/${selectedTenantId}/domains/${domainNameOrId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to remove domain alias.');
      }

      setTenants(prev => prev.map(t => {
        if (t.id.toString() === selectedTenantId) {
          const currentAliases = t.domain_aliases || [];
          return {
            ...t,
            domain_aliases: currentAliases.filter(a => {
              if (typeof a === 'string') return a !== domainNameOrId;
              return a.domain_name !== domainNameOrId && a.id?.toString() !== domainNameOrId.toString();
            })
          };
        }
        return t;
      }));

      setAliasSuccess('Domain alias removed.');
    } catch (err) {
      setAliasError(err.message);
    } finally {
      setAliasLoading(false);
    }
  };

  // Track viewport responsiveness dynamically
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsMobileSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    localStorage.removeItem('token');
    setUser(null);
    setView('landing');
  };

  // Fetch current user complete profile
  const fetchMyProfile = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentUserProfile(data);
        setUser(data);
        // Pre-fill profile tab states
        setProfFirstName(data.first_name || '');
        setProfLastName(data.last_name || '');
        setProfPhone(data.phone_number || '');
        setProfCompany(data.company_name || '');
        setProfAddress(data.billing_address || '');
        setProfCity(data.billing_city || '');
        setProfState(data.billing_state || '');
        setProfZip(data.billing_zip || '');
        setProfCountry(data.billing_country || '');
      } else if (res.status === 401) {
        handleLogout();
      }
    } catch (err) {
      console.error('Failed to load user profile:', err);
    }
  };

  const fetchOrganizationDetails = async () => {
    if (!token || user.role !== 'OPERATOR') return;
    setOrgLoading(true);
    setOrgError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setOrgData(data);
        setOrgLogoUrl(data.logo_url || '');
        // Also fetch other operators in this organization
        const opRes = await fetch(`${API_BASE}/api/v1/organizations/me/operators`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        const opData = await opRes.json();
        if (opRes.ok) {
          setOrgOperators(opData);
        }
        
        // Also fetch delivery addresses
        await fetchDeliveryAddresses();

        // Also fetch organization invitations
        if (data.id) {
          await fetchOrgInvitations(data.id);
        }
      } else {
        if (res.status === 404) {
          setOrgData(null);
          setOrgError('');
        } else {
          setOrgError(extractErrorMessage(data.detail, 'Failed to load organization details.'));
        }
      }
    } catch (err) {
      setOrgError('Network error loading organization details.');
    } finally {
      setOrgLoading(false);
    }
  };

  const fetchOrgInvitations = async (orgId) => {
    if (!token || !orgId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/${orgId}/invitations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setOrgInvitations(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Failed to load org invitations', e);
    }
  };

  const handleSendOrgInvitation = async (e) => {
    e.preventDefault();
    if (!orgData?.id || !orgInviteEmail) return;
    setOrgInviteSubmitting(true);
    setOrgInviteError('');
    setOrgInviteSuccess('');
    setOrgInviteCreatedLink('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/${orgData.id}/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          recipient_email: orgInviteEmail.trim(),
          recipient_name: orgInviteName.trim() || undefined,
          role: orgInviteRole || 'OPERATOR'
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send invitation');
      }
      setOrgInviteSuccess(data.invite_url ? `Invitation created! An email was sent to ${orgInviteEmail.trim()}.` : 'Invitation sent successfully!');
      if (data.invite_url) {
        setOrgInviteCreatedLink(data.invite_url);
      }
      setOrgInviteEmail('');
      setOrgInviteName('');
      await fetchOrgInvitations(orgData.id);
    } catch (err) {
      setOrgInviteError(err.message);
    } finally {
      setOrgInviteSubmitting(false);
    }
  };

  const handleRevokeOrgInvitation = async (inviteId) => {
    if (!orgData?.id || !inviteId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/${orgData.id}/invitations/${inviteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        await fetchOrgInvitations(orgData.id);
      }
    } catch (e) {
      console.error('Failed to revoke org invitation', e);
    }
  };

  const fetchDeliveryAddresses = async () => {
    if (!token || user.role !== 'OPERATOR') return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/me/addresses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setOrgAddresses(data);
      }
    } catch (err) {
      console.error('Error loading delivery addresses:', err);
    }
  };

  const openAgentChat = async (contractId) => {
    setShowAgentChatModal(true);
    setAgentChatLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/chat/agent_contract/${contractId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        setAgentChatMessages(await res.json());
      }
    } catch (err) {
      console.warn('Failed to load agent chat messages', err);
    } finally {
      setAgentChatLoading(false);
    }
  };

  const handleSendAgentChatMessage = async (e, contractId) => {
    e.preventDefault();
    if (!agentChatNewMsg.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/chat/agent_contract/${contractId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({ message_text: agentChatNewMsg.trim() })
      });
      if (res.ok) {
        const msg = await res.json();
        setAgentChatMessages(prev => [...prev, msg]);
        setAgentChatNewMsg('');
      }
    } catch (err) {
      console.warn('Failed to send agent chat message', err);
    }
  };

  const handleRateAgentQoS = async (e, contractId) => {
    e.preventDefault();
    setAgentRatingSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/contracts/${contractId}/rate-qos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          score: Number(agentRatingScore),
          feedback_notes: agentRatingFeedback.trim() || undefined
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not submit rating.');
      setAgentRatingSuccess('Thank you! Your QoS evaluation has been recorded.');
      setTimeout(() => {
        setShowAgentRatingModal(false);
        setAgentRatingSuccess('');
        setAgentRatingFeedback('');
      }, 1800);
    } catch (err) {
      modal.alert('Rating Error', err.message);
    } finally {
      setAgentRatingSubmitting(false);
    }
  };

  const handleAddDeliveryAddress = async (e) => {
    e.preventDefault();
    if (!addrStreet || !addrCity || !addrZip || !addrCountry) {
      setAddrError('Street, City, Zip, and Country are required.');
      return;
    }
    setAddrSubmitting(true);
    setAddrError('');
    setAddrSuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/me/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          label: addrLabel,
          recipient_name: addrRecipient,
          phone: addrPhone,
          street: addrStreet,
          street2: addrStreet2,
          city: addrCity,
          state: addrState,
          zip: addrZip,
          country: addrCountry,
          country_code: addrCountryCode,
          latitude: addrLat ? parseFloat(addrLat) : null,
          longitude: addrLng ? parseFloat(addrLng) : null,
          delivery_instructions: addrInstructions
        })
      });
      const data = await res.json();
      if (res.ok) {
        setAddrSuccess('Address added successfully.');
        setAddrLabel('');
        setAddrRecipient('');
        setAddrPhone('');
        setAddrStreet('');
        setAddrStreet2('');
        setAddrCity('');
        setAddrState('');
        setAddrZip('');
        setAddrCountry('');
        setAddrCountryCode('');
        setAddrLat(null);
        setAddrLng(null);
        setAddrInstructions('');
        setShowAddAddressForm(false);
        await fetchDeliveryAddresses();
      } else {
        setAddrError(extractErrorMessage(data.detail, 'Failed to add address.'));
      }
    } catch (err) {
      setAddrError('Network error adding address.');
    } finally {
      setAddrSubmitting(false);
    }
  };

  const handleDeleteDeliveryAddress = async (addrId) => {
    const confirmed = await modal.confirm('Are you sure you want to delete this delivery address?', {
      title: 'Delete Delivery Address',
      confirmText: 'Delete Address',
      danger: true
    });
    if (!confirmed) return;
    setAddrError('');
    setAddrSuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/me/addresses/${addrId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        setAddrSuccess('Address deleted successfully.');
        await fetchDeliveryAddresses();
      } else {
        const data = await res.json();
        setAddrError(extractErrorMessage(data.detail, 'Failed to delete address.'));
      }
    } catch (err) {
      setAddrError('Network error deleting address.');
    }
  };

  const handleSetDefaultAddress = async (addrId) => {
    setAddrError('');
    setAddrSuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations/me/addresses/${addrId}/default`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        setAddrSuccess('Default address updated.');
        await fetchDeliveryAddresses();
      } else {
        const data = await res.json();
        setAddrError(extractErrorMessage(data.detail, 'Failed to set default address.'));
      }
    } catch (err) {
      setAddrError('Network error setting default address.');
    }
  };

  const handleUploadOrgLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setOrgLoading(true);
    setOrgError('');
    setOrgSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Logo upload failed.');
      }
      
      const relativePath = data.file.location; // starts with /uploads/
      const fullyQualifiedUrl = `${API_BASE}${relativePath}`;
      
      setOrgLogoUrl(fullyQualifiedUrl);
      setOrgSuccess('Logo uploaded successfully and added to your assets library.');
    } catch (err) {
      setOrgError(err.message);
    } finally {
      setOrgLoading(false);
    }
  };

  const [assetImages, setAssetImages] = useState([]);

  const fetchAssetImages = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/upload`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        const imagesOnly = data.filter(item => {
          const fileObj = item.UploadedFile || item;
          return fileObj && fileObj.mime_type && fileObj.mime_type.startsWith('image/');
        }).map(item => item.UploadedFile || item);
        setAssetImages(imagesOnly);
      }
    } catch (err) {
      console.error('Error fetching asset images:', err);
    }
  };

  const handleRegisterOrganization = async (e) => {
    e.preventDefault();
    if (!orgName) {
      setOrgError('Company Name is required.');
      return;
    }
    setOrgSubmitting(true);
    setOrgError('');
    setOrgSuccess('');
    
    // Construct social media serialized string
    const socialLinks = JSON.stringify({
      twitter: orgTwitter,
      facebook: orgFacebook,
      instagram: orgInstagram
    });

    const isUpdate = Boolean(orgData && orgData.id);

    try {
      const res = await fetch(`${API_BASE}/api/v1/organizations${isUpdate ? '/me' : ''}`, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          name: orgName,
          website_address: orgWebsite,
          bookings_link: orgBookings,
          social_media: socialLinks,
          enquiries_email: orgEnquiriesEmail,
          billing_address: orgBillingAddress,
          billing_city: orgBillingCity,
          billing_state: orgBillingState,
          billing_zip: orgBillingZip,
          billing_country: orgBillingCountry,
          logo_url: orgLogoUrl
        })
      });
      const data = await res.json();
      if (res.ok) {
        if (isUpdate) {
          setOrgSuccess('Organization details updated successfully.');
          setOrgData(data);
          setIsEditingOrg(false);
        } else {
          setOrgSuccess('Organization registered successfully! You are now an Operator.');
          setOrgData(data.organization);
          
          // Update user state locally and globally
          const updatedUser = {
            ...user,
            role: 'OPERATOR',
            organization_id: data.organization ? data.organization.id : null
          };
          setUser(updatedUser);
          setCurrentUserProfile(updatedUser);
          
          // Fetch organization operators list
          const opRes = await fetch(`${API_BASE}/api/v1/organizations/me/operators`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Tenant-Domain': window.location.hostname
            }
          });
          const opData = await opRes.json();
          if (opRes.ok) {
            setOrgOperators(opData);
          }
        }
      } else {
        setOrgError(extractErrorMessage(data.detail, `Failed to ${isUpdate ? 'update' : 'register'} organization.`));
      }
    } catch (err) {
      setOrgError(`Network error ${isUpdate ? 'updating' : 'registering'} organization.`);
    } finally {
      setOrgSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'organisation' && user.role === 'OPERATOR') {
      fetchOrganizationDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user.role, token, API_BASE]);

  useEffect(() => {
    fetchMyProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, API_BASE]);



  useEffect(() => {
    if (isGlobalRole && (activeTab === 'users' || activeTab === 'production' || activeTab === 'settings' || activeTab === 'agent_network')) {
      const fetchTenants = async () => {
        try {
          const res = await fetch(`${API_BASE}/api/v1/users/tenants`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Tenant-Domain': window.location.hostname
            }
          });
          const data = await res.json();
          if (res.ok) {
            const resolved = data.map(t => ({
              ...t,
              logo_url: t.logo_url && t.logo_url.includes('/uploads/') ? `${API_BASE ? API_BASE.replace(/\/+$/, '') : ''}${t.logo_url.substring(t.logo_url.indexOf('/uploads/'))}` : t.logo_url,
              background_url: t.background_url && t.background_url.includes('/uploads/') ? `${API_BASE ? API_BASE.replace(/\/+$/, '') : ''}${t.background_url.substring(t.background_url.indexOf('/uploads/'))}` : t.background_url
            }));
            setTenants(resolved);
          }
        } catch (err) {
          console.error('Failed to load tenants:', err);
        }
      };
      fetchTenants();
    }
  }, [isGlobalRole, activeTab, API_BASE, token]);

  // Debounce search query for user manager
  useEffect(() => {
    if (searchQuery === debouncedUserSearch) return;
    const timer = setTimeout(() => {
      setDebouncedUserSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedUserSearch]);

  // Fetch users list with pagination support
  const fetchUsers = async (pageOverride, sizeOverride) => {
    setLoading(true);
    setError('');
    try {
      const pageToUse = pageOverride !== undefined ? pageOverride : userPage;
      const sizeToUse = sizeOverride !== undefined ? sizeOverride : userPageSize;

      const params = new URLSearchParams();
      params.append('page', pageToUse);
      params.append('limit', sizeToUse);
      if (debouncedUserSearch) params.append('search', debouncedUserSearch);
      if (roleFilter && roleFilter !== 'ALL') params.append('role', roleFilter);
      if (isGlobalRole && selectedTenantFilter && selectedTenantFilter !== 'ALL' && selectedTenantFilter !== 'GLOBAL') {
        params.append('tenant_id', selectedTenantFilter);
      }

      const res = await fetch(`${API_BASE}/api/v1/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) {
          handleLogout();
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error(data.detail || 'Failed to fetch users list.');
      }

      if (Array.isArray(data)) {
        // Fallback or unpaginated response
        setUsers(data);
        setTotalUsers(data.length);
        setUserTotalPages(Math.max(1, Math.ceil(data.length / sizeToUse)));
      } else if (data && Array.isArray(data.users)) {
        // Paginated server response
        setUsers(data.users);
        setTotalUsers(data.total !== undefined ? data.total : data.users.length);
        setUserTotalPages(data.total_pages || Math.max(1, Math.ceil((data.total || data.users.length) / sizeToUse)));
      } else {
        setUsers([]);
        setTotalUsers(0);
        setUserTotalPages(1);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token, API_BASE, userPage, userPageSize, debouncedUserSearch, roleFilter, selectedTenantFilter]);

  // Toggle user active status (suspend / reactivate)
  const toggleUserStatus = async (targetUser) => {
    setError('');
    const originalUsers = [...users];
    
    // Optimistic UI update
    setUsers(users.map(u => u.id === targetUser.id ? { ...u, is_active: !u.is_active } : u));
    
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/${targetUser.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({ is_active: !targetUser.is_active })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update user status.');
      }
      setUsers(users.map(u => u.id === targetUser.id ? data : u));
    } catch (err) {
      setError(err.message);
      setUsers(originalUsers);
    }
  };

  // Delete user account
  const handleDeleteUser = async (targetUser) => {
    const confirmed = await modal.confirm(`Are you sure you want to permanently delete user "${targetUser.email}"? This action cannot be undone.`, {
      title: 'Delete User Account',
      confirmText: 'Delete User',
      danger: true
    });
    if (!confirmed) {
      return;
    }
    setError('');
    const originalUsers = [...users];
    setUsers(users.filter(u => u.id !== targetUser.id));
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/${targetUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to delete user.');
      }
    } catch (err) {
      setError(err.message);
      setUsers(originalUsers);
    }
  };

  // Create new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    try {
      const payload = {
        email: newEmail,
        password: newPassword,
        role: newRole,
        first_name: newFirstName,
        last_name: newLastName
      };

      if (isGlobalRole) {
        if (newTenantId && newTenantId !== 'GLOBAL') {
          payload.tenant_id = parseInt(newTenantId);
        }
      } else if (currentUserProfile?.tenant_id) {
        payload.tenant_id = currentUserProfile.tenant_id;
      }

      const res = await fetch(`${API_BASE}/api/v1/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create user.');
      }
      setFormSuccess(`User ${data.email} successfully registered!`);
      setUsers([data, ...users]);
      
      // Clear form fields
      setNewEmail('');
      setNewPassword('');
      setNewFirstName('');
      setNewLastName('');
      setNewTenantId('');
      
      setTimeout(() => {
        setShowAddForm(false);
        setFormSuccess('');
      }, 1500);
    } catch (err) {
      setFormError(err.message);
    }
  };

  // Open Edit User Modal
  const openEditModal = (targetUser) => {
    setEditingUser(targetUser);
    setEditFirstName(targetUser.first_name || '');
    setEditLastName(targetUser.last_name || '');
    setEditRole(targetUser.role || 'END_USER');
    setEditTenantId(targetUser.tenant_id ? targetUser.tenant_id.toString() : 'GLOBAL');
    setEditPassword('');
    setEditTwoFactorOverride(targetUser.two_factor_override || '');
    setEditPhone(targetUser.phone_number || '');
    setEditCompany(targetUser.company_name || '');
    setEditAddress(targetUser.billing_address || '');
    setEditCity(targetUser.billing_city || '');
    setEditState(targetUser.billing_state || '');
    setEditZip(targetUser.billing_zip || '');
    setEditCountry(targetUser.billing_country || '');
    setEditError('');
    setEditSuccess('');
  };

  // Update user profile details, password, and billing information
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditSuccess('');
    setEditLoading(true);

    const payload = {
      first_name: editFirstName,
      last_name: editLastName,
      role: editRole,
      phone_number: editPhone,
      company_name: editCompany,
      billing_address: editAddress,
      billing_city: editCity,
      billing_state: editState,
      billing_zip: editZip,
      billing_country: editCountry
    };

    if (isAdminRole) {
      const isTargetGlobal = editingUser?.role && ['GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD'].includes(editingUser.role);
      if (isGlobalRole || !isTargetGlobal) {
        payload.two_factor_override = editTwoFactorOverride || null;
      }
    }

    if (isGlobalRole) {
      payload.tenant_id = (editTenantId === 'GLOBAL' || !editTenantId) ? null : parseInt(editTenantId);
    }

    if (editPassword && editingUser.has_password) {
      payload.password = editPassword;
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1/users/${editingUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update user details.');
      }
      setEditSuccess('User details updated successfully!');
      setUsers(users.map(u => u.id === editingUser.id ? data : u));
      
      // If we are updating ourselves, sync profile too
      if (editingUser.id === currentUserProfile.id) {
        setCurrentUserProfile(data);
      }

      setTimeout(() => {
        setEditingUser(null);
        setEditSuccess('');
      }, 1200);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Open dedicated Reset Password modal
  const openResetPasswordModal = (targetUser) => {
    setResetPasswordUser(targetUser);
    setResetMode('auto_email');
    setResetHidePassword(true);
    setResetSendEmail(true);
    setNewResetPassword('');
    setResetRevealedPassword('');
    setResetCopied(false);
    setResetError('');
    setResetSuccess('');
  };

  // Generate extremely secure random password (24 characters, high entropy)
  const generateRandomPassword = () => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%&*-_=+?';
    const all = upper + lower + digits + symbols;

    const getRandomChar = (str) => {
      const arr = new Uint32Array(1);
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(arr);
        return str[arr[0] % str.length];
      }
      return str.charAt(Math.floor(Math.random() * str.length));
    };

    let chars = [
      getRandomChar(upper), getRandomChar(upper), getRandomChar(upper),
      getRandomChar(lower), getRandomChar(lower), getRandomChar(lower),
      getRandomChar(digits), getRandomChar(digits), getRandomChar(digits),
      getRandomChar(symbols), getRandomChar(symbols), getRandomChar(symbols)
    ];
    while (chars.length < 24) {
      chars.push(getRandomChar(all));
    }
    for (let i = chars.length - 1; i > 0; i--) {
      const arr = new Uint32Array(1);
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(arr);
        const j = arr[0] % (i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
      } else {
        const j = Math.floor(Math.random() * (i + 1));
        [chars[i], chars[j]] = [chars[j], chars[i]];
      }
    }
    setNewResetPassword(chars.join(''));
  };

  // Submit dedicated Reset Password
  const handleResetUserPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setResetError('');
    setResetSuccess('');
    setResetRevealedPassword('');
    setResetCopied(false);
    setResetLoading(true);

    try {
      const isAuto = resetMode === 'auto_email';
      const payload = isAuto ? {
        auto_generate: true,
        send_email: true,
        hide_from_admin: resetHidePassword,
        password_length: 24
      } : {
        password: newResetPassword,
        auto_generate: false,
        send_email: resetSendEmail,
        hide_from_admin: false
      };

      const res = await fetch(`${API_BASE}/api/v1/users/${resetPasswordUser.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to reset user password.');
      }

      setResetSuccess(data.message || 'Password reset successfully!');

      if (data.generated_password) {
        setResetRevealedPassword(data.generated_password);
      }

      if (data.user) {
        setUsers(prev => prev.map(u => u.id === resetPasswordUser.id ? { ...u, ...data.user } : u));
      }

      // If no password was revealed to the admin, auto-close modal after 2.5s
      if (!data.generated_password) {
        setTimeout(() => {
          setResetPasswordUser(null);
          setNewResetPassword('');
          setResetSuccess('');
          setResetRevealedPassword('');
          setResetCopied(false);
        }, 2500);
      }
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  // Save current user's own profile settings (from Profile tab)
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfError('');
    setProfSuccessMsg('');
    setProfLoading(true);

    const payload = {
      first_name: profFirstName,
      last_name: profLastName,
      phone_number: profPhone,
      company_name: profCompany,
      billing_address: profAddress,
      billing_city: profCity,
      billing_state: profState,
      billing_zip: profZip,
      billing_country: profCountry
    };

    if (profPassword && currentUserProfile.has_password) {
      payload.password = profPassword;
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1/users/${currentUserProfile.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save profile settings.');
      }
      setProfSuccessMsg('Profile settings saved successfully!');
      setCurrentUserProfile(data);
      setProfPassword(''); // Clear password field after successful reset
    } catch (err) {
      setProfError(err.message);
    } finally {
      setProfLoading(false);
    }
  };

  const handleToggleMy2FA = async (enable) => {
    setProfTwoFactorLoading(true);
    setProfError('');
    setProfSuccessMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/me/2fa`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          two_factor_enabled: enable
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update 2FA setting.');
      }
      setProfSuccessMsg(data.message || 'Two-factor authentication updated.');
      setCurrentUserProfile(prev => prev ? {
        ...prev,
        two_factor_enabled: data.two_factor_enabled,
        is_2fa_enforced: data.is_2fa_enforced
      } : prev);
    } catch (err) {
      setProfError(err.message);
    } finally {
      setProfTwoFactorLoading(false);
    }
  };

  // Physical Coin Mint API methods
  const fetchPackages = async () => {
    setLoadingPackages(true);
    setPackagesError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/mint/packages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch packages.');
      setPackages(data);
    } catch (err) {
      setPackagesError(err.message);
    } finally {
      setLoadingPackages(false);
    }
  };

  const fetchOrders = async () => {
    setLoadingOrders(true);
    setOrdersError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/mint/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch orders.');
      setOrders(data);
    } catch (err) {
      setOrdersError(err.message);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchAllDesigns = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/designs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (res.ok) {
        setAllDesigns(data);
      }
    } catch (err) {
      console.error('Failed to fetch designs:', err);
    }
  };

  const fetchAvailableSystemCoins = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/physical-coins`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        const sysCoins = Array.isArray(data) ? data.filter(c => c.is_system_coin) : [];
        setAvailableSystemCoins(sysCoins);
        setAllPhysicalCoins(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to fetch system coins:', err);
    }
  }, [token, API_BASE]);

  const fetchFilaments = useCallback(async () => {
    if (!token) return;
    setLoadingFilaments(true);
    setFilamentsError(null);
    try {
      let url = `${API_BASE}/api/v1/filaments`;
      const params = [];
      if (isGlobalRole && filamentTenantFilter !== 'ALL') {
        params.push(`tenant_id=${filamentTenantFilter}`);
      }
      if (filamentMaterialFilter !== 'ALL') {
        params.push(`material=${encodeURIComponent(filamentMaterialFilter)}`);
      }
      if (filamentSearch.trim()) {
        params.push(`search=${encodeURIComponent(filamentSearch.trim())}`);
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to fetch filaments.');
      }
      const data = await res.json();
      setFilaments(data);
    } catch (err) {
      console.error('Failed to load filaments:', err);
      setFilamentsError(err.message);
    } finally {
      setLoadingFilaments(false);
    }
  }, [API_BASE, token, isGlobalRole, filamentTenantFilter, filamentMaterialFilter, filamentSearch]);

  const fetchSkuPresets = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/filaments/sku-presets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        setSkuPresets(data || []);
      }
    } catch (e) {
      console.error('Failed to fetch SKU presets', e);
    }
  }, [API_BASE, token]);

  const handleSkuLookup = async (skuToLookup) => {
    const rawSku = (skuToLookup !== undefined ? skuToLookup : filamentSku).trim();
    if (!rawSku) {
      setSkuLookupNotice('Please enter or select a SKU code to look up.');
      setSkuLookupSuccess(null);
      return;
    }
    setLookingUpSku(true);
    setSkuLookupNotice(null);
    setSkuLookupSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/filaments/sku-lookup?sku=${encodeURIComponent(rawSku)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          if (data.brand) setFilamentBrand(data.brand);
          if (data.product_name) setFilamentProductName(data.product_name);
          if (data.color_name) setFilamentColorName(data.color_name);
          if (data.color_hex) setFilamentColorHex(data.color_hex);
          if (data.material_type) setFilamentMaterialType(data.material_type);
          if (data.weight) setFilamentWeight(data.weight);
          if (data.sku) setFilamentSku(data.sku);
          setSkuLookupSuccess(`Auto-filled: ${data.brand} ${data.product_name} - ${data.color_name} (${data.material_type})`);
        } else {
          setSkuLookupNotice(`SKU "${rawSku}" not found in catalog. You can still fill in the details manually.`);
        }
      } else {
        setSkuLookupNotice('Unable to look up SKU. Please verify input.');
      }
    } catch (err) {
      console.error('SKU lookup error:', err);
      setSkuLookupNotice('Error connecting to SKU lookup service.');
    } finally {
      setLookingUpSku(false);
    }
  };

  const openAddFilamentModal = () => {
    setEditingFilament(null);
    setFilamentBrand('');
    setFilamentProductName('');
    setFilamentColorName('');
    setFilamentColorHex('#D4AF37');
    setFilamentMaterialType('PLA');
    setFilamentWeight('1kg');
    setFilamentSku('');
    setFilamentIsShared(false);
    setFilamentTargetTenantId('');
    setFilamentFormError(null);
    setSkuLookupNotice(null);
    setSkuLookupSuccess(null);
    setIsAddFilamentModalOpen(true);
    fetchSkuPresets();
  };

  const openEditFilamentModal = (f) => {
    setEditingFilament(f);
    setFilamentBrand(f.brand || '');
    setFilamentProductName(f.product_name || '');
    setFilamentColorName(f.color_name || '');
    setFilamentColorHex(f.color_hex || '#D4AF37');
    setFilamentMaterialType(f.material_type || 'PLA');
    setFilamentWeight(f.weight || '1kg');
    setFilamentSku(f.sku || '');
    setFilamentIsShared(f.is_shared || false);
    setFilamentTargetTenantId(f.tenant_id ? f.tenant_id.toString() : '');
    setFilamentFormError(null);
    setSkuLookupNotice(null);
    setSkuLookupSuccess(null);
    setIsAddFilamentModalOpen(true);
    fetchSkuPresets();
  };

  const handleSaveFilament = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!filamentBrand.trim() || !filamentProductName.trim() || !filamentColorName.trim()) {
      setFilamentFormError('Please fill in brand, product name, and color name.');
      return;
    }
    setSavingFilament(true);
    setFilamentFormError(null);
    try {
      const payload = {
        brand: filamentBrand.trim(),
        product_name: filamentProductName.trim(),
        color_name: filamentColorName.trim(),
        color_hex: filamentColorHex.trim() || '#000000',
        material_type: filamentMaterialType,
        weight: filamentWeight.trim() || '1kg',
        sku: filamentSku.trim() || null
      };
      if (isGlobalRole) {
        payload.is_shared = filamentIsShared;
        payload.tenant_id = filamentTargetTenantId ? parseInt(filamentTargetTenantId) : null;
      }

      const url = editingFilament
        ? `${API_BASE}/api/v1/filaments/${editingFilament.id}`
        : `${API_BASE}/api/v1/filaments`;
      const method = editingFilament ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to save filament.');
      }

      setIsAddFilamentModalOpen(false);
      setEditingFilament(null);
      fetchFilaments();
    } catch (err) {
      console.error(err);
      setFilamentFormError(err.message);
    } finally {
      setSavingFilament(false);
    }
  };

  const handleDeleteFilament = async (filamentId, name) => {
    const confirmed = await modal.confirm(`Are you sure you want to delete the filament "${name}"?`, {
      title: 'Delete Filament',
      confirmText: 'Delete Filament',
      danger: true
    });
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/filaments/${filamentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to delete filament.');
      }
      fetchFilaments();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleShareFilament = async (filamentId) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/filaments/${filamentId}/toggle-share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to toggle filament sharing.');
      }
      fetchFilaments();
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    if ((activeTab === 'mint' || activeTab === 'production') && user.role !== 'END_USER') {
      fetchPackages();
      fetchOrders();
      fetchAllDesigns();
      fetchFilaments();
      fetchAvailableSystemCoins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token, API_BASE, fetchFilaments, fetchAvailableSystemCoins]);

  useEffect(() => {
    if (activeTab === 'production' && productionSubTab === 'filaments') {
      fetchFilaments();
    }
  }, [activeTab, productionSubTab, filamentTenantFilter, filamentMaterialFilter, filamentSearch, fetchFilaments]);

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setCheckoutLoading(true);
    setCheckoutError('');
    setCheckoutSuccess('');
    try {
      if (cart && cart.length > 0) {
        const payload = {
          items: cart.map(item => ({
            package_id: item.package.id,
            quantity: item.quantity
          })),
          billing_address: checkoutBillingAddress,
          billing_city: checkoutBillingCity,
          billing_state: checkoutBillingState,
          billing_zip: checkoutBillingZip,
          billing_country: checkoutBillingCountry
        };

        const res = await fetch(`${API_BASE}/api/v1/mint/orders/checkout-cart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          },
          body: JSON.stringify(payload)
        });
        const createdOrders = await res.json();
        if (!res.ok) throw new Error(createdOrders.detail || 'Failed to place cart orders.');

        let totalBilled = 0;
        for (const order of createdOrders) {
          const payRes = await fetch(`${API_BASE}/api/v1/mint/orders/${order.id}/pay`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Tenant-Domain': window.location.hostname
            }
          });
          const payData = await payRes.json();
          if (!payRes.ok) throw new Error(payData.detail || `Simulated payment processing failed for Order #${order.id}.`);
          totalBilled += payData.total_price;
        }

        setCheckoutSuccess(`Cart checkout successful! Paid $${totalBilled.toFixed(2)} USD for ${createdOrders.length} orders.`);
        setCart([]);
        fetchOrders();

        if (currentUserProfile) {
          const profileRes = await fetch(`${API_BASE}/api/v1/users/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const profileData = await profileRes.json();
          if (profileRes.ok) {
            setCurrentUserProfile(profileData);
          }
        }

        setTimeout(() => {
          setShowCheckoutModal(false);
          setCheckoutSuccess('');
          setSelectedPackage(null);
          setActiveTab('production');
        }, 2000);
      } else {
        const orderBody = {
          package_id: selectedPackage.id,
          billing_address: checkoutBillingAddress,
          billing_city: checkoutBillingCity,
          billing_state: checkoutBillingState,
          billing_zip: checkoutBillingZip,
          billing_country: checkoutBillingCountry
        };
        if (selectedPackage.coin_template_mode === 'library' && checkoutLibraryCoinId) {
          orderBody.library_coin_id = parseInt(checkoutLibraryCoinId, 10);
        }
        if (selectedPackage.card_template_mode === 'library' && checkoutLibraryCardId) {
          orderBody.library_card_id = parseInt(checkoutLibraryCardId, 10);
        }

        const res = await fetch(`${API_BASE}/api/v1/mint/orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          },
          body: JSON.stringify(orderBody)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to place order.');
        
        const payRes = await fetch(`${API_BASE}/api/v1/mint/orders/${data.id}/pay`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Domain': window.location.hostname
          }
        });
        const payData = await payRes.json();
        if (!payRes.ok) throw new Error(payData.detail || 'Simulated payment processing failed.');

        setCheckoutSuccess('Order successfully placed and paid ($' + payData.total_price.toFixed(2) + ' USD)!');
        setCheckoutLibraryCoinId('');
        setCheckoutLibraryCardId('');
        fetchOrders();
        
        if (currentUserProfile) {
          const profileRes = await fetch(`${API_BASE}/api/v1/users/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const profileData = await profileRes.json();
          if (profileRes.ok) {
            setCurrentUserProfile(profileData);
          }
        }

        setTimeout(() => {
          setShowCheckoutModal(false);
          setCheckoutSuccess('');
          setSelectedPackage(null);
        }, 2000);
      }
    } catch (err) {
      setCheckoutError(err.message);
    } finally {
      setCheckoutLoading(false);
    }
  };


  const handleAuthorizeArtwork = async (orderId, type, status) => {
    try {
      const body = {};
      if (type === 'face_1') body.face_1 = status;
      if (type === 'face_2') body.face_2 = status;
      if (type === 'brag') body.brag_page = status;

      const res = await fetch(`${API_BASE}/api/v1/mint/orders/${orderId}/authorize-artwork`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to authorize artwork.');
      }
      fetchOrders();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAssignFilaments = async (e) => {
    e.preventDefault();
    setFilamentLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/mint/orders/${selectedOrderForFilaments.id}/assign-filaments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          filament_primary: operatorFilamentPrimary,
          filament_accent: operatorFilamentAccent
        })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to assign filaments.');
      }
      setSelectedOrderForFilaments(null);
      fetchOrders();
    } catch (err) {
      alert(err.message);
    } finally {
      setFilamentLoading(false);
    }
  };

  const handleAssignLibraryItem = async (orderId, itemType, itemId) => {
    try {
      setAssigningLibraryLoading(prev => ({ ...prev, [`${orderId}_${itemType}`]: true }));
      const payload = {};
      if (itemType === 'coin') {
        payload.physical_coin_id = parseInt(itemId, 10);
      } else if (itemType === 'card') {
        payload.trophy_card_id = parseInt(itemId, 10);
      }

      const res = await fetch(`${API_BASE}/api/v1/mint/orders/${orderId}/assign-library-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Failed to assign library ${itemType}.`);

      if (itemType === 'coin') {
        setEditingLibraryCoinOrderId(null);
      } else {
        setEditingLibraryCardOrderId(null);
      }
      fetchOrders();
    } catch (err) {
      alert(err.message || `Failed to assign library ${itemType}`);
    } finally {
      setAssigningLibraryLoading(prev => ({ ...prev, [`${orderId}_${itemType}`]: false }));
    }
  };

  const handleOpenVaultedCoinsModal = async (order) => {
    setVaultedCoinsModalOrder(order);
    setVaultedCoinsLoading(true);
    setVaultedCoinsError('');
    setVaultedCoinsList([]);
    try {
      const currentToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/orders/${order.id}`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to load vaulted coins');
      }
      setVaultedCoinsList(data.coins || []);
    } catch (err) {
      setVaultedCoinsError(err.message || 'Failed to fetch vaulted coins for this order');
    } finally {
      setVaultedCoinsLoading(false);
    }
  };

  const handleCreateCustomPackage = async (e) => {
    e.preventDefault();
    setCustomPkgLoading(true);
    setCustomPkgError('');
    setCustomPkgSuccess('');
    try {
      // Build items config
      const itemsConfig = { ...customPkgItems };
      
      const coinMode = customPkgCoinTemplate === 'blank'
        ? 'blank'
        : (customPkgCoinTemplate === 'library' || customPkgCoinTemplate.startsWith('library_')
          ? 'library'
          : (customPkgCoinTemplate.startsWith('system_') ? 'system' : 'none'));
      const sysCoinId = customPkgCoinTemplate.startsWith('system_')
        ? parseInt(customPkgCoinTemplate.replace('system_', ''), 10)
        : (customPkgCoinTemplate.startsWith('library_') ? parseInt(customPkgCoinTemplate.replace('library_', ''), 10) : null);
      const cardMode = customPkgCardTemplate === 'blank'
        ? 'blank'
        : (customPkgCardTemplate === 'library' || customPkgCardTemplate.startsWith('library_')
          ? 'library'
          : (customPkgCardTemplate.startsWith('template_') ? 'template' : 'none'));
      const trophyCardId = customPkgCardTemplate.startsWith('template_')
        ? parseInt(customPkgCardTemplate.replace('template_', ''), 10)
        : (customPkgCardTemplate.startsWith('library_') ? parseInt(customPkgCardTemplate.replace('library_', ''), 10) : null);

      const res = await fetch(`${API_BASE}/api/v1/mint/packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({
          name: customPkgName,
          description: customPkgDescription,
          price: parseFloat(customPkgPrice),
          coin_quantity: parseInt(customPkgCoins),
          items_config: itemsConfig,
          start_date: customPkgStartDate ? new Date(customPkgStartDate).toISOString() : null,
          end_date: customPkgEndDate ? new Date(customPkgEndDate).toISOString() : null,
          coin_template_mode: coinMode,
          system_coin_id: sysCoinId,
          card_template_mode: cardMode,
          trophy_card_template_id: trophyCardId
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create package.');
      
      setCustomPkgSuccess('Custom package created successfully!');
      fetchPackages();
      
      // Reset custom package form
      setCustomPkgName('');
      setCustomPkgDescription('');
      setCustomPkgPrice('');
      setCustomPkgCoins('25');
      setCustomPkgCoinTemplate('none');
      setCustomPkgCardTemplate('none');
      setCustomPkgItems({
        annual_hosting: 0,
        standard_coin: 25,
        custom_face_design: 0,
        custom_brag_page: 0,
        custom_species_design: 0,
        challenge_coin_set: 0,
        special_edition_setup: 0,
        special_metallic_coin: 0,
        special_mounts: 0,
        shipping_cost: 0
      });
      setCustomPkgStartDate('');
      setCustomPkgEndDate('');

      setTimeout(() => {
        setShowCustomPackageModal(false);
        setCustomPkgSuccess('');
      }, 1500);
    } catch (err) {
      setCustomPkgError(err.message);
    } finally {
      setCustomPkgLoading(false);
    }
  };

  const handleAddToCart = (pkg) => {
    setCart((prevCart) => {
      const existing = prevCart.find(item => item.package.id === pkg.id);
      if (existing) {
        return prevCart.map(item =>
          item.package.id === pkg.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { package: pkg, quantity: 1 }];
    });
  };

  const handleRemoveFromCart = (pkgId) => {
    setCart((prevCart) => prevCart.filter(item => item.package.id !== pkgId));
  };

  const handleUpdateCartQuantity = (pkgId, newQty) => {
    if (newQty <= 0) {
      handleRemoveFromCart(pkgId);
      return;
    }
    setCart((prevCart) =>
      prevCart.map(item =>
        item.package.id === pkgId
          ? { ...item, quantity: newQty }
          : item
      )
    );
  };

  const handleClearCart = () => {
    setCart([]);
  };

  const handleOpenEditPackage = (pkg) => {
    setSelectedPackageForEdit(pkg);
    fetchAvailableSystemCoins();
    fetchAllDesigns();
    setEditPkgName(pkg.name || '');
    setEditPkgDescription(pkg.description || '');
    setEditPkgPrice(pkg.price || '');
    setEditPkgCoins(pkg.coin_quantity || 0);
    setEditPkgIsActive(pkg.is_active !== undefined ? pkg.is_active : true);
    setEditPkgStartDate(pkg.start_date ? new Date(pkg.start_date).toISOString().slice(0, 16) : '');
    setEditPkgEndDate(pkg.end_date ? new Date(pkg.end_date).toISOString().slice(0, 16) : '');
    setEditPkgItems(pkg.items_config || {
      annual_hosting: 0,
      standard_coin: 0,
      custom_face_design: 0,
      custom_brag_page: 0,
      custom_species_design: 0,
      challenge_coin_set: 0,
      special_edition_setup: 0,
      special_metallic_coin: 0,
      special_mounts: 0,
      shipping_cost: 0
    });

    if (pkg.coin_template_mode === 'blank') {
      setEditPkgCoinTemplate('blank');
    } else if (pkg.coin_template_mode === 'library') {
      setEditPkgCoinTemplate(pkg.system_coin_id ? `library_${pkg.system_coin_id}` : 'library');
    } else if (pkg.coin_template_mode === 'system' && pkg.system_coin_id) {
      setEditPkgCoinTemplate(`system_${pkg.system_coin_id}`);
    } else {
      setEditPkgCoinTemplate('none');
    }

    if (pkg.card_template_mode === 'blank') {
      setEditPkgCardTemplate('blank');
    } else if (pkg.card_template_mode === 'library') {
      setEditPkgCardTemplate(pkg.trophy_card_template_id ? `library_${pkg.trophy_card_template_id}` : 'library');
    } else if (pkg.card_template_mode === 'template' && pkg.trophy_card_template_id) {
      setEditPkgCardTemplate(`template_${pkg.trophy_card_template_id}`);
    } else {
      setEditPkgCardTemplate('none');
    }

    setEditPkgError('');
    setEditPkgSuccess('');
  };

  const handleUpdatePackage = async (e) => {
    e.preventDefault();
    setEditPkgLoading(true);
    setEditPkgError('');
    setEditPkgSuccess('');

    try {
      const coinMode = editPkgCoinTemplate === 'blank'
        ? 'blank'
        : (editPkgCoinTemplate === 'library' || editPkgCoinTemplate.startsWith('library_')
          ? 'library'
          : (editPkgCoinTemplate.startsWith('system_') ? 'system' : 'none'));
      const sysCoinId = editPkgCoinTemplate.startsWith('system_')
        ? parseInt(editPkgCoinTemplate.replace('system_', ''), 10)
        : (editPkgCoinTemplate.startsWith('library_') ? parseInt(editPkgCoinTemplate.replace('library_', ''), 10) : null);
      const cardMode = editPkgCardTemplate === 'blank'
        ? 'blank'
        : (editPkgCardTemplate === 'library' || editPkgCardTemplate.startsWith('library_')
          ? 'library'
          : (editPkgCardTemplate.startsWith('template_') ? 'template' : 'none'));
      const trophyCardId = editPkgCardTemplate.startsWith('template_')
        ? parseInt(editPkgCardTemplate.replace('template_', ''), 10)
        : (editPkgCardTemplate.startsWith('library_') ? parseInt(editPkgCardTemplate.replace('library_', ''), 10) : null);

      const payload = {
        name: editPkgName,
        description: editPkgDescription,
        price: parseFloat(editPkgPrice),
        coin_quantity: parseInt(editPkgCoins),
        items_config: editPkgItems,
        is_active: editPkgIsActive,
        start_date: editPkgStartDate ? new Date(editPkgStartDate).toISOString() : null,
        end_date: editPkgEndDate ? new Date(editPkgEndDate).toISOString() : null,
        coin_template_mode: coinMode,
        system_coin_id: sysCoinId,
        card_template_mode: cardMode,
        trophy_card_template_id: trophyCardId
      };

      const res = await fetch(`${API_BASE}/api/v1/mint/packages/${selectedPackageForEdit.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update package template.');
      }

      setEditPkgSuccess('Package template updated successfully!');
      fetchPackages();
      setTimeout(() => {
        setSelectedPackageForEdit(null);
        setEditPkgSuccess('');
      }, 1500);
    } catch (err) {
      setEditPkgError(err.message);
    } finally {
      setEditPkgLoading(false);
    }
  };

  const handleProcessOrder = async (orderId, nextStatus = null, nextStage = null) => {
    try {
      const payload = {};
      if (nextStatus) payload.status = nextStatus;
      if (nextStage !== null) payload.production_stage = nextStage;
      const res = await fetch(`${API_BASE}/api/v1/mint/orders/${orderId}/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to process order.');
      }
      fetchOrders();
    } catch (err) {
      alert(err.message === 'Order not found.' ? 'Order not found (this order may have already been cleared or updated). The orders list has been refreshed.' : err.message);
      fetchOrders();
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const confirmed = await modal.confirm(`Are you sure you want to delete Order #${orderId}?\n\nThis will permanently remove this incomplete order, any unissued coin allocations, and associated records.\n\nThis action cannot be undone.`, {
      title: `Delete Order #${orderId}`,
      confirmText: 'Delete Order',
      danger: true
    });
    if (!confirmed) return;

    try {
      const currentToken = token || sessionStorage.getItem('token') || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/v1/mint/orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'X-Tenant-Domain': window.location.hostname
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to delete order.');
      }
      fetchOrders();
    } catch (err) {
      alert(err.message || 'Failed to delete order.');
      fetchOrders();
    }
  };

  if (!tenantConfig || !user) return null;

  const isAdminRole = ["GLOBAL_ADMIN", "GLOBAL_POWER", "GLOBAL_STANDARD", "TENANT_ADMIN"].includes(user.role);

  const backgroundStyle = tenantConfig.background_url
    ? { backgroundImage: `url(${tenantConfig.background_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: 'var(--panel-bg, var(--bg-color))' };

  // Filtered users calculation
  const filteredUsers = users.filter(u => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch = !q ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q);
      
    const matchesRole = 
      roleFilter === 'ALL' || 
      u.role === roleFilter;

    let matchesTenant = true;
    if (isGlobalRole) {
      if (selectedTenantFilter === 'GLOBAL') {
        matchesTenant = u.tenant_id === null;
      } else if (selectedTenantFilter !== 'ALL') {
        matchesTenant = u.tenant_id === parseInt(selectedTenantFilter);
      }
    }
      
    return matchesSearch && matchesRole && matchesTenant;
  });

  const getUserRoleBadgeStyle = (role) => {
    switch (role) {
      case 'GLOBAL_ADMIN':
      case 'GLOBAL_POWER':
        return { bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.35)', color: '#ef4444' };
      case 'GLOBAL_STANDARD':
        return { bg: 'rgba(107, 114, 128, 0.12)', border: 'rgba(107, 114, 128, 0.35)', color: '#6b7280' };
      case 'TENANT_ADMIN':
        return { bg: 'rgba(2, 132, 199, 0.12)', border: 'rgba(2, 132, 199, 0.35)', color: '#0284c7' };
      case 'OPERATOR':
        return { bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.35)', color: '#10b981' };
      case 'AGENT':
        return { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.35)', color: '#8b5cf6' };
      case 'END_USER':
      default:
        return { bg: 'rgba(100, 116, 139, 0.12)', border: 'rgba(100, 116, 139, 0.35)', color: '#64748b' };
    }
  };

  const isItemInUserOrOrgLibrary = (item) => {
    if (!item) return false;
    if (user?.organization_id && item.organization_id === user.organization_id) return true;
    if (item.associated_user_id === user?.id || item.created_by_id === user?.id) return true;
    if (user?.role === 'TENANT_ADMIN' && !user?.organization_id) return true;
    if (['GLOBAL_ADMIN', 'GLOBAL_POWER'].includes(user?.role) && !user?.organization_id) return true;
    return false;
  };

  const getMyLibraryCoins = () => {
    return allPhysicalCoins.filter(c => {
      if (c.is_system_coin) return false;
      if (!isItemInUserOrOrgLibrary(c)) return false;
      const isApproved = c.status === 'approved' || c.status === 'published';
      const hasFacesAndFilaments = Boolean(c.face_1_design_id && c.face_2_design_id && c.body_filament && c.accent_filament);
      return (isApproved || hasFacesAndFilaments) && c.status !== 'archived';
    });
  };

  const getMyLibraryCards = () => {
    return allDesigns.filter(d => {
      if (d.design_type !== 'brag_page') return false;
      if (d.config && (d.config.is_system_template === true || d.config.is_system_template === 'true')) return false;
      if (!isItemInUserOrOrgLibrary(d)) return false;
      const status = d.config?.status;
      const isApproved = status === 'approved' || status === 'published' || d.config?.is_template;
      return isApproved && status !== 'archived';
    });
  };

  const renderCoinTemplatePreview = (val) => {
    if (!val || val === 'none') return null;

    if (val === 'blank') {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px dashed var(--border-color)',
          borderRadius: '8px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            border: '2px dashed var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            color: 'var(--text-mute)',
            flexShrink: 0
          }}>
            🪙
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)' }}>
              ✨ Blank Coin Preset
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
              Creates a blank physical coin in the buyer's inventory upon purchase so they can select faces & filaments.
            </span>
          </div>
        </div>
      );
    }

    if (val === 'library') {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          backgroundColor: 'rgba(63, 185, 80, 0.08)',
          border: '1px solid rgba(63, 185, 80, 0.3)',
          borderRadius: '8px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            backgroundColor: 'rgba(63, 185, 80, 0.15)',
            border: '2px solid rgba(63, 185, 80, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            flexShrink: 0
          }}>
            📚
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#3fb950' }}>
              📚 Operator Library Preset (No Proofing Required)
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
              The purchasing operator selects an approved coin directly from their library. Faces and filaments are pre-authorized automatically, skipping the proofing step.
            </span>
          </div>
        </div>
      );
    }

    if (val.startsWith('system_') || val.startsWith('library_')) {
      const isLibrary = val.startsWith('library_');
      const sId = parseInt(val.replace(isLibrary ? 'library_' : 'system_', ''), 10);
      const coin = (isLibrary ? allPhysicalCoins.find(c => c.id === sId) : availableSystemCoins.find(c => c.id === sId)) || allPhysicalCoins.find(c => c.id === sId) || availableSystemCoins.find(c => c.id === sId);
      if (!coin) return null;

      const headsDesign = allDesigns.find(d => d.id === coin.face_1_design_id);
      const tailsDesign = allDesigns.find(d => d.id === coin.face_2_design_id);
      const headsImg = headsDesign?.config?.previewImage || headsDesign?.config?.imageUrl || headsDesign?.config?.preview_image || headsDesign?.config?.photoUrl;
      const tailsImg = tailsDesign?.config?.previewImage || tailsDesign?.config?.imageUrl || tailsDesign?.config?.preview_image || tailsDesign?.config?.photoUrl;
      const isFull = Boolean(coin.face_1_design_id && coin.face_2_design_id && coin.body_filament && coin.accent_filament);

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          padding: '12px 14px',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px' }}>{isLibrary ? '📚' : '🪙'}</span>
              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)' }}>
                {coin.name} (#{coin.id}) {isLibrary ? '— My Library' : ''}
              </span>
            </div>
            {isLibrary ? (
              <span style={{
                fontSize: '10px',
                fontWeight: '700',
                padding: '2px 8px',
                borderRadius: '10px',
                backgroundColor: 'rgba(63, 185, 80, 0.15)',
                color: '#3fb950',
                border: '1px solid rgba(63, 185, 80, 0.3)'
              }}>
                ✓ Approved (No Proofing)
              </span>
            ) : (
              <span style={{
                fontSize: '10px',
                fontWeight: '700',
                padding: '2px 8px',
                borderRadius: '10px',
                backgroundColor: isFull ? 'rgba(63, 185, 80, 0.15)' : 'rgba(210, 153, 34, 0.15)',
                color: isFull ? '#3fb950' : '#d29922',
                border: isFull ? '1px solid rgba(63, 185, 80, 0.3)' : '1px solid rgba(210, 153, 34, 0.3)'
              }}>
                {isFull ? '✓ Ready for Production' : '⚠️ Incomplete'}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            {/* Heads (Face 1) */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '2px solid #c9a054',
                backgroundColor: 'rgba(0,0,0,0.25)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {headsImg ? (
                  <img src={headsImg} alt="Heads (Face 1)" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: '9px', color: 'var(--text-mute)', textAlign: 'center', padding: '2px' }}>
                    {coin.face_1_design_id ? `#${coin.face_1_design_id}` : 'Blank'}
                  </span>
                )}
              </div>
              <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-mute)' }}>Heads (Face 1)</span>
            </div>

            {/* Tails (Face 2) */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '2px solid #94a3b8',
                backgroundColor: 'rgba(0,0,0,0.25)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {tailsImg ? (
                  <img src={tailsImg} alt="Tails (Face 2)" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: '9px', color: 'var(--text-mute)', textAlign: 'center', padding: '2px' }}>
                    {coin.face_2_design_id ? `#${coin.face_2_design_id}` : 'Blank'}
                  </span>
                )}
              </div>
              <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-mute)' }}>Tails (Face 2)</span>
            </div>

            {/* Specs & Filaments */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '11px', flex: 1, minWidth: '160px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ color: 'var(--text-mute)' }}>Body:</span>
                <span style={{ fontWeight: '600', color: 'var(--text-main)', wordBreak: 'break-word' }}>
                  {coin.body_filament || <em style={{ color: '#d29922' }}>Not selected</em>}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ color: 'var(--text-mute)' }}>Accent:</span>
                <span style={{ fontWeight: '600', color: 'var(--text-main)', wordBreak: 'break-word' }}>
                  {coin.accent_filament || <em style={{ color: '#d29922' }}>Not selected</em>}
                </span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '2px', lineHeight: '1.3' }}>
                {isLibrary
                  ? 'Approved library coin applies directly to orders with no proofing step.'
                  : (isFull ? 'Ready for immediate manufacturing upon order payment.' : 'Duplicated for operator to finalize proofing.')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderCardTemplatePreview = (val) => {
    if (!val || val === 'none') return null;

    if (val === 'blank') {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px dashed var(--border-color)',
          borderRadius: '8px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            width: '46px',
            height: '64px',
            borderRadius: '5px',
            border: '2px dashed var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: 'var(--text-mute)',
            flexShrink: 0
          }}>
            🪪
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)' }}>
              ✨ Blank Trophy Card Preset
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
              Creates a new blank trophy card in the buyer's cards inventory for custom proofing from scratch.
            </span>
          </div>
        </div>
      );
    }

    if (val === 'library') {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          backgroundColor: 'rgba(63, 185, 80, 0.08)',
          border: '1px solid rgba(63, 185, 80, 0.3)',
          borderRadius: '8px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            width: '46px',
            height: '64px',
            borderRadius: '5px',
            backgroundColor: 'rgba(63, 185, 80, 0.15)',
            border: '2px solid rgba(63, 185, 80, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            flexShrink: 0
          }}>
            📚
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#3fb950' }}>
              📚 Operator Library Preset (No Proofing Required)
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
              The purchasing operator selects an approved trophy card directly from their library. The card is pre-authorized automatically, skipping the proofing step.
            </span>
          </div>
        </div>
      );
    }

    if (val.startsWith('template_') || val.startsWith('library_')) {
      const isLibrary = val.startsWith('library_');
      const tId = parseInt(val.replace(isLibrary ? 'library_' : 'template_', ''), 10);
      const card = allDesigns.find(d => d.id === tId);
      if (!card) return null;

      const frontImg = card.config?.preview_image || card.config?.previewImage || card.config?.imageUrl;
      const backImg = card.config?.preview_image_back;
      const isSys = Boolean(card.config && (card.config.is_system_template === true || card.config.is_system_template === 'true'));
      const dims = card.config?.cardDimensions || { width: 400, height: 560 };

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          padding: '12px 14px',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px' }}>{isLibrary ? '📚' : '🪪'}</span>
              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-main)' }}>
                {card.name} (#{card.id}) {isLibrary ? '— My Library' : ''}
              </span>
            </div>
            {isLibrary ? (
              <span style={{
                fontSize: '10px',
                fontWeight: '700',
                padding: '2px 8px',
                borderRadius: '10px',
                backgroundColor: 'rgba(63, 185, 80, 0.15)',
                color: '#3fb950',
                border: '1px solid rgba(63, 185, 80, 0.3)'
              }}>
                ✓ Approved (No Proofing)
              </span>
            ) : isSys ? (
              <span style={{
                fontSize: '10px',
                fontWeight: '800',
                padding: '2px 8px',
                borderRadius: '10px',
                backgroundColor: 'rgba(234, 179, 8, 0.15)',
                color: '#facc15',
                border: '1px solid rgba(234, 179, 8, 0.35)',
                letterSpacing: '0.5px'
              }}>
                ⭐ System Template
              </span>
            ) : (
              <span style={{
                fontSize: '10px',
                fontWeight: '600',
                padding: '2px 8px',
                borderRadius: '10px',
                backgroundColor: 'rgba(56, 139, 253, 0.15)',
                color: '#58a6ff',
                border: '1px solid rgba(56, 139, 253, 0.3)'
              }}>
                Card Template
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            {/* Front Card Preview */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '54px',
                height: '76px',
                borderRadius: '5px',
                overflow: 'hidden',
                border: '1px solid var(--border-color)',
                backgroundColor: 'rgba(0,0,0,0.25)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {frontImg ? (
                  <img src={frontImg} alt="Card Front" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: '9px', color: 'var(--text-mute)', textAlign: 'center', padding: '2px' }}>
                    No Front
                  </span>
                )}
              </div>
              <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-mute)' }}>Front Face</span>
            </div>

            {/* Back Card Preview */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '54px',
                height: '76px',
                borderRadius: '5px',
                overflow: 'hidden',
                border: '1px solid var(--border-color)',
                backgroundColor: 'rgba(0,0,0,0.25)',
                boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {backImg ? (
                  <img src={backImg} alt="Card Back" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: '9px', color: 'var(--text-mute)', textAlign: 'center', padding: '2px' }}>
                    No Back
                  </span>
                )}
              </div>
              <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-mute)' }}>Back Face</span>
            </div>

            {/* Card details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', flex: 1, minWidth: '160px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ color: 'var(--text-mute)' }}>Format:</span>
                <span style={{ fontWeight: '600', color: 'var(--text-main)' }}>
                  {dims.width} × {dims.height} px
                </span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4', marginTop: '2px' }}>
                {isLibrary
                  ? 'Approved library card attaches directly to order upon purchase with no proofing step.'
                  : (isSys
                    ? 'Official predesigned system template will duplicate into buyer\'s trophy cards grid upon purchase.'
                    : 'Card template will duplicate into buyer\'s trophy cards grid for proofing upon purchase.')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderSidebarNavItem = (id, label, iconSvg) => {
    const isActive = activeTab === id;
    return (
      <button
        key={id}
        onClick={() => {
          setActiveTab(id);
          if (isMobile) setIsMobileSidebarOpen(false);
        }}
        title={isSidebarCollapsed ? label : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isSidebarCollapsed ? 0 : '12px',
          width: '100%',
          padding: isSidebarCollapsed ? '10px 0' : '9px 12px',
          justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
          borderRadius: '7px',
          border: isActive ? '1px solid var(--accent-color)' : '1px solid transparent',
          backgroundColor: isActive ? 'rgba(128, 128, 128, 0.14)' : 'transparent',
          color: isActive ? 'var(--primary-color)' : 'var(--menu-text, var(--text-main))',
          fontWeight: isActive ? '600' : '500',
          fontSize: '13px',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          boxSizing: 'border-box',
          marginBottom: '2px',
          position: 'relative'
        }}
        onMouseOver={(e) => {
          if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(128, 128, 128, 0.08)';
        }}
        onMouseOut={(e) => {
          if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isActive ? 'var(--primary-color)' : 'var(--menu-text-mute, var(--text-mute))',
          flexShrink: 0
        }}>
          {iconSvg}
        </span>
        {!isSidebarCollapsed && (
          <span style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {label}
          </span>
        )}
        {isActive && (
          <span style={{
            position: 'absolute',
            left: 0,
            top: '6px',
            bottom: '6px',
            width: '3px',
            borderRadius: '0 3px 3px 0',
            backgroundColor: 'var(--primary-color)'
          }} />
        )}
      </button>
    );
  };

  const renderSidebarSectionHeader = (title) => {
    if (isSidebarCollapsed) {
      return <div style={{ height: '8px' }} />;
    }
    return (
      <div style={{
        fontSize: '10px',
        fontWeight: '700',
        letterSpacing: '0.6px',
        color: 'var(--menu-text-mute, var(--text-mute))',
        textTransform: 'uppercase',
        padding: '14px 12px 4px 12px',
        userSelect: 'none'
      }}>
        {title}
      </div>
    );
  };

  const currentTabTitle = activeTab === 'production' && user.role === 'OPERATOR' ? 'Orders' :
    activeTab === 'overview' ? (user.role === 'AGENT' ? 'Agent Partner Portal' : 'Overview') :
    activeTab === 'agent_network' ? 'Agent Network' :
    activeTab === 'production' ? 'Production' :
    activeTab === 'mint' ? 'Coin Mint' :
    activeTab === 'vault' ? (user?.role === 'END_USER' ? 'Personal Vault' : 'The Vault') :
    activeTab === 'users' ? 'User Manager' :
    activeTab === 'companies' ? 'Company Manager' :
    activeTab === 'data_manager' ? 'Data Manager' :
    activeTab === 'design' ? 'Design Studio' :
    activeTab === 'assets' ? 'Assets / Media' :
    activeTab === 'brag' ? 'Trophy Cards' :
    activeTab === 'operator_coins' ? 'Coins' :
    activeTab === 'organisation' ? 'Organisation' :
    activeTab === 'settings' ? 'Site Settings' :
    activeTab === 'support' ? 'Support & Help Center' :
    activeTab === 'profile' ? 'My Profile & Account' : activeTab;

  return (
    <div style={{
      ...backgroundStyle,
      flex: 1,
      minHeight: 0,
      height: '100%',
      maxHeight: '100%',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden',
      color: 'var(--text-main)',
      position: 'relative',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Mobile Drawer Backdrop */}
      {isMobile && isMobileSidebarOpen && (
        <div
          onClick={() => setIsMobileSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 998,
            backdropFilter: 'blur(3px)'
          }}
        />
      )}

      {/* Modern Left Sidebar Navigation */}
      <aside style={{
        width: isMobile ? '270px' : (isSidebarCollapsed ? '68px' : '250px'),
        background: 'var(--menu-bg, var(--panel-bg))',
        borderRight: '1px solid var(--border-color)',
        display: isMobile ? (isMobileSidebarOpen ? 'flex' : 'none') : 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '16px 10px',
        transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        flexShrink: 0,
        boxSizing: 'border-box',
        zIndex: isMobile ? 999 : 50,
        ...(isMobile ? {
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          boxShadow: '4px 0 24px rgba(0, 0, 0, 0.5)',
          overflowY: 'auto'
        } : {
          height: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden'
        })
      }}>
        {/* Navigation Sections */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Sidebar Brand / Role Header */}
          <div style={{
            padding: isSidebarCollapsed ? '4px 0 12px 0' : '4px 8px 12px 8px',
            borderBottom: '1px solid var(--border-color)',
            marginBottom: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
            gap: '8px'
          }}>
            {!isSidebarCollapsed ? (
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  color: 'var(--primary-color)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {user.role.replace('_', ' ')}
                </div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--menu-text, var(--text-main))',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {tenantConfig?.brand_name || 'TrophyCoin'}
                </div>
              </div>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)' }}>
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            )}
            {isMobile && (
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-mute)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '2px 6px'
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* 1. General Section */}
          {renderSidebarSectionHeader('General')}
          {renderSidebarNavItem(
            'overview',
            user?.role === 'AGENT' ? 'Agent Portal' : 'Overview',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="9" />
              <rect x="14" y="3" width="7" height="5" />
              <rect x="14" y="12" width="7" height="9" />
              <rect x="3" y="16" width="7" height="5" />
            </svg>
          )}
          {renderSidebarNavItem(
            'vault',
            user?.role === 'END_USER' ? 'Personal Vault' : 'The Vault',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="12" cy="12" r="5" />
              <path d="M12 7v2" /><path d="M12 15v2" /><path d="M7 12h2" /><path d="M15 12h2" />
            </svg>
          )}

          {/* 2. Operations Section */}
          {user.role !== 'END_USER' && (
            <>
              {renderSidebarSectionHeader('Operations')}
              {renderSidebarNavItem(
                'production',
                user.role === 'OPERATOR' ? 'Orders' : 'Production',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
              )}
              {renderSidebarNavItem(
                'mint',
                'Coin Mint',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v12M12 10a2 2 0 0 0-2 2 2 2 0 0 0 2 2h2a2 2 0 0 1 2 2 2 2 0 0 1-2 2m-2-12a2 2 0 0 1 2-2h2" />
                </svg>
              )}
              {(user.role === 'OPERATOR' || user.role === 'AGENT') && renderSidebarNavItem(
                'operator_coins',
                'Coins',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <path d="M12 8v8" />
                </svg>
              )}
            </>
          )}

          {/* 3. Creative Section */}
          {(isGlobalRole || user.role === 'TENANT_ADMIN' || user.role === 'OPERATOR' || user.role === 'AGENT' || user.role === 'END_USER') && (
            <>
              {renderSidebarSectionHeader('Creative')}
              {(isGlobalRole || user.role === 'TENANT_ADMIN') && renderSidebarNavItem(
                'design',
                'Design Studio',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              )}
              {(user.role === 'OPERATOR' || user.role === 'AGENT') && renderSidebarNavItem(
                'assets',
                'Assets / Media',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              )}
              {(user.role === 'END_USER' || user.role === 'OPERATOR' || user.role === 'AGENT') && renderSidebarNavItem(
                'brag',
                'Trophy Cards',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              )}
            </>
          )}

          {/* 4. Administration Section */}
          {(isAdminRole || isGlobalRole || user.role === 'TENANT_ADMIN' || user.role === 'END_USER' || user.role === 'OPERATOR' || user.role === 'AGENT') && (
            <>
              {renderSidebarSectionHeader('Administration')}
              {isAdminRole && renderSidebarNavItem(
                'users',
                'User Manager',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              )}
              {(isAdminRole || isGlobalRole || user.role === 'TENANT_ADMIN') && renderSidebarNavItem(
                'companies',
                'Company Manager',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
                  <path d="M9 22v-4h6v4" />
                  <path d="M8 6h.01" /><path d="M16 6h.01" />
                  <path d="M8 10h.01" /><path d="M16 10h.01" />
                  <path d="M8 14h.01" /><path d="M16 14h.01" />
                </svg>
              )}
              {(isAdminRole || isGlobalRole || user.role === 'TENANT_ADMIN') && renderSidebarNavItem(
                'agent_network',
                'Agent Network',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              )}
              {(isGlobalRole || user.role === 'TENANT_ADMIN') && renderSidebarNavItem(
                'data_manager',
                'Data Manager',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22c5.523 0 10-2.239 10-5V7c0-2.761-4.477-5-10-5S2 4.239 2 7v10c0 2.761 4.477 5 10 5z" />
                  <path d="M2 7c0 2.21 4.477 4 10 4s10-1.79 10-4" />
                  <path d="M2 12c0 2.21 4.477 4 10 4s10-1.79 10-4" />
                </svg>
              )}
              {(user.role === 'END_USER' || user.role === 'OPERATOR' || user.role === 'AGENT') && renderSidebarNavItem(
                'organisation',
                'Organisation',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              )}
            </>
          )}
        </div>

        {/* 5. Bottom System & Collapse Controls */}
        <div style={{
          borderTop: '1px solid var(--border-color)',
          paddingTop: '8px',
          marginTop: '16px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {hasSettingsAccess && renderSidebarNavItem(
            'settings',
            'Site Settings',
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          )}
          {!isMobile && (
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: isSidebarCollapsed ? 0 : '10px',
                width: '100%',
                padding: isSidebarCollapsed ? '10px 0' : '9px 12px',
                justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                borderRadius: '7px',
                border: '1px solid transparent',
                backgroundColor: 'transparent',
                color: 'var(--menu-text-mute, var(--text-mute))',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                marginTop: '4px',
                boxSizing: 'border-box'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(128, 128, 128, 0.08)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <polyline points={isSidebarCollapsed ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
              </svg>
              {!isSidebarCollapsed && <span>Collapse</span>}
            </button>
          )}
        </div>
      </aside>

      {/* Main Workspace Area */}
      <main 
        ref={mainContentRef}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          height: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: isMobile ? '12px 10px' : '24px 30px',
          boxSizing: 'border-box'
        }}
      >
        {/* Mobile Navigation Trigger Bar */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '14px',
            padding: '10px 14px',
            background: 'var(--menu-bg, var(--panel-bg))',
            border: '1px solid var(--border-color)',
            borderRadius: '8px'
          }}>
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '6px 12px',
                color: 'var(--text-main)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              Navigation
            </button>
            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--primary-color)' }}>
              {currentTabTitle}
            </span>
          </div>
        )}

        {/* Tab Content Panel Sheet */}
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          padding: isMobile ? '16px' : '24px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          zIndex: 0,
          flex: 1,
          width: '100%',
          boxSizing: 'border-box'
        }}>

          {/* Tab Content: 1. Overview */}
          {activeTab === 'overview' && (
            user.role === 'AGENT' ? (
              <AgentPortal
                user={user}
                currentUserProfile={currentUserProfile}
                tenantConfig={tenantConfig}
                onNavigateToMint={() => setActiveTab('mint')}
              />
            ) : (
              <OverviewTab
                user={user}
                currentUserProfile={currentUserProfile}
                tenantConfig={tenantConfig}
                isMobile={isMobile}
                setActiveTab={setActiveTab}
                apiBase={API_BASE}
              />
            )
          )}

        {/* Tab Content: Coin Mint */}
        {activeTab === 'mint' && user.role !== 'END_USER' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><circle cx="12" cy="12" r="10" /></svg>
                  Coin Minting & Package Manager
                </h3>
                <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: 'var(--text-mute)' }}>
                  Order reward coin packages, configure custom artwork verification, select filaments, and manage production queues.
                </p>
              </div>
              
              {user.role === 'TENANT_ADMIN' && (
                <button
                  onClick={() => {
                    setCustomPkgError('');
                    setCustomPkgSuccess('');
                    setCustomPkgCoinTemplate('none');
                    setCustomPkgCardTemplate('none');
                    fetchAvailableSystemCoins();
                    fetchAllDesigns();
                    setShowCustomPackageModal(true);
                  }}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 18px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  Create Custom Package +
                </button>
              )}
            </div>


            {/* Error displays */}
            {packagesError && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.2)',
                borderRadius: '6px',
                padding: '12px 16px',
                color: '#ff7b72',
                fontSize: '13px'
              }}>
                {packagesError}
              </div>
            )}

            {/* Tab Inner Sections: Grid/Flex Layout for Browse & Cart */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: (user.role === 'OPERATOR' && cart.length > 0 && !isMobile) ? '2fr 1.2fr' : '1fr',
              gap: '24px',
              alignItems: 'flex-start'
            }}>
              
              {/* SECTION 1: BROWSE PACKAGES */}
              <div>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
                  Available Package Catalog
                </h4>
                
                {loadingPackages ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-mute)' }}>
                    Loading packages...
                  </div>
                ) : packages.length === 0 ? (
                  <div style={{
                    border: '1px dashed var(--border-color)',
                    borderRadius: '8px',
                    padding: '40px',
                    textAlign: 'center',
                    color: 'var(--text-mute)'
                  }}>
                    No active packages configured.
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
                    gap: '20px'
                  }}>
                    {packages.map((pkg) => {
                      const containsCoins = pkg.coin_quantity > 0;
                      const canEdit = isGlobalRole || user.role === 'TENANT_ADMIN';
                      return (
                        <div
                          key={pkg.id}
                          style={{
                            background: 'var(--card-bg)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px',
                            padding: '20px',
                            boxShadow: 'var(--card-shadow)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            gap: '15px',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {pkg.is_custom && (
                            <span style={{
                              position: 'absolute',
                              top: '12px',
                              right: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: 'rgba(56, 139, 253, 0.25)',
                              color: '#58a6ff',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              zIndex: 35,
                              backdropFilter: 'blur(8px)'
                            }}>
                              Tenant Custom
                            </span>
                          )}

                          {/* Dynamic Predefined Artwork Showcase (Overlapped Coins & Fanned Cards) */}
                          <PackageArtShowcase
                            pkg={pkg}
                            availableSystemCoins={availableSystemCoins}
                            allPhysicalCoins={allPhysicalCoins}
                            allDesigns={allDesigns}
                          />

                          <div>
                            <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {pkg.name} {containsCoins && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}><circle cx="12" cy="12" r="10" /></svg>
                              )}
                            </h5>
                            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                              {pkg.description || 'No description provided.'}
                            </p>
                            
                            <div style={{
                              background: 'var(--panel-bg)',
                              borderRadius: '8px',
                              padding: '12px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px'
                            }}>
                              <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)', textTransform: 'uppercase' }}>Inclusions:</div>
                              {Object.entries(pkg.items_config || {})
                                .filter(([_, val]) => typeof val === 'number' ? val > 0 : (parseInt(val, 10) > 0 || (!!val && val !== '0' && val !== 'false')))
                                .map(([key, val]) => (
                                  <div key={key} style={{ fontSize: '13px', display: 'flex', justifyItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                                      {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                    </span>
                                    <span style={{ fontWeight: '600' }}>x{val}</span>
                                  </div>
                                ))}
                              </div>

                              {((pkg.coin_template_mode && pkg.coin_template_mode !== 'none') || (pkg.card_template_mode && pkg.card_template_mode !== 'none')) && (
                                <div style={{
                                  marginTop: '8px',
                                  padding: '8px 10px',
                                  backgroundColor: 'rgba(56, 139, 253, 0.08)',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(56, 139, 253, 0.2)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                  fontSize: '11px'
                                }}>
                                  <div style={{ fontWeight: '700', color: 'var(--primary-color)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Purchase Automation:</div>
                                  {pkg.coin_template_mode === 'blank' && (
                                    <div style={{ color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <span>🪙</span> <span>Coin: <strong>Blank Coin</strong> (Proofing)</span>
                                    </div>
                                  )}
                                  {pkg.coin_template_mode === 'library' && (
                                    <div style={{ color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <span>📚</span> <span>Coin: <strong>{pkg.system_coin ? pkg.system_coin.name : (pkg.system_coin_id ? `Library Coin #${pkg.system_coin_id}` : 'Operator Library')}</strong> (Library — No Proofing)</span>
                                    </div>
                                  )}
                                  {pkg.coin_template_mode === 'system' && (
                                    <div style={{ color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <span>🪙</span> <span>Coin: <strong>{pkg.system_coin ? pkg.system_coin.name : `System Coin #${pkg.system_coin_id}`}</strong></span>
                                    </div>
                                  )}
                                  {pkg.card_template_mode === 'blank' && (
                                    <div style={{ color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <span>🪪</span> <span>Card: <strong>Blank Trophy Card</strong></span>
                                    </div>
                                  )}
                                  {pkg.card_template_mode === 'library' && (
                                    <div style={{ color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <span>📚</span> <span>Card: <strong>{pkg.trophy_card_template ? pkg.trophy_card_template.name : (pkg.trophy_card_template_id ? `Library Card #${pkg.trophy_card_template_id}` : 'Operator Library')}</strong> (Library — No Proofing)</span>
                                    </div>
                                  )}
                                  {pkg.card_template_mode === 'template' && (
                                    <div style={{ color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <span>🪪</span> <span>Card: <strong>{pkg.trophy_card_template ? pkg.trophy_card_template.name : `Card Template #${pkg.trophy_card_template_id}`}</strong></span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '5px'
                          }}>
                            <div>
                              <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Package Base Price:</div>
                              <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--accent-color)' }}>
                                ${pkg.price.toFixed(2)} <span style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-mute)' }}>USD</span>
                              </div>
                            </div>
                            
                            {canEdit && (
                              <button
                                onClick={() => handleOpenEditPackage(pkg)}
                                style={{
                                  backgroundColor: 'transparent',
                                  color: 'var(--text-mute)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '6px',
                                  padding: '8px 12px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.color = 'var(--text-main)';
                                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.color = 'var(--text-mute)';
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                ✎ Edit
                              </button>
                            )}

                            {user.role === 'OPERATOR' && (
                              <button
                                onClick={() => {
                                  handleAddToCart(pkg);
                                }}
                                style={{
                                  backgroundColor: 'var(--primary-color)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '8px 16px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                Add to Cart
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* SECTION 2: CART PANEL FOR OPERATORS */}
              {user.role === 'OPERATOR' && cart.length > 0 && (
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: 'var(--card-shadow)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  position: 'sticky',
                  top: '20px'
                }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
                    Your Cart ({cart.reduce((sum, item) => sum + item.quantity, 0)})
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '300px', overflowY: 'auto' }}>
                    {cart.map((item) => (
                      <div key={item.package.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>{item.package.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>${item.package.price.toFixed(2)} USD each</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => handleUpdateCartQuantity(item.package.id, item.quantity - 1)}
                            style={{ width: '22px', height: '22px', backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-main)', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}
                          >-</button>
                          <span style={{ fontSize: '13px', fontWeight: '600', minWidth: '16px', textAlign: 'center' }}>{item.quantity}</span>
                          <button
                            onClick={() => handleUpdateCartQuantity(item.package.id, item.quantity + 1)}
                            style={{ width: '22px', height: '22px', backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-main)', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}
                          >+</button>
                          <button
                            onClick={() => handleRemoveFromCart(item.package.id)}
                            style={{ border: 'none', backgroundColor: 'transparent', color: '#ff7b72', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px', marginLeft: '6px' }}
                            title="Remove"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: 'var(--text-mute)' }}>Subtotal:</span>
                      <strong style={{ color: 'var(--text-main)' }}>
                        ${cart.reduce((sum, item) => sum + (item.package.price * item.quantity), 0).toFixed(2)} USD
                      </strong>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button
                        onClick={handleClearCart}
                        style={{ flex: 1, backgroundColor: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-mute)', borderRadius: '6px', padding: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPackage(null);
                          setCheckoutBillingAddress(currentUserProfile?.billing_address || '');
                          setCheckoutBillingCity(currentUserProfile?.billing_city || '');
                          setCheckoutBillingState(currentUserProfile?.billing_state || '');
                          setCheckoutBillingZip(currentUserProfile?.billing_zip || '');
                          setCheckoutBillingCountry(currentUserProfile?.billing_country || '');
                          setShowCheckoutModal(true);
                        }}
                        style={{ flex: 2, backgroundColor: 'var(--primary-color)', border: 'none', color: 'white', borderRadius: '6px', padding: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                      >
                        Checkout
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab Content: The Vault */}
        {activeTab === 'vault' && (
          <CoinVaultTab
            user={user}
            tenantConfig={tenantConfig}
            isGlobalRole={isGlobalRole}
            isAdminRole={isAdminRole}
            tenants={tenants}
            selectedTenantId={selectedTenantId}
            setSelectedTenantId={setSelectedTenantId}
          />
        )}

        {/* Tab Content: Production */}
        {activeTab === 'production' && user.role !== 'END_USER' && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Sub-tab Navigation for Production Users */}
            {(isGlobalRole || user.role === 'TENANT_ADMIN') && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                borderBottom: '1px solid var(--border-color)',
                paddingBottom: '0',
                gap: '4px',
                zIndex: 1,
                position: 'relative'
              }}>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setProductionSubTab('orders')}
                    style={{
                      background: productionSubTab === 'orders' ? 'var(--panel-color)' : 'transparent',
                      color: productionSubTab === 'orders' ? 'var(--primary-color)' : 'var(--text-mute)',
                      border: '1px solid transparent',
                      borderLeftColor: productionSubTab === 'orders' ? 'var(--border-color)' : 'transparent',
                      borderRightColor: productionSubTab === 'orders' ? 'var(--border-color)' : 'transparent',
                      borderTopColor: productionSubTab === 'orders' ? 'var(--border-color)' : 'transparent',
                      borderBottomColor: productionSubTab === 'orders' ? 'var(--panel-color)' : 'transparent',
                      borderRadius: '8px 8px 0 0',
                      padding: '8px 18px',
                      fontSize: '13px',
                      fontWeight: productionSubTab === 'orders' ? '600' : '500',
                      cursor: 'pointer',
                      marginBottom: '-1px',
                      transition: 'all 0.15s ease',
                      zIndex: productionSubTab === 'orders' ? 2 : 1,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: productionSubTab === 'orders' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = 'var(--text-main)';
                      if (productionSubTab !== 'orders') {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                        e.currentTarget.style.borderRightColor = 'var(--border-color)';
                        e.currentTarget.style.borderTopColor = 'var(--border-color)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (productionSubTab !== 'orders') {
                        e.currentTarget.style.color = 'var(--text-mute)';
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeftColor = 'transparent';
                        e.currentTarget.style.borderRightColor = 'transparent';
                        e.currentTarget.style.borderTopColor = 'transparent';
                      } else {
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                      <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
                    </svg>
                    <span>Orders</span>
                    {(() => {
                      const count = (isGlobalRole || user.role === 'TENANT_ADMIN')
                        ? orders.filter(o => o.status !== 'in_production').length
                        : orders.length;
                      return count > 0 ? (
                        <span style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'var(--text-mute)',
                          borderRadius: '10px',
                          padding: '1px 6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {count}
                        </span>
                      ) : null;
                    })()}
                  </button>

                  {(isGlobalRole || user.role === 'TENANT_ADMIN') && (
                  <button
                    type="button"
                    onClick={() => setProductionSubTab('production_line')}
                    style={{
                      background: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? 'var(--panel-color)' : 'transparent',
                      color: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? 'var(--primary-color)' : 'var(--text-mute)',
                      border: '1px solid transparent',
                      borderLeftColor: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? 'var(--border-color)' : 'transparent',
                      borderRightColor: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? 'var(--border-color)' : 'transparent',
                      borderTopColor: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? 'var(--border-color)' : 'transparent',
                      borderBottomColor: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? 'var(--panel-color)' : 'transparent',
                      borderRadius: '8px 8px 0 0',
                      padding: '8px 18px',
                      fontSize: '13px',
                      fontWeight: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? '600' : '500',
                      cursor: 'pointer',
                      marginBottom: '-1px',
                      transition: 'all 0.15s ease',
                      zIndex: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? 2 : 1,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: (productionSubTab === 'production_line' || productionSubTab === 'in_production') ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = 'var(--text-main)';
                      if (productionSubTab !== 'production_line' && productionSubTab !== 'in_production') {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                        e.currentTarget.style.borderRightColor = 'var(--border-color)';
                        e.currentTarget.style.borderTopColor = 'var(--border-color)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (productionSubTab !== 'production_line' && productionSubTab !== 'in_production') {
                        e.currentTarget.style.color = 'var(--text-mute)';
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeftColor = 'transparent';
                        e.currentTarget.style.borderRightColor = 'transparent';
                        e.currentTarget.style.borderTopColor = 'transparent';
                      } else {
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                      <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                    </svg>
                    <span>Production Line</span>
                    {(() => {
                      const count = orders.filter(o => o.status === 'in_production').length;
                      return count > 0 ? (
                        <span style={{
                          backgroundColor: 'rgba(160, 196, 255, 0.2)',
                          color: '#a0c4ff',
                          borderRadius: '10px',
                          padding: '1px 6px',
                          fontSize: '11px',
                          fontWeight: '700'
                        }}>
                          {count}
                        </span>
                      ) : null;
                    })()}
                  </button>
                  )}

                  {(isGlobalRole || user.role === 'TENANT_ADMIN') && (
                    <button
                      type="button"
                      onClick={() => setProductionSubTab('filaments')}
                      style={{
                        background: productionSubTab === 'filaments' ? 'var(--panel-color)' : 'transparent',
                        color: productionSubTab === 'filaments' ? 'var(--primary-color)' : 'var(--text-mute)',
                        border: '1px solid transparent',
                        borderLeftColor: productionSubTab === 'filaments' ? 'var(--border-color)' : 'transparent',
                        borderRightColor: productionSubTab === 'filaments' ? 'var(--border-color)' : 'transparent',
                        borderTopColor: productionSubTab === 'filaments' ? 'var(--border-color)' : 'transparent',
                        borderBottomColor: productionSubTab === 'filaments' ? 'var(--panel-color)' : 'transparent',
                        borderRadius: '8px 8px 0 0',
                        padding: '8px 18px',
                        fontSize: '13px',
                        fontWeight: productionSubTab === 'filaments' ? '600' : '500',
                        cursor: 'pointer',
                        marginBottom: '-1px',
                        transition: 'all 0.15s ease',
                        zIndex: productionSubTab === 'filaments' ? 2 : 1,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: productionSubTab === 'filaments' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.color = 'var(--text-main)';
                        if (productionSubTab !== 'filaments') {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                          e.currentTarget.style.borderLeftColor = 'var(--border-color)';
                          e.currentTarget.style.borderRightColor = 'var(--border-color)';
                          e.currentTarget.style.borderTopColor = 'var(--border-color)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (productionSubTab !== 'filaments') {
                          e.currentTarget.style.color = 'var(--text-mute)';
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderLeftColor = 'transparent';
                          e.currentTarget.style.borderRightColor = 'transparent';
                          e.currentTarget.style.borderTopColor = 'transparent';
                        } else {
                          e.currentTarget.style.color = 'var(--primary-color)';
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="12" r="4" />
                        <line x1="12" y1="2" x2="12" y2="4" />
                        <line x1="12" y1="20" x2="12" y2="22" />
                        <line x1="20" y1="12" x2="22" y2="12" />
                        <line x1="2" y1="12" x2="4" y2="12" />
                      </svg>
                      <span>Filaments</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={{
              background: (isGlobalRole || user.role === 'TENANT_ADMIN') ? 'var(--panel-color)' : 'transparent',
              border: (isGlobalRole || user.role === 'TENANT_ADMIN') ? '1px solid var(--border-color)' : 'none',
              borderTop: 'none',
              borderRadius: (isGlobalRole || user.role === 'TENANT_ADMIN') ? '0 0 8px 8px' : '0',
              padding: (isGlobalRole || user.role === 'TENANT_ADMIN') ? '24px' : '0px',
              boxShadow: (isGlobalRole || user.role === 'TENANT_ADMIN') ? '0 4px 12px rgba(0, 0, 0, 0.05)' : 'none',
              position: 'relative',
              zIndex: 0
            }}>
              {(productionSubTab === 'orders' || user?.role === 'OPERATOR') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

                {/* SECTION 2: ORDERS */}
                <div>
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>
                    Orders
                  </h4>
                {ordersError && (
                  <div style={{
                    backgroundColor: 'rgba(248, 81, 73, 0.15)',
                    border: '1px solid rgba(248, 81, 73, 0.2)',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    color: '#ff7b72',
                    fontSize: '13px',
                    marginBottom: '15px'
                  }}>
                    {ordersError}
                  </div>
                )}
                
                {(() => {
                  const displayedOrders = (isGlobalRole || user.role === 'TENANT_ADMIN')
                    ? orders.filter(o => o.status !== 'in_production')
                    : orders;

                  if (loadingOrders) {
                    return (
                      <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-mute)' }}>
                        Loading orders...
                      </div>
                    );
                  }

                  if (displayedOrders.length === 0) {
                    return (
                      <div style={{
                        border: '1px dashed var(--border-color)',
                        borderRadius: '8px',
                        padding: '40px',
                        textAlign: 'center',
                        color: 'var(--text-mute)'
                      }}>
                        No active orders found.
                      </div>
                    );
                  }

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {displayedOrders.map((order) => {
                      const isUnpaid = order.payment_status === 'unpaid';
                      const details = order.coin_details && order.coin_details[0];
                      const items = order.items_snapshot?.items || {};
                      const pkg = packages.find(p => p.id === order.package_id);
                      const hasTrophyCard = (items.custom_brag_page > 0) || 
                        Boolean(details?.brag_page_design_id) || 
                        Boolean(pkg?.trophy_card_template_id) || 
                        Boolean(pkg?.card_template_mode && pkg.card_template_mode !== 'none') || 
                        Boolean(order.items_snapshot?.trophy_card_template_id) || 
                        Boolean(order.items_snapshot?.card_template_mode && order.items_snapshot.card_template_mode !== 'none');
                      
                      const physicalCoin = details?.physical_coin_id ? (allPhysicalCoins.find(c => c.id === details.physical_coin_id) || availableSystemCoins.find(c => c.id === details.physical_coin_id)) : null;
                      const sysCoinId = order.items_snapshot?.system_coin_id || pkg?.system_coin_id;
                      const sysCoin = sysCoinId ? (availableSystemCoins.find(c => c.id === sysCoinId) || allPhysicalCoins.find(c => c.id === sysCoinId)) : null;

                      const isFullyDefinedSystemCoin = physicalCoin
                        ? Boolean(physicalCoin.is_system_coin && physicalCoin.face_1_design_id && physicalCoin.face_2_design_id && physicalCoin.body_filament && physicalCoin.accent_filament)
                        : Boolean(
                            (order.items_snapshot?.coin_template_mode === 'system' || pkg?.coin_template_mode === 'system') &&
                            sysCoin && sysCoin.face_1_design_id && sysCoin.face_2_design_id && sysCoin.body_filament && sysCoin.accent_filament
                          );
                      const coinToProofId = details?.physical_coin_id || sysCoinId;

                      const hasCoin = Boolean(
                        details?.physical_coin_id ||
                        details?.face_1_design_id ||
                        details?.face_2_design_id ||
                        details?.filament_primary ||
                        items.standard_coin > 0 ||
                        items.special_metallic_coin > 0 ||
                        sysCoinId ||
                        (pkg?.coin_template_mode && pkg.coin_template_mode !== 'none') ||
                        (order.items_snapshot?.coin_template_mode && order.items_snapshot.coin_template_mode !== 'none') ||
                        details
                      );

                      const coinName = physicalCoin?.name || sysCoin?.name || pkg?.name || 'Custom Physical Coin';
                      const coinDisplayId = details?.physical_coin_id || sysCoinId || physicalCoin?.id || sysCoin?.id || null;

                      const face1Id = details?.face_1_design_id || physicalCoin?.face_1_design_id || sysCoin?.face_1_design_id || null;
                      const face2Id = details?.face_2_design_id || physicalCoin?.face_2_design_id || sysCoin?.face_2_design_id || null;

                      const face1Info = getDesignInfo(face1Id);
                      const face2Info = getDesignInfo(face2Id);

                      const bodyFilament = details?.filament_primary || physicalCoin?.body_filament || sysCoin?.body_filament || null;
                      const accentFilament = details?.filament_accent || physicalCoin?.accent_filament || sysCoin?.accent_filament || null;

                      const bodyColorHex = resolveFilamentHex(bodyFilament, '#1A1A1A');
                      const accentColorHex = resolveFilamentHex(accentFilament, '#D4AF37');

                      // Approval & readiness determinations
                      const isLibraryCoin = Boolean(
                        order.items_snapshot?.coin_template_mode === 'library' ||
                        pkg?.coin_template_mode === 'library'
                      );
                      const hasSelectedLibraryCoin = Boolean(details?.physical_coin_id);
                      const isCoinApproved = !hasCoin || isFullyDefinedSystemCoin || (isLibraryCoin ? hasSelectedLibraryCoin : Boolean(details?.face_1_authorized && details?.face_2_authorized));

                      const cardTplId = details?.brag_page_design_id || order.items_snapshot?.trophy_card_template_id || pkg?.trophy_card_template_id;
                      const cardInfo = cardTplId ? getDesignInfo(cardTplId) : null;
                      const isSystemCard = Boolean(
                        cardInfo?.isSystemTemplate ||
                        cardInfo?.design?.config?.is_from_system_template ||
                        (pkg?.trophy_card_template_id && getDesignInfo(pkg.trophy_card_template_id).isSystemTemplate) ||
                        (order.items_snapshot?.trophy_card_template_id && getDesignInfo(order.items_snapshot.trophy_card_template_id).isSystemTemplate)
                      );
                      const isLibraryCard = Boolean(
                        order.items_snapshot?.card_template_mode === 'library' ||
                        pkg?.card_template_mode === 'library' ||
                        cardInfo?.design?.config?.is_from_library
                      );
                      const hasSelectedLibraryCard = Boolean(details?.brag_page_design_id);
                      const isCardApproved = !hasTrophyCard || isSystemCard || (isLibraryCard ? hasSelectedLibraryCard : Boolean(details?.brag_page_authorized));
                      const isFilamentApproved = items.special_metallic_coin > 0 ? Boolean(bodyFilament && accentFilament) : true;
                      const isOrderApproved = isCoinApproved && isCardApproved && isFilamentApproved;

                      const canManageOrder = ['GLOBAL_ADMIN', 'GLOBAL_POWER', 'TENANT_ADMIN'].includes(user?.role) || order.user_id === user?.id || (user?.organization_id && order.organization_id === user.organization_id);
                      const canUserProcessOrder = ['GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD', 'TENANT_ADMIN'].includes(user?.role);
                      const canSendToProduction = !isUnpaid && isOrderApproved && !['in_production', 'shipping', 'shipped', 'completed'].includes(order.status) && canUserProcessOrder;
                      const canUserDeleteOrder = ['GLOBAL_ADMIN', 'GLOBAL_POWER', 'TENANT_ADMIN'].includes(user?.role);
                      const canDeleteOrder = canUserDeleteOrder && (order.is_deletable !== undefined ? order.is_deletable : (order.status !== 'completed' && (!order.issued_coins_count || order.issued_coins_count === 0)));

                      // Status colors mapping
                      const getStatusBadge = (status) => {
                        const style = { fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '12px' };
                        switch (status) {
                          case 'pending_payment':
                            return <span style={{ ...style, backgroundColor: 'rgba(234, 179, 8, 0.15)', color: '#eab308' }}>Pending Payment</span>;
                          case 'pending_artwork':
                            return <span style={{ ...style, backgroundColor: 'rgba(56, 139, 253, 0.15)', color: '#58a6ff' }}>Pending Artwork Sign-off</span>;
                          case 'ready_for_production':
                            return <span style={{ ...style, backgroundColor: 'rgba(189, 178, 255, 0.15)', color: '#bdb2ff' }}>Ready for Production</span>;
                          case 'in_production':
                            return <span style={{ ...style, backgroundColor: 'rgba(160, 196, 255, 0.15)', color: '#a0c4ff' }}>In Production</span>;
                          case 'shipping':
                            return <span style={{ ...style, backgroundColor: 'rgba(56, 139, 253, 0.2)', color: '#58a6ff' }}>Shipping</span>;
                          case 'shipped':
                            return <span style={{ ...style, backgroundColor: 'rgba(46, 160, 67, 0.15)', color: '#3fb950' }}>Shipped</span>;
                          case 'completed':
                            return <span style={{ ...style, backgroundColor: 'rgba(46, 160, 67, 0.3)', color: '#2ea44f' }}>Completed</span>;
                          default:
                            return <span style={{ ...style, backgroundColor: 'var(--border-color)', color: 'var(--text-mute)' }}>{status}</span>;
                        }
                      };

                      return (
                        <div
                          key={order.id}
                          style={{
                            background: 'var(--card-bg)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            padding: '20px',
                            boxShadow: 'var(--card-shadow)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '15px'
                          }}
                        >
                          {/* Order Metadata */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                            <div>
                              <strong style={{ fontSize: '14px', color: 'var(--text-main)' }}>
                                Order #{order.id} &bull; {order.package_name}
                              </strong>
                               <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px' }}>
                                Placed on {new Date(order.created_at).toLocaleString()}
                              </div>
                              {order.ordered_by_user_email && (
                                <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                  Ordered by: <span style={{ color: 'var(--text-main)', fontWeight: '500' }}>{order.ordered_by_user_name}</span> ({order.ordered_by_user_email})
                                  {order.ordered_by_organization_name && order.ordered_by_organization_name !== 'N/A' && (
                                    <> &bull; Organisation: <span style={{ color: 'var(--text-main)', fontWeight: '500' }}>{order.ordered_by_organization_name}</span></>
                                  )}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '700',
                                backgroundColor: isUnpaid ? 'rgba(248, 81, 73, 0.15)' : 'rgba(46, 160, 67, 0.15)',
                                color: isUnpaid ? '#ff7b72' : '#3fb950',
                                padding: '2px 8px',
                                borderRadius: '4px'
                              }}>
                                {order.payment_status.toUpperCase()}
                              </span>
                              {getStatusBadge(order.status)}

                              {canSendToProduction && (
                                <button
                                  type="button"
                                  onClick={() => handleProcessOrder(order.id, 'in_production')}
                                  style={{
                                    backgroundColor: '#2ea44f',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '5px 12px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 2px 5px rgba(46, 164, 79, 0.3)',
                                    transition: 'all 0.15s ease'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2c974b'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2ea44f'}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                  </svg>
                                  Send to Production
                                </button>
                              )}

                              {['shipping', 'shipped'].includes(order.status) && canUserProcessOrder && (
                                <button
                                  type="button"
                                  onClick={() => handleProcessOrder(order.id, 'completed')}
                                  style={{
                                    backgroundColor: '#2ea44f',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '5px 12px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    boxShadow: '0 2px 5px rgba(46, 164, 79, 0.3)',
                                    transition: 'all 0.15s ease'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2c974b'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2ea44f'}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                  Mark Completed ✓
                                </button>
                              )}

                              {canDeleteOrder && (
                                <button
                                  type="button"
                                  data-testid={`delete-order-btn-${order.id}`}
                                  onClick={() => handleDeleteOrder(order.id)}
                                  style={{
                                    backgroundColor: 'rgba(248, 81, 73, 0.12)',
                                    color: '#ff7b72',
                                    border: '1px solid rgba(248, 81, 73, 0.35)',
                                    borderRadius: '6px',
                                    padding: '5px 12px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    transition: 'all 0.15s ease'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#d73a49';
                                    e.currentTarget.style.color = '#ffffff';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(248, 81, 73, 0.12)';
                                    e.currentTarget.style.color = '#ff7b72';
                                  }}
                                  title="Delete incomplete order with no issued coins"
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                  Delete Order
                                </button>
                              )}
                            </div>
                          </div>

                          <div style={{ borderBottom: '1px solid var(--border-color)' }} />

                          {/* Items and Price */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)', marginBottom: '5px' }}>Items ordered:</div>
                              {(() => {
                                const validItems = Object.entries(items).filter(([_, val]) => 
                                  typeof val === 'number' ? val > 0 : (parseInt(val, 10) > 0 || (!!val && val !== '0' && val !== 'false'))
                                );
                                if (validItems.length === 0) {
                                  return <div style={{ fontSize: '12px', color: 'var(--text-mute)', fontStyle: 'italic' }}>Package standard inclusions</div>;
                                }
                                return (
                                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: 'var(--text-mute)' }}>
                                    {validItems.map(([key, val]) => (
                                      <li key={key}>
                                        {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}: <strong style={{ color: 'var(--text-main)' }}>x{val}</strong>
                                      </li>
                                    ))}
                                  </ul>
                                );
                              })()}
                              {order.items_snapshot.hosting_auto_added && (
                                <div style={{ fontSize: '11px', color: '#ff7b72', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                                  Annual Hosting was automatically added (no active subscription detected).
                                </div>
                              )}
                              {order.items_snapshot.shipping_auto_added && (
                                <div style={{ fontSize: '11px', color: 'var(--primary-color)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>
                                  Shipping cost automatically added.
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>Total Price Billed:</span>
                              <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--accent-color)' }}>
                                ${order.total_price.toFixed(2)} USD
                              </div>
                            </div>
                          </div>

                          {/* Artwork Assignment / Sign-off / Filament Section (For Custom Coin / Card orders) */}
                          {(hasCoin || hasTrophyCard) && (
                            <div style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.15)',
                              borderRadius: '8px',
                              padding: '16px',
                              marginTop: '10px',
                              border: '1px solid var(--border-color)'
                            }}>
                              <h6 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>
                                Coin & Trophy Card Specifications & Sign-off Details
                              </h6>
                              
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : ((hasCoin && hasTrophyCard) ? 'repeat(2, 1fr)' : '1fr'),
                                gap: '15px',
                                marginBottom: user.role === 'TENANT_ADMIN' && order.payment_status === 'paid' ? '12px' : '0px'
                              }}>
                                
                                {/* Consolidated Physical Coin Card (Dual Face + Swatches) */}
                                {hasCoin && (
                                  <div style={{
                                    background: 'var(--panel-bg)',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px'
                                  }}>
                                    {/* Coin Header */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                                        <span style={{ fontSize: '15px' }}>🪙</span>
                                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={coinName}>
                                          {coinName}
                                        </span>
                                        {coinDisplayId && (
                                          <span style={{ fontSize: '11px', color: 'var(--text-mute)', flexShrink: 0 }}>
                                            #{coinDisplayId}
                                          </span>
                                        )}
                                      </div>

                                      {/* Top Status Tag */}
                                      <div>
                                        {isFullyDefinedSystemCoin ? (
                                          <span style={{ backgroundColor: 'rgba(56, 139, 253, 0.2)', color: '#58a6ff', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                            ⭐ System Coin
                                          </span>
                                        ) : isLibraryCoin ? (
                                          hasSelectedLibraryCoin ? (
                                            <span style={{ backgroundColor: 'rgba(46, 160, 67, 0.2)', color: '#3fb950', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                              📚 Library Coin (Approved)
                                            </span>
                                          ) : (
                                            <span style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', color: '#eab308', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                              📚 Select Library Coin
                                            </span>
                                          )
                                        ) : (details?.face_1_authorized && details?.face_2_authorized) ? (
                                          <span style={{ backgroundColor: 'rgba(46, 160, 67, 0.2)', color: '#3fb950', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                            ✓ Both Faces Approved
                                          </span>
                                        ) : (face1Id || face2Id) ? (
                                          <span style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', color: '#eab308', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                            ⚠️ Sign-off Required
                                          </span>
                                        ) : (
                                          <span style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', color: 'var(--text-mute)', fontSize: '10px', padding: '2px 6px', borderRadius: '4px' }}>
                                            Pending Artwork
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Library Selection Mode OR Dual Circular Previews */}
                                    {isLibraryCoin && (!details?.physical_coin_id || editingLibraryCoinOrderId === order.id) ? (
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                        padding: '12px',
                                        backgroundColor: 'rgba(63, 185, 80, 0.08)',
                                        border: '1px solid rgba(63, 185, 80, 0.3)',
                                        borderRadius: '6px'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#3fb950', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span>📚</span> <span>Select Approved Library Coin</span>
                                          </div>
                                          {editingLibraryCoinOrderId === order.id && (
                                            <button
                                              type="button"
                                              onClick={() => setEditingLibraryCoinOrderId(null)}
                                              style={{
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                color: 'var(--text-mute)',
                                                fontSize: '11px',
                                                cursor: 'pointer',
                                                padding: '2px 6px'
                                              }}
                                            >
                                              ✕ Cancel
                                            </button>
                                          )}
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                                          Choose an approved coin from your library for this order. Faces and filaments are pre-authorized with no proofing required:
                                        </div>
                                        {(() => {
                                          const myCoins = getMyLibraryCoins();
                                          const currentSelected = selectedLibraryCoinsForOrders[order.id] !== undefined
                                            ? selectedLibraryCoinsForOrders[order.id]
                                            : (details?.physical_coin_id ? String(details.physical_coin_id) : (myCoins[0]?.id ? String(myCoins[0].id) : ''));

                                          if (myCoins.length === 0) {
                                            return (
                                              <div style={{ fontSize: '11px', color: '#eab308', fontStyle: 'italic', padding: '4px 0' }}>
                                                ⚠️ No approved coins found in your library. Please create and approve a coin in your Coins grid first.
                                              </div>
                                            );
                                          }

                                          return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                              <select
                                                value={currentSelected}
                                                onChange={(e) => setSelectedLibraryCoinsForOrders(prev => ({ ...prev, [order.id]: e.target.value }))}
                                                style={{
                                                  backgroundColor: 'var(--input-bg)',
                                                  color: 'var(--input-text)',
                                                  border: '1px solid var(--border-color)',
                                                  borderRadius: '6px',
                                                  padding: '8px 10px',
                                                  fontSize: '12px'
                                                }}
                                              >
                                                {myCoins.map(coin => (
                                                  <option key={coin.id} value={coin.id}>
                                                    {coin.name} (#{coin.id}) {coin.body_filament ? `[${coin.body_filament} / ${coin.accent_filament || 'Default'}]` : ''}
                                                  </option>
                                                ))}
                                              </select>
                                              {canManageOrder && (
                                                <button
                                                  type="button"
                                                  disabled={!currentSelected || assigningLibraryLoading[`${order.id}_coin`]}
                                                  onClick={() => handleAssignLibraryItem(order.id, 'coin', currentSelected)}
                                                  style={{
                                                    backgroundColor: '#2ea44f',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '7px 14px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px'
                                                  }}
                                                >
                                                  {assigningLibraryLoading[`${order.id}_coin`] ? 'Applying...' : '✓ Apply Coin from Library'}
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    ) : (
                                      <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                      gap: '12px',
                                      padding: '10px 8px',
                                      backgroundColor: 'rgba(0, 0, 0, 0.18)',
                                      borderRadius: '6px',
                                      border: '1px solid rgba(255, 255, 255, 0.04)'
                                    }}>
                                      {/* Heads (Face 1) */}
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '6px' }}>
                                        {face1Info?.preview ? (
                                          <div style={{
                                            width: '74px',
                                            height: '74px',
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            border: '2px solid rgba(212, 175, 55, 0.6)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                            backgroundColor: 'rgba(0,0,0,0.4)',
                                            flexShrink: 0
                                          }}>
                                            <img
                                              src={face1Info.preview}
                                              alt="Heads (Face 1)"
                                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                          </div>
                                        ) : (
                                          <div style={{
                                            width: '74px',
                                            height: '74px',
                                            borderRadius: '50%',
                                            border: '2px dashed var(--border-color)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--text-mute)',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                            flexShrink: 0
                                          }}>
                                            {face1Id ? 'No image' : 'Blank'}
                                          </div>
                                        )}

                                        <div style={{ maxWidth: '100%' }}>
                                          <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={face1Info?.name || 'Face 1'}>
                                            Heads: {face1Info?.name || (face1Id ? `Design #${face1Id}` : 'Not assigned')}
                                          </div>
                                          {face1Id && (
                                            <div style={{ fontSize: '10px', color: 'var(--text-mute)' }}>
                                              ID: #{face1Id}
                                            </div>
                                          )}
                                        </div>

                                        {/* Face 1 Sign-off status & Operator Button */}
                                        {(isFullyDefinedSystemCoin || isLibraryCoin) ? (
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                            <span style={{ color: '#3fb950', fontSize: '10px', fontWeight: '600' }}>✓ Pre-approved</span>
                                          </div>
                                        ) : face1Id ? (
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', width: '100%' }}>
                                            {details?.face_1_authorized ? (
                                              <span style={{ color: '#3fb950', fontSize: '10px', fontWeight: '600' }}>✓ Signed off</span>
                                            ) : (
                                              <span style={{ color: '#ff7b72', fontSize: '10px', fontWeight: '600' }}>✗ Pending sign-off</span>
                                            )}
                                            {user.role === 'OPERATOR' && order.user_id === user.id && (
                                              <button
                                                onClick={() => handleAuthorizeArtwork(order.id, 'face_1', !details?.face_1_authorized)}
                                                style={{
                                                  backgroundColor: details?.face_1_authorized ? 'rgba(248, 81, 73, 0.15)' : 'var(--primary-color)',
                                                  color: details?.face_1_authorized ? '#ff7b72' : 'white',
                                                  border: 'none',
                                                  borderRadius: '4px',
                                                  padding: '3px 6px',
                                                  fontSize: '10px',
                                                  cursor: 'pointer',
                                                  width: '90%'
                                                }}
                                              >
                                                {details?.face_1_authorized ? 'Revoke' : 'Authorize'}
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <span style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic' }}>
                                            Awaiting design
                                          </span>
                                        )}
                                      </div>

                                      {/* Tails (Face 2) */}
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '6px' }}>
                                        {face2Info?.preview ? (
                                          <div style={{
                                            width: '74px',
                                            height: '74px',
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            border: '2px solid rgba(192, 192, 192, 0.6)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                            backgroundColor: 'rgba(0,0,0,0.4)',
                                            flexShrink: 0
                                          }}>
                                            <img
                                              src={face2Info.preview}
                                              alt="Tails (Face 2)"
                                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                          </div>
                                        ) : (
                                          <div style={{
                                            width: '74px',
                                            height: '74px',
                                            borderRadius: '50%',
                                            border: '2px dashed var(--border-color)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--text-mute)',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                            flexShrink: 0
                                          }}>
                                            {face2Id ? 'No image' : 'Blank'}
                                          </div>
                                        )}

                                        <div style={{ maxWidth: '100%' }}>
                                          <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={face2Info?.name || 'Face 2'}>
                                            Tails: {face2Info?.name || (face2Id ? `Design #${face2Id}` : 'Not assigned')}
                                          </div>
                                          {face2Id && (
                                            <div style={{ fontSize: '10px', color: 'var(--text-mute)' }}>
                                              ID: #{face2Id}
                                            </div>
                                          )}
                                        </div>

                                        {/* Face 2 Sign-off status & Operator Button */}
                                        {(isFullyDefinedSystemCoin || isLibraryCoin) ? (
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                            <span style={{ color: '#3fb950', fontSize: '10px', fontWeight: '600' }}>✓ Pre-approved</span>
                                          </div>
                                        ) : face2Id ? (
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', width: '100%' }}>
                                            {details?.face_2_authorized ? (
                                              <span style={{ color: '#3fb950', fontSize: '10px', fontWeight: '600' }}>✓ Signed off</span>
                                            ) : (
                                              <span style={{ color: '#ff7b72', fontSize: '10px', fontWeight: '600' }}>✗ Pending sign-off</span>
                                            )}
                                            {user.role === 'OPERATOR' && order.user_id === user.id && (
                                              <button
                                                onClick={() => handleAuthorizeArtwork(order.id, 'face_2', !details?.face_2_authorized)}
                                                style={{
                                                  backgroundColor: details?.face_2_authorized ? 'rgba(248, 81, 73, 0.15)' : 'var(--primary-color)',
                                                  color: details?.face_2_authorized ? '#ff7b72' : 'white',
                                                  border: 'none',
                                                  borderRadius: '4px',
                                                  padding: '3px 6px',
                                                  fontSize: '10px',
                                                  cursor: 'pointer',
                                                  width: '90%'
                                                }}
                                              >
                                                {details?.face_2_authorized ? 'Revoke' : 'Authorize'}
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <span style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic' }}>
                                            Awaiting design
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                    {/* Color Swatches (Body & Accent) */}
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      flexWrap: 'wrap',
                                      gap: '8px',
                                      padding: '7px 10px',
                                      backgroundColor: 'var(--input-bg)',
                                      borderRadius: '6px',
                                      border: '1px solid var(--border-color)'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                        {/* Body Color Swatch */}
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                                          <span style={{
                                            width: '13px',
                                            height: '13px',
                                            borderRadius: '50%',
                                            backgroundColor: bodyColorHex,
                                            border: '1.5px solid rgba(255,255,255,0.4)',
                                            boxShadow: '0 0 4px rgba(0,0,0,0.5)',
                                            display: 'inline-block',
                                            flexShrink: 0
                                          }} />
                                          <span style={{ color: 'var(--text-mute)', fontWeight: '600' }}>Body:</span>
                                          <span style={{ color: 'var(--text-main)', fontWeight: '500', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={bodyFilament || 'Default PLA'}>
                                            {bodyFilament || 'Default PLA'}
                                          </span>
                                        </div>

                                        {/* Accent Color Swatch */}
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                                          <span style={{
                                            width: '13px',
                                            height: '13px',
                                            borderRadius: '50%',
                                            backgroundColor: accentColorHex,
                                            border: '1.5px solid rgba(255,255,255,0.4)',
                                            boxShadow: '0 0 4px rgba(0,0,0,0.5)',
                                            display: 'inline-block',
                                            flexShrink: 0
                                          }} />
                                          <span style={{ color: 'var(--text-mute)', fontWeight: '600' }}>Accent:</span>
                                          <span style={{ color: 'var(--text-main)', fontWeight: '500', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={accentFilament || 'Default Metallic'}>
                                            {accentFilament || 'Default Metallic'}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Operator Change Filaments button */}
                                      {user.role === 'OPERATOR' && order.user_id === user.id && !isFullyDefinedSystemCoin && !isLibraryCoin && (
                                        <button
                                          onClick={() => {
                                            setOperatorFilamentPrimary(details?.filament_primary || 'Gold PLA');
                                            setOperatorFilamentAccent(details?.filament_accent || 'Silver PLA');
                                            setSelectedOrderForFilaments(order);
                                          }}
                                          style={{
                                            backgroundColor: 'transparent',
                                            color: 'var(--primary-color)',
                                            border: '1px solid var(--primary-color)',
                                            borderRadius: '4px',
                                            padding: '2px 7px',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Change Filaments
                                        </button>
                                      )}
                                    </div>

                                    {/* Metallic order warning if unselected */}
                                    {items.special_metallic_coin > 0 && !details?.filament_primary && (
                                      <span style={{ fontSize: '10px', color: '#ff7b72', fontStyle: 'italic', display: 'block', marginTop: '-4px' }}>
                                        ⚠️ Metallic order: filaments must be selected
                                      </span>
                                    )}

                                    {/* Footer / Proofing Link / Non-revocation Note */}
                                    {isFullyDefinedSystemCoin ? (
                                      <div style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic', textAlign: 'center' }}>
                                        Pre-approved System Coin &bull; Approval cannot be revoked
                                      </div>
                                    ) : isLibraryCoin ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                        <div style={{ fontSize: '10px', color: '#3fb950', fontWeight: '500', fontStyle: 'italic', textAlign: 'center' }}>
                                          ✓ Approved Library Coin &bull; No proofing step required
                                        </div>
                                        {canManageOrder && !['in_production', 'shipping', 'shipped', 'completed'].includes(order.status) && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setSelectedLibraryCoinsForOrders(prev => ({ ...prev, [order.id]: String(details?.physical_coin_id || '') }));
                                              setEditingLibraryCoinOrderId(order.id);
                                            }}
                                            style={{
                                              backgroundColor: 'transparent',
                                              color: '#3fb950',
                                              border: '1px solid rgba(63, 185, 80, 0.4)',
                                              borderRadius: '4px',
                                              padding: '2px 8px',
                                              fontSize: '10px',
                                              fontWeight: '600',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            Change Library Coin
                                          </button>
                                        )}
                                      </div>
                                    ) : coinToProofId ? (
                                      <button
                                        onClick={() => navigateToCoinProofing(coinToProofId)}
                                        style={{
                                          backgroundColor: 'rgba(56, 139, 253, 0.15)',
                                          color: '#58a6ff',
                                          border: '1px solid rgba(56, 139, 253, 0.3)',
                                          borderRadius: '4px',
                                          padding: '5px 10px',
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '5px',
                                          width: '100%'
                                        }}
                                      >
                                        🪙 Open Coin Proofing System &rarr;
                                      </button>
                                    ) : null}
                                  </div>
                                )}

                                {/* Slot 3: Trophy Card / Brag Page */}
                                <div style={{ background: 'var(--panel-bg)', padding: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {hasTrophyCard ? (
                                    (isLibraryCard && (!details?.brag_page_design_id || editingLibraryCardOrderId === order.id)) ? (
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                        padding: '12px',
                                        backgroundColor: 'rgba(63, 185, 80, 0.08)',
                                        border: '1px solid rgba(63, 185, 80, 0.3)',
                                        borderRadius: '6px'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#3fb950', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span>📚</span> <span>Select Approved Trophy Card</span>
                                          </div>
                                          {editingLibraryCardOrderId === order.id && (
                                            <button
                                              type="button"
                                              onClick={() => setEditingLibraryCardOrderId(null)}
                                              style={{
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                color: 'var(--text-mute)',
                                                fontSize: '11px',
                                                cursor: 'pointer',
                                                padding: '2px 6px'
                                              }}
                                            >
                                              ✕ Cancel
                                            </button>
                                          )}
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                                          Choose an approved trophy card from your library for this order. It will be pre-authorized immediately with no proofing required:
                                        </div>
                                        {(() => {
                                          const myCards = getMyLibraryCards();
                                          const currentSelected = selectedLibraryCardsForOrders[order.id] !== undefined
                                            ? selectedLibraryCardsForOrders[order.id]
                                            : (details?.brag_page_design_id ? String(details.brag_page_design_id) : (myCards[0]?.id ? String(myCards[0].id) : ''));

                                          if (myCards.length === 0) {
                                            return (
                                              <div style={{ fontSize: '11px', color: '#eab308', fontStyle: 'italic', padding: '4px 0' }}>
                                                ⚠️ No approved trophy cards found in your library. Please create and approve a card in your Cards grid first.
                                              </div>
                                            );
                                          }

                                          return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                              <select
                                                value={currentSelected}
                                                onChange={(e) => setSelectedLibraryCardsForOrders(prev => ({ ...prev, [order.id]: e.target.value }))}
                                                style={{
                                                  backgroundColor: 'var(--input-bg)',
                                                  color: 'var(--input-text)',
                                                  border: '1px solid var(--border-color)',
                                                  borderRadius: '6px',
                                                  padding: '8px 10px',
                                                  fontSize: '12px'
                                                }}
                                              >
                                                {myCards.map(card => (
                                                  <option key={card.id} value={card.id}>
                                                    {card.name} (#{card.id})
                                                  </option>
                                                ))}
                                              </select>
                                              {canManageOrder && (
                                                <button
                                                  type="button"
                                                  disabled={!currentSelected || assigningLibraryLoading[`${order.id}_card`]}
                                                  onClick={() => handleAssignLibraryItem(order.id, 'card', currentSelected)}
                                                  style={{
                                                    backgroundColor: '#2ea44f',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '7px 14px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px'
                                                  }}
                                                >
                                                  {assigningLibraryLoading[`${order.id}_card`] ? 'Applying...' : '✓ Apply Card from Library'}
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    ) : details?.brag_page_design_id ? (() => {
                                      const info = getDesignInfo(details.brag_page_design_id);
                                      const isSystemTemplate = Boolean(
                                        info.isSystemTemplate || 
                                        info.design?.config?.is_from_system_template ||
                                        (pkg?.trophy_card_template_id && getDesignInfo(pkg.trophy_card_template_id).isSystemTemplate) ||
                                        (order.items_snapshot?.trophy_card_template_id && getDesignInfo(order.items_snapshot.trophy_card_template_id).isSystemTemplate)
                                      );
                                      const isLibraryCard = Boolean(
                                        order.items_snapshot?.card_template_mode === 'library' ||
                                        pkg?.card_template_mode === 'library' ||
                                        info.design?.config?.is_from_library ||
                                        info.design?.config?.status === 'approved'
                                      );
                                      const isTemplate = info.isTemplate || Boolean(pkg?.trophy_card_template_id || order.items_snapshot?.trophy_card_template_id);

                                      return (
                                        <>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                            <div style={{ fontSize: '12px', color: 'var(--text-mute)', fontWeight: '600' }}>
                                              🪪 Trophy Card: {info.name}
                                            </div>
                                            {isSystemTemplate ? (
                                              <span style={{ backgroundColor: 'rgba(56, 139, 253, 0.2)', color: '#58a6ff', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                ⭐ System Template
                                              </span>
                                            ) : isLibraryCard ? (
                                              <span style={{ backgroundColor: 'rgba(46, 160, 67, 0.2)', color: '#3fb950', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                📚 Library Card (Approved)
                                              </span>
                                            ) : isTemplate ? (
                                              <span style={{ backgroundColor: 'rgba(163, 113, 247, 0.2)', color: '#a371f7', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                ★ Card Template
                                              </span>
                                            ) : null}
                                          </div>

                                          <div style={{ display: 'grid', gridTemplateColumns: info.previewBack ? '1fr 1fr' : '1fr', gap: '6px' }}>
                                            <div>
                                              {info.previewBack && <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginBottom: '2px' }}>Front Face</div>}
                                              {info.preview ? (
                                                <img 
                                                  src={info.preview} 
                                                  alt={`${info.name} Front`} 
                                                  style={{ 
                                                    width: '100%', 
                                                    height: '110px', 
                                                    objectFit: 'contain', 
                                                    borderRadius: '6px', 
                                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                                    border: '1px solid var(--border-color)'
                                                  }} 
                                                />
                                              ) : (
                                                <div style={{ 
                                                  height: '110px', 
                                                  display: 'flex', 
                                                  alignItems: 'center', 
                                                  justifyContent: 'center', 
                                                  fontSize: '11px', 
                                                  color: 'var(--text-mute)',
                                                  borderRadius: '6px', 
                                                  backgroundColor: 'rgba(0,0,0,0.2)',
                                                  border: '1px solid var(--border-color)',
                                                  fontStyle: 'italic'
                                                }}>
                                                  No preview available
                                                </div>
                                              )}
                                            </div>

                                            {info.previewBack && (
                                              <div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginBottom: '2px' }}>Back Face</div>
                                                <img 
                                                  src={info.previewBack} 
                                                  alt={`${info.name} Back`} 
                                                  style={{ 
                                                    width: '100%', 
                                                    height: '110px', 
                                                    objectFit: 'contain', 
                                                    borderRadius: '6px', 
                                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                                    border: '1px solid var(--border-color)'
                                                  }} 
                                                />
                                              </div>
                                            )}
                                          </div>

                                          {isSystemTemplate ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                                              <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                                ID: #{details.brag_page_design_id}
                                              </span>
                                              <span style={{ color: '#3fb950', fontSize: '11px', fontWeight: '600' }}>✓ Pre-approved System Template</span>
                                              <span style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic' }}>Approval cannot be revoked</span>
                                            </div>
                                          ) : isLibraryCard ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                                              <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                                ID: #{details.brag_page_design_id}
                                              </span>
                                              <span style={{ color: '#3fb950', fontSize: '11px', fontWeight: '600' }}>✓ Pre-approved Library Card</span>
                                              <span style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic' }}>Approved from library &bull; No proofing required</span>
                                              {canManageOrder && !['in_production', 'shipping', 'shipped', 'completed'].includes(order.status) && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setSelectedLibraryCardsForOrders(prev => ({ ...prev, [order.id]: String(details?.brag_page_design_id || '') }));
                                                    setEditingLibraryCardOrderId(order.id);
                                                  }}
                                                  style={{
                                                    backgroundColor: 'transparent',
                                                    color: '#3fb950',
                                                    border: '1px solid rgba(63, 185, 80, 0.4)',
                                                    borderRadius: '4px',
                                                    padding: '2px 8px',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    marginTop: '4px',
                                                    alignSelf: 'flex-start'
                                                  }}
                                                >
                                                  Change Library Card
                                                </button>
                                              )}
                                            </div>
                                          ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                                              <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                                ID: #{details.brag_page_design_id}
                                              </span>
                                              {details.brag_page_authorized ? (
                                                <span style={{ color: '#3fb950', fontSize: '11px', fontWeight: '600' }}>✓ Signed off</span>
                                              ) : (
                                                <span style={{ color: '#ff7b72', fontSize: '11px', fontWeight: '600' }}>✗ Awaiting Operator approval</span>
                                              )}
                                              {user.role === 'OPERATOR' && order.user_id === user.id && (
                                                <button
                                                  onClick={() => handleAuthorizeArtwork(order.id, 'brag', !details.brag_page_authorized)}
                                                  style={{
                                                    backgroundColor: details.brag_page_authorized ? 'rgba(248, 81, 73, 0.15)' : 'var(--primary-color)',
                                                    color: details.brag_page_authorized ? '#ff7b72' : 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '11px',
                                                    cursor: 'pointer',
                                                    marginTop: '4px'
                                                  }}
                                                >
                                                  {details.brag_page_authorized ? 'Revoke Approval' : 'Authorize Trophy Card'}
                                                </button>
                                              )}
                                              <button
                                                onClick={() => navigateToCardProofing(details.brag_page_design_id)}
                                                style={{
                                                  backgroundColor: 'rgba(163, 113, 247, 0.15)',
                                                  color: '#a371f7',
                                                  border: '1px solid rgba(163, 113, 247, 0.3)',
                                                  borderRadius: '4px',
                                                  padding: '4px 8px',
                                                  fontSize: '11px',
                                                  fontWeight: '600',
                                                  cursor: 'pointer',
                                                  marginTop: '2px',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '4px'
                                                }}
                                              >
                                                🪪 Open Card Proofing &rarr;
                                              </button>
                                            </div>
                                          )}
                                        </>
                                      );
                                    })() : (() => {
                                      const tplId = order.items_snapshot?.trophy_card_template_id || pkg?.trophy_card_template_id;
                                      if (tplId) {
                                        const tplInfo = getDesignInfo(tplId);
                                        const isSystemTemplate = Boolean(tplInfo.isSystemTemplate || tplInfo.design?.config?.is_from_system_template);
                                        const isLibraryCard = Boolean(
                                          order.items_snapshot?.card_template_mode === 'library' ||
                                          pkg?.card_template_mode === 'library' ||
                                          tplInfo.design?.config?.is_from_library
                                        );
                                        return (
                                          <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                              <div style={{ fontSize: '12px', color: 'var(--text-mute)', fontWeight: '600' }}>
                                                🪪 Trophy Card: {tplInfo.name}
                                              </div>
                                              {isSystemTemplate ? (
                                                <span style={{ backgroundColor: 'rgba(56, 139, 253, 0.2)', color: '#58a6ff', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                  ⭐ System Template
                                                </span>
                                              ) : isLibraryCard ? (
                                                <span style={{ backgroundColor: 'rgba(46, 160, 67, 0.2)', color: '#3fb950', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                  📚 Library Card (Approved)
                                                </span>
                                              ) : (
                                                <span style={{ backgroundColor: 'rgba(163, 113, 247, 0.2)', color: '#a371f7', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                  ★ Card Template
                                                </span>
                                              )}
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: tplInfo.previewBack ? '1fr 1fr' : '1fr', gap: '6px' }}>
                                              <div>
                                                {tplInfo.previewBack && <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginBottom: '2px' }}>Front Face</div>}
                                                {tplInfo.preview ? (
                                                  <img 
                                                    src={tplInfo.preview} 
                                                    alt={`${tplInfo.name} Front`} 
                                                    style={{ 
                                                      width: '100%', 
                                                      height: '110px', 
                                                      objectFit: 'contain', 
                                                      borderRadius: '6px', 
                                                      backgroundColor: 'rgba(0,0,0,0.2)',
                                                      border: '1px solid var(--border-color)'
                                                    }} 
                                                  />
                                                ) : (
                                                  <div style={{ 
                                                    height: '110px', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center', 
                                                    fontSize: '11px', 
                                                    color: 'var(--text-mute)',
                                                    borderRadius: '6px', 
                                                    backgroundColor: 'rgba(0,0,0,0.2)',
                                                    border: '1px solid var(--border-color)',
                                                    fontStyle: 'italic'
                                                  }}>
                                                    Pre-configured Template #{tplId}
                                                  </div>
                                                )}
                                              </div>

                                              {tplInfo.previewBack && (
                                                <div>
                                                  <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginBottom: '2px' }}>Back Face</div>
                                                  <img 
                                                    src={tplInfo.previewBack} 
                                                    alt={`${tplInfo.name} Back`} 
                                                    style={{ 
                                                      width: '100%', 
                                                      height: '110px', 
                                                      objectFit: 'contain', 
                                                      borderRadius: '6px', 
                                                      backgroundColor: 'rgba(0,0,0,0.2)',
                                                      border: '1px solid var(--border-color)'
                                                    }} 
                                                  />
                                                </div>
                                              )}
                                            </div>

                                            {isSystemTemplate ? (
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                                                <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                                  Template ID: #{tplId}
                                                </span>
                                                <span style={{ color: '#3fb950', fontSize: '11px', fontWeight: '600' }}>
                                                  ✓ Pre-approved System Template
                                                </span>
                                                <span style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic' }}>
                                                  Approval cannot be revoked
                                                </span>
                                              </div>
                                            ) : isLibraryCard ? (
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                                                <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                                  Template ID: #{tplId}
                                                </span>
                                                <span style={{ color: '#3fb950', fontSize: '11px', fontWeight: '600' }}>
                                                  ✓ Pre-approved Library Card
                                                </span>
                                                <span style={{ fontSize: '10px', color: 'var(--text-mute)', fontStyle: 'italic' }}>
                                                  Approved from library &bull; No proofing required
                                                </span>
                                                {canManageOrder && !['in_production', 'shipping', 'shipped', 'completed'].includes(order.status) && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setSelectedLibraryCardsForOrders(prev => ({ ...prev, [order.id]: String(tplId || '') }));
                                                      setEditingLibraryCardOrderId(order.id);
                                                    }}
                                                    style={{
                                                      backgroundColor: 'transparent',
                                                      color: '#3fb950',
                                                      border: '1px solid rgba(63, 185, 80, 0.4)',
                                                      borderRadius: '4px',
                                                      padding: '2px 8px',
                                                      fontSize: '10px',
                                                      fontWeight: '600',
                                                      cursor: 'pointer',
                                                      marginTop: '4px',
                                                      alignSelf: 'flex-start'
                                                    }}
                                                  >
                                                    Change Library Card
                                                  </button>
                                                )}
                                              </div>
                                            ) : (
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                                <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                                  Template ID: #{tplId}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#58a6ff', fontStyle: 'italic' }}>
                                                  Package template defined. Assigned upon processing.
                                                </span>
                                                <button
                                                  onClick={() => navigateToCardProofing(tplId)}
                                                  style={{
                                                    backgroundColor: 'rgba(163, 113, 247, 0.15)',
                                                    color: '#a371f7',
                                                    border: '1px solid rgba(163, 113, 247, 0.3)',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    marginTop: '4px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '4px'
                                                  }}
                                                >
                                                  🪪 Open Card Proofing &rarr;
                                                </button>
                                              </div>
                                            )}
                                          </>
                                        );
                                      }
                                      return (
                                        <>
                                          <div style={{ fontSize: '12px', color: 'var(--text-mute)', fontWeight: '600', marginBottom: '8px' }}>Trophy Card Configuration</div>
                                          <span style={{ fontSize: '12px', color: 'var(--text-mute)', fontStyle: 'italic' }}>Blank card preset (awaiting custom design or admin assignment).</span>
                                        </>
                                      );
                                    })()
                                  ) : (
                                    <>
                                      <div style={{ fontSize: '12px', color: 'var(--text-mute)', fontWeight: '600', marginBottom: '8px' }}>Trophy Card Configuration</div>
                                      <span style={{ fontSize: '12px', color: 'var(--text-mute)', fontStyle: 'italic' }}>Not included in this package.</span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Order Action: Send to Production button */}
                              {canSendToProduction && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleProcessOrder(order.id, 'in_production')}
                                    style={{
                                      backgroundColor: '#2ea44f',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '7px 15px',
                                      fontSize: '12px',
                                      fontWeight: '700',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      boxShadow: '0 2px 6px rgba(46, 164, 79, 0.35)',
                                      transition: 'all 0.15s ease'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2c974b'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2ea44f'}
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                    </svg>
                                    Send to Production
                                  </button>
                                </div>
                              )}

                              {['shipping', 'shipped'].includes(order.status) && canUserProcessOrder && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleProcessOrder(order.id, 'completed')}
                                    style={{
                                      backgroundColor: '#2ea44f',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      padding: '7px 15px',
                                      fontSize: '12px',
                                      fontWeight: '700',
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      boxShadow: '0 2px 6px rgba(46, 164, 79, 0.35)',
                                      transition: 'all 0.15s ease'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2c974b'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2ea44f'}
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    Mark Completed ✓
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              </div>

              {/* SECTION 3: GLOBAL MINING LIST (Global Admin only) */}
              {isGlobalRole && (
                <div style={{
                  backgroundColor: 'rgba(189, 178, 255, 0.05)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginTop: '15px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: '700', color: '#bdb2ff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                    Global Production & Mining Jobs Queue
                  </h4>
                  <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: 'var(--text-mute)' }}>
                    System administrator control queue. Displays all orders across all tenants that have completed payment, authorized all face artworks, and assigned filaments.
                  </p>

                  {orders.filter(o => o.status === 'ready_for_production').length === 0 ? (
                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px dashed var(--border-color)',
                      borderRadius: '8px',
                      padding: '30px',
                      textAlign: 'center',
                      color: 'var(--text-mute)',
                      fontSize: '13px'
                    }}>
                      No jobs currently pending in the production queue.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {orders.filter(o => o.status === 'ready_for_production').map((job) => {
                        const jobDetails = job.coin_details && job.coin_details[0];
                        return (
                          <div
                            key={job.id}
                            style={{
                              background: 'var(--card-bg)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '8px',
                              padding: '16px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '15px'
                            }}
                          >
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>
                                Job #{job.id} &bull; Tenant ID: {job.tenant_id}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '3px' }}>
                                Product: <strong>{job.package_name}</strong> &bull; Quantity: <strong>{job.items_snapshot?.items?.coins || job.items_snapshot?.items?.standard_coin || 0} coins</strong>
                              </div>
                              {jobDetails && (
                                <div style={{ fontSize: '11px', color: 'var(--primary-color)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  <span>Filaments: <strong>{jobDetails.filament_primary || 'Default'} / {jobDetails.filament_accent || 'Default'}</strong> &bull;
                                  Design IDs: <strong>#{jobDetails.face_1_design_id} (Front) / #{jobDetails.face_2_design_id} (Back)</strong></span>
                                  {Boolean(job.items_snapshot?.coin_template_mode === 'library') && (
                                    <span style={{ backgroundColor: 'rgba(46, 160, 67, 0.2)', color: '#3fb950', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                      📚 Library Coin (Approved)
                                    </span>
                                  )}
                                </div>
                              )}
                              {(() => {
                                const cardDesignId = jobDetails?.brag_page_design_id || job.items_snapshot?.trophy_card_template_id;
                                if (!cardDesignId) return null;
                                const cardInfo = getDesignInfo(cardDesignId);
                                const isLibraryCard = Boolean(
                                  job.items_snapshot?.card_template_mode === 'library' || 
                                  cardInfo.design?.config?.is_from_library
                                );
                                return (
                                  <div style={{ fontSize: '11px', color: '#a371f7', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span>🪪 Trophy Card: <strong>{cardInfo.name} (#{cardDesignId})</strong></span>
                                    {cardInfo.isSystemTemplate && (
                                      <span style={{ backgroundColor: 'rgba(56, 139, 253, 0.2)', color: '#58a6ff', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                        ⭐ System Template
                                      </span>
                                    )}
                                    {isLibraryCard && (
                                      <span style={{ backgroundColor: 'rgba(46, 160, 67, 0.2)', color: '#3fb950', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                        📚 Library Card (Approved)
                                      </span>
                                    )}
                                    {cardInfo.isTemplate && !cardInfo.isSystemTemplate && !isLibraryCard && (
                                      <span style={{ backgroundColor: 'rgba(163, 113, 247, 0.2)', color: '#a371f7', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                        ★ Card Template
                                      </span>
                                    )}
                                    {jobDetails && (
                                      <span style={{ color: jobDetails.brag_page_authorized ? '#3fb950' : '#ff7b72', fontWeight: '600' }}>
                                        {jobDetails.brag_page_authorized ? '✓ Authorized' : '✗ Awaiting Sign-off'}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleProcessOrder(job.id, 'in_production')}
                                style={{
                                  backgroundColor: 'var(--accent-color)',
                                  color: 'var(--bg-color)',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  cursor: 'pointer'
                                }}
                              >
                                Start Minting
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Active jobs in production */}
                  {orders.filter(o => ['in_production', 'shipped'].includes(o.status)).length > 0 && (
                    <div style={{ marginTop: '30px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-mute)', marginBottom: '10px' }}>Active Production Jobs:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {orders.filter(o => ['in_production', 'shipped'].includes(o.status)).map((job) => {
                          const jDetails = job.coin_details && job.coin_details[0];
                          const cardDesignId = jDetails?.brag_page_design_id || job.items_snapshot?.trophy_card_template_id;
                          const cardInfo = cardDesignId ? getDesignInfo(cardDesignId) : null;
                          return (
                            <div key={job.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.1)', padding: '10px 16px', borderRadius: '6px', border: '1px solid var(--border-color)', flexWrap: 'wrap', gap: '10px' }}>
                              <div>
                                <span style={{ fontSize: '13px' }}>
                                  Job #{job.id} &bull; <strong>{job.package_name}</strong> ({job.status === 'in_production' ? 'Printing' : 'Shipped'})
                                </span>
                                {cardInfo && (
                                  <div style={{ fontSize: '11px', color: '#a371f7', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>🪪 Trophy Card: <strong>{cardInfo.name}</strong></span>
                                    {cardInfo.isSystemTemplate && (
                                      <span style={{ backgroundColor: 'rgba(56, 139, 253, 0.2)', color: '#58a6ff', fontSize: '9px', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>⭐ System</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {job.status === 'in_production' && (
                                  <button
                                    onClick={() => handleProcessOrder(job.id, 'shipped')}
                                    style={{ backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}
                                  >
                                    Ship Package
                                  </button>
                                )}
                                {job.status === 'shipped' && (
                                  <button
                                    onClick={() => handleProcessOrder(job.id, 'completed')}
                                    style={{ backgroundColor: '#2ea44f', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}
                                  >
                                    Mark Completed ✓
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Sub-Tab: Production Line */}
            {(productionSubTab === 'production_line' || productionSubTab === 'in_production') && (isGlobalRole || user.role === 'TENANT_ADMIN') && (() => {
              // Filter orders that are actively in production
              const eligibleOrders = orders.filter(o => o.status === 'in_production');

              const filteredOrders = eligibleOrders.filter(o => {
                if (inProductionSearch && inProductionSearch.trim()) {
                  const q = inProductionSearch.toLowerCase().trim();
                  const matchId = String(o.id).includes(q);
                  const matchPkg = (o.package_name || '').toLowerCase().includes(q);
                  const matchUser = (o.ordered_by_user_name || '').toLowerCase().includes(q) || (o.ordered_by_user_email || '').toLowerCase().includes(q);
                  const matchOrg = (o.ordered_by_organization_name || '').toLowerCase().includes(q);
                  return matchId || matchPkg || matchUser || matchOrg;
                }
                return true;
              });

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Top Header & Filter Controls */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '15px',
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '16px 20px',
                    boxShadow: 'var(--card-shadow)'
                  }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9, color: 'var(--primary-color)' }}>
                          <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                        </svg>
                        Production Line
                      </h4>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                        Orders currently in physical coin manufacturing and card printing.
                      </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{
                        backgroundColor: 'rgba(160, 196, 255, 0.15)',
                        color: '#a0c4ff',
                        padding: '5px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {eligibleOrders.length} In Production
                      </span>

                      {/* Search box */}
                      <input
                        type="text"
                        placeholder="Search orders, operators..."
                        value={inProductionSearch}
                        onChange={(e) => setInProductionSearch(e.target.value)}
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          color: 'var(--input-text)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          outline: 'none',
                          minWidth: '200px'
                        }}
                      />
                    </div>
                  </div>

                  {/* Selected Order Action Banner for Stage 3 */}
                  {(() => {
                    const selectedProductionOrder = filteredOrders.find(o => o.id === selectedProductionOrderId);
                    const selectedOrderStage = (typeof selectedProductionOrder?.items_snapshot?.production_stage === 'number') ? selectedProductionOrder.items_snapshot.production_stage : 1;
                    if (!selectedProductionOrder || selectedOrderStage < 3) return null;

                    return (
                      <div style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.35)',
                        borderRadius: '8px',
                        padding: '12px 18px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '22px' }}>🪙</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>
                              Order #{selectedProductionOrder.id} Selected &bull; {selectedProductionOrder.package_name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#34d399', fontWeight: '600', marginTop: '2px' }}>
                              Stage {selectedOrderStage}: Write Coin URL to Chip &mdash; {selectedProductionOrder.items_snapshot?.quantity || 1} physical coin(s) in CoinVault
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenVaultedCoinsModal(selectedProductionOrder)}
                            style={{
                              backgroundColor: '#10b981',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              fontSize: '12px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 6px rgba(16, 185, 129, 0.35)',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                          >
                            <span>🪙</span> List VaultedCoins ({selectedProductionOrder.items_snapshot?.quantity || 1})
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedProductionOrderId(null)}
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              color: 'var(--text-mute)',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                          >
                            Deselect
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Grid Table */}
                  {filteredOrders.length === 0 ? (
                    <div style={{
                      background: 'var(--card-bg)',
                      border: '1px dashed var(--border-color)',
                      borderRadius: '8px',
                      padding: '50px 20px',
                      textAlign: 'center',
                      color: 'var(--text-mute)'
                    }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, margin: '0 auto 12px auto', display: 'block' }}>
                        <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                      </svg>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)', marginBottom: '4px' }}>
                        No orders currently in production
                      </div>
                      <div style={{ fontSize: '12px', maxWidth: '420px', margin: '0 auto' }}>
                        Orders will appear here once sent to production from the Orders tab.
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      overflowX: 'auto',
                      boxShadow: 'var(--card-shadow)'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                            <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontWeight: '600', fontSize: '12px', width: '14%' }}>order#</th>
                            <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontWeight: '600', fontSize: '12px', width: '16%' }}>operator</th>
                            <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontWeight: '600', fontSize: '12px', width: '26%' }}>progress</th>
                            <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontWeight: '600', fontSize: '12px', width: '20%' }}>coin template</th>
                            <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontWeight: '600', fontSize: '12px', width: '6%', textAlign: 'center' }}>qty</th>
                            <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontWeight: '600', fontSize: '12px', width: '18%', textAlign: 'center' }}>trophy card</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrders.map(order => {
                            const details = order.coin_details && order.coin_details[0];
                            const items = order.items_snapshot?.items || {};
                            const pkg = packages.find(p => p.id === order.package_id);
                            const physicalCoin = details?.physical_coin_id ? (allPhysicalCoins.find(c => c.id === details.physical_coin_id) || availableSystemCoins.find(c => c.id === details.physical_coin_id)) : null;
                            const sysCoinId = order.items_snapshot?.system_coin_id || pkg?.system_coin_id;
                            const sysCoin = sysCoinId ? (availableSystemCoins.find(c => c.id === sysCoinId) || allPhysicalCoins.find(c => c.id === sysCoinId)) : null;

                            const isFullyDefinedSystemCoin = physicalCoin
                              ? Boolean(physicalCoin.is_system_coin && physicalCoin.face_1_design_id && physicalCoin.face_2_design_id && physicalCoin.body_filament && physicalCoin.accent_filament)
                              : Boolean(
                                  (order.items_snapshot?.coin_template_mode === 'system' || pkg?.coin_template_mode === 'system') &&
                                  sysCoin && sysCoin.face_1_design_id && sysCoin.face_2_design_id && sysCoin.body_filament && sysCoin.accent_filament
                                );

                            const hasCoin = Boolean(
                              details?.physical_coin_id ||
                              details?.face_1_design_id ||
                              details?.face_2_design_id ||
                              details?.filament_primary ||
                              items.standard_coin > 0 ||
                              items.special_metallic_coin > 0 ||
                              sysCoinId ||
                              (pkg?.coin_template_mode && pkg.coin_template_mode !== 'none') ||
                              (order.items_snapshot?.coin_template_mode && order.items_snapshot.coin_template_mode !== 'none') ||
                              details
                            );

                            const coinName = physicalCoin?.name || sysCoin?.name || pkg?.name || 'Custom Physical Coin';
                            const coinDisplayId = details?.physical_coin_id || sysCoinId || physicalCoin?.id || sysCoin?.id || null;

                            const face1Id = details?.face_1_design_id || physicalCoin?.face_1_design_id || sysCoin?.face_1_design_id || null;
                            const face2Id = details?.face_2_design_id || physicalCoin?.face_2_design_id || sysCoin?.face_2_design_id || null;

                            const face1Info = getDesignInfo(face1Id);
                            const face2Info = getDesignInfo(face2Id);

                            const bodyFilament = details?.filament_primary || physicalCoin?.body_filament || sysCoin?.body_filament || null;
                            const accentFilament = details?.filament_accent || physicalCoin?.accent_filament || sysCoin?.accent_filament || null;

                            const bodyColorHex = resolveFilamentHex(bodyFilament, '#1A1A1A');
                            const accentColorHex = resolveFilamentHex(accentFilament, '#D4AF37');

                            const hasTrophyCard = (items.custom_brag_page > 0) || 
                              Boolean(details?.brag_page_design_id) || 
                              Boolean(pkg?.trophy_card_template_id) || 
                              Boolean(pkg?.card_template_mode && pkg.card_template_mode !== 'none') || 
                              Boolean(order.items_snapshot?.trophy_card_template_id) || 
                              Boolean(order.items_snapshot?.card_template_mode && order.items_snapshot.card_template_mode !== 'none');

                            const cardTplId = details?.brag_page_design_id || order.items_snapshot?.trophy_card_template_id || pkg?.trophy_card_template_id;
                            const cardInfo = cardTplId ? getDesignInfo(cardTplId) : null;
                            const isSystemCard = Boolean(
                              cardInfo?.isSystemTemplate || 
                              cardInfo?.design?.config?.is_from_system_template ||
                              (pkg?.trophy_card_template_id && getDesignInfo(pkg.trophy_card_template_id).isSystemTemplate) ||
                              (order.items_snapshot?.trophy_card_template_id && getDesignInfo(order.items_snapshot.trophy_card_template_id).isSystemTemplate)
                            );

                            const totalQuantity = (items.standard_coin || 0) + (items.special_metallic_coin || 0) + (items.coins || 0) || order.items_snapshot?.quantity || 1;

                            const canUserProcessOrder = ['GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD', 'TENANT_ADMIN'].includes(user?.role);
                            const isSelected = selectedProductionOrderId === order.id;
                            const rawStage = order.items_snapshot?.production_stage;
                            const stageNum = (typeof rawStage === 'number' && rawStage >= 1 && rawStage <= 5) ? rawStage : 1;

                            return (
                              <tr
                                key={order.id}
                                onClick={() => setSelectedProductionOrderId(isSelected ? null : order.id)}
                                style={{
                                  borderBottom: '1px solid var(--border-color)',
                                  backgroundColor: isSelected
                                    ? 'rgba(56, 139, 253, 0.12)'
                                    : (order.status === 'in_production' ? 'rgba(160, 196, 255, 0.02)' : 'transparent'),
                                  borderLeft: isSelected ? '4px solid #10b981' : '4px solid transparent',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.15s ease, border-left-color 0.15s ease'
                                }}
                              >
                                {/* Column 1: order# */}
                                <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                                  <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-main)' }}>
                                    #{order.id}
                                  </div>
                                  <div style={{ fontSize: '12px', color: 'var(--primary-color)', fontWeight: '500', marginTop: '2px' }}>
                                    {order.package_name}
                                  </div>
                                  <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                                    {new Date(order.created_at).toLocaleDateString()}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                                    <span style={{
                                      fontSize: '10px',
                                      fontWeight: '700',
                                      backgroundColor: order.payment_status === 'paid' ? 'rgba(46, 160, 67, 0.15)' : 'rgba(248, 81, 73, 0.15)',
                                      color: order.payment_status === 'paid' ? '#3fb950' : '#ff7b72',
                                      padding: '1px 6px',
                                      borderRadius: '4px',
                                      display: 'inline-block'
                                    }}>
                                      {order.payment_status.toUpperCase()}
                                    </span>
                                    {isSelected && (
                                      <span style={{
                                        fontSize: '9px',
                                        fontWeight: '700',
                                        backgroundColor: 'rgba(56, 139, 253, 0.2)',
                                        color: '#58a6ff',
                                        padding: '1px 5px',
                                        borderRadius: '3px'
                                      }}>
                                        SELECTED
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* Column 2: operator */}
                                <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                                  <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-main)' }}>
                                    {order.ordered_by_user_name || `User #${order.user_id}`}
                                  </div>
                                  {order.ordered_by_user_email && (
                                    <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                                      {order.ordered_by_user_email}
                                    </div>
                                  )}
                                  {order.ordered_by_organization_name && order.ordered_by_organization_name !== 'N/A' && (
                                    <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                      <span>🏢</span> {order.ordered_by_organization_name}
                                    </div>
                                  )}

                                  {order.items_snapshot?.booked_by ? (
                                    <div style={{
                                      marginTop: '6px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      backgroundColor: 'rgba(56, 139, 253, 0.15)',
                                      color: '#58a6ff',
                                      padding: '2px 7px',
                                      borderRadius: '10px',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}>
                                      <span>🛠️</span> Booked: {order.items_snapshot.booked_by.name || order.items_snapshot.booked_by.email}
                                    </div>
                                  ) : (
                                    <div style={{
                                      marginTop: '6px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                      color: 'var(--text-mute)',
                                      padding: '2px 7px',
                                      borderRadius: '10px',
                                      fontSize: '10px'
                                    }}>
                                      <span>⏳</span> Unassigned Queue
                                    </div>
                                  )}
                                </td>

                                {/* Column 3: progress */}
                                <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                                  {(() => {
                                    const rawStage = order.items_snapshot?.production_stage;
                                    const stageNum = (typeof rawStage === 'number' && rawStage >= 1 && rawStage <= 5) ? rawStage : 1;
                                    const currentStage = PRODUCTION_STAGES.find(s => s.id === stageNum) || PRODUCTION_STAGES[0];
                                    const nextStage = stageNum < 5 ? PRODUCTION_STAGES[stageNum] : null;

                                    return (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                                        {/* Stage Header: Step label + Percentage Gauge */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                              width: '18px',
                                              height: '18px',
                                              borderRadius: '50%',
                                              backgroundColor: stageNum === 5 ? '#10b981' : 'var(--primary-color)',
                                              color: '#ffffff',
                                              fontSize: '10px',
                                              fontWeight: '700',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              flexShrink: 0
                                            }}>
                                              {stageNum}
                                            </span>
                                            <span style={{
                                              fontSize: '12px',
                                              fontWeight: '600',
                                              color: 'var(--text-main)'
                                            }}>
                                              {currentStage.name}
                                            </span>
                                          </div>

                                          <span style={{
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            color: stageNum === 5 ? '#3fb950' : 'var(--primary-color)',
                                            backgroundColor: stageNum === 5 ? 'rgba(46, 160, 67, 0.15)' : 'rgba(56, 139, 253, 0.15)',
                                            border: `1px solid ${stageNum === 5 ? 'rgba(46, 160, 67, 0.3)' : 'rgba(56, 139, 253, 0.3)'}`,
                                            padding: '1px 7px',
                                            borderRadius: '10px',
                                            flexShrink: 0
                                          }}>
                                            {currentStage.percent}%
                                          </span>
                                        </div>

                                        {/* 5-Stage Segmented Progress Bar */}
                                        <div style={{
                                          display: 'grid',
                                          gridTemplateColumns: 'repeat(5, 1fr)',
                                          gap: '4px',
                                          width: '100%'
                                        }}>
                                          {PRODUCTION_STAGES.map((s) => {
                                            const isCompletedOrActive = s.id <= stageNum;
                                            const isCurrent = s.id === stageNum;
                                            return (
                                              <div
                                                key={s.id}
                                                title={`Stage ${s.id}: ${s.name} (${s.percent}%)`}
                                                onClick={() => {
                                                  if (canUserProcessOrder && s.id !== stageNum) {
                                                    handleProcessOrder(order.id, null, s.id);
                                                  }
                                                }}
                                                style={{
                                                  height: '6px',
                                                  borderRadius: '3px',
                                                  backgroundColor: isCompletedOrActive
                                                    ? (stageNum === 5 ? '#10b981' : isCurrent ? 'var(--primary-color)' : '#60a5fa')
                                                    : 'rgba(255, 255, 255, 0.12)',
                                                  boxShadow: isCurrent ? '0 0 6px rgba(56, 139, 253, 0.6)' : 'none',
                                                  cursor: canUserProcessOrder ? 'pointer' : 'default',
                                                  transition: 'all 0.2s ease'
                                                }}
                                              />
                                            );
                                          })}
                                        </div>

                                        {/* Stage Advancement Controls */}
                                        {canUserProcessOrder && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                            {stageNum < 5 ? (
                                              <button
                                                type="button"
                                                onClick={() => handleProcessOrder(order.id, null, stageNum + 1)}
                                                style={{
                                                  flex: 1,
                                                  backgroundColor: 'var(--primary-color)',
                                                  color: '#ffffff',
                                                  border: 'none',
                                                  borderRadius: '5px',
                                                  padding: '5px 8px',
                                                  fontSize: '11px',
                                                  fontWeight: '600',
                                                  cursor: 'pointer',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '4px',
                                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                  transition: 'all 0.15s ease'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                                                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                                              >
                                                <span>Advance &rarr; {nextStage?.short}</span>
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() => handleProcessOrder(order.id, 'shipping')}
                                                style={{
                                                  flex: 1,
                                                  backgroundColor: '#2ea44f',
                                                  color: '#ffffff',
                                                  border: 'none',
                                                  borderRadius: '5px',
                                                  padding: '5px 8px',
                                                  fontSize: '11px',
                                                  fontWeight: '700',
                                                  cursor: 'pointer',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '4px',
                                                  boxShadow: '0 2px 5px rgba(46, 164, 79, 0.35)',
                                                  transition: 'all 0.15s ease'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2c974b'}
                                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2ea44f'}
                                              >
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                  <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                                <span>Complete &rarr; Shipping</span>
                                              </button>
                                            )}

                                            {stageNum > 1 && (
                                              <button
                                                type="button"
                                                title="Revert to previous stage"
                                                onClick={() => handleProcessOrder(order.id, null, stageNum - 1)}
                                                style={{
                                                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                                                  color: 'var(--text-mute)',
                                                  border: '1px solid var(--border-color)',
                                                  borderRadius: '5px',
                                                  padding: '5px 7px',
                                                  fontSize: '11px',
                                                  cursor: 'pointer',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  transition: 'all 0.15s ease'
                                                }}
                                                onMouseOver={(e) => {
                                                  e.currentTarget.style.color = 'var(--text-main)';
                                                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                                                }}
                                                onMouseOut={(e) => {
                                                  e.currentTarget.style.color = 'var(--text-mute)';
                                                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                                                }}
                                              >
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                  <polyline points="15 18 9 12 15 6" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>

                                {/* Column 4: coin template */}
                                <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                                  {hasCoin ? (() => {
                                    const coinTooltip = `${coinName}${coinDisplayId ? ` (ID #${coinDisplayId})` : ''}${isFullyDefinedSystemCoin ? ' • System Coin' : ''}`;
                                    return (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {/* Front Face */}
                                        <div
                                          title={coinTooltip}
                                          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', cursor: 'pointer' }}
                                        >
                                          <div style={{
                                            width: '38px',
                                            height: '38px',
                                            borderRadius: '50%',
                                            border: `2px solid ${accentColorHex}`,
                                            backgroundColor: bodyColorHex,
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.35)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}>
                                            {face1Info.preview ? (
                                              <img src={face1Info.preview} alt={coinTooltip} title={coinTooltip} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                              <span style={{ fontSize: '8px', color: accentColorHex, fontWeight: 'bold' }}>Front</span>
                                            )}
                                          </div>
                                          <span style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Front</span>
                                        </div>

                                        {/* Back Face */}
                                        <div
                                          title={coinTooltip}
                                          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', cursor: 'pointer' }}
                                        >
                                          <div style={{
                                            width: '38px',
                                            height: '38px',
                                            borderRadius: '50%',
                                            border: `2px solid ${accentColorHex}`,
                                            backgroundColor: bodyColorHex,
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.35)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}>
                                            {face2Info.preview ? (
                                              <img src={face2Info.preview} alt={coinTooltip} title={coinTooltip} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                              <span style={{ fontSize: '8px', color: accentColorHex, fontWeight: 'bold' }}>Back</span>
                                            )}
                                          </div>
                                          <span style={{ fontSize: '9px', color: 'var(--text-mute)' }}>Back</span>
                                        </div>

                                        {/* Two Color Swatches (no description) */}
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            padding: '4px 6px',
                                            borderRadius: '12px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                            border: '1px solid var(--border-color)'
                                          }}
                                          title={`Body: ${bodyFilament || 'Default'} • Accent: ${accentFilament || 'Default'}`}
                                        >
                                          <span
                                            style={{
                                              width: '12px',
                                              height: '12px',
                                              borderRadius: '50%',
                                              backgroundColor: bodyColorHex,
                                              border: '1px solid rgba(255,255,255,0.4)',
                                              display: 'inline-block'
                                            }}
                                            title={`Body Filament: ${bodyFilament || 'Default'}`}
                                          />
                                          <span
                                            style={{
                                              width: '12px',
                                              height: '12px',
                                              borderRadius: '50%',
                                              backgroundColor: accentColorHex,
                                              border: '1px solid rgba(255,255,255,0.4)',
                                              display: 'inline-block'
                                            }}
                                            title={`Accent Filament: ${accentFilament || 'Default'}`}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })() : (
                                    <span style={{ fontSize: '12px', color: 'var(--text-mute)', fontStyle: 'italic' }}>
                                      No coin included
                                    </span>
                                  )}
                                </td>

                                {/* Column 5: qty */}
                                <td style={{ padding: '14px 16px', verticalAlign: 'middle', textAlign: 'center' }}>
                                  <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>
                                    {totalQuantity}
                                  </div>
                                  <div style={{ fontSize: '10px', color: 'var(--text-mute)' }}>
                                    coins
                                  </div>

                                  {/* Vaulted Coins safe-with-eye icon button for Stage >= 3 */}
                                  {stageNum >= 3 && (
                                    <div style={{ marginTop: '6px' }}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedProductionOrderId(order.id);
                                          handleOpenVaultedCoinsModal(order);
                                        }}
                                        style={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                          color: 'var(--text-mute)',
                                          border: '1px solid var(--border-color)',
                                          borderRadius: '6px',
                                          padding: '4px 6px',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          transition: 'all 0.15s ease'
                                        }}
                                        onMouseOver={(e) => {
                                          e.currentTarget.style.backgroundColor = 'rgba(56, 189, 248, 0.15)';
                                          e.currentTarget.style.color = '#38bdf8';
                                          e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.4)';
                                        }}
                                        onMouseOut={(e) => {
                                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                          e.currentTarget.style.color = 'var(--text-mute)';
                                          e.currentTarget.style.borderColor = 'var(--border-color)';
                                        }}
                                        title="view coins in vault"
                                        aria-label="view coins in vault"
                                      >
                                        <svg
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.8"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          style={{ display: 'block' }}
                                        >
                                          {/* Safe / Vault Box */}
                                          <rect x="3" y="3" width="18" height="18" rx="3" />
                                          {/* Safe Door Hinges */}
                                          <line x1="3" y1="7" x2="1.5" y2="7" />
                                          <line x1="3" y1="17" x2="1.5" y2="17" />
                                          {/* Eye symbol representing vault inspection */}
                                          <path d="M7 12c1.5-2.5 3.5-3.5 5-3.5s3.5 1 5 3.5c-1.5 2.5-3.5 3.5-5 3.5s-3.5-1-5-3.5z" />
                                          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </td>

                                {/* Column 6: trophy card */}
                                <td style={{ padding: '14px 16px', verticalAlign: 'middle', textAlign: 'center' }}>
                                  {hasTrophyCard ? (() => {
                                    const cardTooltip = `${cardInfo?.name || 'Trophy Card'}${cardTplId ? ` (ID #${cardTplId})` : ''}${isSystemCard ? ' • System Card' : cardInfo?.isTemplate ? ' • Template Card' : ''}`;
                                    return (
                                      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                        {/* Larger Card Preview with Alt & Title Tooltip */}
                                        <div
                                          title={cardTooltip}
                                          style={{
                                            width: '48px',
                                            height: '64px',
                                            borderRadius: '6px',
                                            backgroundColor: 'rgba(0,0,0,0.3)',
                                            border: '1px solid var(--border-color)',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                                            cursor: 'pointer',
                                            transition: 'transform 0.15s ease, border-color 0.15s ease'
                                          }}
                                          onMouseOver={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.05)';
                                            e.currentTarget.style.borderColor = 'rgba(56, 139, 253, 0.5)';
                                          }}
                                          onMouseOut={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                            e.currentTarget.style.borderColor = 'var(--border-color)';
                                          }}
                                        >
                                          {cardInfo?.preview ? (
                                            <img
                                              src={cardInfo.preview}
                                              alt={cardTooltip}
                                              title={cardTooltip}
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain'
                                              }}
                                            />
                                          ) : (
                                            <div style={{ fontSize: '20px' }}>
                                              🪪
                                            </div>
                                          )}
                                        </div>

                                        {/* Signed off / Pre-approved Badge below the card */}
                                        <div>
                                          {isSystemCard ? (
                                            <span style={{
                                              color: '#3fb950',
                                              fontSize: '9px',
                                              fontWeight: '700',
                                              backgroundColor: 'rgba(46, 160, 67, 0.12)',
                                              border: '1px solid rgba(46, 160, 67, 0.3)',
                                              padding: '1px 5px',
                                              borderRadius: '4px',
                                              whiteSpace: 'nowrap',
                                              display: 'inline-block'
                                            }}>
                                              ✓ Pre-approved
                                            </span>
                                          ) : details?.brag_page_authorized ? (
                                            <span style={{
                                              color: '#3fb950',
                                              fontSize: '9px',
                                              fontWeight: '700',
                                              backgroundColor: 'rgba(46, 160, 67, 0.12)',
                                              border: '1px solid rgba(46, 160, 67, 0.3)',
                                              padding: '1px 5px',
                                              borderRadius: '4px',
                                              whiteSpace: 'nowrap',
                                              display: 'inline-block'
                                            }}>
                                              ✓ Signed off
                                            </span>
                                          ) : (
                                            <span style={{
                                              color: '#ff7b72',
                                              fontSize: '9px',
                                              fontWeight: '600',
                                              backgroundColor: 'rgba(248, 81, 73, 0.12)',
                                              border: '1px solid rgba(248, 81, 73, 0.3)',
                                              padding: '1px 5px',
                                              borderRadius: '4px',
                                              whiteSpace: 'nowrap',
                                              display: 'inline-block'
                                            }}>
                                              ✗ Awaiting sign-off
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })() : (
                                    <span style={{ fontSize: '12px', color: 'var(--text-mute)', fontStyle: 'italic' }}>
                                      None
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Filaments Catalog Sub-Tab */}
            {productionSubTab === 'filaments' && (isGlobalRole || user.role === 'TENANT_ADMIN') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Filaments Top Controls Bar */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '15px',
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <h4 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)' }}>
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="12" r="4" />
                        <line x1="12" y1="2" x2="12" y2="4" />
                        <line x1="12" y1="20" x2="12" y2="22" />
                        <line x1="20" y1="12" x2="22" y2="12" />
                        <line x1="2" y1="12" x2="4" y2="12" />
                      </svg>
                      Production Filaments
                    </h4>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)' }}>
                      Available filaments and color specs configured for 3D physical coin minting.
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {/* Search Input */}
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={filamentSearch}
                        onChange={(e) => setFilamentSearch(e.target.value)}
                        placeholder="Search color, brand..."
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '7px 12px 7px 30px',
                          fontSize: '12px',
                          color: 'var(--input-text)',
                          width: '170px',
                          outline: 'none'
                        }}
                      />
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ position: 'absolute', left: '10px', top: '9px', color: 'var(--text-mute)' }}>
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </div>

                    {/* Material Filter */}
                    <select
                      value={filamentMaterialFilter}
                      onChange={(e) => setFilamentMaterialFilter(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '7px 10px',
                        fontSize: '12px',
                        color: 'var(--input-text)',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="ALL">All Materials</option>
                      <option value="PLA">PLA</option>
                      <option value="ABS">ABS</option>
                      <option value="PETG">PETG</option>
                      <option value="TPU">TPU</option>
                      <option value="ASA">ASA</option>
                      <option value="Nylon">Nylon</option>
                      <option value="PC">PC</option>
                    </select>

                    {/* Global Admin Tenant Filter */}
                    {isGlobalRole && tenants && tenants.length > 0 && (
                      <select
                        value={filamentTenantFilter}
                        onChange={(e) => setFilamentTenantFilter(e.target.value)}
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '7px 10px',
                          fontSize: '12px',
                          color: 'var(--input-text)',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="ALL">All Tenants</option>
                        {tenants.map(t => (
                          <option key={t.id} value={t.id}>{t.brand_name || t.domain_name}</option>
                        ))}
                      </select>
                    )}

                    {/* Add Filament Button */}
                    <button
                      type="button"
                      onClick={openAddFilamentModal}
                      style={{
                        backgroundColor: 'var(--primary-color)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '7px 14px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        transition: 'filter 0.15s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
                      onMouseOut={(e) => e.currentTarget.style.filter = 'none'}
                    >
                      <span style={{ fontSize: '15px', lineHeight: 1 }}>＋</span>
                      <span>Add Filament</span>
                    </button>
                  </div>
                </div>

                {/* Error Banner */}
                {filamentsError && (
                  <div style={{
                    backgroundColor: 'rgba(248, 81, 73, 0.15)',
                    border: '1px solid rgba(248, 81, 73, 0.2)',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    color: '#ff7b72',
                    fontSize: '13px'
                  }}>
                    {filamentsError}
                  </div>
                )}

                {/* Loading or Empty or Grid */}
                {loadingFilaments ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-mute)', border: '1px solid var(--border-color)', borderRadius: '12px', background: 'var(--card-bg)' }}>
                    Loading filaments catalog...
                  </div>
                ) : filaments.length === 0 ? (
                  <div style={{
                    border: '1px dashed var(--border-color)',
                    borderRadius: '12px',
                    padding: '50px 20px',
                    textAlign: 'center',
                    color: 'var(--text-mute)',
                    background: 'var(--card-bg)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{ fontSize: '32px' }}>🧵</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-main)' }}>No Filaments Configured</div>
                    <div style={{ fontSize: '12px', maxWidth: '380px' }}>
                      There are currently no filaments registered matching your filter. Click "+ Add Filament" to register custom brand, color, and spool specifications.
                    </div>
                    <button
                      type="button"
                      onClick={openAddFilamentModal}
                      style={{
                        marginTop: '6px',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ＋ Add First Filament
                    </button>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '16px'
                  }}>
                    {filaments.map((f) => (
                      <div
                        key={f.id}
                        style={{
                          background: 'var(--card-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '12px',
                          padding: '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          gap: '12px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          transition: 'border-color 0.15s ease'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary-color)'}
                        onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
                      >
                        <div>
                          {/* Card Header with Swatch */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              {/* Round Color Swatch */}
                              <div
                                style={{
                                  width: '38px',
                                  height: '38px',
                                  borderRadius: '50%',
                                  backgroundColor: f.color_hex || '#333',
                                  border: '2px solid var(--border-color)',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                  flexShrink: 0
                                }}
                                title={`Hex: ${f.color_hex}`}
                              />
                              <div>
                                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>
                                  {f.color_name}
                                </h5>
                                <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontFamily: 'monospace' }}>
                                  {f.color_hex}
                                </span>
                              </div>
                            </div>

                            {/* Material Badge */}
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '700',
                              backgroundColor: 'rgba(56, 139, 253, 0.12)',
                              color: '#58a6ff',
                              border: '1px solid rgba(56, 139, 253, 0.25)',
                              letterSpacing: '0.5px'
                            }}>
                              {f.material_type}
                            </span>
                          </div>

                          {/* Details Row */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px',
                            marginTop: '14px',
                            padding: '10px',
                            background: 'var(--panel-color)',
                            borderRadius: '8px',
                            fontSize: '11px'
                          }}>
                            <div>
                              <span style={{ color: 'var(--text-mute)', display: 'block' }}>Brand</span>
                              <strong style={{ color: 'var(--text-main)' }}>{f.brand}</strong>
                            </div>
                            <div>
                              <span style={{ color: 'var(--text-mute)', display: 'block' }}>Product</span>
                              <strong style={{ color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }} title={f.product_name}>
                                {f.product_name}
                              </strong>
                            </div>
                            <div>
                              <span style={{ color: 'var(--text-mute)', display: 'block' }}>Spool Weight</span>
                              <strong style={{ color: 'var(--text-main)' }}>{f.weight}</strong>
                            </div>
                            <div>
                              <span style={{ color: 'var(--text-mute)', display: 'block' }}>SKU</span>
                              <strong style={{ color: 'var(--text-main)', fontFamily: f.sku ? 'monospace' : 'inherit' }} title={f.sku || 'No SKU'}>
                                {f.sku || '—'}
                              </strong>
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <span style={{ color: 'var(--text-mute)', display: 'block' }}>Scope</span>
                              {f.is_shared ? (
                                <span style={{ color: '#2ea44f', fontWeight: '600' }}>🌐 Shared with all tenants</span>
                              ) : (
                                <span style={{ color: 'var(--text-main)', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }} title={f.tenant_name}>
                                  🏢 {f.tenant_name || 'Tenant'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Card Footer Actions */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderTop: '1px solid var(--border-color)',
                          paddingTop: '10px',
                          marginTop: '4px',
                          gap: '6px'
                        }}>
                          {isGlobalRole && (
                            <button
                              type="button"
                              onClick={() => handleToggleShareFilament(f.id)}
                              style={{
                                background: 'none',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '10px',
                                fontWeight: '600',
                                color: f.is_shared ? '#eab308' : '#2ea44f',
                                cursor: 'pointer'
                              }}
                              title={f.is_shared ? "Unshare with other tenants" : "Share with all tenants"}
                            >
                              {f.is_shared ? '🔒 Unshare' : '🌐 Share'}
                            </button>
                          )}

                          <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
                            {f.can_edit && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openEditFilamentModal(f)}
                                  style={{
                                    backgroundColor: 'transparent',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    padding: '4px 10px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: 'var(--text-main)',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteFilament(f.id, f.color_name)}
                                  style={{
                                    backgroundColor: 'transparent',
                                    border: '1px solid rgba(248, 81, 73, 0.3)',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#ff7b72',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        {/* CHECKOUT MODAL */}
            {showCheckoutModal && (selectedPackage || cart.length > 0) && createPortal(
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(5px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                padding: '20px'
              }}>
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  width: '100%',
                  maxWidth: '520px',
                  padding: '28px',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  boxSizing: 'border-box'
                }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)' }}><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                      Secure Package Checkout
                    </h3>
                    <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: 'var(--text-mute)' }}>
                      Complete your order with simulated USD payment wall billing.
                    </p>
                  </div>

                  {checkoutError && (
                    <div style={{
                      backgroundColor: 'rgba(248, 81, 73, 0.15)',
                      border: '1px solid rgba(248, 81, 73, 0.2)',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      color: '#ff7b72',
                      fontSize: '13px'
                    }}>
                      {checkoutError}
                    </div>
                  )}

                  {checkoutSuccess && (
                    <div style={{
                      backgroundColor: 'rgba(56, 139, 253, 0.15)',
                      border: '1px solid rgba(56, 139, 253, 0.2)',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      color: '#58a6ff',
                      fontSize: '13px'
                    }}>
                      {checkoutSuccess}
                    </div>
                  )}

                  {/* Pricing Breakdown Calculation */}
                  {(() => {
                    const hasSub = currentUserProfile?.annual_subscription_expires_at && 
                      new Date(currentUserProfile.annual_subscription_expires_at) > new Date();

                    let itemsList = [];
                    if (cart && cart.length > 0) {
                      itemsList = cart;
                    } else if (selectedPackage) {
                      itemsList = [{ package: selectedPackage, quantity: 1 }];
                    }

                    const packagesSubtotal = itemsList.reduce((sum, item) => sum + (item.package.price * item.quantity), 0);

                    let needsHosting = !hasSub;
                    itemsList.forEach(item => {
                      if (item.package.items_config.annual_hosting > 0) {
                        needsHosting = false;
                      }
                    });
                    const hostingPrice = needsHosting ? 120.00 : 0;

                    let shippingPrice = 0;
                    itemsList.forEach(item => {
                      if (item.package.coin_quantity > 0 && !item.package.items_config.shipping_cost) {
                        shippingPrice += 20.00 * item.quantity;
                      }
                    });

                    const totalPrice = packagesSubtotal + hostingPrice + shippingPrice;

                    return (
                      <form onSubmit={handlePlaceOrder} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        
                        {/* Summary breakdown */}
                        <div style={{
                          background: 'var(--panel-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>Price Breakdown:</div>
                          
                          {itemsList.map(item => (
                            <div key={item.package.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                              <span>{item.package.name} (x{item.quantity}):</span>
                              <span style={{ fontWeight: '600' }}>${(item.package.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}

                          {hostingPrice > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#ff7b72', alignItems: 'center', gap: '6px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                                Annual Hosting (No Active Sub):
                              </span>
                              <span style={{ fontWeight: '600' }}>+${hostingPrice.toFixed(2)}</span>
                            </div>
                          )}
                          {shippingPrice > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--primary-color)', alignItems: 'center', gap: '6px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>
                                Shipping cost (Coins ordered):
                              </span>
                              <span style={{ fontWeight: '600' }}>+${shippingPrice.toFixed(2)}</span>
                            </div>
                          )}
                          <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '800' }}>
                            <span style={{ color: 'var(--text-main)' }}>Total Billed (USD):</span>
                            <span style={{ color: 'var(--accent-color)' }}>${totalPrice.toFixed(2)} USD</span>
                          </div>
                        </div>

                        {/* Operator Library Pre-selection (Optional at checkout, or can select in Orders) */}
                        {selectedPackage && (selectedPackage.coin_template_mode === 'library' || selectedPackage.card_template_mode === 'library') && (
                          <div style={{
                            backgroundColor: 'rgba(63, 185, 80, 0.08)',
                            border: '1px solid rgba(63, 185, 80, 0.3)',
                            borderRadius: '8px',
                            padding: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#3fb950', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>📚</span> <span>Select From Your Library (Optional)</span>
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                              This package uses Operator Library mode. You can pick your pre-approved coin/card now, or decide later in your Orders view.
                            </div>

                            {selectedPackage.coin_template_mode === 'library' && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                                  🪙 Approved Coin:
                                </label>
                                {(() => {
                                  const myCoins = getMyLibraryCoins();
                                  return (
                                    <select
                                      value={checkoutLibraryCoinId}
                                      onChange={(e) => setCheckoutLibraryCoinId(e.target.value)}
                                      style={{
                                        backgroundColor: 'var(--input-bg)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        color: 'var(--input-text)',
                                        fontSize: '12px'
                                      }}
                                    >
                                      <option value="">-- Choose on order later, or select now --</option>
                                      {myCoins.map(coin => (
                                        <option key={coin.id} value={coin.id}>
                                          {coin.name} (#{coin.id}) {coin.body_filament ? `[${coin.body_filament} / ${coin.accent_filament || 'Default'}]` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  );
                                })()}
                              </div>
                            )}

                            {selectedPackage.card_template_mode === 'library' && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                                  🪪 Approved Trophy Card:
                                </label>
                                {(() => {
                                  const myCards = getMyLibraryCards();
                                  return (
                                    <select
                                      value={checkoutLibraryCardId}
                                      onChange={(e) => setCheckoutLibraryCardId(e.target.value)}
                                      style={{
                                        backgroundColor: 'var(--input-bg)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        color: 'var(--input-text)',
                                        fontSize: '12px'
                                      }}
                                    >
                                      <option value="">-- Choose on order later, or select now --</option>
                                      {myCards.map(card => (
                                        <option key={card.id} value={card.id}>
                                          {card.name} (#{card.id})
                                        </option>
                                      ))}
                                    </select>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Billing details form fields */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Street Billing Address</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. 123 Harbor Side Way"
                            value={checkoutBillingAddress}
                            onChange={(e) => setCheckoutBillingAddress(e.target.value)}
                            style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                          />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>City</label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. Boston"
                              value={checkoutBillingCity}
                              onChange={(e) => setCheckoutBillingCity(e.target.value)}
                              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>State/Province</label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. MA"
                              value={checkoutBillingState}
                              onChange={(e) => setCheckoutBillingState(e.target.value)}
                              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                            />
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Postal / Zip Code</label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. 02108"
                              value={checkoutBillingZip}
                              onChange={(e) => setCheckoutBillingZip(e.target.value)}
                              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Country</label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. United States"
                              value={checkoutBillingCountry}
                              onChange={(e) => setCheckoutBillingCountry(e.target.value)}
                              style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                            />
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '15px' }}>
                          <button
                            type="button"
                            onClick={() => setShowCheckoutModal(false)}
                            style={{
                              backgroundColor: 'transparent',
                              color: 'var(--text-mute)',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '10px 16px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={checkoutLoading}
                            style={{
                              backgroundColor: '#2ea44f',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '10px 18px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            {checkoutLoading ? 'Processing Paywall...' : 'Pay & Order Now'}
                          </button>
                        </div>
                      </form>
                    );
                  })()}
                </div>
              </div>,
              document.body
            )}

            {/* FILAMENT SELECT MODAL */}
            {selectedOrderForFilaments && createPortal(
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(5px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                padding: '20px'
              }}>
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  width: '100%',
                  maxWidth: '400px',
                  padding: '24px',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  boxSizing: 'border-box'
                }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main)' }}>
                      🧵 Filament Material Selections
                    </h3>
                    <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: 'var(--text-mute)' }}>
                      Assign specific filament configurations for production.
                    </p>
                  </div>

                  <form onSubmit={handleAssignFilaments} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>Primary Filament</label>
                      <select
                        value={operatorFilamentPrimary}
                        onChange={(e) => setOperatorFilamentPrimary(e.target.value)}
                        style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-text)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px', fontSize: '13px' }}
                      >
                        {availableFilamentOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>Accent / Secondary Filament</label>
                      <select
                        value={operatorFilamentAccent}
                        onChange={(e) => setOperatorFilamentAccent(e.target.value)}
                        style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-text)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px', fontSize: '13px' }}
                      >
                        {availableFilamentOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedOrderForFilaments(null)}
                        style={{
                          backgroundColor: 'transparent',
                          color: 'var(--text-mute)',
                          border: 'none',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={filamentLoading}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Save Filament Mapping
                      </button>
                    </div>
                  </form>
                </div>
              </div>,
              document.body
            )}

        {/* VAULTED COINS LIST MODAL */}
        {vaultedCoinsModalOrder && createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '20px',
            boxSizing: 'border-box'
          }}>
            <div style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '860px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              overflow: 'hidden'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '18px 24px',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.02)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>
                    🪙
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Vaulted Coins &bull; Order #{vaultedCoinsModalOrder.id}
                    </h3>
                    <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                      {vaultedCoinsModalOrder.package_name} &bull; Customer: {vaultedCoinsModalOrder.ordered_by_user_name || vaultedCoinsModalOrder.ordered_by_user_email}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setVaultedCoinsModalOrder(null);
                    setVaultedCoinsList([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-mute)',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = 'var(--text-main)'}
                  onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-mute)'}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div style={{
                padding: '20px 24px',
                overflowY: 'auto',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {vaultedCoinsLoading ? (
                  <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-mute)' }}>
                    <div style={{ fontSize: '36px', animation: 'spin 1s linear infinite', marginBottom: '10px' }}>🪙</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>Loading Vaulted Coins...</div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>Querying CoinVault entries for Order #{vaultedCoinsModalOrder.id}</div>
                  </div>
                ) : vaultedCoinsError ? (
                  <div style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    padding: '16px',
                    color: '#fca5a5',
                    fontSize: '13px'
                  }}>
                    ⚠️ {vaultedCoinsError}
                  </div>
                ) : vaultedCoinsList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-mute)' }}>
                    <div style={{ fontSize: '36px', marginBottom: '8px' }}>📭</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>No Vaulted Coins Found</div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Coins are minted into CoinVault automatically when advancing to Stage 3 (Write Coin URL to Chip).
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Summary Statistics Bar */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '10px',
                      padding: '12px 16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
                        <div>
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Total Minted: </span>
                          <strong style={{ fontSize: '13px', color: 'var(--text-main)' }}>{vaultedCoinsList.length} Coins</strong>
                        </div>
                        <div>
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Issued: </span>
                          <strong style={{ fontSize: '13px', color: 'var(--success-text, #15803d)' }}>
                            {vaultedCoinsList.filter(c => c.is_issued).length}
                          </strong>
                        </div>
                        <div>
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Unissued: </span>
                          <strong style={{ fontSize: '13px', color: 'var(--warning-text, #b45309)' }}>
                            {vaultedCoinsList.filter(c => !c.is_issued).length}
                          </strong>
                        </div>
                        <div style={{ borderLeft: '1px solid var(--border-color, rgba(255,255,255,0.1))', paddingLeft: '14px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                          <div title="Public impressions via RFID chip or direct URL">
                            <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Public Views: </span>
                            <strong style={{ fontSize: '13px', color: 'var(--info-text, #0369a1)' }}>
                              {vaultedCoinsList.reduce((acc, c) => acc + (c.views_count || 0), 0)}
                            </strong>
                          </div>
                          <div title="Total link shares by viewers">
                            <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Shares: </span>
                            <strong style={{ fontSize: '13px', color: '#a5b4fc' }}>
                              {vaultedCoinsList.reduce((acc, c) => acc + (c.shares_count || 0), 0)}
                            </strong>
                          </div>
                          <div title="Views driven from shared links">
                            <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Shared Views: </span>
                            <strong style={{ fontSize: '13px', color: '#c084fc' }}>
                              {vaultedCoinsList.reduce((acc, c) => acc + (c.shared_views_count || 0), 0)}
                            </strong>
                          </div>
                          <div title="Click-throughs to host website, booking, and email">
                            <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Host Clicks: </span>
                            <strong style={{ fontSize: '13px', color: '#34d399' }}>
                              {vaultedCoinsList.reduce((acc, c) => acc + (c.website_clicks_count || 0) + (c.email_clicks_count || 0) + (c.booking_clicks_count || 0), 0)}
                            </strong>
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                        Order #{vaultedCoinsModalOrder.id} &bull; Minted: {vaultedCoinsList[0]?.minted_at ? new Date(vaultedCoinsList[0].minted_at).toLocaleDateString() : 'Active'}
                      </div>
                    </div>

                    {/* Coins List */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {vaultedCoinsList.map((coin) => {
                        const isCopied = copiedCoinCode === coin.public_code;
                        const adminPreviewUrl = `${coin.rfid_url}${coin.rfid_url.includes('?') ? '&' : '?'}admin_preview=true`;
                        return (
                          <div
                            key={coin.id}
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.25)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '10px',
                              padding: '14px 16px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '10px'
                            }}
                          >
                            {/* Top info row */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                  backgroundColor: 'rgba(56, 189, 248, 0.15)',
                                  border: '1px solid rgba(56, 189, 248, 0.3)',
                                  color: '#38bdf8',
                                  padding: '3px 9px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '700'
                                }}>
                                  Coin #{coin.coin_number} of {coin.total_coins}
                                </span>
                                <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                                  Token: <code style={{ color: 'var(--text-main)', fontSize: '12px' }}>{coin.public_code}</code>
                                </span>
                                <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                                  &bull; {coin.card_name}
                                </span>
                              </div>

                              {coin.is_issued ? (
                                <span style={{
                                  backgroundColor: 'var(--success-bg, rgba(16, 185, 129, 0.15))',
                                  border: '1px solid var(--success-border, rgba(16, 185, 129, 0.35))',
                                  color: 'var(--success-text, #15803d)',
                                  padding: '3px 10px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '700'
                                }}>
                                  ✓ Issued {coin.issued_at ? `(${new Date(coin.issued_at).toLocaleDateString()})` : ''}
                                </span>
                              ) : (
                                <span style={{
                                  backgroundColor: 'var(--warning-bg, rgba(234, 179, 8, 0.15))',
                                  border: '1px solid var(--warning-border, rgba(234, 179, 8, 0.35))',
                                  color: 'var(--warning-text, #b45309)',
                                  padding: '3px 10px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '700'
                                }}>
                                  ⏳ Unissued (Pending Operator Personalization)
                                </span>
                              )}
                            </div>

                            {/* Engagement & Performance Metrics Bar */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '8px',
                              backgroundColor: 'rgba(255, 255, 255, 0.03)',
                              border: '1px solid rgba(255, 255, 255, 0.07)',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '11px'
                            }}>
                              <span style={{ color: 'var(--text-mute)', fontWeight: '600' }}>Metrics:</span>
                              <span style={{
                                backgroundColor: 'rgba(56, 189, 248, 0.12)',
                                border: '1px solid rgba(56, 189, 248, 0.25)',
                                color: '#38bdf8',
                                padding: '2px 7px',
                                borderRadius: '4px',
                                fontWeight: '700'
                              }} title="Public impressions from RFID chip tap or direct coin URL">
                                👁️ {coin.views_count || 0} Public Views
                              </span>
                              <span style={{
                                backgroundColor: 'rgba(99, 102, 241, 0.12)',
                                border: '1px solid rgba(99, 102, 241, 0.25)',
                                color: '#a5b4fc',
                                padding: '2px 7px',
                                borderRadius: '4px',
                                fontWeight: '700'
                              }} title="Number of times shared by viewers">
                                ↗️ {coin.shares_count || 0} Shares
                              </span>
                              <span style={{
                                backgroundColor: 'rgba(168, 85, 247, 0.12)',
                                border: '1px solid rgba(168, 85, 247, 0.25)',
                                color: '#c084fc',
                                padding: '2px 7px',
                                borderRadius: '4px',
                                fontWeight: '700'
                              }} title="Views driven from shared links">
                                🔗 {coin.shared_views_count || 0} Shared Views
                              </span>
                              <span style={{
                                backgroundColor: 'rgba(16, 185, 129, 0.12)',
                                border: '1px solid rgba(16, 185, 129, 0.25)',
                                color: '#34d399',
                                padding: '2px 7px',
                                borderRadius: '4px',
                                fontWeight: '700'
                              }} title="Click-throughs to host website, booking, and email">
                                🎯 {(coin.website_clicks_count || 0) + (coin.email_clicks_count || 0) + (coin.booking_clicks_count || 0)} Host Clicks
                                <span style={{ opacity: 0.85, fontWeight: 'normal', marginLeft: '4px' }}>
                                  ({coin.website_clicks_count || 0} web &bull; {coin.booking_clicks_count || 0} book &bull; {coin.email_clicks_count || 0} mail)
                                </span>
                              </span>
                              {coin.last_viewed_at && (
                                <span style={{ color: 'var(--text-mute)', marginLeft: 'auto', fontSize: '10px' }}>
                                  Last Active: {new Date(coin.last_viewed_at).toLocaleDateString()} {new Date(coin.last_viewed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>

                            {/* NFC RFID URL with Copy & Open */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)', minWidth: '70px' }}>
                                RFID URL:
                              </div>
                              <input
                                type="text"
                                readOnly
                                value={coin.rfid_url}
                                style={{
                                  flex: 1,
                                  minWidth: '220px',
                                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '6px',
                                  padding: '6px 10px',
                                  color: 'var(--text-main)',
                                  fontSize: '12px',
                                  fontFamily: 'monospace'
                                }}
                                onClick={(e) => e.target.select()}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (navigator.clipboard && navigator.clipboard.writeText) {
                                    navigator.clipboard.writeText(coin.rfid_url).catch(() => {});
                                  } else {
                                    try {
                                      const ta = document.createElement('textarea');
                                      ta.value = coin.rfid_url;
                                      ta.style.position = 'fixed';
                                      ta.style.left = '-9999px';
                                      document.body.appendChild(ta);
                                      ta.select();
                                      document.execCommand('copy');
                                      document.body.removeChild(ta);
                                    } catch (err) {}
                                  }
                                  setCopiedCoinCode(coin.public_code);
                                  setTimeout(() => setCopiedCoinCode(null), 2000);
                                }}
                                style={{
                                  backgroundColor: isCopied ? '#10b981' : 'rgba(255, 255, 255, 0.08)',
                                  color: isCopied ? '#ffffff' : 'var(--text-main)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '6px',
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {isCopied ? '✓ Copied!' : '📋 Copy URL'}
                              </button>
                              <a
                                href={adminPreviewUrl}
                                target="_blank"
                                rel="noreferrer"
                                title="Open card in Admin Preview mode (will not increment public views count)"
                                style={{
                                  backgroundColor: 'rgba(56, 139, 253, 0.15)',
                                  color: '#58a6ff',
                                  border: '1px solid rgba(56, 139, 253, 0.3)',
                                  borderRadius: '6px',
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  textDecoration: 'none',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                🔗 Open View &rarr;
                              </a>
                            </div>

                            {/* Previews and Details for Issued Coins */}
                            {coin.is_issued && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '14px',
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                borderRadius: '8px',
                                padding: '10px 12px',
                                flexWrap: 'wrap'
                              }}>
                                {/* Thumbnails */}
                                {coin.preview_front_url && (
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <img
                                        src={coin.preview_front_url}
                                        alt="Front Preview"
                                        style={{ width: '48px', height: '67px', objectFit: 'contain', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}
                                      />
                                      <div style={{ fontSize: '9px', color: 'var(--text-mute)', marginTop: '2px' }}>Front</div>
                                    </div>
                                    {coin.preview_back_url && (
                                      <div style={{ textAlign: 'center' }}>
                                        <img
                                          src={coin.preview_back_url}
                                          alt="Back Preview"
                                          style={{ width: '48px', height: '67px', objectFit: 'contain', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}
                                        />
                                        <div style={{ fontSize: '9px', color: 'var(--text-mute)', marginTop: '2px' }}>Back</div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Card Data Tags */}
                                <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-mute)' }}>Personalized Fields:</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {Object.entries(coin.card_data || {}).map(([k, v]) => (
                                      <span key={k} style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(255, 255, 255, 0.08)',
                                        borderRadius: '4px',
                                        padding: '2px 6px',
                                        fontSize: '11px',
                                        color: 'var(--text-main)'
                                      }}>
                                        <span style={{ color: 'var(--text-mute)' }}>{k}:</span> <strong>{String(v)}</strong>
                                      </span>
                                    ))}
                                  </div>
                                  {coin.md5_front && (
                                    <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '4px', fontFamily: 'monospace' }}>
                                      MD5 Front: {coin.md5_front.substring(0, 12)}... &bull; MD5 Back: {coin.md5_back?.substring(0, 12)}...
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '14px 24px',
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: 'rgba(255, 255, 255, 0.02)'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setVaultedCoinsModalOrder(null);
                    setVaultedCoinsList([]);
                  }}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: 'var(--text-main)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '8px 18px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ADD / EDIT FILAMENT MODAL */}
        {isAddFilamentModalOpen && createPortal(
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px'
          }}>
            <div style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '460px',
              padding: '24px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 35px rgba(0,0,0,0.25)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              boxSizing: 'border-box'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🧵</span>
                  {editingFilament ? 'Edit Filament Specification' : 'Add Production Filament'}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsAddFilamentModalOpen(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-mute)', fontSize: '18px', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              {filamentFormError && (
                <div style={{
                  backgroundColor: 'rgba(248, 81, 73, 0.15)',
                  border: '1px solid rgba(248, 81, 73, 0.2)',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#ff7b72',
                  fontSize: '12px'
                }}>
                  {filamentFormError}
                </div>
              )}

              <form onSubmit={handleSaveFilament} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* SKU Lookup & Auto-Fill Section */}
                <div style={{
                  background: 'var(--panel-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '14px' }}>⚡</span>
                      <strong style={{ fontSize: '12px', color: 'var(--text-main)' }}>Auto-Fill from SKU Lookup</strong>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Bambu, eSUN, Polymaker, etc.</span>
                  </div>

                  {/* SKU Input & Lookup Button */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input
                        type="text"
                        list="sku-presets-list"
                        placeholder="Enter SKU (e.g. 10100, BL-PLA-GLD, ES-PLA-BLK)..."
                        value={filamentSku}
                        onChange={(e) => {
                          setFilamentSku(e.target.value);
                          if (skuLookupNotice) setSkuLookupNotice(null);
                          if (skuLookupSuccess) setSkuLookupSuccess(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSkuLookup(filamentSku);
                          }
                        }}
                        style={{
                          width: '100%',
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '13px',
                          outline: 'none',
                          fontFamily: filamentSku ? 'monospace' : 'inherit',
                          boxSizing: 'border-box'
                        }}
                      />
                      <datalist id="sku-presets-list">
                        {skuPresets.map((p, idx) => (
                          <option key={idx} value={p.sku}>
                            {p.brand} - {p.product_name} ({p.color_name})
                          </option>
                        ))}
                      </datalist>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSkuLookup(filamentSku)}
                      disabled={lookingUpSku || !filamentSku.trim()}
                      style={{
                        background: filamentSku.trim() ? 'var(--primary-color)' : 'var(--panel-color)',
                        color: filamentSku.trim() ? 'white' : 'var(--text-mute)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '8px 14px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: filamentSku.trim() && !lookingUpSku ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {lookingUpSku ? (
                        <>
                          <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                          Looking up...
                        </>
                      ) : (
                        <>
                          <span>🔍</span>
                          Lookup SKU
                        </>
                      )}
                    </button>
                  </div>

                  {/* Preset quick selector dropdown */}
                  {skuPresets.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-mute)', whiteSpace: 'nowrap' }}>Or pick known preset:</span>
                      <select
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val) {
                            setFilamentSku(val);
                            handleSkuLookup(val);
                          }
                        }}
                        defaultValue=""
                        style={{
                          flex: 1,
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '5px 8px',
                          color: 'var(--input-text)',
                          fontSize: '11px',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="" disabled>-- Select a manufacturer filament --</option>
                        {skuPresets.map((p, idx) => (
                          <option key={idx} value={p.sku}>
                            {p.brand}: {p.product_name} - {p.color_name} [{p.sku}]
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Notification banners */}
                  {skuLookupSuccess && (
                    <div style={{
                      backgroundColor: 'rgba(46, 164, 79, 0.15)',
                      border: '1px solid rgba(46, 164, 79, 0.3)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: '#3fb950',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>✓</span>
                      <span>{skuLookupSuccess}</span>
                    </div>
                  )}

                  {skuLookupNotice && (
                    <div style={{
                      backgroundColor: 'rgba(210, 153, 34, 0.12)',
                      border: '1px solid rgba(210, 153, 34, 0.25)',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: '#d29922',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>ℹ</span>
                      <span>{skuLookupNotice}</span>
                    </div>
                  )}
                </div>

                {/* Brand & Product */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Brand *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Bambu Lab, eSUN"
                      value={filamentBrand}
                      onChange={(e) => setFilamentBrand(e.target.value)}
                      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Product Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. PLA Basic, PETG HF"
                      value={filamentProductName}
                      onChange={(e) => setFilamentProductName(e.target.value)}
                      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                {/* Color Name & Color Picker */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '10px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Color Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Silk Gold, Matte Navy"
                      value={filamentColorName}
                      onChange={(e) => setFilamentColorName(e.target.value)}
                      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Color Swatch *</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="color"
                        value={filamentColorHex}
                        onChange={(e) => setFilamentColorHex(e.target.value)}
                        style={{
                          width: '42px',
                          height: '35px',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          padding: '1px'
                        }}
                      />
                      <input
                        type="text"
                        value={filamentColorHex}
                        onChange={(e) => setFilamentColorHex(e.target.value)}
                        placeholder="#D4AF37"
                        maxLength={7}
                        style={{
                          width: '75px',
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '7px 8px',
                          color: 'var(--input-text)',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Material Type & Weight */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Material Type</label>
                    <select
                      value={filamentMaterialType}
                      onChange={(e) => setFilamentMaterialType(e.target.value)}
                      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }}
                    >
                      <option value="PLA">PLA</option>
                      <option value="PLA+">PLA+</option>
                      <option value="ABS">ABS</option>
                      <option value="PETG">PETG</option>
                      <option value="TPU">TPU</option>
                      <option value="ASA">ASA</option>
                      <option value="Nylon">Nylon</option>
                      <option value="PC">PC</option>
                      <option value="Metal Composite">Metal Composite</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Spool Weight</label>
                    <input
                      type="text"
                      placeholder="e.g. 1kg, 1000g, 750g"
                      value={filamentWeight}
                      onChange={(e) => setFilamentWeight(e.target.value)}
                      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                {/* Global Admin Sharing / Tenant Assignment */}
                {isGlobalRole && (
                  <div style={{
                    background: 'var(--panel-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                      <input
                        type="checkbox"
                        checked={filamentIsShared}
                        onChange={(e) => setFilamentIsShared(e.target.checked)}
                      />
                      <span>🌐 Share with all tenants (Global catalog)</span>
                    </label>

                    {!filamentIsShared && tenants && tenants.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: '600' }}>Assign to specific tenant:</label>
                        <select
                          value={filamentTargetTenantId}
                          onChange={(e) => setFilamentTargetTenantId(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '6px 10px', color: 'var(--input-text)', fontSize: '12px', outline: 'none' }}
                        >
                          <option value="">Global / Unassigned</option>
                          {tenants.map(t => (
                            <option key={t.id} value={t.id}>{t.brand_name || t.domain_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                  <button
                    type="button"
                    onClick={() => setIsAddFilamentModalOpen(false)}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-main)',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingFilament}
                    style={{
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 18px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      opacity: savingFilament ? 0.7 : 1
                    }}
                  >
                    {savingFilament ? 'Saving...' : (editingFilament ? 'Save Changes' : 'Add Filament')}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

            {/* CUSTOM PACKAGE CREATOR MODAL (Tenant Admin only) */}
            {showCustomPackageModal && createPortal(
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(5px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                padding: '20px'
              }}>
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  width: '100%',
                  maxWidth: '560px',
                  padding: '24px',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  boxSizing: 'border-box'
                }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main)' }}>
                      🛠️ Construct Custom Package
                    </h3>
                    <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: 'var(--text-mute)' }}>
                      Combine billable items, set custom package pricing, and configure special time/date promotions.
                    </p>
                  </div>

                  {customPkgError && (
                    <div style={{
                      backgroundColor: 'rgba(248, 81, 73, 0.15)',
                      border: '1px solid rgba(248, 81, 73, 0.2)',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      color: '#ff7b72',
                      fontSize: '13px'
                    }}>
                      {customPkgError}
                    </div>
                  )}

                  {customPkgSuccess && (
                    <div style={{
                      backgroundColor: 'rgba(56, 139, 253, 0.15)',
                      border: '1px solid rgba(56, 139, 253, 0.2)',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      color: '#58a6ff',
                      fontSize: '13px'
                    }}>
                      {customPkgSuccess}
                    </div>
                  )}

                  <form onSubmit={handleCreateCustomPackage} style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%', boxSizing: 'border-box' }}>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Package Name</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Summer Promo Pack"
                        value={customPkgName}
                        onChange={(e) => setCustomPkgName(e.target.value)}
                        style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Description</label>
                      <textarea
                        rows={2}
                        placeholder="e.g. Includes 25 standard coins plus custom brag page setup..."
                        value={customPkgDescription}
                        onChange={(e) => setCustomPkgDescription(e.target.value)}
                        style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px', resize: 'none', fontFamily: 'inherit' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Price (USD)</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          placeholder="e.g. 350.00"
                          value={customPkgPrice}
                          onChange={(e) => setCustomPkgPrice(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Coin Quantity</label>
                        <input
                          type="number"
                          required
                          placeholder="e.g. 25"
                          value={customPkgCoins}
                          onChange={(e) => {
                            setCustomPkgCoins(e.target.value);
                            const val = parseInt(e.target.value) || 0;
                            setCustomPkgItems(prev => ({ ...prev, standard_coin: val }));
                          }}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                    </div>

                    <div style={{ borderBottom: '1px solid var(--border-color)', margin: '5px 0' }} />

                    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>Items Configuration Checklist:</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px 20px' }}>
                      {Object.keys(customPkgItems).map(itemKey => (
                        <div key={itemKey} style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                            {itemKey.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </span>
                          <input
                            type="number"
                            min="0"
                            value={customPkgItems[itemKey]}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              setCustomPkgItems(prev => ({
                                ...prev,
                                [itemKey]: val
                              }));
                              if (itemKey === 'standard_coin') {
                                setCustomPkgCoins(val.toString());
                              }
                            }}
                            style={{ width: '60px', backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '2px 6px', color: 'var(--input-text)', fontSize: '12px', textAlign: 'right' }}
                          />
                        </div>
                      ))}
                    </div>

                    <div style={{ borderBottom: '1px solid var(--border-color)', margin: '5px 0' }} />

                    {/* Template Automation (Coin & Trophy Card) */}
                    <div style={{
                      background: 'var(--panel-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Purchased Package Automation:
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px', lineHeight: '1.4' }}>
                          Optional coin and trophy card presets applied when an operator purchases this package.
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', boxSizing: 'border-box' }}>
                        {/* Coin Selector */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%', boxSizing: 'border-box' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                            🪙 Coin Preset / Template:
                          </label>
                          <select
                            value={customPkgCoinTemplate}
                            onChange={(e) => setCustomPkgCoinTemplate(e.target.value)}
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              padding: '8px 10px',
                              color: 'var(--input-text)',
                              fontSize: '13px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden'
                            }}
                          >
                            <option value="none">None (Default - No coin attached)</option>
                            <option value="blank">✨ Blank Coin (Operator proofs from scratch)</option>
                            <option value="library">📚 Operator Library (Operator selects from their approved library — No Proofing)</option>
                            {availableSystemCoins.length > 0 && (
                              <optgroup label="Available System Coins">
                                {availableSystemCoins.map(coin => {
                                  const isFull = Boolean(coin.face_1_design_id && coin.face_2_design_id && coin.body_filament && coin.accent_filament);
                                  return (
                                    <option key={coin.id} value={`system_${coin.id}`}>
                                      {coin.name} (#{coin.id}) {isFull ? '— ✓ Ready' : '— ⚠️ Needs Proofing'}
                                    </option>
                                  );
                                })}
                              </optgroup>
                            )}
                          </select>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                            {customPkgCoinTemplate === 'none' && 'No coin will be created or attached upon purchase.'}
                            {customPkgCoinTemplate === 'blank' && 'Creates a new blank coin in the operator\'s coins grid for proofing.'}
                            {customPkgCoinTemplate === 'library' && (
                              <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                📚 Operator Library: purchasing operator selects an approved coin from their library upon purchase. Pre-authorized with no proofing required.
                              </span>
                            )}
                            {customPkgCoinTemplate.startsWith('library_') && (
                              <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                ✓ Approved library coin: attached directly to orders with no proofing step required.
                              </span>
                            )}
                            {customPkgCoinTemplate.startsWith('system_') && (() => {
                              const sId = parseInt(customPkgCoinTemplate.replace('system_', ''), 10);
                              const coin = availableSystemCoins.find(c => c.id === sId);
                              if (!coin) return null;
                              const isFull = Boolean(coin.face_1_design_id && coin.face_2_design_id && coin.body_filament && coin.accent_filament);
                              return isFull ? (
                                <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                  ✓ Fully defined: applied directly to the order and ready for production upon payment.
                                </span>
                              ) : (
                                <span style={{ color: '#d29922', fontWeight: '500' }}>
                                  ⚠️ Incomplete: will duplicate into the operator's coins grid to complete face/filament proofing.
                                </span>
                              );
                            })()}
                          </div>
                          {renderCoinTemplatePreview(customPkgCoinTemplate)}
                        </div>

                        {/* Trophy Card Selector */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%', boxSizing: 'border-box' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                            🪪 Trophy Card Preset / Template:
                          </label>
                          <select
                            value={customPkgCardTemplate}
                            onChange={(e) => setCustomPkgCardTemplate(e.target.value)}
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              padding: '8px 10px',
                              color: 'var(--input-text)',
                              fontSize: '13px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden'
                            }}
                          >
                            <option value="none">None (Default - No trophy card)</option>
                            <option value="blank">✨ Blank Trophy Card (Operator designs from scratch)</option>
                            <option value="library">📚 Operator Library (Operator selects from their approved library — No Proofing)</option>
                            {(() => {
                              const bragDesigns = allDesigns.filter(d => d.design_type === 'brag_page');
                              const systemCards = bragDesigns.filter(d => d.config && (d.config.is_system_template === true || d.config.is_system_template === 'true'));
                              const otherCards = bragDesigns.filter(d => 
                                !(d.config && (d.config.is_system_template === true || d.config.is_system_template === 'true'))
                              );
                              return (
                                <>
                                  {systemCards.length > 0 && (
                                    <optgroup label="Available System Card Templates">
                                      {systemCards.map(card => (
                                        <option key={card.id} value={`template_${card.id}`}>
                                          ⭐ {card.name} (#{card.id}) — Pre-designed System Template
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {otherCards.length > 0 && (
                                    <optgroup label="Other Card Templates & Designs">
                                      {otherCards.map(card => (
                                        <option key={card.id} value={`template_${card.id}`}>
                                          {card.name} (#{card.id}) {card.config && (card.config.is_template === true || card.config.is_template === 'true') ? '★ Official Template' : ''}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                </>
                              );
                            })()}
                          </select>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                            {customPkgCardTemplate === 'none' && 'No trophy card will be created or attached upon purchase.'}
                            {customPkgCardTemplate === 'blank' && 'Creates a new blank trophy card in the operator\'s cards grid for proofing.'}
                            {customPkgCardTemplate === 'library' && (
                              <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                📚 Operator Library: purchasing operator selects an approved trophy card from their library upon purchase. Pre-authorized with no proofing required.
                              </span>
                            )}
                            {customPkgCardTemplate.startsWith('library_') && (
                              <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                ✓ Approved library card: pre-approved card automatically duplicated with no proofing step required.
                              </span>
                            )}
                            {customPkgCardTemplate.startsWith('template_') && (() => {
                              const tId = parseInt(customPkgCardTemplate.replace('template_', ''), 10);
                              const card = allDesigns.find(d => d.id === tId);
                              const isSys = card && card.config && (card.config.is_system_template === true || card.config.is_system_template === 'true');
                              return isSys ? (
                                <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                  ⭐ Pre-designed System Card Template: will automatically duplicate into the operator's trophy cards grid upon purchase.
                                </span>
                              ) : (
                                <span style={{ color: '#58a6ff', fontWeight: '500' }}>
                                  🪪 Card template will duplicate into the operator's trophy cards grid for proofing upon purchase.
                                </span>
                              );
                            })()}
                          </div>
                          {renderCardTemplatePreview(customPkgCardTemplate)}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Offer Start Date</label>
                        <input
                          type="datetime-local"
                          value={customPkgStartDate}
                          onChange={(e) => setCustomPkgStartDate(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Offer End Date</label>
                        <input
                          type="datetime-local"
                          value={customPkgEndDate}
                          onChange={(e) => setCustomPkgEndDate(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                      <button
                        type="button"
                        onClick={() => setShowCustomPackageModal(false)}
                        style={{
                          backgroundColor: 'transparent',
                          color: 'var(--text-mute)',
                          border: 'none',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={customPkgLoading}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 18px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        {customPkgLoading ? 'Creating...' : 'Create Package'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>,
              document.body
            )}

            {/* EDIT PACKAGE TEMPLATE MODAL */}
            {selectedPackageForEdit && createPortal(
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(5px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                padding: '20px'
              }}>
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  width: '100%',
                  maxWidth: '560px',
                  padding: '24px',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  boxSizing: 'border-box'
                }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main)' }}>
                      ✏️ Edit Package Template
                    </h3>
                    <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: 'var(--text-mute)' }}>
                      Modify name, pricing, item configuration, active status, or validity dates.
                    </p>
                  </div>

                  {editPkgError && (
                    <div style={{
                      backgroundColor: 'rgba(248, 81, 73, 0.15)',
                      border: '1px solid rgba(248, 81, 73, 0.2)',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      color: '#ff7b72',
                      fontSize: '13px'
                    }}>
                      {editPkgError}
                    </div>
                  )}

                  {editPkgSuccess && (
                    <div style={{
                      backgroundColor: 'rgba(46, 160, 67, 0.15)',
                      border: '1px solid rgba(46, 160, 67, 0.2)',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      color: '#3fb950',
                      fontSize: '13px'
                    }}>
                      {editPkgSuccess}
                    </div>
                  )}

                  <form onSubmit={handleUpdatePackage} style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600' }}>Package Name</label>
                        <input
                          type="text"
                          required
                          value={editPkgName}
                          onChange={(e) => setEditPkgName(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '10px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600' }}>Price (USD)</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          value={editPkgPrice}
                          onChange={(e) => setEditPkgPrice(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '10px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600' }}>Description</label>
                      <textarea
                        value={editPkgDescription}
                        onChange={(e) => setEditPkgDescription(e.target.value)}
                        rows={2}
                        style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '10px', color: 'var(--input-text)', fontSize: '13px', resize: 'vertical' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600' }}>Coins quantity</label>
                        <input
                          type="number"
                          required
                          value={editPkgCoins}
                          onChange={(e) => setEditPkgCoins(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '10px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', justifyContent: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginTop: '15px' }}>
                          <input
                            type="checkbox"
                            checked={editPkgIsActive}
                            onChange={(e) => setEditPkgIsActive(e.target.checked)}
                          />
                          Active Template
                        </label>
                      </div>
                    </div>

                    <div style={{ borderBottom: '1px solid var(--border-color)', margin: '5px 0' }} />

                    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Items Configuration Checklist:</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px 20px' }}>
                      {Object.keys(editPkgItems).map(itemKey => (
                        <div key={itemKey} style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                            {itemKey.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </span>
                          <input
                            type="number"
                            min="0"
                            value={editPkgItems[itemKey]}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              setEditPkgItems(prev => ({
                                ...prev,
                                [itemKey]: val
                              }));
                              if (itemKey === 'standard_coin') {
                                setEditPkgCoins(val.toString());
                              }
                            }}
                            style={{ width: '60px', backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '2px 6px', color: 'var(--input-text)', fontSize: '12px', textAlign: 'right' }}
                          />
                        </div>
                      ))}
                    </div>

                    <div style={{ borderBottom: '1px solid var(--border-color)', margin: '5px 0' }} />

                    {/* Template Automation (Coin & Trophy Card) */}
                    <div style={{
                      background: 'var(--panel-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Purchased Package Automation:
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px', lineHeight: '1.4' }}>
                          Optional coin and trophy card presets applied when an operator purchases this package.
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', boxSizing: 'border-box' }}>
                        {/* Coin Selector */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%', boxSizing: 'border-box' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600' }}>
                            🪙 Coin Preset / Template:
                          </label>
                          <select
                            value={editPkgCoinTemplate}
                            onChange={(e) => setEditPkgCoinTemplate(e.target.value)}
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              padding: '8px 10px',
                              color: 'var(--input-text)',
                              fontSize: '13px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden'
                            }}
                          >
                            <option value="none">None (Default - No coin attached)</option>
                            <option value="blank">✨ Blank Coin (Operator proofs from scratch)</option>
                            <option value="library">📚 Operator Library (Operator selects from their approved library — No Proofing)</option>
                            {availableSystemCoins.length > 0 && (
                              <optgroup label="Available System Coins">
                                {availableSystemCoins.map(coin => {
                                  const isFull = Boolean(coin.face_1_design_id && coin.face_2_design_id && coin.body_filament && coin.accent_filament);
                                  return (
                                    <option key={coin.id} value={`system_${coin.id}`}>
                                      {coin.name} (#{coin.id}) {isFull ? '— ✓ Ready' : '— ⚠️ Needs Proofing'}
                                    </option>
                                  );
                                })}
                              </optgroup>
                            )}
                          </select>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                            {editPkgCoinTemplate === 'none' && 'No coin will be created or attached upon purchase.'}
                            {editPkgCoinTemplate === 'blank' && 'Creates a new blank coin in the operator\'s coins grid for proofing.'}
                            {editPkgCoinTemplate === 'library' && (
                              <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                📚 Operator Library: purchasing operator selects an approved coin from their library upon purchase. Pre-authorized with no proofing required.
                              </span>
                            )}
                            {editPkgCoinTemplate.startsWith('library_') && (
                              <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                ✓ Approved library coin: attached directly to orders with no proofing step required.
                              </span>
                            )}
                            {editPkgCoinTemplate.startsWith('system_') && (() => {
                              const sId = parseInt(editPkgCoinTemplate.replace('system_', ''), 10);
                              const coin = availableSystemCoins.find(c => c.id === sId);
                              if (!coin) return null;
                              const isFull = Boolean(coin.face_1_design_id && coin.face_2_design_id && coin.body_filament && coin.accent_filament);
                              return isFull ? (
                                <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                  ✓ Fully defined: applied directly to the order and ready for production upon payment.
                                </span>
                              ) : (
                                <span style={{ color: '#d29922', fontWeight: '500' }}>
                                  ⚠️ Incomplete: will duplicate into the operator's coins grid to complete face/filament proofing.
                                </span>
                              );
                            })()}
                          </div>
                          {renderCoinTemplatePreview(editPkgCoinTemplate)}
                        </div>

                        {/* Trophy Card Selector */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%', boxSizing: 'border-box' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600' }}>
                            🪪 Trophy Card Preset / Template:
                          </label>
                          <select
                            value={editPkgCardTemplate}
                            onChange={(e) => setEditPkgCardTemplate(e.target.value)}
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              padding: '8px 10px',
                              color: 'var(--input-text)',
                              fontSize: '13px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden'
                            }}
                          >
                            <option value="none">None (Default - No trophy card)</option>
                            <option value="blank">✨ Blank Trophy Card (Operator designs from scratch)</option>
                            <option value="library">📚 Operator Library (Operator selects from their approved library — No Proofing)</option>
                            {(() => {
                              const bragDesigns = allDesigns.filter(d => d.design_type === 'brag_page');
                              const systemCards = bragDesigns.filter(d => d.config && (d.config.is_system_template === true || d.config.is_system_template === 'true'));
                              const otherCards = bragDesigns.filter(d => 
                                !(d.config && (d.config.is_system_template === true || d.config.is_system_template === 'true'))
                              );
                              return (
                                <>
                                  {systemCards.length > 0 && (
                                    <optgroup label="Available System Card Templates">
                                      {systemCards.map(card => (
                                        <option key={card.id} value={`template_${card.id}`}>
                                          ⭐ {card.name} (#{card.id}) — Pre-designed System Template
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {otherCards.length > 0 && (
                                    <optgroup label="Other Card Templates & Designs">
                                      {otherCards.map(card => (
                                        <option key={card.id} value={`template_${card.id}`}>
                                          {card.name} (#{card.id}) {card.config && (card.config.is_template === true || card.config.is_template === 'true') ? '★ Official Template' : ''}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                </>
                              );
                            })()}
                          </select>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                            {editPkgCardTemplate === 'none' && 'No trophy card will be created or attached upon purchase.'}
                            {editPkgCardTemplate === 'blank' && 'Creates a new blank trophy card in the operator\'s cards grid for proofing.'}
                            {editPkgCardTemplate === 'library' && (
                              <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                📚 Operator Library: purchasing operator selects an approved trophy card from their library upon purchase. Pre-authorized with no proofing required.
                              </span>
                            )}
                            {editPkgCardTemplate.startsWith('library_') && (
                              <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                ✓ Approved library card: pre-approved card automatically duplicated with no proofing step required.
                              </span>
                            )}
                            {editPkgCardTemplate.startsWith('template_') && (() => {
                              const tId = parseInt(editPkgCardTemplate.replace('template_', ''), 10);
                              const card = allDesigns.find(d => d.id === tId);
                              const isSys = card && card.config && (card.config.is_system_template === true || card.config.is_system_template === 'true');
                              return isSys ? (
                                <span style={{ color: '#3fb950', fontWeight: '500' }}>
                                  ⭐ Pre-designed System Card Template: will automatically duplicate into the operator's trophy cards grid upon purchase.
                                </span>
                              ) : (
                                <span style={{ color: '#58a6ff', fontWeight: '500' }}>
                                  🪪 Card template will duplicate into the operator's trophy cards grid for proofing upon purchase.
                                </span>
                              );
                            })()}
                          </div>
                          {renderCardTemplatePreview(editPkgCardTemplate)}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600' }}>Start Validity Date (Optional)</label>
                        <input
                          type="datetime-local"
                          value={editPkgStartDate}
                          onChange={(e) => setEditPkgStartDate(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600' }}>End Validity Date (Optional)</label>
                        <input
                          type="datetime-local"
                          value={editPkgEndDate}
                          onChange={(e) => setEditPkgEndDate(e.target.value)}
                          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '6px', padding: '8px', color: 'var(--input-text)', fontSize: '13px' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '15px' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedPackageForEdit(null)}
                        style={{
                          backgroundColor: 'transparent',
                          color: 'var(--text-mute)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 16px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={editPkgLoading}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 18px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        {editPkgLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>,
              document.body
            )}

        {/* Tab Content: Organisation Management */}
        {activeTab === 'organisation' && (user.role === 'END_USER' || user.role === 'OPERATOR' || user.role === 'AGENT') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '20px',
            }}>
              <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', color: 'var(--primary-color)' }}>
                Organisation Management
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)' }}>
                {user.role === 'OPERATOR' 
                  ? "View and manage company details, online bookings, and authorized operators for your organization." 
                  : "Register your organization to manage collaborative operations, bookings, and commercial brand assets."}
              </p>
            </div>

            {orgError && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.4)',
                borderRadius: '6px',
                color: '#ff7b72',
                padding: '12px',
                fontSize: '13px'
              }}>
                <strong>Error:</strong> {typeof orgError === 'string' ? orgError : JSON.stringify(orgError)}
              </div>
            )}

            {orgSuccess && (
              <div style={{
                backgroundColor: 'rgba(56, 139, 253, 0.15)',
                border: '1px solid rgba(56, 139, 253, 0.4)',
                borderRadius: '6px',
                color: '#58a6ff',
                padding: '12px',
                fontSize: '13px'
              }}>
                {orgSuccess}
              </div>
            )}

            {(user.role === 'END_USER' || isEditingOrg || (user.role === 'OPERATOR' && !orgData && !orgLoading)) ? (
              (user.role === 'END_USER' && !showRegistrationForm) ? (
                /* BENEFITS WRITE-UP */
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)' }}>
                      Why Register an Organisation?
                    </h4>
                    <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: 'var(--text-mute)' }}>
                      Unlock corporate tools and features designed to help your company scale its operations. Transitioning to an organization unlocks collaborative access and white-labeled asset properties:
                    </p>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                    gap: '20px',
                    margin: '10px 0'
                  }}>
                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                      <strong style={{ fontSize: '14px', color: 'var(--text-main)' }}>Multi-Operator Access</strong>
                      <span style={{ fontSize: '12px', color: 'var(--text-mute)', lineHeight: '1.5' }}>
                        Invite, assign, and authorize multiple operator team members to design and manage your digital coin collections.
                      </span>
                    </div>

                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                      <strong style={{ fontSize: '14px', color: 'var(--text-main)' }}>Bookings & Support Link</strong>
                      <span style={{ fontSize: '12px', color: 'var(--text-mute)', lineHeight: '1.5' }}>
                        Embed website listings, live online scheduling portals, social media pages, and direct customer enquiries to your minted coins.
                      </span>
                    </div>

                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                      <strong style={{ fontSize: '14px', color: 'var(--text-main)' }}>Commercial Billing</strong>
                      <span style={{ fontSize: '12px', color: 'var(--text-mute)', lineHeight: '1.5' }}>
                        Link corporate billing, invoicing, and localized metadata tags to scale large-format customer campaigns.
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowRegistrationForm(true)}
                    style={{
                      alignSelf: 'center',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    Register Now
                  </button>
                </div>
              ) : (
                /* REGISTRATION FORM */
                <form 
                  onSubmit={handleRegisterOrganization}
                  style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px'
                  }}
                >
                  <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                    {orgData ? "Edit Organisation Details" : (user.role === 'OPERATOR' ? "Register Your Organisation" : "Register Company Details")}
                  </h4>

                  {/* Logo Management */}
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.01)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
                    <div style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      {orgLogoUrl ? (
                        <img src={orgLogoUrl} alt="Logo Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '28px', color: 'var(--text-mute)', fontWeight: 'bold' }}>
                          {orgName ? orgName.charAt(0).toUpperCase() : '🏢'}
                        </span>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>Organisation Logo</span>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <label style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'inline-block'
                        }}>
                          Upload Logo
                          <input type="file" accept="image/*" onChange={handleUploadOrgLogo} style={{ display: 'none' }} />
                        </label>

                        <button
                          type="button"
                          onClick={async () => {
                            await fetchAssetImages();
                            setShowAssetPicker(true);
                          }}
                          style={{
                            backgroundColor: 'transparent',
                            color: 'var(--primary-color)',
                            border: '1px solid var(--primary-color)',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Choose from Assets
                        </button>

                        {orgLogoUrl && (
                          <button
                            type="button"
                            onClick={() => setOrgLogoUrl('')}
                            style={{
                              backgroundColor: 'transparent',
                              color: '#ff7b72',
                              border: 'none',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove Logo
                          </button>
                        )}
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Recommended dimensions: square PNG or JPG, min 200x200px.</span>
                    </div>
                  </div>

                  {/* Asset Picker Modal/Popover */}
                  {showAssetPicker && createPortal(
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100vw',
                      height: '100vh',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      backdropFilter: 'blur(5px)',
                      zIndex: 99999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '20px'
                    }}>
                      <div style={{
                        background: 'var(--card-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        width: '100%',
                        maxWidth: '500px',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: '0 20px 35px rgba(0,0,0,0.3)',
                        boxSizing: 'border-box'
                      }}>
                        <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong style={{ fontSize: '15px', color: 'var(--text-main)' }}>Select Image Asset</strong>
                          <button
                            type="button"
                            onClick={() => setShowAssetPicker(false)}
                            style={{ background: 'none', border: 'none', color: 'var(--text-mute)', fontSize: '18px', cursor: 'pointer' }}
                          >
                            ×
                          </button>
                        </div>
                        
                        <div style={{ padding: '16px', overflowY: 'auto', flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '12px' }}>
                          {assetImages.length === 0 ? (
                            <span style={{ gridColumn: '1/-1', color: 'var(--text-mute)', fontSize: '13px', textAlign: 'center', fontStyle: 'italic', padding: '20px' }}>
                              No image assets uploaded yet.
                            </span>
                          ) : (
                            assetImages.map((asset) => {
                              const path = asset.storage_location || asset.location || '';
                              const assetUrl = path.startsWith('http') ? path : `${API_BASE}${path}`;
                              return (
                                <div
                                  key={asset.id}
                                  onClick={() => {
                                    setOrgLogoUrl(assetUrl);
                                    setShowAssetPicker(false);
                                  }}
                                  style={{
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px',
                                    padding: '4px',
                                    cursor: 'pointer',
                                    backgroundColor: 'var(--input-bg)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <img src={assetUrl} alt={asset.original_name} style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '4px' }} />
                                  <span style={{ fontSize: '10px', color: 'var(--text-mute)', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', width: '100%', textAlign: 'center' }}>
                                    {asset.original_name}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}

                  {/* Company Info */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)' }}>Company / Org Name *</label>
                      <input
                        type="text"
                        required
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)' }}>Website Address</label>
                      <input
                        type="url"
                        value={orgWebsite}
                        onChange={(e) => setOrgWebsite(e.target.value)}
                        placeholder="https://example.com"
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)' }}>Bookings Portal Link</label>
                      <input
                        type="url"
                        value={orgBookings}
                        onChange={(e) => setOrgBookings(e.target.value)}
                        placeholder="https://example.com/bookings"
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)' }}>Enquiries Email Address</label>
                      <input
                        type="email"
                        value={orgEnquiriesEmail}
                        onChange={(e) => setOrgEnquiriesEmail(e.target.value)}
                        placeholder="info@example.com"
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                  </div>

                  {/* Social Media Links */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-color)', textTransform: 'uppercase' }}>Social Channels</span>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Twitter (X) URL</label>
                        <input
                          type="url"
                          value={orgTwitter}
                          onChange={(e) => setOrgTwitter(e.target.value)}
                          placeholder="https://twitter.com/..."
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            color: 'var(--input-text)',
                            fontSize: '12px'
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Facebook URL</label>
                        <input
                          type="url"
                          value={orgFacebook}
                          onChange={(e) => setOrgFacebook(e.target.value)}
                          placeholder="https://facebook.com/..."
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            color: 'var(--input-text)',
                            fontSize: '12px'
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Instagram URL</label>
                        <input
                          type="url"
                          value={orgInstagram}
                          onChange={(e) => setOrgInstagram(e.target.value)}
                          placeholder="https://instagram.com/..."
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            color: 'var(--input-text)',
                            fontSize: '12px'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Billing Details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-color)', textTransform: 'uppercase' }}>Billing Address</span>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Street Address</label>
                      <input
                        type="text"
                        value={orgBillingAddress}
                        onChange={(e) => setOrgBillingAddress(e.target.value)}
                        placeholder="123 Corporate Blvd"
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '12px'
                        }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>City</label>
                        <input
                          type="text"
                          value={orgBillingCity}
                          onChange={(e) => setOrgBillingCity(e.target.value)}
                          placeholder="San Francisco"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '8px 10px',
                            color: 'var(--input-text)',
                            fontSize: '12px'
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>State / Region</label>
                        <input
                          type="text"
                          value={orgBillingState}
                          onChange={(e) => setOrgBillingState(e.target.value)}
                          placeholder="CA"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '8px 10px',
                            color: 'var(--input-text)',
                            fontSize: '12px'
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>ZIP / Postcode</label>
                        <input
                          type="text"
                          value={orgBillingZip}
                          onChange={(e) => setOrgBillingZip(e.target.value)}
                          placeholder="94107"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '8px 10px',
                            color: 'var(--input-text)',
                            fontSize: '12px'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '300px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Country</label>
                      <input
                        type="text"
                        value={orgBillingCountry}
                        onChange={(e) => setOrgBillingCountry(e.target.value)}
                        placeholder="United States"
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '12px'
                        }}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (user.role === 'OPERATOR' && orgData) {
                          setIsEditingOrg(false);
                        } else {
                          setShowRegistrationForm(false);
                        }
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        color: 'var(--text-mute)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={orgSubmitting}
                      style={{
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: orgSubmitting ? 'not-allowed' : 'pointer',
                        opacity: orgSubmitting ? 0.6 : 1
                      }}
                    >
                      {orgSubmitting ? 'Saving...' : (orgData ? 'Save Changes' : (user.role === 'OPERATOR' ? 'Register Organisation' : 'Save & Transition to Operator'))}
                    </button>
                  </div>
                </form>
              )
            ) : (
              /* OPERATOR Card & Details View */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {orgLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-mute)' }}>
                    Loading organization profile...
                  </div>
                ) : orgData ? (
                  <>
                    <div style={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '24px',
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1.5fr 1fr',
                      gap: '24px'
                    }}>
                      {/* Left: General info */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div style={{ position: 'relative', width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'var(--input-bg)', border: '2px solid var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                            {orgData.logo_url ? (
                              <img src={orgData.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: '22px', color: 'var(--text-main)', fontWeight: 'bold' }}>
                                {orgData.name.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--primary-color)', letterSpacing: '1px' }}>
                              REGISTERED COMPANY
                            </span>
                            <h2 style={{ margin: '4px 0 0 0', fontSize: '24px', color: 'var(--text-main)' }}>
                              {orgData.name}
                            </h2>
                            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                              Active on tenant domain: {tenantConfig.domain_name}
                            </p>
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                          <div style={{ display: 'flex', gap: '8px', fontSize: '13px' }}>
                            <strong style={{ width: '120px', color: 'var(--text-mute)' }}>Website:</strong>
                            {orgData.website_address ? (
                              <a href={orgData.website_address} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>
                                {orgData.website_address}
                              </a>
                            ) : (
                              <span style={{ color: 'var(--text-mute)', fontStyle: 'italic' }}>None configured</span>
                            )}
                          </div>

                          <div style={{ display: 'flex', gap: '8px', fontSize: '13px' }}>
                            <strong style={{ width: '120px', color: 'var(--text-mute)' }}>Bookings Portal:</strong>
                            {orgData.bookings_link ? (
                              <a href={orgData.bookings_link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>
                                {orgData.bookings_link}
                              </a>
                            ) : (
                              <span style={{ color: 'var(--text-mute)', fontStyle: 'italic' }}>None configured</span>
                            )}
                          </div>

                          <div style={{ display: 'flex', gap: '8px', fontSize: '13px' }}>
                            <strong style={{ width: '120px', color: 'var(--text-mute)' }}>Enquiries Email:</strong>
                            {orgData.enquiries_email ? (
                              <a href={`mailto:${orgData.enquiries_email}`} style={{ color: 'var(--primary-color)' }}>
                                {orgData.enquiries_email}
                              </a>
                            ) : (
                              <span style={{ color: 'var(--text-mute)', fontStyle: 'italic' }}>None configured</span>
                            )}
                          </div>
                        </div>

                        {/* Social Links parser */}
                        {(() => {
                          let socials = { twitter: '', facebook: '', instagram: '' };
                          try {
                            if (orgData.social_media) socials = JSON.parse(orgData.social_media);
                          } catch (e) {}

                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-mute)', textTransform: 'uppercase' }}>Social Channels</span>
                              <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                {socials.twitter && (
                                  <a href={socials.twitter} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    🐦 Twitter/X
                                  </a>
                                )}
                                {socials.facebook && (
                                  <a href={socials.facebook} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    📘 Facebook
                                  </a>
                                )}
                                {socials.instagram && (
                                  <a href={socials.instagram} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    📷 Instagram
                                  </a>
                                )}
                                {!socials.twitter && !socials.facebook && !socials.instagram && (
                                  <span style={{ fontSize: '12px', color: 'var(--text-mute)', fontStyle: 'italic' }}>No social profiles linked.</span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setOrgName(orgData.name || '');
                          setOrgWebsite(orgData.website_address || '');
                          setOrgBookings(orgData.bookings_link || '');
                          setOrgEnquiriesEmail(orgData.enquiries_email || '');
                          
                          let socials = { twitter: '', facebook: '', instagram: '' };
                          try {
                            if (orgData.social_media) socials = JSON.parse(orgData.social_media);
                          } catch (e) {}
                          setOrgTwitter(socials.twitter || '');
                          setOrgFacebook(socials.facebook || '');
                          setOrgInstagram(socials.instagram || '');
                          
                          setOrgBillingAddress(orgData.billing_address || '');
                          setOrgBillingCity(orgData.billing_city || '');
                          setOrgBillingState(orgData.billing_state || '');
                          setOrgBillingZip(orgData.billing_zip || '');
                          setOrgBillingCountry(orgData.billing_country || '');
                          
                          setIsEditingOrg(true);
                          setOrgSuccess('');
                          setOrgError('');
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          color: 'var(--primary-color)',
                          border: '1px solid var(--primary-color)',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          marginTop: '10px',
                          alignSelf: 'flex-start'
                        }}
                      >
                        Edit Organisation Details
                      </button>

                      {/* Right: Billing details */}
                      <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.01)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        <strong style={{ fontSize: '13px', color: 'var(--text-main)', borderBottom: '1px solid var(--border-color)', paddingBottom: '6px' }}>
                          Corporate Billing Address
                        </strong>
                        <div style={{ fontSize: '13px', color: 'var(--text-mute)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <span>{orgData.billing_address || 'No street address'}</span>
                          <span>
                            {orgData.billing_city || ''}
                            {orgData.billing_state ? `, ${orgData.billing_state}` : ''} 
                            {orgData.billing_zip ? ` ${orgData.billing_zip}` : ''}
                          </span>
                          <span>{orgData.billing_country || ''}</span>
                        </div>
                      </div>
                    </div>

                    {/* Dedicated Agent Partner Card */}
                    <div style={{
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginTop: '20px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)' }}>
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            Dedicated Agent Partner
                          </h4>
                          <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                            Your designated territory agent providing direct onboarding, service, and campaign support.
                          </span>
                        </div>
                        {orgData.active_agent && orgData.contract_info && (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => openAgentChat(orgData.contract_info.contract_id)}
                              style={{
                                backgroundColor: 'var(--primary-color)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 14px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                              Contact Agent
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setAgentRatingScore(5);
                                setAgentRatingFeedback('');
                                setAgentRatingSuccess('');
                                setShowAgentRatingModal(true);
                              }}
                              style={{
                                backgroundColor: 'transparent',
                                color: '#f59e0b',
                                border: '1px solid #f59e0b',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                              </svg>
                              Rate Service Quality (QoS)
                            </button>
                          </div>
                        )}
                      </div>

                      {orgData.active_agent ? (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
                          gap: '16px'
                        }}>
                          <div style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            padding: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                          }}>
                            <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-mute)', fontWeight: '700' }}>
                              Agent Representative
                            </span>
                            <strong style={{ fontSize: '15px', color: 'var(--text-main)' }}>
                              {orgData.active_agent.name}
                            </strong>
                            <span style={{ fontSize: '12px', color: 'var(--primary-color)' }}>
                              {orgData.active_agent.email}
                            </span>
                            {orgData.active_agent.phone && (
                              <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                                Tel: {orgData.active_agent.phone}
                              </span>
                            )}
                          </div>

                          <div style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            padding: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                          }}>
                            <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-mute)', fontWeight: '700' }}>
                              Contract Status
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                              <span style={{
                                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                color: '#10b981',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                borderRadius: '12px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: '700'
                              }}>
                                {orgData.contract_info?.status || 'ACTIVE'}
                              </span>
                              <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                                {orgData.contract_info?.duration_months ? `${orgData.contract_info.duration_months} Months Term` : 'Active'}
                              </span>
                            </div>
                            {orgData.contract_info?.end_date && (
                              <span style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>
                                Valid until: {new Date(orgData.contract_info.end_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          <div style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            padding: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                          }}>
                            <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-mute)', fontWeight: '700' }}>
                              Service Level Agreement (SLA)
                            </span>
                            <span style={{ fontSize: '13px', color: 'var(--text-main)', fontWeight: '600' }}>
                              Monitored by Tenant Administration
                            </span>
                            <span style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                              Feedback and response times are tracked. You can rate your agent's service anytime.
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          border: '1px dashed var(--border-color)',
                          borderRadius: '8px',
                          padding: '16px',
                          textAlign: 'center',
                          color: 'var(--text-mute)',
                          fontSize: '13px'
                        }}>
                          <p style={{ margin: 0, fontWeight: '600', color: 'var(--text-main)' }}>
                            Direct Tenant Support
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                            No independent agent partner is currently assigned to your organization. The tenant operations team manages your account directly.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Delivery Addresses Management */}
                    <div style={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginTop: '20px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                        <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)' }}>
                          Delivery Addresses
                        </h4>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddAddressForm(!showAddAddressForm);
                            setAddrError('');
                            setAddrSuccess('');
                          }}
                          style={{
                            backgroundColor: 'transparent',
                            color: 'var(--primary-color)',
                            border: '1px solid var(--primary-color)',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {showAddAddressForm ? 'Cancel' : '+ Add Address'}
                        </button>
                      </div>

                      {addrError && (
                        <div style={{
                          backgroundColor: 'rgba(248, 81, 73, 0.15)',
                          border: '1px solid rgba(248, 81, 73, 0.4)',
                          borderRadius: '6px',
                          color: '#ff7b72',
                          padding: '10px',
                          fontSize: '12px',
                          marginBottom: '12px'
                        }}>
                          {typeof addrError === 'string' ? addrError : JSON.stringify(addrError)}
                        </div>
                      )}

                      {addrSuccess && (
                        <div style={{
                          backgroundColor: 'rgba(56, 139, 253, 0.15)',
                          border: '1px solid rgba(56, 139, 253, 0.4)',
                          borderRadius: '6px',
                          color: '#58a6ff',
                          padding: '10px',
                          fontSize: '12px',
                          marginBottom: '12px'
                        }}>
                          {addrSuccess}
                        </div>
                      )}

                      {showAddAddressForm && (
                        <form onSubmit={handleAddDeliveryAddress} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px', padding: '16px', backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main)' }}>New Delivery Address</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Smart Fill-in & Courier Ready</span>
                          </div>

                          {/* Smart Autocomplete Search */}
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--primary-color)', display: 'block', marginBottom: '4px' }}>
                              ⚡ Smart Fill-in (Type to search global addresses)
                            </label>
                            <AddressAutocompleteInput
                              apiBase={API_BASE}
                              placeholder="Start typing delivery address (e.g. 120 Albert St Auckland)..."
                              countryFilter={addrCountryCode}
                              onSelectAddress={(parsed) => {
                                setAddrStreet(parsed.street || '');
                                if (parsed.street2) setAddrStreet2(parsed.street2);
                                setAddrCity(parsed.city || '');
                                setAddrState(parsed.state || '');
                                setAddrZip(parsed.zip || '');
                                setAddrCountry(parsed.country || '');
                                if (parsed.country_code) setAddrCountryCode(parsed.country_code);
                                if (parsed.latitude) setAddrLat(parsed.latitude);
                                if (parsed.longitude) setAddrLng(parsed.longitude);
                              }}
                            />
                          </div>

                          {/* Label, Recipient & Phone */}
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1.2fr 1fr', gap: '10px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Address Label</label>
                              <input
                                type="text"
                                value={addrLabel}
                                onChange={(e) => setAddrLabel(e.target.value)}
                                placeholder="e.g. Main Warehouse, Downtown Office"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Recipient / Attention</label>
                              <input
                                type="text"
                                value={addrRecipient}
                                onChange={(e) => setAddrRecipient(e.target.value)}
                                placeholder="e.g. Daniel Barclay"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Phone (Courier Contact)</label>
                              <input
                                type="text"
                                value={addrPhone}
                                onChange={(e) => setAddrPhone(e.target.value)}
                                placeholder="+27 82 123 4567"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                          </div>
                          
                          {/* Street 1 and Street 2 */}
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '10px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Street Address *</label>
                              <input
                                type="text"
                                required
                                value={addrStreet}
                                onChange={(e) => setAddrStreet(e.target.value)}
                                placeholder="123 Main St"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Unit / Suite / Floor</label>
                              <input
                                type="text"
                                value={addrStreet2}
                                onChange={(e) => setAddrStreet2(e.target.value)}
                                placeholder="Suite 4B"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                          </div>

                          {/* City, State, ZIP */}
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '10px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>City *</label>
                              <input
                                type="text"
                                required
                                value={addrCity}
                                onChange={(e) => setAddrCity(e.target.value)}
                                placeholder="Cape Town"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>State / Region</label>
                              <input
                                type="text"
                                value={addrState}
                                onChange={(e) => setAddrState(e.target.value)}
                                placeholder="Western Cape"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>ZIP / Postcode *</label>
                              <input
                                type="text"
                                required
                                value={addrZip}
                                onChange={(e) => setAddrZip(e.target.value)}
                                placeholder="8001"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                          </div>

                          {/* Country & Country Code & Instructions */}
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '10px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Country *</label>
                              <input
                                type="text"
                                required
                                value={addrCountry}
                                onChange={(e) => setAddrCountry(e.target.value)}
                                placeholder="South Africa"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>ISO Code (e.g. ZA, US, NZ)</label>
                              <input
                                type="text"
                                maxLength={2}
                                value={addrCountryCode}
                                onChange={(e) => setAddrCountryCode(e.target.value.toUpperCase())}
                                placeholder="ZA"
                                style={{
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  color: 'var(--input-text)',
                                  fontSize: '12px',
                                  textTransform: 'uppercase'
                                }}
                              />
                            </div>
                          </div>

                          {/* Delivery Instructions */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Courier Notes / Delivery Instructions</label>
                            <input
                              type="text"
                              value={addrInstructions}
                              onChange={(e) => setAddrInstructions(e.target.value)}
                              placeholder="e.g. Gate code #4120, deliver to Reception on 2nd floor"
                              style={{
                                backgroundColor: 'var(--input-bg)',
                                border: '1px solid var(--input-border)',
                                borderRadius: '6px',
                                padding: '8px 10px',
                                color: 'var(--input-text)',
                                fontSize: '12px'
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
                            <button
                              type="button"
                              onClick={() => setShowAddAddressForm(false)}
                              style={{
                                backgroundColor: 'transparent',
                                color: 'var(--text-mute)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                padding: '8px 14px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={addrSubmitting}
                              style={{
                                backgroundColor: 'var(--primary-color)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '8px 18px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: addrSubmitting ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {addrSubmitting ? 'Adding...' : 'Add Delivery Address'}
                            </button>
                          </div>
                        </form>
                      )}

                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1.5px solid var(--border-color)', color: 'var(--text-mute)', textAlign: 'left' }}>
                              <th style={{ padding: '8px' }}>Delivery Address</th>
                              <th style={{ padding: '8px', textAlign: 'center' }}>Default</th>
                              <th style={{ padding: '8px', textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orgAddresses.length === 0 ? (
                              <tr>
                                <td colSpan="3" style={{ padding: '16px', color: 'var(--text-mute)', textAlign: 'center', fontStyle: 'italic' }}>
                                  No delivery addresses registered.
                                </td>
                              </tr>
                            ) : (
                              orgAddresses.map((addr) => (
                                <tr key={addr.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                  <td style={{ padding: '8px', color: 'var(--text-main)', lineHeight: '1.4' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '2px' }}>
                                      <span>{addr.label || 'Delivery Address'}</span>
                                      {addr.country_code && (
                                        <span style={{
                                          fontSize: '10px',
                                          fontWeight: '700',
                                          padding: '1px 5px',
                                          borderRadius: '3px',
                                          backgroundColor: 'rgba(99, 102, 241, 0.15)',
                                          color: '#818cf8',
                                          border: '1px solid rgba(99, 102, 241, 0.3)'
                                        }}>
                                          {addr.country_code.toUpperCase()}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '13px' }}>
                                      {addr.street}{addr.street2 ? `, ${addr.street2}` : ''}
                                    </div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                      {addr.city}{addr.state ? `, ${addr.state}` : ''} {addr.zip}, {addr.country}
                                    </div>
                                    {(addr.recipient_name || addr.phone) && (
                                      <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                                        Attn: {addr.recipient_name || 'N/A'}{addr.phone ? ` • Tel: ${addr.phone}` : ''}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: '8px', textAlign: 'center' }}>
                                    {addr.is_default ? (
                                      <span style={{
                                        backgroundColor: 'rgba(46, 160, 67, 0.15)',
                                        color: '#3fb950',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '600'
                                      }}>
                                        Default
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleSetDefaultAddress(addr.id)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: 'var(--primary-color)',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          padding: 0,
                                          textDecoration: 'underline'
                                        }}
                                      >
                                        Set Default
                                      </button>
                                    )}
                                  </td>
                                  <td style={{ padding: '8px', textAlign: 'right' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteDeliveryAddress(addr.id)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#ff7b72',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Authorized Operators List */}
                    <div style={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '20px',
                      marginTop: '20px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)' }}>
                            Authorized Operators & Team Members
                          </h4>
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontWeight: 'normal' }}>
                            {orgOperators.length} Active Operators in {orgData?.name || 'this Organization'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setOrgInviteEmail('');
                            setOrgInviteName('');
                            setOrgInviteRole('OPERATOR');
                            setOrgInviteError('');
                            setOrgInviteSuccess('');
                            setOrgInviteCreatedLink('');
                            setShowOrgInviteModal(true);
                          }}
                          style={{
                            backgroundColor: 'var(--primary-color)',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '7px 14px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>➕</span>
                          <span>Invite Team Member</span>
                        </button>
                      </div>

                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1.5px solid var(--border-color)', color: 'var(--text-mute)', textAlign: 'left' }}>
                              <th style={{ padding: '8px' }}>Name</th>
                              <th style={{ padding: '8px' }}>Email Address</th>
                              <th style={{ padding: '8px' }}>Role clearance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orgOperators.map((op) => (
                              <tr key={op.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <td style={{ padding: '8px', color: 'var(--text-main)' }}>
                                  {op.first_name || ''} {op.last_name || ''}
                                </td>
                                <td style={{ padding: '8px', color: 'var(--text-mute)' }}>{op.email}</td>
                                <td style={{ padding: '8px' }}>
                                  <span style={{
                                    backgroundColor: 'rgba(56, 139, 253, 0.15)',
                                    color: 'var(--primary-color)',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: '600'
                                  }}>
                                    {op.role}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pending Team Invitations Section */}
                      <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h5 style={{ margin: 0, fontSize: '13px', color: 'var(--text-main)', fontWeight: '700' }}>
                            Pending & Sent Invitations
                          </h5>
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                            {orgInvitations.filter(inv => inv.status === 'PENDING').length} Pending
                          </span>
                        </div>

                        {orgInvitations.length === 0 ? (
                          <div style={{ fontSize: '12px', color: 'var(--text-mute)', padding: '12px 0', textAlign: 'center' }}>
                            No pending invitations. Click "Invite Team Member" above to add colleagues to your organization.
                          </div>
                        ) : (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-mute)', textAlign: 'left' }}>
                                  <th style={{ padding: '8px' }}>Recipient</th>
                                  <th style={{ padding: '8px' }}>Role Clearance</th>
                                  <th style={{ padding: '8px' }}>Sent Date / Expiry</th>
                                  <th style={{ padding: '8px' }}>Status</th>
                                  <th style={{ padding: '8px', textAlign: 'right' }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orgInvitations.map((inv) => (
                                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                    <td style={{ padding: '8px' }}>
                                      <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>
                                        {inv.recipient_name || inv.recipient_email}
                                      </div>
                                      {inv.recipient_name && (
                                        <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                                          {inv.recipient_email}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <span style={{
                                        backgroundColor: 'rgba(56, 139, 253, 0.12)',
                                        color: 'var(--primary-color)',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        fontSize: '11px',
                                        fontWeight: '600'
                                      }}>
                                        {inv.role}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px', color: 'var(--text-mute)', fontSize: '11px' }}>
                                      <div>Sent: {new Date(inv.created_at).toLocaleDateString()}</div>
                                      <div>Expires: {new Date(inv.expires_at).toLocaleDateString()}</div>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                      <span style={{
                                        backgroundColor: inv.status === 'PENDING' ? 'rgba(234, 179, 8, 0.15)' :
                                          inv.status === 'ACCEPTED' ? 'rgba(46, 160, 67, 0.15)' : 'rgba(248, 81, 73, 0.15)',
                                        color: inv.status === 'PENDING' ? '#eab308' :
                                          inv.status === 'ACCEPTED' ? '#3fb950' : '#ff7b72',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '600'
                                      }}>
                                        {inv.status}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>
                                      {inv.status === 'PENDING' && (
                                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const origin = window.location.origin;
                                              const inviteUrl = `${origin}/register?invite=${inv.invite_token}`;
                                              navigator.clipboard.writeText(inviteUrl);
                                              modal.notify('Invite URL copied to clipboard!', 'info');
                                            }}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              color: 'var(--primary-color)',
                                              cursor: 'pointer',
                                              fontSize: '11px',
                                              textDecoration: 'underline'
                                            }}
                                          >
                                            Copy Link
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleRevokeOrgInvitation(inv.id)}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              color: '#ff7b72',
                                              cursor: 'pointer',
                                              fontSize: '11px',
                                              textDecoration: 'underline'
                                            }}
                                          >
                                            Revoke
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-mute)' }}>
                    Failed to fetch organization data. Please reload page.
                  </div>
                )}

                {/* MODAL: Invite Team Member to Organization */}
                {showOrgInviteModal && orgData && createPortal(
                  <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
                    padding: '20px'
                  }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowOrgInviteModal(false);
                    }
                  }}
                  >
                    <div style={{
                      backgroundColor: 'var(--card-bg, var(--panel-bg))',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      width: '100%',
                      maxWidth: '480px',
                      padding: '24px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '20px' }}>✉️</span>
                          <h3 style={{ margin: 0, fontSize: '17px', color: 'var(--primary-color)' }}>
                            Invite Team Member
                          </h3>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowOrgInviteModal(false)}
                          style={{ background: 'none', border: 'none', color: 'var(--text-mute)', fontSize: '20px', cursor: 'pointer' }}
                        >
                          &times;
                        </button>
                      </div>

                      <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                        Invite a colleague to join <strong>{orgData.name}</strong>. An email will be dispatched with a registration link. Upon signing up, they will automatically gain shared access to your organization's orders, card templates, coins, assets, and vaulted items.
                      </p>

                      {orgInviteError && (
                        <div style={{
                          backgroundColor: 'rgba(248, 81, 73, 0.15)',
                          border: '1px solid rgba(248, 81, 73, 0.4)',
                          borderRadius: '6px',
                          color: '#ff7b72',
                          padding: '10px 12px',
                          fontSize: '12px'
                        }}>
                          {orgInviteError}
                        </div>
                      )}

                      {orgInviteSuccess && (
                        <div style={{
                          backgroundColor: 'rgba(46, 160, 67, 0.15)',
                          border: '1px solid rgba(46, 160, 67, 0.4)',
                          borderRadius: '6px',
                          color: '#3fb950',
                          padding: '10px 12px',
                          fontSize: '12px'
                        }}>
                          <div>{orgInviteSuccess}</div>
                          {orgInviteCreatedLink && (
                            <div style={{ marginTop: '8px' }}>
                              <input
                                type="text"
                                readOnly
                                value={orgInviteCreatedLink}
                                onClick={(e) => e.target.select()}
                                style={{
                                  width: '100%',
                                  backgroundColor: 'var(--input-bg)',
                                  border: '1px solid var(--input-border)',
                                  borderRadius: '4px',
                                  padding: '6px 8px',
                                  fontSize: '11px',
                                  color: 'var(--input-text)',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <form onSubmit={handleSendOrgInvitation} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                            Recipient Email Address *
                          </label>
                          <input
                            type="email"
                            required
                            value={orgInviteEmail}
                            onChange={(e) => setOrgInviteEmail(e.target.value)}
                            placeholder="colleague@example.com"
                            style={{
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              padding: '9px 12px',
                              color: 'var(--input-text)',
                              fontSize: '13px'
                            }}
                          />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                            Recipient Full Name (Optional)
                          </label>
                          <input
                            type="text"
                            value={orgInviteName}
                            onChange={(e) => setOrgInviteName(e.target.value)}
                            placeholder="Jane Doe"
                            style={{
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              padding: '9px 12px',
                              color: 'var(--input-text)',
                              fontSize: '13px'
                            }}
                          />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                            Assigned Role Clearance
                          </label>
                          <select
                            value={orgInviteRole}
                            onChange={(e) => setOrgInviteRole(e.target.value)}
                            style={{
                              backgroundColor: 'var(--input-bg)',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              padding: '9px 12px',
                              color: 'var(--input-text)',
                              fontSize: '13px'
                            }}
                          >
                            <option value="OPERATOR">Operator (Full Store & Production Workspace Access)</option>
                            <option value="AGENT">Agent (Sales & Partner Referral Access)</option>
                          </select>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                          <button
                            type="button"
                            onClick={() => setShowOrgInviteModal(false)}
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              fontSize: '13px',
                              color: 'var(--text-mute)',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={orgInviteSubmitting}
                            style={{
                              backgroundColor: 'var(--primary-color)',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 18px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: orgInviteSubmitting ? 'not-allowed' : 'pointer',
                              opacity: orgInviteSubmitting ? 0.7 : 1
                            }}
                          >
                            {orgInviteSubmitting ? 'Sending Invitation...' : 'Send Invitation'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            )}
            {/* MODAL: Direct Chat with Assigned Agent */}
            {showAgentChatModal && orgData?.contract_info && createPortal(
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
              }}>
                <div style={{
                  backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)',
                  borderRadius: '12px', width: '90%', maxWidth: '520px', height: '560px',
                  display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
                }}>
                  {/* Chat Header */}
                  <div style={{
                    padding: '16px', borderBottom: '1px solid var(--border-color)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)' }}>
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        Support: {orgData.active_agent?.name || 'Agent Partner'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '2px' }}>
                        {orgData.active_agent?.email} &bull; Contract #{orgData.contract_info.contract_id}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAgentChatModal(false)}
                      style={{
                        background: 'none', border: 'none', color: 'var(--text-mute)',
                        fontSize: '18px', cursor: 'pointer', padding: '4px 8px'
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Chat Messages Timeline */}
                  <div style={{
                    flex: 1, padding: '16px', overflowY: 'auto',
                    display: 'flex', flexDirection: 'column', gap: '12px'
                  }}>
                    {agentChatLoading ? (
                      <div style={{ textAlign: 'center', color: 'var(--text-mute)', margin: 'auto' }}>Loading messages...</div>
                    ) : agentChatMessages.length === 0 ? (
                      <div style={{ textAlign: 'center', color: 'var(--text-mute)', margin: 'auto', fontSize: '13px', lineHeight: '1.6' }}>
                        No direct messages yet.<br />
                        Send an inquiry or request assistance from your assigned agent.
                      </div>
                    ) : (
                      agentChatMessages.map(m => {
                        const isMe = m.sender_id === user?.id;
                        return (
                          <div
                            key={m.id}
                            style={{
                              alignSelf: isMe ? 'flex-end' : 'flex-start',
                              maxWidth: '80%',
                              backgroundColor: isMe ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.06)',
                              color: isMe ? '#ffffff' : 'var(--text-main)',
                              padding: '10px 14px',
                              borderRadius: '10px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>
                              {isMe ? 'You' : (orgData.active_agent?.name || 'Agent')} &bull; {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                              {m.message_text}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Chat Input Bar */}
                  <form onSubmit={(e) => handleSendAgentChatMessage(e, orgData.contract_info.contract_id)} style={{
                    padding: '12px 16px', borderTop: '1px solid var(--border-color)',
                    display: 'flex', gap: '8px', backgroundColor: 'var(--panel-bg)'
                  }}>
                    <input
                      type="text"
                      value={agentChatNewMsg}
                      onChange={(e) => setAgentChatNewMsg(e.target.value)}
                      placeholder="Type a message to your agent..."
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: '6px',
                        backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                        border: '1px solid var(--border-color)', color: 'var(--text-main)',
                        fontSize: '13px'
                      }}
                    />
                    <button
                      type="submit"
                      style={{
                        padding: '10px 18px', borderRadius: '6px',
                        backgroundColor: 'var(--primary-color)', border: 'none',
                        color: '#ffffff', fontWeight: '700', cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Send
                    </button>
                  </form>
                </div>
              </div>,
              document.body
            )}

            {/* MODAL: Rate Agent Service Quality (QoS) */}
            {showAgentRatingModal && orgData?.contract_info && createPortal(
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
              }}>
                <div style={{
                  backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)',
                  borderRadius: '12px', width: '90%', maxWidth: '480px',
                  display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.4)', gap: '18px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#f59e0b' }}>★</span> Rate Service Quality (QoS)
                      </h3>
                      <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                        Evaluating agent {orgData.active_agent?.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAgentRatingModal(false)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-mute)', fontSize: '18px', cursor: 'pointer' }}
                    >
                      ✕
                    </button>
                  </div>

                  {agentRatingSuccess ? (
                    <div style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.15)',
                      border: '1px solid rgba(16, 185, 129, 0.4)',
                      borderRadius: '8px', padding: '16px', color: '#10b981',
                      textAlign: 'center', fontSize: '14px', fontWeight: '600'
                    }}>
                      ✓ {agentRatingSuccess}
                    </div>
                  ) : (
                    <form onSubmit={(e) => handleRateAgentQoS(e, orgData.contract_info.contract_id)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)', display: 'block', marginBottom: '8px' }}>
                          Quality Rating (1 to 5 Stars)
                        </label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {[1, 2, 3, 4, 5].map(star => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setAgentRatingScore(star)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: '28px', color: star <= agentRatingScore ? '#f59e0b' : 'var(--text-mute)',
                                padding: '2px', transition: 'transform 0.1s'
                              }}
                              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              ★
                            </button>
                          ))}
                          <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)', marginLeft: '6px' }}>
                            {agentRatingScore === 5 ? '5 - Outstanding' :
                             agentRatingScore === 4 ? '4 - Very Good' :
                             agentRatingScore === 3 ? '3 - Satisfactory' :
                             agentRatingScore === 2 ? '2 - Below Average' : '1 - Poor'}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)', display: 'block', marginBottom: '6px' }}>
                          Feedback & Service Notes (Optional)
                        </label>
                        <textarea
                          rows={3}
                          value={agentRatingFeedback}
                          onChange={e => setAgentRatingFeedback(e.target.value)}
                          placeholder="Describe responsiveness, problem resolution, and communication quality..."
                          style={{
                            width: '100%', padding: '10px 12px', borderRadius: '6px',
                            backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                            border: '1px solid var(--border-color)', color: 'var(--text-main)',
                            fontSize: '13px', boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                        Ratings directly inform tenant service level monitoring. Agents that fall below minimum quality thresholds are subject to administrative review or contract voiding.
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                        <button
                          type="button"
                          onClick={() => setShowAgentRatingModal(false)}
                          style={{
                            backgroundColor: 'transparent', border: '1px solid var(--border-color)',
                            color: 'var(--text-main)', padding: '8px 16px', borderRadius: '6px',
                            fontSize: '13px', cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={agentRatingSubmitting}
                          style={{
                            backgroundColor: 'var(--primary-color)', border: 'none',
                            color: '#ffffff', padding: '8px 18px', borderRadius: '6px',
                            fontSize: '13px', fontWeight: '700', cursor: 'pointer'
                          }}
                        >
                          {agentRatingSubmitting ? 'Submitting...' : 'Submit Evaluation'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>,
              document.body
            )}
          </div>
        )}

        {/* Tab Content: Data Manager */}
        {activeTab === 'data_manager' && (isGlobalRole || user.role === 'TENANT_ADMIN') && (
          <DataManager user={user} />
        )}

        {/* Tab Content: Design Studio */}
        {activeTab === 'design' && (isGlobalRole || user.role === 'TENANT_ADMIN') && (
          <DesignStudio 
            user={user} 
            tenantConfig={tenantConfig} 
            initialTab={designStudioInitialTab || 'coin'} 
            initialCoinId={designStudioInitialCoinId}
            initialProofingDesignId={designStudioInitialDesignId}
          />
        )}

        {/* Tab Content: Trophy Cards (For normal users) */}
        {activeTab === 'brag' && (user.role === 'END_USER' || user.role === 'OPERATOR' || user.role === 'AGENT') && (
          <DesignStudio 
            user={user} 
            tenantConfig={tenantConfig} 
            initialTab="brag" 
            hideTabHeaders={true} 
            initialProofingDesignId={designStudioInitialDesignId}
          />
        )}

        {/* Tab Content: Coins (Operator only sidebar tab) */}
        {activeTab === 'operator_coins' && (user.role === 'OPERATOR' || user.role === 'AGENT') && (
          <DesignStudio 
            user={user} 
            tenantConfig={tenantConfig} 
            initialTab="coin" 
            hideTabHeaders={true} 
            initialCoinId={designStudioInitialCoinId}
          />
        )}

        {/* Tab Content: Assets / Media (Operator only) */}
        {activeTab === 'assets' && (user.role === 'OPERATOR' || user.role === 'AGENT') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                Assets & Media Files
              </h3>
              <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: 'var(--text-mute)' }}>
                Upload, preview, share and manage custom coin faces, logos, backgrounds and brag page media.
              </p>
            </div>
            
            <div style={{
              background: 'var(--panel-color)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
            }}>
              <AssetManager
                user={user}
                tenantConfig={tenantConfig}
              />
            </div>
          </div>
        )}

        {/* Tab Content: Company Manager */}
        {activeTab === 'companies' && (isAdminRole || isGlobalRole || user.role === 'TENANT_ADMIN') && (
          <CompanyManager
            user={user}
            tenantConfig={tenantConfig}
            isGlobalRole={isGlobalRole}
            isAdminRole={isAdminRole}
            tenants={tenants}
            selectedTenantId={selectedTenantId}
            setSelectedTenantId={setSelectedTenantId}
          />
        )}

        {/* Tab Content: Agent Network Manager */}
        {activeTab === 'agent_network' && (isAdminRole || isGlobalRole || user.role === 'TENANT_ADMIN') && (
          <AgentNetworkManager
            user={user}
            tenantConfig={tenantConfig}
            isGlobalRole={isGlobalRole}
            isAdminRole={isAdminRole}
            tenants={tenants}
            selectedTenantId={selectedTenantId}
            apiBase={API_BASE}
          />
        )}

        {/* Tab Content: 2. User Manager */}
        {activeTab === 'users' && isAdminRole && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '700',
                color: 'var(--text-main)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)', flexShrink: 0 }}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>{isGlobalRole ? 'All Global & Tenant Users' : `Users under ${tenantConfig.brand_name}`}</span>
              </h3>
              <button
                onClick={() => {
                  setFormError('');
                  setFormSuccess('');
                  if (selectedTenantFilter && selectedTenantFilter !== 'ALL' && selectedTenantFilter !== 'GLOBAL') {
                    setNewTenantId(selectedTenantFilter);
                  } else if (currentUserProfile?.tenant_id) {
                    setNewTenantId(currentUserProfile.tenant_id.toString());
                  } else {
                    setNewTenantId('');
                  }
                  setShowAddForm(true);
                }}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                + Add User
              </button>
            </div>

            {/* Dynamic Search and Role Filters */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: '16px',
              alignItems: isMobile ? 'stretch' : 'center',
              background: 'var(--card-bg, var(--panel-bg))',
              padding: '14px 16px',
              borderRadius: '10px',
              border: '1px solid var(--border-color)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: isMobile ? '1 1 auto' : '0 0 auto'
              }}>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: 'var(--text-mute)', whiteSpace: 'nowrap' }}>Search User Accounts</label>
                <input
                  type="text"
                  placeholder="Filter by name or email address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: 'var(--input-text)',
                    fontSize: '13px',
                    height: '38px',
                    width: isMobile ? '100%' : '240px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
                flex: isMobile ? '1 1 auto' : '0 1 auto'
              }}>
                <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: 'var(--text-mute)', whiteSpace: 'nowrap' }}>Filter by Role</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', minHeight: '38px', alignItems: 'center' }}>
                  {(isGlobalRole 
                    ? ['ALL', 'TENANT_ADMIN', 'OPERATOR', 'AGENT', 'END_USER']
                    : ['ALL', 'OPERATOR', 'AGENT', 'END_USER']
                  ).map(f => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setRoleFilter(f)}
                      style={{
                        backgroundColor: roleFilter === f ? 'var(--primary-color)' : 'transparent',
                        color: roleFilter === f ? 'white' : 'var(--text-main)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        height: '38px',
                        boxSizing: 'border-box',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s'
                      }}
                    >
                      {f === 'ALL' ? 'All' : f.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {isGlobalRole && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flex: isMobile ? '1 1 auto' : '0 0 auto'
                }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: 'var(--text-mute)', whiteSpace: 'nowrap' }}>Filter by Tenant</label>
                  <select
                    value={selectedTenantFilter}
                    onChange={(e) => setSelectedTenantFilter(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: 'var(--input-text)',
                      fontSize: '13px',
                      height: '38px',
                      boxSizing: 'border-box',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="ALL">All Tenants</option>
                    <option value="GLOBAL">Global / Unscoped Users</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id.toString()}>{t.brand_name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {error && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.4)',
                borderRadius: '6px',
                color: '#ff7b72',
                padding: '12px',
                fontSize: '13px'
              }}>
                <strong>Error:</strong> {typeof error === 'string' ? error : JSON.stringify(error)}
              </div>
            )}

            {/* Create User Modal Dialog */}
            {showAddForm && createPortal(
              <div
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowAddForm(false);
                    setFormError('');
                    setFormSuccess('');
                  }
                }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100vw',
                  height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(5px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 99999,
                  padding: '20px',
                  boxSizing: 'border-box'
                }}
              >
                <div
                  style={{
                    background: 'var(--card-bg, var(--panel-bg))',
                    border: '1px solid var(--border-color)',
                    borderRadius: '14px',
                    maxWidth: '520px',
                    width: '100%',
                    padding: '24px',
                    boxShadow: '0 20px 35px rgba(0, 0, 0, 0.35)',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}
                >
                  {/* Modal Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid var(--border-color)',
                    paddingBottom: '14px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <line x1="20" y1="8" x2="20" y2="14" />
                        <line x1="23" y1="11" x2="17" y2="11" />
                      </svg>
                      <h3 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '18px', fontWeight: '700' }}>
                        Register New User Account
                      </h3>
                    </div>
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setFormError('');
                        setFormSuccess('');
                      }}
                      aria-label="Close modal"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-mute)',
                        fontSize: '22px',
                        cursor: 'pointer',
                        padding: '0 4px',
                        lineHeight: '1'
                      }}
                    >
                      &times;
                    </button>
                  </div>

                  <p style={{ color: 'var(--text-mute)', fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
                    Create a new user account with initial role permissions and a temporary password.
                  </p>

                  {formError && (
                    <div style={{
                      backgroundColor: 'rgba(248, 81, 73, 0.1)',
                      border: '1px solid rgba(248, 81, 73, 0.4)',
                      borderRadius: '6px',
                      color: '#ff7b72',
                      padding: '10px 12px',
                      fontSize: '13px'
                    }}>
                      <strong>Error:</strong> {formError}
                    </div>
                  )}

                  {formSuccess && (
                    <div style={{
                      backgroundColor: 'rgba(63, 185, 80, 0.1)',
                      border: '1px solid rgba(63, 185, 80, 0.4)',
                      borderRadius: '6px',
                      color: '#3fb950',
                      padding: '10px 12px',
                      fontSize: '13px'
                    }}>
                      {formSuccess}
                    </div>
                  )}

                  <form onSubmit={handleCreateUser} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: '14px'
                    }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>First Name</label>
                        <input
                          type="text"
                          value={newFirstName}
                          onChange={(e) => setNewFirstName(e.target.value)}
                          required
                          placeholder="e.g. John"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '9px 12px',
                            color: 'var(--input-text)',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Last Name</label>
                        <input
                          type="text"
                          value={newLastName}
                          onChange={(e) => setNewLastName(e.target.value)}
                          required
                          placeholder="e.g. Doe"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '9px 12px',
                            color: 'var(--input-text)',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Email Address</label>
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        required
                        placeholder="john.doe@example.com"
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '9px 12px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: '14px'
                    }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Temporary Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          minLength={8}
                          placeholder="Min. 8 characters"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '9px 12px',
                            color: 'var(--input-text)',
                            fontSize: '13px'
                          }}
                        />
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Account Role</label>
                        <select
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value)}
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '9px 12px',
                            color: 'var(--input-text)',
                            fontSize: '13px'
                          }}
                        >
                          {isGlobalRole ? (
                            <>
                              <option value="GLOBAL_ADMIN">Global Admin</option>
                              <option value="GLOBAL_POWER">Global Power User</option>
                              <option value="GLOBAL_STANDARD">Global Standard User</option>
                              <option value="TENANT_ADMIN">Tenant Admin</option>
                              <option value="OPERATOR">Tenant Operator</option>
                              <option value="AGENT">Tenant Agent</option>
                              <option value="END_USER">End User</option>
                            </>
                          ) : (
                            <>
                              <option value="TENANT_ADMIN">Tenant Admin</option>
                              <option value="OPERATOR">Tenant Operator</option>
                              <option value="AGENT">Tenant Agent</option>
                              <option value="END_USER">End User</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>

                    {isGlobalRole && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Tenant Assignment</label>
                        <select
                          value={newTenantId}
                          onChange={(e) => setNewTenantId(e.target.value)}
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '9px 12px',
                            color: 'var(--input-text)',
                            fontSize: '13px'
                          }}
                        >
                          <option value="GLOBAL">Global / Unscoped (Cross-Tenant Access)</option>
                          {tenants.map(t => (
                            <option key={t.id} value={t.id.toString()}>{t.brand_name} ({t.domain_name})</option>
                          ))}
                        </select>
                        <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                          Assign this user to a specific tenant brand (required for Operators and Tenant Admins).
                        </span>
                      </div>
                    )}

                    {/* Modal Footer Actions */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: '10px',
                      marginTop: '8px',
                      borderTop: '1px solid var(--border-color)',
                      paddingTop: '16px'
                    }}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddForm(false);
                          setFormError('');
                          setFormSuccess('');
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          color: 'var(--text-mute)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '9px 16px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '9px 18px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Create User Account
                      </button>
                    </div>
                  </form>
                </div>
              </div>,
              document.body
            )}

            {/* User Manager Data Grid Container */}
            <div style={{
              background: 'var(--card-bg, var(--panel-bg))',
              border: '1px solid var(--border-color)',
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
            }}>
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-mute)', fontSize: '13px' }}>
                  Fetching user database records...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-mute)' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.7 }}>👥</div>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>No Users Found</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '4px' }}>
                    No database records matched your search query or filters.
                  </div>
                </div>
              ) : isMobile ? (
                // Mobile Layout: Responsive cards with icon-based controls
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {filteredUsers.map(u => (
                    <div
                      key={u.id}
                      style={{
                        background: 'var(--card-bg)',
                        border: `1px solid ${u.is_active ? 'var(--border-color)' : 'rgba(248, 81, 73, 0.4)'}`,
                        borderRadius: '8px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        opacity: u.is_active ? 1 : 0.8
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            background: 'rgba(2, 132, 199, 0.12)',
                            border: '1px solid rgba(2, 132, 199, 0.35)',
                            color: 'var(--primary-color, #0284c7)',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '11px',
                            fontWeight: 700
                          }}>
                            #{u.id}
                          </span>
                          <span style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-main)' }}>
                            {u.first_name} {u.last_name}
                          </span>
                        </div>
                        {(() => {
                          const rStyle = getUserRoleBadgeStyle(u.role);
                          return (
                            <span style={{
                              backgroundColor: rStyle.bg,
                              border: `1px solid ${rStyle.border}`,
                              color: rStyle.color,
                              borderRadius: '12px',
                              padding: '3px 8px',
                              fontSize: '11px',
                              fontWeight: '700'
                            }}>
                              {u.role.replace('_', ' ')}
                            </span>
                          );
                        })()}
                      </div>
                      
                      <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                        Email: <span style={{ color: 'var(--text-main)', fontWeight: 500 }}>{u.email}</span>
                      </div>

                      {isGlobalRole && (
                        <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                          Tenant: <span style={{ color: 'var(--text-main)', fontWeight: 500 }}>
                            {u.tenant_id 
                              ? (tenants.find(t => t.id === u.tenant_id)?.brand_name || `Tenant #${u.tenant_id}`) 
                              : 'Global / Unscoped'}
                          </span>
                        </div>
                      )}

                      {u.company_name && (
                        <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                          Company: <span style={{ color: 'var(--text-main)' }}>{u.company_name}</span>
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-mute)' }}>
                        <span>2FA:</span>
                        {u.two_factor_override === 'enforce' ? (
                          <span style={{ backgroundColor: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>
                            🛡️ Enforced (Admin)
                          </span>
                        ) : u.two_factor_override === 'exempt' ? (
                          <span style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)', color: '#eab308', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>
                            ⚠️ Exempt (Admin)
                          </span>
                        ) : u.is_2fa_enforced ? (
                          <span style={{ backgroundColor: 'rgba(56, 139, 253, 0.15)', color: '#58a6ff', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>
                            🛡️ Enforced (Policy)
                          </span>
                        ) : u.two_factor_enabled ? (
                          <span style={{ backgroundColor: 'rgba(46, 160, 67, 0.15)', color: '#3fb950', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>
                            ✅ Enabled
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-mute)', fontSize: '11px' }}>Disabled</span>
                        )}
                      </div>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderTop: '1px solid var(--border-color)',
                        paddingTop: '10px',
                        marginTop: '4px'
                      }}>
                        {u.is_active ? (
                          <span style={{
                            background: 'rgba(16, 185, 129, 0.15)',
                            border: '1px solid rgba(16, 185, 129, 0.4)',
                            color: '#059669',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            Active
                          </span>
                        ) : (
                          <span style={{
                            background: 'rgba(248, 81, 73, 0.15)',
                            border: '1px solid rgba(248, 81, 73, 0.4)',
                            color: '#ef4444',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            Suspended
                          </span>
                        )}

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => openEditModal(u)}
                            title="Edit User Profile & Billing Info"
                            aria-label="Edit User"
                            style={{
                              backgroundColor: 'var(--card-bg)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s ease',
                              color: 'var(--text-main)'
                            }}
                          >
                            ✏️
                          </button>

                          {/* Hide key icon for Google / Apple OAuth accounts */}
                          {u.has_password && (
                            <button
                              type="button"
                              onClick={() => openResetPasswordModal(u)}
                              title="Reset User Password"
                              aria-label="Reset Password"
                              style={{
                                backgroundColor: 'var(--card-bg)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.15s ease',
                                color: 'var(--text-main)'
                              }}
                            >
                              🔑
                            </button>
                          )}

                          {u.id !== user.id && (
                            <button
                              type="button"
                              onClick={() => toggleUserStatus(u)}
                              title={u.is_active ? "Suspend User Account" : "Reactivate User Account"}
                              aria-label={u.is_active ? "Suspend User" : "Reactivate User"}
                              style={{
                                backgroundColor: u.is_active ? 'rgba(248, 81, 73, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                                border: `1px solid ${u.is_active ? 'rgba(248, 81, 73, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              {u.is_active ? '🚫' : '🔓'}
                            </button>
                          )}

                          {u.id !== user.id && (
                            <button
                              type="button"
                              onClick={() => handleDeleteUser(u)}
                              title="Delete User Account"
                              aria-label="Delete User"
                              style={{
                                backgroundColor: 'rgba(248, 81, 73, 0.1)',
                                border: '1px solid rgba(248, 81, 73, 0.3)',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Layout: Table matching Vault grid styling
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    textAlign: 'left',
                    fontSize: '12px'
                  }}>
                    <thead>
                      <tr style={{ background: 'rgba(128, 128, 128, 0.08)', borderBottom: '1px solid var(--border-color)', color: 'var(--text-main)' }}>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>User ID</th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Email</th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Role</th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>2FA Security</th>
                        {isGlobalRole && (
                          <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Tenant</th>
                        )}
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Full Name</th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)' }}>Company</th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-main)', minWidth: '110px', whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'right', color: 'var(--text-main)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(u => (
                        <tr 
                          key={u.id}
                          style={{
                            borderBottom: '1px solid var(--border-color)',
                            transition: 'background-color 0.15s ease',
                            opacity: u.is_active ? 1 : 0.8
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(128, 128, 128, 0.06)'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {/* User ID Column */}
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              background: 'rgba(2, 132, 199, 0.12)',
                              border: '1px solid rgba(2, 132, 199, 0.35)',
                              color: 'var(--primary-color, #0284c7)',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              fontSize: '11px',
                              fontWeight: 700
                            }}>
                              #{u.id}
                            </span>
                          </td>

                          {/* Email Column */}
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{u.email}</div>
                          </td>

                          {/* Role Column */}
                          <td style={{ padding: '12px 16px' }}>
                            {(() => {
                              const rStyle = getUserRoleBadgeStyle(u.role);
                              return (
                                <span style={{
                                  backgroundColor: rStyle.bg,
                                  border: `1px solid ${rStyle.border}`,
                                  color: rStyle.color,
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {u.role.replace('_', ' ')}
                                </span>
                              );
                            })()}
                          </td>

                          {/* 2FA Security Column */}
                          <td style={{ padding: '12px 16px' }}>
                            {u.two_factor_override === 'enforce' ? (
                              <span style={{
                                backgroundColor: 'rgba(168, 85, 247, 0.15)',
                                border: '1px solid rgba(168, 85, 247, 0.4)',
                                color: '#a855f7',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                              }}>
                                🛡️ Enforced (Admin)
                              </span>
                            ) : u.two_factor_override === 'exempt' ? (
                              <span style={{
                                backgroundColor: 'rgba(234, 179, 8, 0.15)',
                                border: '1px solid rgba(234, 179, 8, 0.4)',
                                color: '#eab308',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                              }}>
                                ⚠️ Exempt (Admin)
                              </span>
                            ) : u.is_2fa_enforced ? (
                              <span style={{
                                backgroundColor: 'rgba(56, 139, 253, 0.15)',
                                border: '1px solid rgba(56, 139, 253, 0.4)',
                                color: '#58a6ff',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                              }}>
                                🛡️ Enforced (Policy)
                              </span>
                            ) : u.two_factor_enabled ? (
                              <span style={{
                                backgroundColor: 'rgba(46, 160, 67, 0.15)',
                                border: '1px solid rgba(46, 160, 67, 0.4)',
                                color: '#3fb950',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '700',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                              }}>
                                ✅ Enabled
                              </span>
                            ) : (
                              <span style={{
                                backgroundColor: 'rgba(107, 114, 128, 0.12)',
                                border: '1px solid rgba(107, 114, 128, 0.3)',
                                color: 'var(--text-mute)',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '500',
                                display: 'inline-flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap'
                              }}>
                                Disabled
                              </span>
                            )}
                          </td>

                          {/* Tenant Column (Global Roles only) */}
                          {isGlobalRole && (
                            <td style={{ padding: '12px 16px' }}>
                              {u.tenant_id ? (
                                <span style={{
                                  backgroundColor: 'rgba(99, 102, 241, 0.12)',
                                  border: '1px solid rgba(99, 102, 241, 0.35)',
                                  color: '#6366f1',
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {tenants.find(t => t.id === u.tenant_id)?.brand_name || `Tenant #${u.tenant_id}`}
                                </span>
                              ) : (
                                <span style={{
                                  backgroundColor: 'rgba(107, 114, 128, 0.12)',
                                  border: '1px solid rgba(107, 114, 128, 0.3)',
                                  color: 'var(--text-mute)',
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  whiteSpace: 'nowrap'
                                }}>
                                  Global / Unscoped
                                </span>
                              )}
                            </td>
                          )}

                          {/* Full Name Column */}
                          <td style={{ padding: '12px 16px', color: 'var(--text-main)' }}>
                            <div style={{ fontWeight: 500, color: 'var(--text-main)' }}>{u.first_name} {u.last_name}</div>
                          </td>

                          {/* Company Column */}
                          <td style={{ padding: '12px 16px', color: 'var(--text-mute)' }}>
                            {u.company_name ? (
                              <div style={{ color: 'var(--text-main)' }}>{u.company_name}</div>
                            ) : (
                              <span style={{ color: 'var(--text-mute)', opacity: 0.6 }}>-</span>
                            )}
                          </td>

                          {/* Status Column */}
                          <td style={{ padding: '12px 16px', minWidth: '110px', whiteSpace: 'nowrap' }}>
                            {u.is_active ? (
                              <span style={{
                                background: 'rgba(16, 185, 129, 0.15)',
                                border: '1px solid rgba(16, 185, 129, 0.4)',
                                color: '#059669',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                              }}>
                                Active
                              </span>
                            ) : (
                              <span style={{
                                background: 'rgba(248, 81, 73, 0.15)',
                                border: '1px solid rgba(248, 81, 73, 0.4)',
                                color: '#ef4444',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                              }}>
                                Suspended
                              </span>
                            )}
                          </td>

                          {/* Actions Column */}
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                onClick={() => openEditModal(u)}
                                title="Edit User Profile & Billing Info"
                                aria-label="Edit User"
                                style={{
                                  backgroundColor: 'var(--card-bg)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '6px',
                                  padding: '6px 10px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'var(--text-main)'
                                }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary-color)'}
                                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                              >
                                ✏️
                              </button>

                              {/* Only show key icon (🔑) for users with local password credentials */}
                              {u.has_password ? (
                                <button
                                  type="button"
                                  onClick={() => openResetPasswordModal(u)}
                                  title="Reset User Password"
                                  aria-label="Reset Password"
                                  style={{
                                    backgroundColor: 'var(--card-bg)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--text-main)'
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary-color)'}
                                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                                >
                                  🔑
                                </button>
                              ) : (
                                <span title="Google/Apple Account" style={{ fontSize: '12px', padding: '0 6px', opacity: 0.6 }}>
                                  🛡️
                                </span>
                              )}

                              {u.id !== user.id ? (
                                <button
                                  type="button"
                                  onClick={() => toggleUserStatus(u)}
                                  title={u.is_active ? "Suspend User Account" : "Reactivate User Account"}
                                  aria-label={u.is_active ? "Suspend User" : "Reactivate User"}
                                  style={{
                                    backgroundColor: u.is_active ? 'rgba(248, 81, 73, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                                    border: `1px solid ${u.is_active ? 'rgba(248, 81, 73, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                  {u.is_active ? '🚫' : '🔓'}
                                </button>
                              ) : (
                                <span style={{ color: 'var(--text-mute)', fontSize: '11px', fontStyle: 'italic', padding: '0 4px' }}>
                                  Self
                                </span>
                              )}

                              {u.id !== user.id && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteUser(u)}
                                  title="Delete User Account"
                                  aria-label="Delete User"
                                  style={{
                                    backgroundColor: 'rgba(248, 81, 73, 0.1)',
                                    border: '1px solid rgba(248, 81, 73, 0.3)',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* User Manager Pagination Bar */}
              {filteredUsers.length > 0 && (
                <PaginationBar
                  currentPage={userPage}
                  totalPages={userTotalPages || Math.max(1, Math.ceil(totalUsers / userPageSize))}
                  pageSize={userPageSize}
                  totalItems={totalUsers || filteredUsers.length}
                  onPageChange={(p) => setUserPage(p)}
                  onPageSizeChange={(s) => {
                    setUserPageSize(s);
                    setUserPage(1);
                  }}
                  pageSizeOptions={[25, 50, 100]}
                  itemName="users"
                />
              )}
            </div>

          </div>
        )}

        {/* Tab Content: 3. Site Settings Customizer */}
        {activeTab === 'settings' && (
          hasSettingsAccess ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {styleError && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.4)',
                borderRadius: '6px',
                color: '#ff7b72',
                padding: '12px',
                fontSize: '13px'
              }}>
                <strong>Error:</strong> {styleError}
              </div>
            )}

            {styleSuccess && (
              <div style={{
                backgroundColor: 'rgba(56, 139, 253, 0.15)',
                border: '1px solid rgba(56, 139, 253, 0.4)',
                borderRadius: '6px',
                color: '#58a6ff',
                padding: '12px',
                fontSize: '13px'
              }}>
                {styleSuccess}
              </div>
            )}

            {/* Global Admin: Select Tenant Dropdown & New Tenant Button */}
            {isGlobalRole && (
              <div style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)', display: 'block' }}>
                      Select Tenant Domain to Customize
                    </label>
                    <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                      Switch active tenant to view and modify branding, colors, and layout.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCreateTenantError('');
                      setIsCreateTenantModalOpen(true);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: 'var(--primary-color)',
                      color: 'var(--btn-primary-text, #fff)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 14px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'opacity 0.15s ease'
                    }}
                  >
                    <span>➕</span>
                    <span>New Tenant Domain</span>
                  </button>
                </div>
                <select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: 'var(--input-text)',
                    fontSize: '14px',
                    maxWidth: '450px',
                    cursor: 'pointer'
                  }}
                >
                  {tenants.map(t => (
                    <option key={t.id} value={t.id.toString()}>{t.brand_name} ({t.domain_name})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Global Admin: Multi-Domain / Domain Aliases Configuration */}
            {isGlobalRole && selectedTenantId && (
              <div style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>🌐</span>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)' }}>
                      Domain Aliases & Multi-Domain Routing
                    </h4>
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                      Attach additional web domains that automatically route to <strong>{tenants.find(t => t.id.toString() === selectedTenantId)?.brand_name || 'this brand'}</strong> (e.g. <code>.com</code>, <code>.net</code>, or custom client domains).
                    </p>
                  </div>
                </div>

                {aliasSuccess && (
                  <div style={{ backgroundColor: 'rgba(56, 139, 253, 0.15)', border: '1px solid rgba(56, 139, 253, 0.4)', borderRadius: '6px', color: '#58a6ff', padding: '10px', fontSize: '13px' }}>
                    {aliasSuccess}
                  </div>
                )}
                {aliasError && (
                  <div style={{ backgroundColor: 'rgba(248, 81, 73, 0.15)', border: '1px solid rgba(248, 81, 73, 0.4)', borderRadius: '6px', color: '#ff7b72', padding: '10px', fontSize: '13px' }}>
                    {aliasError}
                  </div>
                )}

                {/* Primary Domain Display */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  backgroundColor: 'var(--panel-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      backgroundColor: 'var(--primary-color)',
                      color: 'var(--btn-primary-text, #fff)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '700'
                    }}>
                      PRIMARY
                    </span>
                    <span data-testid="tenant-primary-domain" style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>
                      {tenants.find(t => t.id.toString() === selectedTenantId)?.domain_name}
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                    Canonical Brand Domain
                  </span>
                </div>

                {/* Configured Domain Aliases List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                    Configured Domain Aliases
                  </label>
                  {(() => {
                    const currentTenant = tenants.find(t => t.id.toString() === selectedTenantId);
                    const aliases = currentTenant?.domain_aliases || [];
                    if (aliases.length === 0) {
                      return (
                        <div style={{
                          padding: '12px',
                          border: '1px dashed var(--border-color)',
                          borderRadius: '6px',
                          fontSize: '13px',
                          color: 'var(--text-mute)',
                          textAlign: 'center'
                        }}>
                          No additional domain aliases configured yet. Visitors accessing any alias domain will automatically resolve to this brand.
                        </div>
                      );
                    }
                    return (
                      <div data-testid="domain-aliases-list" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {aliases.map((alias) => {
                          const aliasName = typeof alias === 'string' ? alias : alias.domain_name;
                          const aliasId = typeof alias === 'string' ? alias : alias.id;
                          return (
                            <div
                              key={aliasName}
                              data-testid={`domain-alias-item-${aliasName}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                backgroundColor: 'var(--panel-bg)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                  backgroundColor: 'rgba(56, 139, 253, 0.15)',
                                  color: '#58a6ff',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '700'
                                }}>
                                  ALIAS
                                </span>
                                <span style={{ fontSize: '13px', color: 'var(--text-main)', fontWeight: '500' }}>
                                  {aliasName}
                                </span>
                              </div>
                              <button
                                type="button"
                                data-testid={`remove-alias-btn-${aliasName}`}
                                onClick={() => handleDeleteAlias(aliasId || aliasName)}
                                disabled={aliasLoading}
                                style={{
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  color: '#ff7b72',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 8px'
                                }}
                              >
                                ✕ Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Add Alias Form */}
                <form onSubmit={handleAddAlias} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: '4px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1, minWidth: '260px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-mute)' }}>Add New Domain Alias</label>
                    <input
                      type="text"
                      data-testid="add-domain-alias-input"
                      value={aliasInput}
                      onChange={(e) => setAliasInput(e.target.value)}
                      placeholder="e.g. fishermanstale.net or app.fishermanstale.com"
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '9px 12px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="add-domain-alias-btn"
                    disabled={aliasLoading || !aliasInput.trim()}
                    style={{
                      backgroundColor: 'var(--primary-color)',
                      color: 'var(--btn-primary-text, #fff)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '9px 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      height: '38px',
                      opacity: (aliasLoading || !aliasInput.trim()) ? 0.6 : 1
                    }}
                  >
                    {aliasLoading ? 'Adding...' : '+ Add Domain Alias'}
                  </button>
                </form>
              </div>
            )}

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Standardized Sub-tab Selectors: Styling vs Keys vs Onboarding Settings */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              borderBottom: '1px solid var(--border-color)',
              gap: '4px',
              zIndex: 1,
              position: 'relative',
              marginTop: '4px'
            }}>
              <button
                type="button"
                data-testid="tab-settings-styling"
                onClick={() => setSettingsTab('styling')}
                style={{
                  backgroundColor: settingsTab === 'styling' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  color: settingsTab === 'styling' ? 'var(--primary-color)' : 'var(--text-mute)',
                  border: '1px solid transparent',
                  borderLeftColor: settingsTab === 'styling' ? 'var(--border-color)' : 'transparent',
                  borderRightColor: settingsTab === 'styling' ? 'var(--border-color)' : 'transparent',
                  borderTopColor: settingsTab === 'styling' ? 'var(--border-color)' : 'transparent',
                  borderBottomColor: settingsTab === 'styling' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  borderRadius: '8px 8px 0 0',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: settingsTab === 'styling' ? '600' : '500',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s ease',
                  zIndex: settingsTab === 'styling' ? 2 : 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: settingsTab === 'styling' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                </svg>
                Styling
              </button>

              <button
                type="button"
                data-testid="tab-settings-mail"
                onClick={() => setSettingsTab('mail')}
                style={{
                  backgroundColor: settingsTab === 'mail' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  color: settingsTab === 'mail' ? 'var(--primary-color)' : 'var(--text-mute)',
                  border: '1px solid transparent',
                  borderLeftColor: settingsTab === 'mail' ? 'var(--border-color)' : 'transparent',
                  borderRightColor: settingsTab === 'mail' ? 'var(--border-color)' : 'transparent',
                  borderTopColor: settingsTab === 'mail' ? 'var(--border-color)' : 'transparent',
                  borderBottomColor: settingsTab === 'mail' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  borderRadius: '8px 8px 0 0',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: settingsTab === 'mail' ? '600' : '500',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s ease',
                  zIndex: settingsTab === 'mail' ? 2 : 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: settingsTab === 'mail' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M22 7l-10 7L2 7" />
                </svg>
                Mail
              </button>

              <button
                type="button"
                data-testid="tab-settings-keys"
                onClick={() => setSettingsTab('keys')}
                style={{
                  backgroundColor: settingsTab === 'keys' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  color: settingsTab === 'keys' ? 'var(--primary-color)' : 'var(--text-mute)',
                  border: '1px solid transparent',
                  borderLeftColor: settingsTab === 'keys' ? 'var(--border-color)' : 'transparent',
                  borderRightColor: settingsTab === 'keys' ? 'var(--border-color)' : 'transparent',
                  borderTopColor: settingsTab === 'keys' ? 'var(--border-color)' : 'transparent',
                  borderBottomColor: settingsTab === 'keys' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  borderRadius: '8px 8px 0 0',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: settingsTab === 'keys' ? '600' : '500',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s ease',
                  zIndex: settingsTab === 'keys' ? 2 : 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: settingsTab === 'keys' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2l-2 2m-1.5 6.1L12.9 4.7a6.5 6.5 0 1 0-7.8 8.9L1.5 17.2a2 2 0 0 0-.5 1.4V22h3.4a2 2 0 0 0 1.4-.5l3.6-3.6a6.5 6.5 0 0 0 8.1-1.8z" />
                </svg>
                Auth / Keys
              </button>

              <button
                type="button"
                data-testid="tab-settings-onboarding"
                onClick={() => setSettingsTab('onboarding')}
                style={{
                  backgroundColor: settingsTab === 'onboarding' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  color: settingsTab === 'onboarding' ? 'var(--primary-color)' : 'var(--text-mute)',
                  border: '1px solid transparent',
                  borderLeftColor: settingsTab === 'onboarding' ? 'var(--border-color)' : 'transparent',
                  borderRightColor: settingsTab === 'onboarding' ? 'var(--border-color)' : 'transparent',
                  borderTopColor: settingsTab === 'onboarding' ? 'var(--border-color)' : 'transparent',
                  borderBottomColor: settingsTab === 'onboarding' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  borderRadius: '8px 8px 0 0',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: settingsTab === 'onboarding' ? '600' : '500',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s ease',
                  zIndex: settingsTab === 'onboarding' ? 2 : 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: settingsTab === 'onboarding' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
                Onboarding Settings
              </button>

              <button
                type="button"
                data-testid="tab-settings-mobile"
                onClick={() => setSettingsTab('mobile')}
                style={{
                  backgroundColor: settingsTab === 'mobile' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  color: settingsTab === 'mobile' ? 'var(--primary-color)' : 'var(--text-mute)',
                  border: '1px solid transparent',
                  borderLeftColor: settingsTab === 'mobile' ? 'var(--border-color)' : 'transparent',
                  borderRightColor: settingsTab === 'mobile' ? 'var(--border-color)' : 'transparent',
                  borderTopColor: settingsTab === 'mobile' ? 'var(--border-color)' : 'transparent',
                  borderBottomColor: settingsTab === 'mobile' ? 'var(--panel-color, var(--card-bg))' : 'transparent',
                  borderRadius: '8px 8px 0 0',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: settingsTab === 'mobile' ? '600' : '500',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s ease',
                  zIndex: settingsTab === 'mobile' ? 2 : 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: settingsTab === 'mobile' ? '0 -3px 8px rgba(0, 0, 0, 0.08)' : 'none'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
                Mobile Web
              </button>
            </div>

            {settingsTab === 'styling' && (
            <form
              onSubmit={handleSaveSiteSettings}
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}
            >
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>Brand Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Fisherman's Tale"
                    value={styleBrandName}
                    onChange={(e) => setStyleBrandName(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'var(--input-text)',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Share Splash Payoff Line</span>
                    <span style={{ fontSize: '11px', fontWeight: '400', color: 'var(--text-mute)' }}>Social &amp; Share modal text</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Official Certified Trophy Card"
                    value={styleSharePayoffLine}
                    onChange={(e) => setStyleSharePayoffLine(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'var(--input-text)',
                      fontSize: '14px'
                    }}
                  />
                  <span style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px' }}>
                    Replaces &quot;Official Certified Trophy Card&quot; on the card share splash, social link previews, and generated OG preview images.
                  </span>
                </div>
              </div>

              {/* Mail Settings Relocation Notice */}
              <div style={{
                background: 'var(--panel-color, rgba(255, 255, 255, 0.02))',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '20px' }}>📬</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                      Mail &amp; Automated Messaging Settings
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                      Outbound mail relays, sender addresses, automated templates, and live email client mockups have moved to the dedicated Mail tab.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSettingsTab('mail')}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Open Mail Settings &rarr;
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
                <ColorOrGradientField
                  label="Primary Theme Color"
                  value={stylePrimaryColor}
                  onChange={setStylePrimaryColor}
                  onOpenGradient={() => handleOpenGradientModal('primary')}
                  defaultSolid="#bdb2ff"
                />
                <ColorOrGradientField
                  label="Accent Theme Color"
                  value={styleAccentColor}
                  onChange={setStyleAccentColor}
                  onOpenGradient={() => handleOpenGradientModal('accent')}
                  defaultSolid="#a0c4ff"
                />
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', flexWrap: 'wrap' }}>
                <ColorOrGradientField
                  label="Card Background Color"
                  value={styleCardColor}
                  onChange={setStyleCardColor}
                  onOpenGradient={() => handleOpenGradientModal('card')}
                  defaultSolid="#ffffff"
                />
                <ColorOrGradientField
                  label="Panel Canvas Color"
                  value={stylePanelColor}
                  onChange={setStylePanelColor}
                  onOpenGradient={() => handleOpenGradientModal('panel')}
                  defaultSolid="#f8f9fa"
                />
                <ColorOrGradientField
                  label="Menu Panel Color"
                  value={styleMenuColor}
                  onChange={setStyleMenuColor}
                  onOpenGradient={() => handleOpenGradientModal('menu')}
                  defaultSolid="#f8f9fa"
                />
                <ColorOrGradientField
                  label="Header Strip Color"
                  value={styleHeaderColor}
                  onChange={setStyleHeaderColor}
                  onOpenGradient={() => handleOpenGradientModal('header')}
                  defaultSolid="#f8f9fa"
                />
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', flexWrap: 'wrap' }}>
                <ColorOrGradientField
                  label="Main Text Color"
                  value={styleTextMainColor}
                  onChange={setStyleTextMainColor}
                  onOpenGradient={() => handleOpenGradientModal('textMain')}
                  defaultSolid="#111827"
                />
                <ColorOrGradientField
                  label="Muted Headings & Subtext"
                  value={styleTextMuteColor}
                  onChange={setStyleTextMuteColor}
                  onOpenGradient={() => handleOpenGradientModal('textMute')}
                  defaultSolid="#4b5563"
                />
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', flexWrap: 'wrap' }}>
                <ColorOrGradientField
                  label="Input Box Background Color"
                  value={styleInputBgColor}
                  onChange={setStyleInputBgColor}
                  onOpenGradient={() => handleOpenGradientModal('input')}
                  defaultSolid="#ffffff"
                />
                <ColorOrGradientField
                  label="Input Box Border Color"
                  value={styleInputBorderColor}
                  onChange={setStyleInputBorderColor}
                  onOpenGradient={() => handleOpenGradientModal('inputBorder')}
                  defaultSolid={isDarkColor(stylePanelColor) ? 'rgba(255, 255, 255, 0.15)' : '#d1d5db'}
                />
                <ColorOrGradientField
                  label="Input Box Text Color"
                  value={styleInputTextColor}
                  onChange={setStyleInputTextColor}
                  onOpenGradient={() => handleOpenGradientModal('inputText')}
                  defaultSolid={isDarkColor(stylePanelColor) ? '#f3f4f6' : '#111827'}
                />
                <ColorOrGradientField
                  label="Disabled State Color"
                  value={styleDisabledColor}
                  onChange={setStyleDisabledColor}
                  onOpenGradient={() => handleOpenGradientModal('disabled')}
                  defaultSolid="#000000"
                />
              </div>

              {/* Pop-Down System User Messages Styling Section */}
              <div style={{
                background: 'var(--card-bg, #ffffff)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>🔔</span>
                      <span>Pop-Down System User Messages (Toasts &amp; Banners)</span>
                    </h4>
                    <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                      Customize the slide-in toast notifications and alert banners that drop down from the top of the screen.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      type="button"
                      data-testid="test-popdown-toast-btn"
                      onClick={() => {
                        modal.notify("🔔 This is a live preview of a pop-down system user message with your tenant styling!", "info");
                      }}
                      style={{
                        padding: '6px 14px',
                        background: 'var(--primary-gradient, var(--primary-color))',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    >
                      <span>🔔</span>
                      <span>Test Pop-Down Message</span>
                    </button>
                    {(styleToastBgColor || styleToastTextColor) && (
                      <button
                        type="button"
                        onClick={() => {
                          setStyleToastBgColor('');
                          setStyleToastTextColor('');
                        }}
                        style={{
                          padding: '6px 12px',
                          background: 'transparent',
                          color: 'var(--text-mute)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                        title="Reset to theme defaults"
                      >
                        Reset Defaults
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', flexWrap: 'wrap' }}>
                  <ColorOrGradientField
                    label="Pop-Down Message Background"
                    value={styleToastBgColor}
                    onChange={setStyleToastBgColor}
                    onOpenGradient={() => handleOpenGradientModal('toastBg')}
                    defaultSolid={isDarkColor(stylePanelColor) ? '#0f172a' : '#ffffff'}
                  />
                  <ColorOrGradientField
                    label="Pop-Down Message Text Color"
                    value={styleToastTextColor}
                    onChange={setStyleToastTextColor}
                    onOpenGradient={() => handleOpenGradientModal('toastText')}
                    defaultSolid={isDarkColor(stylePanelColor) ? '#ffffff' : '#0f172a'}
                  />
                </div>
              </div>

              {/* Buttons & Interactive Controls Styling Section */}
              <div style={{
                background: 'var(--card-bg, #ffffff)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>🔘</span>
                      <span>Buttons &amp; Interactive Action Controls</span>
                    </h4>
                    <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                      Configure primary button label text color (with contrast safety) and secondary/neutral action button styling.
                    </p>
                  </div>
                  {(styleButtonPrimaryTextColor || styleButtonSecondaryBg || styleButtonSecondaryText || styleButtonSecondaryBorder) && (
                    <button
                      type="button"
                      onClick={() => {
                        setStyleButtonPrimaryTextColor('');
                        setStyleButtonSecondaryBg('');
                        setStyleButtonSecondaryText('');
                        setStyleButtonSecondaryBorder('');
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        color: 'var(--text-mute)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                      title="Reset buttons to default styling"
                    >
                      Reset Button Defaults
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', flexWrap: 'wrap' }}>
                  <ColorOrGradientField
                    label="Primary Button Text Color"
                    value={styleButtonPrimaryTextColor}
                    onChange={setStyleButtonPrimaryTextColor}
                    onOpenGradient={() => handleOpenGradientModal('buttonPrimaryText')}
                    defaultSolid="#ffffff"
                  />
                  <ColorOrGradientField
                    label="Secondary Button Background"
                    value={styleButtonSecondaryBg}
                    onChange={setStyleButtonSecondaryBg}
                    onOpenGradient={() => handleOpenGradientModal('buttonSecondaryBg')}
                    defaultSolid={isDarkColor(stylePanelColor) ? 'rgba(255, 255, 255, 0.05)' : 'transparent'}
                  />
                  <ColorOrGradientField
                    label="Secondary Button Text"
                    value={styleButtonSecondaryText}
                    onChange={setStyleButtonSecondaryText}
                    onOpenGradient={() => handleOpenGradientModal('buttonSecondaryText')}
                    defaultSolid={isDarkColor(stylePanelColor) ? '#d1d5db' : '#374151'}
                  />
                  <ColorOrGradientField
                    label="Secondary Button Border"
                    value={styleButtonSecondaryBorder}
                    onChange={setStyleButtonSecondaryBorder}
                    onOpenGradient={() => handleOpenGradientModal('buttonSecondaryBorder')}
                    defaultSolid={isDarkColor(stylePanelColor) ? '#30363d' : '#d1d5db'}
                  />
                </div>

                {/* Live Button Preview Bar */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: 'var(--panel-color, rgba(0,0,0,0.02))',
                  borderRadius: '8px',
                  border: '1px dashed var(--border-color)',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)' }}>Button Previews:</span>
                  <button
                    type="button"
                    style={{
                      background: stylePrimaryColor || 'var(--primary-color)',
                      color: styleButtonPrimaryTextColor || '#ffffff',
                      border: 'none',
                      borderRadius: 'var(--radius-md, 6px)',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'default',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    Primary Action Button
                  </button>
                  <button
                    type="button"
                    style={{
                      background: styleButtonSecondaryBg || 'transparent',
                      color: styleButtonSecondaryText || 'var(--text-main)',
                      border: `1px solid ${styleButtonSecondaryBorder || 'var(--border-color)'}`,
                      borderRadius: 'var(--radius-md, 6px)',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'default'
                    }}
                  >
                    Secondary / Neutral Button
                  </button>
                </div>
              </div>

              {/* Tables & Data Grids Styling Section */}
              <div style={{
                background: 'var(--card-bg, #ffffff)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>📊</span>
                      <span>Tables, Data Grids &amp; Selection Highlights</span>
                    </h4>
                    <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                      Customize table column header backgrounds, column text colors, and selected row active tint.
                    </p>
                  </div>
                  {(styleTableHeaderBg || styleTableHeaderText || styleTableRowSelected) && (
                    <button
                      type="button"
                      onClick={() => {
                        setStyleTableHeaderBg('');
                        setStyleTableHeaderText('');
                        setStyleTableRowSelected('');
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        color: 'var(--text-mute)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                      title="Reset table styles to default"
                    >
                      Reset Table Defaults
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', flexWrap: 'wrap' }}>
                  <ColorOrGradientField
                    label="Table Header Background"
                    value={styleTableHeaderBg}
                    onChange={setStyleTableHeaderBg}
                    onOpenGradient={() => handleOpenGradientModal('tableHeaderBg')}
                    defaultSolid={isDarkColor(stylePanelColor) ? 'rgba(255, 255, 255, 0.03)' : '#f9fafb'}
                  />
                  <ColorOrGradientField
                    label="Table Header Text Color"
                    value={styleTableHeaderText}
                    onChange={setStyleTableHeaderText}
                    onOpenGradient={() => handleOpenGradientModal('tableHeaderText')}
                    defaultSolid={isDarkColor(stylePanelColor) ? '#9ca3af' : '#6b7280'}
                  />
                  <ColorOrGradientField
                    label="Selected Row Highlight"
                    value={styleTableRowSelected}
                    onChange={setStyleTableRowSelected}
                    onOpenGradient={() => handleOpenGradientModal('tableRowSelected')}
                    defaultSolid="rgba(56, 139, 253, 0.12)"
                  />
                </div>

                {/* Live Sample Table Preview */}
                <div style={{
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md, 8px)',
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: styleTableHeaderBg || 'var(--table-header-bg, rgba(255, 255, 255, 0.03))' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: styleTableHeaderText || 'var(--table-header-text, var(--text-mute))', fontWeight: '600' }}>Item ID</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: styleTableHeaderText || 'var(--table-header-text, var(--text-mute))', fontWeight: '600' }}>Title</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: styleTableHeaderText || 'var(--table-header-text, var(--text-mute))', fontWeight: '600' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderTop: '1px solid var(--border-color)', color: 'var(--text-main)' }}>
                        <td style={{ padding: '8px 12px' }}>#TC-101</td>
                        <td style={{ padding: '8px 12px' }}>Standard Row</td>
                        <td style={{ padding: '8px 12px' }}>Active</td>
                      </tr>
                      <tr style={{
                        borderTop: '1px solid var(--border-color)',
                        background: styleTableRowSelected || 'var(--table-row-selected, rgba(56, 139, 253, 0.12))',
                        color: 'var(--text-main)'
                      }}>
                        <td style={{ padding: '8px 12px', fontWeight: '600' }}>#TC-102 (Selected)</td>
                        <td style={{ padding: '8px 12px', fontWeight: '600' }}>Selected Row Highlight</td>
                        <td style={{ padding: '8px 12px' }}>Verified</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Modals & Dialogs Styling Section */}
              <div style={{
                background: 'var(--card-bg, #ffffff)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>🪟</span>
                      <span>Modals, Dialogs &amp; Overlays</span>
                    </h4>
                    <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                      Set custom backdrop surfaces and borders for popup dialogs, detail modals, and overlays.
                    </p>
                  </div>
                  {(styleModalBgColor || styleModalBorderColor) && (
                    <button
                      type="button"
                      onClick={() => {
                        setStyleModalBgColor('');
                        setStyleModalBorderColor('');
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        color: 'var(--text-mute)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                      title="Reset modal styles to default"
                    >
                      Reset Modal Defaults
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', flexWrap: 'wrap' }}>
                  <ColorOrGradientField
                    label="Modal Card Background"
                    value={styleModalBgColor}
                    onChange={setStyleModalBgColor}
                    onOpenGradient={() => handleOpenGradientModal('modalBg')}
                    defaultSolid={isDarkColor(stylePanelColor) ? '#161b22' : '#ffffff'}
                  />
                  <ColorOrGradientField
                    label="Modal Border Color"
                    value={styleModalBorderColor}
                    onChange={setStyleModalBorderColor}
                    onOpenGradient={() => handleOpenGradientModal('modalBorder')}
                    defaultSolid={isDarkColor(stylePanelColor) ? 'rgba(255, 255, 255, 0.15)' : '#e5e7eb'}
                  />
                </div>
              </div>

              {/* Global Typography, Borders & Shape Section */}
              <div style={{
                background: 'var(--card-bg, #ffffff)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>🎨</span>
                      <span>Global Typography, Borders &amp; Shape</span>
                    </h4>
                    <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                      Control site-wide typography font family, corner radius curvature, dividers, and status badge chips.
                    </p>
                  </div>
                  {(styleFontFamily || styleBorderRadius !== 'standard' || styleBorderColor || styleBadgeBgColor || styleBadgeTextColor) && (
                    <button
                      type="button"
                      onClick={() => {
                        setStyleFontFamily('');
                        setStyleBorderRadius('standard');
                        setStyleBorderColor('');
                        setStyleBadgeBgColor('');
                        setStyleBadgeTextColor('');
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        color: 'var(--text-mute)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                      title="Reset typography and shape defaults"
                    >
                      Reset Defaults
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                  {/* Font Family Selector */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                      Brand Font Family
                    </label>
                    <select
                      value={styleFontFamily}
                      onChange={(e) => setStyleFontFamily(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-md, 6px)',
                        padding: '10px',
                        color: 'var(--text-main)',
                        fontSize: '13px'
                      }}
                    >
                      <option value="">System Default (Apple / Segoe / Roboto)</option>
                      <option value="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif">Modern Sans (Inter)</option>
                      <option value="'Poppins', 'Montserrat', -apple-system, sans-serif">Geometric Sans (Poppins / Montserrat)</option>
                      <option value="'Roboto', -apple-system, sans-serif">Clean Tech (Roboto)</option>
                      <option value="'Open Sans', sans-serif">Professional (Open Sans)</option>
                      <option value="'Merriweather', 'Playfair Display', Georgia, serif">Editorial Serif (Merriweather / Playfair)</option>
                      <option value="'JetBrains Mono', 'Fira Code', 'Courier New', monospace">Technical Monospace</option>
                    </select>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                      Applies immediately across all dashboard headers, panels, cards, and buttons.
                    </span>
                  </div>

                  {/* Corner Radius Selector */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                      Corner Roundness (Border Radius)
                    </label>
                    <select
                      value={styleBorderRadius}
                      onChange={(e) => setStyleBorderRadius(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-md, 6px)',
                        padding: '10px',
                        color: 'var(--text-main)',
                        fontSize: '13px'
                      }}
                    >
                      <option value="standard">Standard (4px / 8px / 12px - Balanced)</option>
                      <option value="sharp">Sharp (0px - Modern Square Corners)</option>
                      <option value="smooth">Smooth (6px / 12px / 16px - Soft Contours)</option>
                      <option value="rounded">Rounded (8px / 16px / 24px - Pill Curves)</option>
                    </select>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                      Controls corner roundness of all cards, modals, buttons, and inputs.
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', flexWrap: 'wrap' }}>
                  <ColorOrGradientField
                    label="Global Border / Divider Color"
                    value={styleBorderColor}
                    onChange={setStyleBorderColor}
                    onOpenGradient={() => handleOpenGradientModal('border')}
                    defaultSolid={isDarkColor(stylePanelColor) ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}
                  />
                  <ColorOrGradientField
                    label="Status Badge / Pill Background"
                    value={styleBadgeBgColor}
                    onChange={setStyleBadgeBgColor}
                    onOpenGradient={() => handleOpenGradientModal('badgeBg')}
                    defaultSolid="rgba(99, 102, 241, 0.15)"
                  />
                  <ColorOrGradientField
                    label="Status Badge / Pill Text Color"
                    value={styleBadgeTextColor}
                    onChange={setStyleBadgeTextColor}
                    onOpenGradient={() => handleOpenGradientModal('badgeText')}
                    defaultSolid="#6366f1"
                  />
                </div>

                {/* Live Badge Preview */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 14px',
                  backgroundColor: 'var(--panel-color, rgba(0,0,0,0.02))',
                  borderRadius: 'var(--radius-md, 8px)',
                  border: '1px dashed var(--border-color)',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)' }}>Pill Badge Preview:</span>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '3px 10px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: styleBadgeBgColor || 'var(--badge-bg, rgba(99, 102, 241, 0.15))',
                    color: styleBadgeTextColor || 'var(--badge-text, var(--primary-color))'
                  }}>
                    Sample Status Pill (12 Active)
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
                {/* Brand Logo Selector */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Brand Logo</span>
                    <span style={{ fontSize: '11px', fontWeight: '400', color: 'var(--text-mute)' }}>PNG, SVG, or WebP</span>
                  </label>
                  <div style={{
                    backgroundColor: 'var(--input-bg, rgba(255, 255, 255, 0.03))',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px'
                  }}>
                    {/* Thumbnail preview with checkerboard for transparency */}
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: '#0f172a',
                      backgroundImage: styleLogoUrl
                        ? 'linear-gradient(45deg, #1e293b 25%, transparent 25%), linear-gradient(-45deg, #1e293b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1e293b 75%), linear-gradient(-45deg, transparent 75%, #1e293b 75%)'
                        : 'none',
                      backgroundSize: '10px 10px',
                      backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {styleLogoUrl ? (
                        <img
                          src={styleLogoUrl.startsWith('http') || styleLogoUrl.startsWith('data:') ? styleLogoUrl : `${API_BASE}${styleLogoUrl.startsWith('/') ? '' : '/'}${styleLogoUrl}`}
                          alt="Logo Preview"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-mute)' }}>
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      )}
                    </div>

                    {/* Logo Info and Action Buttons */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div
                        title={styleLogoUrl || 'No logo selected'}
                        style={{
                          fontSize: '12px',
                          color: styleLogoUrl ? 'var(--text-main)' : 'var(--text-mute)',
                          fontWeight: styleLogoUrl ? '500' : '400',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {styleLogoUrl ? styleLogoUrl.split('/').pop() : 'No logo selected'}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setAssetSelectorTarget('logo')}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: 'var(--primary-color)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                          Choose from Assets
                        </button>
                        {styleLogoUrl && (
                          <button
                            type="button"
                            onClick={() => setStyleLogoUrl('')}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: 'transparent',
                              border: '1px solid var(--border-color)',
                              color: 'var(--text-mute)',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#ef4444';
                              e.currentTarget.style.borderColor = '#ef4444';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--text-mute)';
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3b. Site Icon / Favicon Configuration */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)', display: 'block' }}>
                    Site Icon / Favicon
                  </label>
                  <span style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '-4px' }}>
                    Displayed in browser tabs, address bars, bookmarks, and mobile home screens. If empty, your Brand Logo is used automatically.
                  </span>

                  <div style={{
                    background: 'var(--panel-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px'
                  }}>
                    {/* Thumbnail preview */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: '#0f172a',
                      backgroundImage: (styleSiteIconUrl || styleLogoUrl)
                        ? 'linear-gradient(45deg, #1e293b 25%, transparent 25%), linear-gradient(-45deg, #1e293b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1e293b 75%), linear-gradient(-45deg, transparent 75%, #1e293b 75%)'
                        : 'none',
                      backgroundSize: '8px 8px',
                      backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {(styleSiteIconUrl || styleLogoUrl) ? (
                        <img
                          src={(styleSiteIconUrl || styleLogoUrl).startsWith('http') || (styleSiteIconUrl || styleLogoUrl).startsWith('data:') ? (styleSiteIconUrl || styleLogoUrl) : `${API_BASE}${(styleSiteIconUrl || styleLogoUrl).startsWith('/') ? '' : '/'}${(styleSiteIconUrl || styleLogoUrl)}`}
                          alt="Site Icon Preview"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <span style={{ fontSize: '20px' }}>🏆</span>
                      )}
                    </div>

                    {/* Site Icon Info and Buttons */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div
                        title={styleSiteIconUrl || (styleLogoUrl ? 'Using Brand Logo as fallback icon' : 'Using default TrophyCoin icon')}
                        style={{
                          fontSize: '12px',
                          color: styleSiteIconUrl ? 'var(--text-main)' : 'var(--text-mute)',
                          fontWeight: styleSiteIconUrl ? '500' : '400',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {styleSiteIconUrl ? styleSiteIconUrl.split('/').pop() : (styleLogoUrl ? 'Using Brand Logo as fallback icon' : 'Using default TrophyCoin icon')}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setAssetSelectorTarget('site_icon')}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: 'var(--primary-color)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                          Choose from Assets
                        </button>
                        {styleSiteIconUrl && (
                          <button
                            type="button"
                            onClick={() => setStyleSiteIconUrl('')}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: 'transparent',
                              border: '1px solid var(--border-color)',
                              color: 'var(--text-mute)',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#ef4444';
                              e.currentTarget.style.borderColor = '#ef4444';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--text-mute)';
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                            }}
                          >
                            Reset to Default / Logo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Background Canvas Image Selector */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Background Canvas Image</span>
                    <span style={{ fontSize: '11px', fontWeight: '400', color: 'var(--text-mute)' }}>Optional Wallpaper</span>
                  </label>
                  <div style={{
                    backgroundColor: 'var(--input-bg, rgba(255, 255, 255, 0.03))',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px'
                  }}>
                    {/* Thumbnail preview */}
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: '#0f172a',
                      backgroundImage: styleBackgroundUrl
                        ? 'linear-gradient(45deg, #1e293b 25%, transparent 25%), linear-gradient(-45deg, #1e293b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1e293b 75%), linear-gradient(-45deg, transparent 75%, #1e293b 75%)'
                        : 'none',
                      backgroundSize: '10px 10px',
                      backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {styleBackgroundUrl ? (
                        <img
                          src={styleBackgroundUrl.startsWith('http') || styleBackgroundUrl.startsWith('data:') ? styleBackgroundUrl : `${API_BASE}${styleBackgroundUrl.startsWith('/') ? '' : '/'}${styleBackgroundUrl}`}
                          alt="Background Preview"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-mute)' }}>
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      )}
                    </div>

                    {/* Background Info and Action Buttons */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div
                        title={styleBackgroundUrl || 'No background selected'}
                        style={{
                          fontSize: '12px',
                          color: styleBackgroundUrl ? 'var(--text-main)' : 'var(--text-mute)',
                          fontWeight: styleBackgroundUrl ? '500' : '400',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {styleBackgroundUrl ? styleBackgroundUrl.split('/').pop() : 'No background image selected'}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setAssetSelectorTarget('background')}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: 'var(--primary-color)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                          Choose from Assets
                        </button>
                        {styleBackgroundUrl && (
                          <button
                            type="button"
                            onClick={() => setStyleBackgroundUrl('')}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: 'transparent',
                              border: '1px solid var(--border-color)',
                              color: 'var(--text-mute)',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#ef4444';
                              e.currentTarget.style.borderColor = '#ef4444';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--text-mute)';
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Logo Sizing & Header Positioning Controls */}
              <div style={{
                backgroundColor: 'var(--input-bg, rgba(255, 255, 255, 0.03))',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                marginTop: '6px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                      Header Banner & Logo Sizing
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                      Fine-tune your top navigation banner height and brand logo alignment/overlap.
                    </div>
                  </div>
                  {/* Preset Buttons */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setStyleBannerHeight(70);
                        setStyleLogoHeight(40);
                        setStyleLogoOffsetY(0);
                        setStyleLogoOffsetX(0);
                      }}
                      style={{
                        padding: '5px 10px',
                        fontSize: '11px',
                        fontWeight: '500',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'transparent',
                        color: 'var(--text-main)',
                        cursor: 'pointer'
                      }}
                    >
                      Standard (70px Banner / 40px Logo)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStyleBannerHeight(70);
                        setStyleLogoHeight(108);
                        setStyleLogoOffsetY(27);
                        setStyleLogoOffsetX(0);
                      }}
                      style={{
                        padding: '5px 10px',
                        fontSize: '11px',
                        fontWeight: '500',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'transparent',
                        color: 'var(--text-main)',
                        cursor: 'pointer'
                      }}
                    >
                      Large Overlap (108px / +27px)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStyleBannerHeight(90);
                        setStyleLogoHeight(50);
                        setStyleLogoOffsetY(0);
                        setStyleLogoOffsetX(0);
                      }}
                      style={{
                        padding: '5px 10px',
                        fontSize: '11px',
                        fontWeight: '500',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'transparent',
                        color: 'var(--text-main)',
                        cursor: 'pointer'
                      }}
                    >
                      Tall Banner (90px Banner / 50px Logo)
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '16px' }}>
                  {/* Banner Height */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-main)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Banner Height</span>
                      <span style={{ color: 'var(--primary-color)', fontWeight: '600' }}>{styleBannerHeight || 0}px</span>
                    </label>
                    <input
                      type="number"
                      min="45"
                      max="250"
                      step="1"
                      value={styleBannerHeight}
                      onChange={(e) => setStyleBannerHeight(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                      style={{
                        background: 'var(--card-bg, #ffffff)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--text-main)',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>60px compact, 70px standard, 90–120px tall</span>
                  </div>

                  {/* Logo Max Height */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-main)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Logo Height</span>
                      <span style={{ color: 'var(--primary-color)', fontWeight: '600' }}>{styleLogoHeight || 0}px</span>
                    </label>
                    <input
                      type="number"
                      min="20"
                      max="160"
                      step="1"
                      value={styleLogoHeight}
                      onChange={(e) => setStyleLogoHeight(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                      style={{
                        background: 'var(--card-bg, #ffffff)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--text-main)',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>40px standard, 80–120px large</span>
                  </div>

                  {/* Vertical Offset (Y) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-main)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Vertical Offset (Y)</span>
                      <span style={{ color: 'var(--primary-color)', fontWeight: '600' }}>{styleLogoOffsetY || 0}px</span>
                    </label>
                    <input
                      type="number"
                      min="-50"
                      max="100"
                      step="1"
                      value={styleLogoOffsetY}
                      onChange={(e) => setStyleLogoOffsetY(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                      style={{
                        background: 'var(--card-bg, #ffffff)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--text-main)',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>0px flush; positive values overlap downwards</span>
                  </div>

                  {/* Horizontal Offset (X) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-main)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Horizontal Offset (X)</span>
                      <span style={{ color: 'var(--primary-color)', fontWeight: '600' }}>{styleLogoOffsetX || 0}px</span>
                    </label>
                    <input
                      type="number"
                      min="-100"
                      max="100"
                      step="1"
                      value={styleLogoOffsetX}
                      onChange={(e) => setStyleLogoOffsetX(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                      style={{
                        background: 'var(--card-bg, #ffffff)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--text-main)',
                        fontSize: '13px'
                      }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>0px left default; shifts left / right</span>
                  </div>
                </div>
              </div>

                <button
                  type="submit"
                  disabled={styleLoading}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    width: 'fit-content',
                    marginTop: '10px',
                    opacity: styleLoading ? 0.7 : 1
                  }}
                >
                  {styleLoading ? 'Saving Site Settings...' : 'Save Site Settings'}
                </button>
              </form>
            )}

            {/* Tab: Mail */}
            {settingsTab === 'mail' && (
              <TenantMailSettingsTab
                tenant={isGlobalRole && selectedTenantId ? (tenants.find(t => t.id.toString() === selectedTenantId) || tenantConfig) : tenantConfig}
                token={token}
                API_BASE={API_BASE}
                user={user}
                isGlobalRole={isGlobalRole}
                selectedTenantId={selectedTenantId}
                onSaveSuccess={async (updatedTenant) => {
                  if (refreshTheme) await refreshTheme();
                  if (isGlobalRole && updatedTenant) {
                    setTenants(prev => prev.map(t => t.id === updatedTenant.id ? { ...t, ...updatedTenant } : t));
                  }
                }}
              />
            )}

            {/* Tab 2: Auth / Keys */}
            {settingsTab === 'keys' && (
              <div style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                {/* 1. Two-Factor Authentication (2FA) Tenant Security Policy */}
                <div style={{
                  background: 'var(--panel-color, rgba(255, 255, 255, 0.02))',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '22px' }}>🛡️</span>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)', fontWeight: '700' }}>
                          Two-Factor Authentication (2FA) Security Policy
                        </h4>
                        <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                          Enforce mandatory 6-digit email PIN verification upon sign-in for specific user roles under this tenant domain.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleSaveSiteSettings}
                      disabled={styleLoading}
                      style={{
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '9px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        opacity: styleLoading ? 0.7 : 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {styleLoading ? 'Saving...' : 'Save 2FA Policy'}
                    </button>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '12px',
                    marginTop: '6px'
                  }}>
                    {[
                      ...(isGlobalRole ? [{ role: 'GLOBAL_ADMIN', label: 'Global Administrators' }] : []),
                      { role: 'TENANT_ADMIN', label: 'Tenant Administrators' },
                      { role: 'OPERATOR', label: 'Operators & Staff' },
                      { role: 'AGENT', label: 'Agent Partners' },
                      { role: 'END_USER', label: 'End Users & Customers' }
                    ].map(({ role: r, label }) => {
                      const isChecked = Array.isArray(styleEnforce2faRoles) && styleEnforce2faRoles.includes(r);
                      return (
                        <label
                          key={r}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            background: isChecked ? 'rgba(99, 102, 241, 0.08)' : 'var(--card-bg)',
                            border: `1px solid ${isChecked ? 'var(--primary-color)' : 'var(--border-color)'}`,
                            borderRadius: '8px',
                            padding: '12px 14px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const current = Array.isArray(styleEnforce2faRoles) ? [...styleEnforce2faRoles] : [];
                              if (e.target.checked) {
                                if (!current.includes(r)) current.push(r);
                              } else {
                                const idx = current.indexOf(r);
                                if (idx > -1) current.splice(idx, 1);
                              }
                              setStyleEnforce2faRoles(current);
                            }}
                            style={{
                              width: '16px',
                              height: '16px',
                              accentColor: 'var(--primary-color)',
                              cursor: 'pointer'
                            }}
                          />
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                              {label}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-mute)', fontFamily: 'monospace' }}>
                              {r}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div style={{ fontSize: '11px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                    💡 <em>Note:</em> Individual user exemptions or per-user overrides configured by an administrator in the User Manager will take precedence over this tenant-wide role policy.
                  </div>

                  {!isGlobalRole && (
                    <div style={{ fontSize: '11px', color: 'var(--text-mute)', fontStyle: 'italic', marginTop: '2px' }}>
                      ℹ️ Tenant administrators can enforce 2FA for tenant users and customers. Global Administrator authentication is managed at the platform level.
                    </div>
                  )}
                </div>

                {/* 2. Gemini AI Integration Key */}
                <form
                  onSubmit={handleSaveSiteSettings}
                  style={{
                    background: 'var(--panel-color, rgba(255, 255, 255, 0.02))',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>✨</span>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)' }}>
                        Gemini AI Integration Key
                      </h4>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                        Configure the Google Gemini API key used for multi-tenant two-stage AI prompt engineering, design enhancements, and species recognition.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                      API Key (optional)
                    </label>
                    <input
                      type="password"
                      placeholder="Enter Gemini API key (starts with AIzaSy...)"
                      value={styleGeminiApiKey}
                      onChange={(e) => setStyleGeminiApiKey(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '14px',
                        fontFamily: 'monospace'
                      }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                      If supplied, the system will use this key for multi-tenant two-stage prompt engineering and depth map optimization. If empty, the system will fall back to the host environment key or the offline NLP rules engine.
                    </span>
                  </div>

                  <button
                    type="submit"
                    disabled={styleLoading}
                    style={{
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 18px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: 'fit-content',
                      opacity: styleLoading ? 0.7 : 1
                    }}
                  >
                    {styleLoading ? 'Saving...' : 'Save API Key'}
                  </button>
                </form>

                {/* 2. Global Admin: RFID & CoinVault Configuration */}
                {isGlobalRole && (
                  <div style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>📡</span>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)' }}>RFID URL Base Domain & CoinVault Settings</h4>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                          Global domain prepended to minted NFC chip target URLs (e.g. <code>https://tcoin.fyi/c/TOKEN</code>).
                        </p>
                      </div>
                    </div>

                    {rfidDomainSuccess && (
                      <div style={{ backgroundColor: 'rgba(56, 139, 253, 0.15)', border: '1px solid rgba(56, 139, 253, 0.4)', borderRadius: '6px', color: '#58a6ff', padding: '10px', fontSize: '13px' }}>
                        {rfidDomainSuccess}
                      </div>
                    )}
                    {rfidDomainError && (
                      <div style={{ backgroundColor: 'rgba(248, 81, 73, 0.15)', border: '1px solid rgba(248, 81, 73, 0.4)', borderRadius: '6px', color: '#ff7b72', padding: '10px', fontSize: '13px' }}>
                        {rfidDomainError}
                      </div>
                    )}

                    <form onSubmit={handleSaveRfidDomain} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1, minWidth: '280px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Base Domain (Default: https://tcoin.fyi)</label>
                        <input
                          type="text"
                          value={rfidBaseDomain}
                          onChange={(e) => setRfidBaseDomain(e.target.value)}
                          placeholder="https://tcoin.fyi"
                          style={{
                            backgroundColor: 'var(--input-bg)',
                            border: '1px solid var(--input-border)',
                            borderRadius: '6px',
                            padding: '10px',
                            color: 'var(--input-text)',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={rfidDomainLoading}
                        style={{
                          backgroundColor: 'var(--primary-color)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 18px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          height: '42px'
                        }}
                      >
                        {rfidDomainLoading ? 'Saving...' : 'Save Base Domain'}
                      </button>
                    </form>
                    <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                      💡 In local debug environments, requests from localhost automatically route to the local development server for instant mobile and desktop testing.
                    </div>
                  </div>
                )}

                {/* 3. Session API Bearer Token Card */}
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>🔑</span>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)' }}>API Bearer Authentication Token</h4>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                        Current session token for programmatic REST API access (included in <code>Authorization: Bearer &lt;token&gt;</code>).
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="password"
                      readOnly
                      value={token || sessionStorage.getItem('token') || localStorage.getItem('token') || ''}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--text-mute)',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        flex: 1,
                        minWidth: '260px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const activeToken = token || sessionStorage.getItem('token') || localStorage.getItem('token') || '';
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(activeToken).then(() => {
                            setStyleSuccess('Bearer token copied to clipboard!');
                          });
                        }
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-main)',
                        borderRadius: '6px',
                        padding: '10px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Copy Token
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Onboarding Settings */}
            {settingsTab === 'onboarding' && (
              <form
                onSubmit={handleSaveSiteSettings}
                style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px'
                }}
              >
                {/* Onboarding & Tutorial Video Configuration Card */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)' }}>
                          <polygon points="23 7 16 12 23 17 23 7" />
                          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                        </svg>
                        <span>Onboarding &amp; Tutorial Video</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px' }}>
                        Configure the branded video that explains to new users how to log coin data on the Form Wizard.
                      </div>
                    </div>
                  </div>

                  {/* Video Title */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                      Video Guide Title
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. How to Log Your Trophy Coin"
                      value={styleOnboardingVideoTitle}
                      onChange={(e) => setStyleOnboardingVideoTitle(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>

                  {/* Video Source (File / Asset / External URL) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                      Video Source URL or Asset
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        placeholder="e.g. /uploads/FormWizardFishermansTale.com.mp4 or YouTube / Vimeo link"
                        value={styleOnboardingVideoUrl}
                        onChange={(e) => setStyleOnboardingVideoUrl(e.target.value)}
                        style={{
                          flex: 1,
                          minWidth: '220px',
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '10px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setAssetSelectorTarget('video')}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          backgroundColor: 'var(--primary-color)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px 14px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="23 7 16 12 23 17 23 7" />
                          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                        </svg>
                        Choose Video Asset
                      </button>
                      {styleOnboardingVideoUrl && (
                        <button
                          type="button"
                          onClick={() => setStyleOnboardingVideoUrl('')}
                          style={{
                            backgroundColor: 'transparent',
                            border: '1px solid var(--border-color)',
                            color: 'var(--text-mute)',
                            borderRadius: '6px',
                            padding: '10px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                      Upload an MP4/WebM file via Asset Selector, or paste an unlisted YouTube or Vimeo URL.
                    </span>
                  </div>

                  {/* Direct Share Link Box */}
                  {styleOnboardingVideoUrl && (
                    <div style={{
                      backgroundColor: 'rgba(56, 139, 253, 0.08)',
                      border: '1px solid rgba(56, 139, 253, 0.25)',
                      borderRadius: '8px',
                      padding: '12px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--primary-color, #58a6ff)' }}>
                            Direct Video Tutorial Page Link:
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                            Encode this URL onto physical coins (NFC/QR) or share with users.
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              const origin = window.location.origin;
                              const directLink = isGlobalRole && selectedTenantId 
                                ? `${origin}/tutorial?tenant_id=${selectedTenantId}`
                                : `${origin}/tutorial`;
                              if (navigator.clipboard && navigator.clipboard.writeText) {
                                navigator.clipboard.writeText(directLink).then(() => {
                                  setStyleSuccess('Direct tutorial URL copied to clipboard!');
                                });
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid var(--primary-color)',
                              backgroundColor: 'var(--primary-color)',
                              color: '#fff',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            Copy Direct Link
                          </button>
                          <a
                            href={isGlobalRole && selectedTenantId ? `/tutorial?tenant_id=${selectedTenantId}` : '/tutorial'}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color)',
                              backgroundColor: 'var(--card-bg, #1e293b)',
                              color: 'var(--text-main)',
                              fontSize: '12px',
                              fontWeight: '500',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span>Preview Page</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </a>
                        </div>
                      </div>
                      <div style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.25)',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        color: 'var(--text-main)',
                        wordBreak: 'break-all'
                      }}>
                        {isGlobalRole && selectedTenantId ? `${window.location.origin}/tutorial?tenant_id=${selectedTenantId}` : `${window.location.origin}/tutorial`}
                      </div>
                    </div>
                  )}

                  {/* In-Settings Live Video Player Preview */}
                  {styleOnboardingVideoUrl && (
                    <div style={{
                      marginTop: '4px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid var(--border-color)',
                      backgroundColor: '#000',
                      maxWidth: '460px',
                      aspectRatio: '16/9'
                    }}>
                      {styleOnboardingVideoUrl.includes('youtube.com') || styleOnboardingVideoUrl.includes('youtu.be') ? (
                        <iframe
                          title="Tutorial Preview"
                          src={styleOnboardingVideoUrl}
                          style={{ width: '100%', height: '100%', border: 'none' }}
                          allowFullScreen
                        />
                      ) : (
                        <video
                          key={styleOnboardingVideoUrl.startsWith('/uploads') && API_BASE ? `${API_BASE.replace(/\/+$/, '')}${styleOnboardingVideoUrl}` : styleOnboardingVideoUrl}
                          src={styleOnboardingVideoUrl.startsWith('/uploads') && API_BASE ? `${API_BASE.replace(/\/+$/, '')}${styleOnboardingVideoUrl}` : styleOnboardingVideoUrl}
                          controls
                          playsInline
                          preload="metadata"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={styleLoading}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    width: 'fit-content',
                    marginTop: '10px',
                    opacity: styleLoading ? 0.7 : 1
                  }}
                >
                  {styleLoading ? 'Saving Site Settings...' : 'Save Site Settings'}
                </button>
              </form>
            )}

            {/* Tab 4: Mobile Web Studio */}
            {settingsTab === 'mobile' && (
              <MobileWebDesigner
                selectedTenantId={isGlobalRole ? selectedTenantId : (tenantConfig?.id?.toString() || user?.tenant_id?.toString())}
                isGlobalRole={isGlobalRole}
                tenantConfig={isGlobalRole && selectedTenantId ? (tenants.find(t => t.id.toString() === selectedTenantId) || tenantConfig) : tenantConfig}
                onStylesSaved={async (savedStyles) => {
                  if (refreshTheme) await refreshTheme();
                  if (isGlobalRole && selectedTenantId) {
                    setTenants(prev => prev.map(t => t.id.toString() === selectedTenantId ? { ...t, mobile_styles: savedStyles } : t));
                  }
                }}
              />
            )}
          </div>

            {/* Asset Selector Popup Modal */}
            {assetSelectorTarget && (
              <AssetSelectorModal
                isOpen={Boolean(assetSelectorTarget)}
                onClose={() => setAssetSelectorTarget(null)}
                assetType={assetSelectorTarget === 'video' ? 'video' : 'image'}
                title={
                  assetSelectorTarget === 'logo' ? 'Select Brand Logo' :
                  assetSelectorTarget === 'site_icon' ? 'Select Site Icon / Favicon' :
                  assetSelectorTarget === 'video' ? 'Select Onboarding & Tutorial Video' :
                  'Select Background Canvas Image'
                }
                currentValue={
                  assetSelectorTarget === 'logo' ? styleLogoUrl :
                  assetSelectorTarget === 'site_icon' ? styleSiteIconUrl :
                  assetSelectorTarget === 'video' ? styleOnboardingVideoUrl :
                  styleBackgroundUrl
                }
                tenantId={isGlobalRole ? selectedTenantId : (tenantConfig?.id || user?.tenant_id)}
                user={user}
                onSelect={(assetUrl) => {
                  if (assetSelectorTarget === 'logo') {
                    setStyleLogoUrl(assetUrl);
                    setStyleSuccess('Brand logo updated from assets!');
                  } else if (assetSelectorTarget === 'site_icon') {
                    setStyleSiteIconUrl(assetUrl);
                    setStyleSuccess('Site icon updated from assets!');
                  } else if (assetSelectorTarget === 'background') {
                    setStyleBackgroundUrl(assetUrl);
                    setStyleSuccess('Background canvas image updated from assets!');
                  } else if (assetSelectorTarget === 'video') {
                    setStyleOnboardingVideoUrl(assetUrl);
                    setStyleSuccess('Tutorial video updated from assets!');
                  }
                  setAssetSelectorTarget(null);
                }}
              />
            )}

            {/* Gradient Selector Popup Modal */}
            <GradientModal
              isOpen={gradientModalOpen}
              onClose={() => {
                setGradientModalOpen(false);
                setGradientModalTarget(null);
              }}
              title={getGradientModalTitle()}
              value={getGradientModalCurrentValue()}
              onApply={(gradientCss) => {
                handleApplyGradient(gradientCss);
                setStyleSuccess(`${getGradientModalTitle()} updated! Click 'Save Site Settings' to persist.`);
              }}
            />
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center',
            gap: '16px'
          }}>
            <div style={{ fontSize: '48px' }}>🔒</div>
            <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>Access Restricted</h3>
            <p style={{ margin: 0, color: 'var(--text-mute)', maxWidth: '400px', fontSize: '14px' }}>
              You do not have administrative permissions to configure tenant site settings and branding.
            </p>
            <button
              onClick={() => setActiveTab('overview')}
              style={{
                backgroundColor: 'var(--primary-color)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Back to Dashboard
            </button>
          </div>
        ))}

        {/* Tab Content: 3. My Profile Settings Tab */}
        {activeTab === 'profile' && currentUserProfile && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '20px',
            }}>
              <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', color: 'var(--primary-color)' }}>
                My Profile & Account
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-mute)' }}>
                Manage your personal profile details, contact information, billing address, and security credentials. Email: <strong>{currentUserProfile.email}</strong>.
              </p>
            </div>

            {profError && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.4)',
                borderRadius: '6px',
                color: '#ff7b72',
                padding: '12px',
                fontSize: '13px'
              }}>
                <strong>Error:</strong> {profError}
              </div>
            )}

            {profSuccessMsg && (
              <div style={{
                backgroundColor: 'rgba(46, 160, 67, 0.15)',
                border: '1px solid rgba(46, 160, 67, 0.4)',
                borderRadius: '6px',
                color: '#3fb950',
                padding: '12px',
                fontSize: '13px'
              }}>
                {profSuccessMsg}
              </div>
            )}

            {/* Profile update form */}
            <form onSubmit={handleSaveProfile} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: isMobile ? '15px' : '24px',
            }}>
              
              <div style={{ display: 'flex', gap: '15px', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>First Name</label>
                  <input
                    type="text"
                    value={profFirstName}
                    onChange={(e) => setProfFirstName(e.target.value)}
                    required
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Last Name</label>
                  <input
                    type="text"
                    value={profLastName}
                    onChange={(e) => setProfLastName(e.target.value)}
                    required
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Phone Number</label>
                  <input
                    type="text"
                    value={profPhone}
                    onChange={(e) => setProfPhone(e.target.value)}
                    placeholder="e.g. +1 555-0199"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Company Name</label>
                  <input
                    type="text"
                    value={profCompany}
                    onChange={(e) => setProfCompany(e.target.value)}
                    placeholder="e.g. Acme Corp"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              {/* Password resetting block: Only render if user has a password hash */}
              <div style={{ 
                borderTop: '1px solid var(--border-color)', 
                paddingTop: '15px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-mute)' }}>
                  Security Settings
                </h4>
                
                {currentUserProfile.has_password ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxWidth: '300px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>Change Password</label>
                    <input
                      type="password"
                      value={profPassword}
                      onChange={(e) => setProfPassword(e.target.value)}
                      placeholder="Leave blank to keep same"
                      minLength={8}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(56, 139, 253, 0.15)', color: '#58a6ff', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
                    🛡️ Managed Login (Authenticated via Google/Apple). Password updates disabled.
                  </div>
                )}
              </div>

              {/* Billing address parameters */}
              <div style={{ 
                borderTop: '1px solid var(--border-color)', 
                paddingTop: '15px', 
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-mute)' }}>
                  Billing Address Info
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Billing Address</label>
                  <input
                    type="text"
                    value={profAddress}
                    onChange={(e) => setProfAddress(e.target.value)}
                    placeholder="Street name, Suite/Dept"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>City</label>
                    <input
                      type="text"
                      value={profCity}
                      onChange={(e) => setProfCity(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>State / Province</label>
                    <input
                      type="text"
                      value={profState}
                      onChange={(e) => setProfState(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>ZIP / Postal Code</label>
                    <input
                      type="text"
                      value={profZip}
                      onChange={(e) => setProfZip(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>Country</label>
                    <input
                      type="text"
                      value={profCountry}
                      onChange={(e) => setProfCountry(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '10px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={profLoading}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  alignSelf: 'flex-start',
                  marginTop: '10px',
                  opacity: profLoading ? 0.7 : 1
                }}
              >
                {profLoading ? 'Saving Settings...' : 'Save Profile Settings'}
              </button>
            </form>

            {/* Two-Factor Authentication (2FA) User Security Card */}
            <div style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: isMobile ? '16px' : '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(99, 102, 241, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}>
                    🛡️
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)', fontWeight: '700' }}>
                      Two-Factor Authentication (2FA)
                    </h4>
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                      Protect your account by requiring a random 6-digit security code sent to your email on every sign in.
                    </p>
                  </div>
                </div>

                {/* Status Badge */}
                {currentUserProfile.two_factor_override === 'enforce' ? (
                  <span style={{
                    backgroundColor: 'rgba(168, 85, 247, 0.15)',
                    border: '1px solid rgba(168, 85, 247, 0.4)',
                    color: '#a855f7',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    🛡️ Enforced by Administrator
                  </span>
                ) : currentUserProfile.two_factor_override === 'exempt' ? (
                  <span style={{
                    backgroundColor: 'rgba(234, 179, 8, 0.15)',
                    border: '1px solid rgba(234, 179, 8, 0.4)',
                    color: '#eab308',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    ⚠️ Exempted by Administrator
                  </span>
                ) : currentUserProfile.is_2fa_enforced ? (
                  <span style={{
                    backgroundColor: 'rgba(56, 139, 253, 0.15)',
                    border: '1px solid rgba(56, 139, 253, 0.4)',
                    color: '#58a6ff',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    🛡️ Enforced by Tenant Policy
                  </span>
                ) : currentUserProfile.two_factor_enabled ? (
                  <span style={{
                    backgroundColor: 'rgba(46, 160, 67, 0.15)',
                    border: '1px solid rgba(46, 160, 67, 0.4)',
                    color: '#3fb950',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    ✅ Enabled
                  </span>
                ) : (
                  <span style={{
                    backgroundColor: 'rgba(107, 114, 128, 0.12)',
                    border: '1px solid rgba(107, 114, 128, 0.3)',
                    color: 'var(--text-mute)',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Disabled
                  </span>
                )}
              </div>

              <div style={{
                backgroundColor: 'var(--panel-color, rgba(255,255,255,0.03))',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '14px 16px',
                fontSize: '13px',
                color: 'var(--text-mute)',
                lineHeight: '1.5'
              }}>
                {currentUserProfile.two_factor_override === 'enforce' ? (
                  <span>An administrator has marked two-factor authentication as mandatory for your individual user account. This security policy cannot be disabled self-service.</span>
                ) : currentUserProfile.two_factor_override === 'exempt' ? (
                  <span>An administrator has exempted your account from platform two-factor authentication requirements. You can optionally re-enable it for your personal protection below.</span>
                ) : currentUserProfile.is_2fa_enforced ? (
                  <span>Your organization or tenant role (<strong>{currentUserProfile.role}</strong>) requires two-factor authentication on every login per tenant security policy.</span>
                ) : (
                  <span>When enabled, entering your email and password will prompt a temporary 6-digit one-time PIN sent directly to <strong>{currentUserProfile.email}</strong> before granting portal access.</span>
                )}
              </div>

              {/* Action Button */}
              {(!currentUserProfile.is_2fa_enforced || currentUserProfile.two_factor_override === 'exempt') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => handleToggleMy2FA(!currentUserProfile.two_factor_enabled)}
                    disabled={profTwoFactorLoading}
                    style={{
                      backgroundColor: currentUserProfile.two_factor_enabled ? 'rgba(248, 81, 73, 0.15)' : 'var(--primary-color)',
                      color: currentUserProfile.two_factor_enabled ? '#ff7b72' : '#ffffff',
                      border: currentUserProfile.two_factor_enabled ? '1px solid rgba(248, 81, 73, 0.4)' : 'none',
                      borderRadius: '6px',
                      padding: '10px 18px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: profTwoFactorLoading ? 'not-allowed' : 'pointer',
                      opacity: profTwoFactorLoading ? 0.7 : 1,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {profTwoFactorLoading 
                      ? 'Updating 2FA...' 
                      : currentUserProfile.two_factor_enabled 
                      ? 'Disable Two-Factor Authentication' 
                      : 'Enable Two-Factor Authentication'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'support' && (
          <SupportPage
            user={user}
            tenantConfig={tenantConfig}
            setActiveTab={setActiveTab}
            setView={setView}
          />
        )}

        </div>

        {/* Footer inside scrollable content section */}
        <div style={{ marginTop: 'auto', paddingTop: '24px', margin: isMobile ? '24px -10px -12px -10px' : '30px -30px -24px -30px' }}>
          <Footer />
        </div>

      </main>

      {/* Create New Tenant Modal Dialog (Global Admin) */}
      {isCreateTenantModalOpen && (
        <div data-testid="create-tenant-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '16px',
          boxSizing: 'border-box',
          backdropFilter: 'blur(3px)'
        }}>
          <div style={{
            background: 'var(--panel-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '14px',
            maxWidth: '560px',
            width: '100%',
            padding: '26px',
            boxShadow: 'var(--card-shadow)',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid var(--border-color)',
              paddingBottom: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '22px' }}>🏛️</span>
                <div>
                  <h3 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '18px', fontWeight: '700' }}>
                    Create New Tenant Domain
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                    Provision a new white-label tenant domain with custom branding.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateTenantModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-mute)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>

            {/* Error Message Alert */}
            {createTenantError && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.4)',
                borderRadius: '6px',
                color: '#ff7b72',
                padding: '12px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>⚠️</span>
                <span>{createTenantError}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleCreateTenant} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                  Domain Name <span style={{ color: '#ff7b72' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  data-testid="create-tenant-domain-input"
                  placeholder="e.g. fishermanstale.com or custombrand.local"
                  value={newTenantDomain}
                  onChange={(e) => setNewTenantDomain(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: 'var(--input-text)',
                    fontSize: '14px'
                  }}
                />
                <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                  Requests with this HTTP Host header will automatically resolve to this tenant.
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                  Brand / Organization Name <span style={{ color: '#ff7b72' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  data-testid="create-tenant-brand-input"
                  placeholder="e.g. Fisherman's Tale"
                  value={newTenantBrand}
                  onChange={(e) => setNewTenantBrand(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: 'var(--input-text)',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Colors */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Primary Color</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={newTenantPrimaryColor}
                      onChange={(e) => setNewTenantPrimaryColor(e.target.value)}
                      style={{ width: '38px', height: '38px', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer', background: 'transparent' }}
                    />
                    <input
                      type="text"
                      value={newTenantPrimaryColor}
                      onChange={(e) => setNewTenantPrimaryColor(e.target.value)}
                      style={{
                        flex: 1,
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Accent Color</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={newTenantAccentColor}
                      onChange={(e) => setNewTenantAccentColor(e.target.value)}
                      style={{ width: '38px', height: '38px', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer', background: 'transparent' }}
                    />
                    <input
                      type="text"
                      value={newTenantAccentColor}
                      onChange={(e) => setNewTenantAccentColor(e.target.value)}
                      style={{
                        flex: 1,
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Optional Logo URL */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Logo URL (Optional)</label>
                <input
                  type="text"
                  placeholder="https://... or upload later in tenant settings"
                  value={newTenantLogoUrl}
                  onChange={(e) => setNewTenantLogoUrl(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: 'var(--input-text)',
                    fontSize: '13px'
                  }}
                />
              </div>

              {/* Optional No-Reply Email */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>No-Reply Sender Email (Optional)</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Default: no-reply@{newTenantDomain || 'domain'}</span>
                </label>
                <input
                  type="email"
                  data-testid="create-tenant-noreply-email"
                  placeholder={`no-reply@${newTenantDomain || 'tenantdomain.com'}`}
                  value={newTenantNoreplyEmail}
                  onChange={(e) => setNewTenantNoreplyEmail(e.target.value)}
                  style={{
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: 'var(--input-text)',
                    fontSize: '13px'
                  }}
                />
              </div>

              {/* Optional Initial Admin Provisioning */}
              <div style={{
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '14px',
                backgroundColor: 'var(--card-bg)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                  <input
                    type="checkbox"
                    data-testid="create-tenant-admin-checkbox"
                    checked={newTenantCreateAdmin}
                    onChange={(e) => setNewTenantCreateAdmin(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Provision initial Tenant Administrator account</span>
                </label>

                {newTenantCreateAdmin && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-mute)' }}>Admin Email Address</label>
                      <input
                        type="email"
                        data-testid="create-tenant-admin-email"
                        required={newTenantCreateAdmin}
                        placeholder="admin@tenantdomain.com"
                        value={newTenantAdminEmail}
                        onChange={(e) => setNewTenantAdminEmail(e.target.value)}
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-mute)' }}>Initial Admin Password (min 8 chars)</label>
                      <input
                        type="password"
                        data-testid="create-tenant-admin-password"
                        required={newTenantCreateAdmin}
                        placeholder="••••••••"
                        value={newTenantAdminPassword}
                        onChange={(e) => setNewTenantAdminPassword(e.target.value)}
                        style={{
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                borderTop: '1px solid var(--border-color)',
                paddingTop: '14px'
              }}>
                <button
                  type="button"
                  onClick={() => setIsCreateTenantModalOpen(false)}
                  disabled={createTenantLoading}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-main)',
                    borderRadius: '6px',
                    padding: '9px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="create-tenant-submit-btn"
                  disabled={createTenantLoading}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'var(--btn-primary-text, #fff)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '9px 20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: createTenantLoading ? 0.7 : 1
                  }}
                >
                  {createTenantLoading ? 'Creating Tenant...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Test No-Reply Email Modal Dialog */}
      {isTestEmailModalOpen && (
        <div data-testid="test-email-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '16px',
          boxSizing: 'border-box',
          backdropFilter: 'blur(3px)'
        }}>
          <div style={{
            background: 'var(--panel-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '14px',
            maxWidth: '520px',
            width: '100%',
            padding: '24px',
            boxShadow: 'var(--card-shadow)',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid var(--border-color)',
              paddingBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '22px' }}>✉️</span>
                <div>
                  <h3 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '17px', fontWeight: '700' }}>
                    Test No-Reply Email Configuration
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-mute)' }}>
                    Verify claim verification PIN email delivery and sender address.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsTestEmailModalOpen(false);
                  setTestEmailError('');
                  setTestEmailResult(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-mute)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>

            {/* Error Alert */}
            {testEmailError && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.4)',
                borderRadius: '6px',
                color: '#ff7b72',
                padding: '10px 14px',
                fontSize: '13px'
              }}>
                {testEmailError}
              </div>
            )}

            {/* Success / Status Alert */}
            {testEmailResult && (
              <div style={{
                backgroundColor: testEmailResult.delivered
                  ? 'rgba(34, 197, 94, 0.15)'
                  : (testEmailResult.is_simulated ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.15)'),
                border: `1px solid ${testEmailResult.delivered ? 'rgba(34, 197, 94, 0.4)' : (testEmailResult.is_simulated ? 'rgba(234, 179, 8, 0.4)' : 'rgba(239, 68, 68, 0.4)')}`,
                borderRadius: '6px',
                color: testEmailResult.delivered ? '#4ade80' : (testEmailResult.is_simulated ? '#facc15' : '#f87171'),
                padding: '12px 14px',
                fontSize: '13px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}>
                  <span>{testEmailResult.delivered ? '✅ Real Email Dispatched via SMTP!' : (testEmailResult.is_simulated ? '⚠️ Simulation Mode (No SMTP Relay Configured)' : '❌ SMTP Delivery Failed')}</span>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-main)', opacity: 0.9 }}>
                  <div><strong>From:</strong> {testEmailResult.sender_email}</div>
                  <div><strong>To:</strong> {testEmailResult.recipient_email}</div>
                  <div><strong>Sample Verification PIN:</strong> <code style={{ backgroundColor: 'rgba(0, 0, 0, 0.25)', padding: '2px 6px', borderRadius: '4px' }}>{testEmailResult.sample_token}</code></div>
                  <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--text-mute)', borderTop: '1px solid var(--border-color)', paddingTop: '6px' }}>
                    {testEmailResult.message}
                  </div>
                </div>
              </div>
            )}

            {/* Configured Sender Display */}
            <div style={{
              backgroundColor: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-mute)', fontWeight: '600' }}>
                Sender Address (From)
              </span>
              <span style={{ fontSize: '14px', fontFamily: 'monospace', color: 'var(--text-main)', fontWeight: '600' }}>
                {(styleNoreplyEmail && styleNoreplyEmail.trim()) || `no-reply@${((isGlobalRole ? tenants.find(t => t.id.toString() === selectedTenantId)?.domain_name : tenantConfig?.domain_name) || 'tenantdomain.com')}`}
              </span>
              <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                {styleNoreplyEmail && styleNoreplyEmail.trim() ? 'Custom tenant no-reply address' : 'Default tenant fallback (no-reply@domain)'}
              </span>
            </div>

            {/* Recipient Input */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                Recipient Email Address (To)
              </label>
              <input
                type="email"
                required
                data-testid="test-email-recipient-input"
                placeholder="youremail@example.com"
                value={testEmailRecipient}
                onChange={(e) => setTestEmailRecipient(e.target.value)}
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  color: 'var(--input-text)',
                  fontSize: '14px'
                }}
              />
              <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                Defaults to your logged-in administrator email.
              </span>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              borderTop: '1px solid var(--border-color)',
              paddingTop: '14px',
              marginTop: '4px'
            }}>
              <button
                type="button"
                onClick={() => {
                  setIsTestEmailModalOpen(false);
                  setTestEmailError('');
                  setTestEmailResult(null);
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-mute)',
                  borderRadius: '6px',
                  padding: '9px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                type="button"
                data-testid="confirm-send-test-email-btn"
                disabled={testEmailLoading || !testEmailRecipient.trim()}
                onClick={handleSendTestNoreplyEmail}
                style={{
                  backgroundColor: 'var(--primary-color)',
                  color: 'var(--btn-primary-text, #fff)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '9px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: (testEmailLoading || !testEmailRecipient.trim()) ? 0.7 : 1
                }}
              >
                {testEmailLoading ? 'Sending Test Email...' : 'Send Test Verification Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Center Edit User Modal Dialog (Profile Details + Billing Info) */}
      {editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '12px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            background: 'var(--panel-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '14px',
            maxWidth: '520px',
            width: '100%',
            padding: '24px',
            boxShadow: 'var(--card-shadow)',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              borderBottom: '1px solid var(--border-color)', 
              paddingBottom: '12px' 
            }}>
              <h3 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '18px', fontWeight: '700' }}>
                Edit Workspace Details
              </h3>
              <button 
                onClick={() => setEditingUser(null)}
                aria-label="Close modal"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-mute)',
                  fontSize: '22px',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: '1'
                }}
              >
                &times;
              </button>
            </div>

            <p style={{ color: 'var(--text-mute)', fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
              Updating details & security credentials resolved for <strong>{editingUser.email}</strong>.
            </p>

            {editError && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.4)',
                borderRadius: '6px',
                color: '#ff7b72',
                padding: '10px',
                fontSize: '13px'
              }}>
                <strong>Error:</strong> {editError}
              </div>
            )}

            {editSuccess && (
              <div style={{
                backgroundColor: 'rgba(46, 160, 67, 0.15)',
                border: '1px solid rgba(46, 160, 67, 0.4)',
                borderRadius: '6px',
                color: '#3fb950',
                padding: '10px',
                fontSize: '13px'
              }}>
                {editSuccess}
              </div>
            )}

            <form onSubmit={handleUpdateUser} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Profile Details Block */}
              <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>First Name</label>
                  <input
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    required
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Last Name</label>
                  <input
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    required
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              {/* Role & Security settings */}
              <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Role Clearance</label>
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    disabled={editingUser.id === user.id} // Cannot change own role to prevent self lock-out
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  >
                    {isGlobalRole ? (
                      <>
                        <option value="GLOBAL_ADMIN">Global Admin</option>
                        <option value="GLOBAL_POWER">Global Power User</option>
                        <option value="GLOBAL_STANDARD">Global Standard User</option>
                        <option value="TENANT_ADMIN">Tenant Admin</option>
                        <option value="OPERATOR">Tenant Operator</option>
                        <option value="AGENT">Tenant Agent</option>
                        <option value="END_USER">End User</option>
                      </>
                    ) : (
                      <>
                        <option value="TENANT_ADMIN">Tenant Admin</option>
                        <option value="OPERATOR">Tenant Operator</option>
                        <option value="AGENT">Tenant Agent</option>
                        <option value="END_USER">End User</option>
                      </>
                    )}
                  </select>
                </div>
                
                {/* Reset User Password Input: Only show if target user has a password hash */}
                {editingUser.has_password ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>Reset Password</label>
                    <input
                      type="password"
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      placeholder="Leave blank to keep same"
                      minLength={8}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-mute)', fontStyle: 'italic', marginTop: '16px' }}>
                      OAuth Managed Login (Google/Apple)
                    </span>
                  </div>
                )}
              </div>

              {/* 2FA Security Policy Override */}
              {editingUser?.role && ['GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD'].includes(editingUser.role) && !isGlobalRole ? (
                <div style={{
                  backgroundColor: 'rgba(234, 179, 8, 0.08)',
                  border: '1px solid rgba(234, 179, 8, 0.3)',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  fontSize: '12px',
                  color: 'var(--text-mute)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>🛡️</span>
                  <span>Global user authentication policy is managed at the platform level and cannot be modified by tenant administrators.</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>
                    Two-Factor Authentication (2FA) Policy Override
                  </label>
                  <select
                    value={editTwoFactorOverride}
                    onChange={(e) => setEditTwoFactorOverride(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  >
                    <option value="">Default (Follows Tenant Role Policy & User Choice)</option>
                    <option value="enforce">Enforce 2FA (Require 6-digit email PIN on every login)</option>
                    <option value="exempt">Exempt from 2FA (Bypass 2FA requirements)</option>
                  </select>
                  <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                    Administrators can enforce 2FA for this specific user or grant an exemption from tenant-wide policy.
                  </span>
                </div>
              )}

              {/* Tenant Assignment (Global Admins only) */}
              {isGlobalRole && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)' }}>Tenant Assignment</label>
                  <select
                    value={editTenantId}
                    onChange={(e) => setEditTenantId(e.target.value)}
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  >
                    <option value="GLOBAL">Global / Unscoped (Cross-Tenant Access)</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id.toString()}>{t.brand_name} ({t.domain_name})</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                    Reassign this user to a specific tenant brand (required for Operators and Tenant Admins).
                  </span>
                </div>
              )}

              {/* Company & Contact Details */}
              <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Phone Number</label>
                  <input
                    type="text"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="e.g. +1 555-0199"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Company Name</label>
                  <input
                    type="text"
                    value={editCompany}
                    onChange={(e) => setEditCompany(e.target.value)}
                    placeholder="e.g. Acme Corp"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              {/* Billing Information Section */}
              <div style={{ 
                borderTop: '1px solid var(--border-color)', 
                paddingTop: '15px', 
                marginTop: '5px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-mute)' }}>
                  Billing Address details
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Billing Address</label>
                  <input
                    type="text"
                    value={editAddress}
                    onChange={(e) => setEditAddress(e.target.value)}
                    placeholder="Street name, Suite/Dept"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: '6px',
                      padding: '8px',
                      color: 'var(--input-text)',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>City</label>
                    <input
                      type="text"
                      value={editCity}
                      onChange={(e) => setEditCity(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>State / Province</label>
                    <input
                      type="text"
                      value={editState}
                      onChange={(e) => setEditState(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>ZIP / Postal Code</label>
                    <input
                      type="text"
                      value={editZip}
                      onChange={(e) => setEditZip(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>Country</label>
                    <input
                      type="text"
                      value={editCountry}
                      onChange={(e) => setEditCountry(e.target.value)}
                      style={{
                        backgroundColor: 'var(--input-bg)',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        padding: '8px',
                        color: 'var(--input-text)',
                        fontSize: '13px'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                borderTop: '1px solid var(--border-color)',
                paddingTop: '15px',
                marginTop: '10px'
              }}>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-main)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    opacity: editLoading ? '0.7' : '1'
                  }}
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dedicated Reset Password Modal Dialog */}
      {resetPasswordUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001,
          padding: '12px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            background: 'var(--panel-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '14px',
            maxWidth: '460px',
            width: '100%',
            padding: '24px',
            boxShadow: 'var(--card-shadow)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              borderBottom: '1px solid var(--border-color)', 
              paddingBottom: '12px' 
            }}>
              <h3 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '18px', fontWeight: '700' }}>
                Reset User Password
              </h3>
              <button 
                onClick={() => setResetPasswordUser(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-mute)',
                  fontSize: '22px',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: '1'
                }}
              >
                &times;
              </button>
            </div>

            {/* Target User Details Badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '10px 14px'
            }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {resetPasswordUser.first_name ? `${resetPasswordUser.first_name} ${resetPasswordUser.last_name || ''}` : resetPasswordUser.email}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-mute)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {resetPasswordUser.email}
                </div>
              </div>
              <span style={{
                fontSize: '11px',
                fontWeight: '700',
                padding: '3px 8px',
                borderRadius: '12px',
                backgroundColor: 'rgba(37, 99, 235, 0.15)',
                color: 'var(--primary-color)',
                flexShrink: 0
              }}>
                {resetPasswordUser.role}
              </span>
            </div>

            {/* Mode Switcher Tabs */}
            <div style={{
              display: 'flex',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              padding: '3px',
              gap: '4px'
            }}>
              <button
                type="button"
                onClick={() => setResetMode('auto_email')}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: resetMode === 'auto_email' ? 'var(--primary-color)' : 'transparent',
                  color: resetMode === 'auto_email' ? '#ffffff' : 'var(--text-mute)',
                  transition: 'all 0.2s ease'
                }}
              >
                🛡️ Auto-Gen & Email
              </button>
              <button
                type="button"
                onClick={() => setResetMode('manual')}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: resetMode === 'manual' ? 'var(--primary-color)' : 'transparent',
                  color: resetMode === 'manual' ? '#ffffff' : 'var(--text-mute)',
                  transition: 'all 0.2s ease'
                }}
              >
                ✏️ Manual Entry
              </button>
            </div>

            {resetError && (
              <div style={{
                backgroundColor: 'rgba(248, 81, 73, 0.15)',
                border: '1px solid rgba(248, 81, 73, 0.4)',
                borderRadius: '6px',
                color: '#ff7b72',
                padding: '10px 12px',
                fontSize: '13px'
              }}>
                <strong>Error:</strong> {resetError}
              </div>
            )}

            {resetSuccess && (
              <div style={{
                backgroundColor: 'rgba(46, 160, 67, 0.15)',
                border: '1px solid rgba(46, 160, 67, 0.4)',
                borderRadius: '6px',
                color: '#3fb950',
                padding: '10px 12px',
                fontSize: '13px',
                lineHeight: '1.4'
              }}>
                ✓ {resetSuccess}
              </div>
            )}

            {/* Revealed Password Box (if admin requested to see it) */}
            {resetRevealedPassword && (
              <div style={{
                backgroundColor: 'rgba(37, 99, 235, 0.1)',
                border: '1px solid rgba(37, 99, 235, 0.3)',
                borderRadius: '8px',
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-color)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Generated Password (Copy Now)
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <code style={{
                    flex: 1,
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    padding: '8px 10px',
                    backgroundColor: 'var(--input-bg)',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    color: 'var(--text-main)',
                    wordBreak: 'break-all'
                  }}>
                    {resetRevealedPassword}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(resetRevealedPassword);
                      setResetCopied(true);
                      setTimeout(() => setResetCopied(false), 2000);
                    }}
                    style={{
                      backgroundColor: resetCopied ? '#10b981' : 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 14px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    {resetCopied ? '✓ Copied' : '📋 Copy'}
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleResetUserPassword} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {resetMode === 'auto_email' ? (
                <>
                  {/* Security Highlights Banner */}
                  <div style={{
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    border: '1px solid rgba(37, 99, 235, 0.25)',
                    borderRadius: '10px',
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--primary-color)', fontWeight: '700', fontSize: '12px' }}>
                        🔒 Extremely Secure Cryptographic Password
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: '700', backgroundColor: 'rgba(37, 99, 235, 0.2)', color: 'var(--primary-color)', padding: '2px 8px', borderRadius: '10px' }}>
                        24 Characters
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-mute)', lineHeight: '1.4' }}>
                      Generates high-entropy cryptographic credentials (A-Z, a-z, 0-9, symbols) and sends an official branded sign-in email to <strong>{resetPasswordUser.email}</strong>.
                    </div>
                  </div>

                  {/* Zero-Knowledge Confidentiality Checkbox */}
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    cursor: 'pointer',
                    backgroundColor: resetHidePassword ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                    border: `1px solid ${resetHidePassword ? 'rgba(16, 185, 129, 0.3)' : 'var(--border-color)'}`,
                    borderRadius: '8px',
                    padding: '10px 12px'
                  }}>
                    <input
                      type="checkbox"
                      checked={resetHidePassword}
                      onChange={(e) => setResetHidePassword(e.target.checked)}
                      style={{ marginTop: '3px', cursor: 'pointer' }}
                    />
                    <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                      <div style={{ fontWeight: '600', color: resetHidePassword ? '#10b981' : 'var(--text-main)' }}>
                        🛡️ Confidential: Keep password hidden from administrator
                      </div>
                      <div style={{ color: 'var(--text-mute)', fontSize: '11px', marginTop: '2px' }}>
                        {resetHidePassword
                          ? "The generated password is dispatched directly to the user's inbox and will never be revealed on screen or saved in logs."
                          : "The generated password will be emailed to the user AND displayed to you once in this window."}
                      </div>
                    </div>
                  </label>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600' }}>New Password</label>
                      <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Min 8 chars</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={newResetPassword}
                        onChange={(e) => setNewResetPassword(e.target.value)}
                        required={resetMode === 'manual'}
                        minLength={8}
                        placeholder="Enter a new secure password"
                        style={{
                          flex: 1,
                          backgroundColor: 'var(--input-bg)',
                          border: '1px solid var(--input-border)',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          color: 'var(--input-text)',
                          fontSize: '13px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={generateRandomPassword}
                        title="Generate 24-character cryptographic password"
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid var(--border-color)',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          color: 'var(--text-main)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        🎲 Auto Gen
                      </button>
                    </div>
                  </div>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: 'var(--text-main)'
                  }}>
                    <input
                      type="checkbox"
                      checked={resetSendEmail}
                      onChange={(e) => setResetSendEmail(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Send password notification email to <strong>{resetPasswordUser.email}</strong></span>
                  </label>
                </>
              )}

              {/* Action buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                borderTop: '1px solid var(--border-color)',
                paddingTop: '15px',
                marginTop: '6px'
              }}>
                <button
                  type="button"
                  onClick={() => setResetPasswordUser(null)}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: 'var(--text-main)',
                    cursor: 'pointer'
                  }}
                >
                  {resetRevealedPassword ? 'Done' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={resetLoading}
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 22px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    opacity: resetLoading ? '0.7' : '1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {resetLoading
                    ? (resetMode === 'auto_email' ? 'Generating & Emailing...' : 'Resetting...')
                    : (resetMode === 'auto_email' ? '✉️ Generate & Email Password' : 'Reset Password')}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
}
